import { CommercialReplyFailureNotice, type CommercialReplyFailure, type ReplyTemplateRevision, replyFailureAnchor, templateReplyNeedsCorrection } from "../components/CommercialReplyFailureNotice";
import { ApprovalRecipientCard, type RecipientDetails } from "../components/ApprovalRecipientCard";
import { campaignTemplateFlow, templatesInFlowOrder, initialTemplateCompatible } from "../lib/campaign-template-flow";
import { Fragment, type ComponentProps, useEffect, useMemo, useRef, useState } from "react";
import { RunProblemNotice } from "../components/RunProblemNotice";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ArrowRight,
  Bold,
  Bot,
  Building2,
  CalendarPlus,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Clock3,
  ExternalLink,
  Eye,
  Download,
  FileText,
  IndentDecrease,
  IndentIncrease,
  Italic,
  KeyRound,
  LockKeyhole,
  List,
  ListOrdered,
  Link2,
  LoaderCircle,
  Mail,
  PackageOpen,
  Paperclip,
  Pause,
  Palette,
  Play,
  Pencil,
  Plus,
  Quote,
  RefreshCcw,
  Redo2,
  RemoveFormatting,
  Save,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Strikethrough,
  Target,
  Trash2,
  Underline,
  Undo2,
  Upload,
  Users,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  campaignEmailTemplateTypes,
  campaignCompanyCapacity,
  campaignReadinessIssues,
  createCampaignSchema,
  createOfferSchema,
  commonHunterIndustries,
  hunterHeadcountOptions,
  hunterIndustries,
  hunterIndustrySpanishLabel,
  isValidIanaTimeZone,
  normalizeHunterIndustries,
  supportedIanaTimeZones,
  terminalRunStatuses,
  utcToZonedDateTimeLocal,
  zonedDateTimeLocalToUtc,
  type CampaignConfig,
  type CampaignEmailTemplateType,
  type HunterHeadcount,
  type HunterIndustry,
} from "@vicnet/shared";
import { BrandIcon } from "../components/BrandIcon";
import { BrandLogo } from "../components/BrandLogo";
import { StatusBadge, presentationText, statusText } from "../components/StatusBadge";
import { PdfFilePicker } from "../components/PdfFilePicker";
import { WeekdayPicker, weekdayNames } from "../components/WeekdayPicker";
import { CampaignTabs, type CampaignTabItem } from "../components/CampaignTabs";
import { api, ApiError, formatDate } from "../lib/api";
import { hermesCodexConnectionRequired } from "../lib/hermes-onboarding";
import { formCopy, linesFromText, validationFieldMap, validationSummary, visibleErrorMessage } from "../lib/form-copy";
import type { MeetingTeamMember } from "./MeetingTeamPage";

type Campaign = {
  id: string;
  configVersion?: string;
  offerId?: string | null;
  name: string;
  description: string;
  status: string;
  config: CampaignConfig;
  createdAt: string;
  updatedAt: string;
  importSummary?: CampaignImportSummary;
  offer?: Offer | null;
  meetingTeamMembers?: MeetingTeamMember[];
  closerAdminId?: string | null;
  closerWorkflowEnabled?: boolean;
  closerAdmin?: { id: string; displayName: string; username: string; role: string } | null;
  _count?: {
    runs: number;
    companies: number;
    contacts: number;
    approvals: number;
  };
  runs?: Run[];
  contactProgress?: {
    policyVersion: string;
    target: number;
    historicalTotal: number;
    validForReferenceRun: number;
    coveredTotal: number;
    remaining: number;
    referenceRunId: string | null;
    referenceRunStatus: string | null;
    activeRunId: string | null;
    activeRunStatus: string | null;
  };
  contactResolution?: {
    runId: string | null;
    target: number;
    items: Array<{
      companyId: string;
      company: string;
      domain: string | null;
      status: "SELECTED" | "NO_VERIFIED_CONTACT" | "NOT_EVALUATED";
      selectedContact: null | { id: string; firstName: string; lastName: string; role: string; email: string | null };
      candidatesEvaluated: number;
      rejectedCandidates: Array<{ id: string; firstName: string; lastName: string; role: string; email: string | null; reason: string; confidence: number }>;
    }>;
  };
  companies?: Array<{
    id: string;
    score: number;
    rationale: string;
    status?: string;
    accepted?: boolean;
    rejectionReason?: string | null;
    evidenceJson?: string;
    scoreBreakdownJson?: string;
    company: Company;
  }>;
  contacts?: Array<{
    id: string;
    score: number;
    scoreBreakdownJson?: string;
    qualificationReason?: string | null;
    status?: string;
    reuseEligibility?: {
      eligibleInitiallyForNewRun: boolean;
      exclusion: null | {
        reason: string;
        reasonCode: string;
        scope: string;
        excludedAt: string;
        allowsReinstatementWithNewEvidence: boolean;
      };
    };
    contact: Contact & { company: Company };
  }>;
  messages?: Message[];
  approvals?: Approval[];
  meetings?: Array<{
    id: string;
    title: string;
    startsAt: string;
    status: string;
  }>;
  meetingIntents?: Array<{
    id: string;
    status: string;
    note?: string;
    contact: Contact;
  }>;
};

type CampaignImportRow = {
  id: string;
  rowNumber: number;
  email: string;
  status: "ACCEPTED" | "INVALID" | "DUPLICATE" | "EXCLUDED";
  exclusionReason?: string | null;
  errors: string[];
  data: Record<string, string | number | null>;
};

type CampaignImportSummary = {
  hasImportedContacts: boolean;
  importCount: number;
  activeImportCount: number;
  excludedImportCount: number;
  excludedRows?: number;
  exclusionCount?: number;
  totalRows: number;
  acceptedRows: number;
  invalidRows: number;
  duplicateRows: number;
  items: Array<{
    id: string;
    filename: string;
    status: string;
    totalRows: number;
    acceptedRows: number;
    invalidRows: number;
    duplicateRows: number;
    createdAt: string;
    notesRevalidationRows?: number;
    rows: CampaignImportRow[];
  }>;
};
type Asset = {
  id: string;
  offerId: string;
  name: string;
  kind: string;
  url?: string | null;
  content?: string | null;
  approved: boolean;
  everApproved: boolean;
  description?: string | null;
  originalName?: string | null;
  mimeType?: string | null;
  sha256?: string | null;
  sizeBytes?: number | null;
  version?: number;
  status?: string;
  claimsJson?: string;
  parentAssetId?: string | null;
  createdAt: string;
};
type Offer = {
  id: string;
  name: string;
  description: string;
  claimsJson: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  assets?: Asset[];
  _count?: { campaigns: number; assets: number };
};

export type ListingSortField = "createdAt" | "updatedAt" | "name" | "status";
export type ListingSortDirection = "asc" | "desc";

export function sortListingItems<T extends { id: string; name: string; createdAt?: string; updatedAt?: string; status?: string; active?: boolean }>(
  items: T[],
  field: ListingSortField,
  direction: ListingSortDirection,
) {
  const statusOf = (item: T) => item.status ?? (item.active ? "ACTIVE" : "INACTIVE");
  const textOf = (item: T) => field === "name" ? item.name : statusOf(item);
  const dateOf = (item: T) => Date.parse(field === "createdAt" ? item.createdAt ?? "" : item.updatedAt ?? "") || 0;
  return [...items].sort((left, right) => {
    const comparison = field === "createdAt" || field === "updatedAt"
      ? dateOf(left) - dateOf(right)
      : textOf(left).localeCompare(textOf(right), "es", { sensitivity: "base" });
    if (comparison !== 0) return direction === "asc" ? comparison : -comparison;
    return left.name.localeCompare(right.name, "es", { sensitivity: "base" }) || left.id.localeCompare(right.id);
  });
}

function ListingSortControls({ field, direction, onFieldChange, onDirectionChange }: {
  field: ListingSortField;
  direction: ListingSortDirection;
  onFieldChange: (field: ListingSortField) => void;
  onDirectionChange: (direction: ListingSortDirection) => void;
}) {
  return <div className="listing-sort-controls" aria-label="Ordenar resultados">
    <label><span>Ordenar por</span><select value={field} onChange={(event) => onFieldChange(event.target.value as ListingSortField)}>
      <option value="createdAt">Fecha de creación</option>
      <option value="updatedAt">Fecha de actualización</option>
      <option value="name">Nombre</option>
      <option value="status">Estado</option>
    </select></label>
    <label><span>Dirección</span><select value={direction} onChange={(event) => onDirectionChange(event.target.value as ListingSortDirection)}>
      <option value="asc">Ascendente</option>
      <option value="desc">Descendente</option>
    </select></label>
  </div>;
}
type Company = {
  id: string;
  name: string;
  industry: string;
  country: string;
  employees: number;
  domain?: string;
  source?: string;
  providerIndustry?: string | null;
  normalizedIndustry?: string | null;
  industryConfidence?: number | null;
  providerCountry?: string | null;
  domainCountryStatus?: string | null;
};
type Contact = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  email?: string;
  phone?: string | null;
  linkedinUrl?: string | null;
  twitterHandle?: string | null;
  location?: string | null;
  seniority?: string | null;
  department?: string | null;
  decisionMaker?: boolean | null;
  emailVerificationStatus?: string | null;
};
type Step = {
  id: string;
  ordinal: number;
  kind: string;
  label: string;
  status: string;
  errorCode?: string;
  errorMessage?: string;
  outputJson?: string;
  startedAt?: string;
  finishedAt?: string;
};
type AiUsageNumbers = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  reasoningTokens: number;
  apiCallCount: number;
  estimatedCostUsd: number;
  actualCostUsd: number;
};
type AiUsageSummary = {
  source: "HERMES_SESSION_TELEMETRY";
  exact: boolean;
  totals: AiUsageNumbers & { sessions: number; tasks: number };
  byStep: Array<AiUsageNumbers & { stepKind: string; models: string[]; profiles: string[]; sessions: number; tasks: number; hasUnreportedInputOrOutput: boolean }>;
  byModel: Array<AiUsageNumbers & { model: string; sessions: number; steps: string[] }>;
  note: string | null;
};
type Run = {
  id: string;
  campaignId: string;
  status: string;
  provider?: string;
  errorCode?: string;
  errorMessage?: string;
  hermesTaskId?: string;
  hermesRunId?: string;
  providerTimestamp?: string | null;
  degradedReason?: string;
  directionDecision?: "ADVANCE" | "ADVANCE_WITH_CONDITIONS" | "REWORK" | "STOP" | null;
  directionDecisionReason?: string | null;
  directionConditions?: string[];
  directionActorId?: string | null;
  directionActor?: { id: string; username: string; displayName: string } | null;
  directionDecidedAt?: string | null;
  resumedFromRunId?: string | null;
  rootRunId?: string | null;
  resumeFromStep?: string | null;
  attemptNumber?: number;
  resumeContextHash?: string | null;
  resumePlan?: {
    eligible: boolean;
    resumeFromStep: string;
    reusedThroughStep: string;
    avoidedProviders: string[];
    reusedSteps: string[];
    sourceMessageCount: number;
    reason?: string | null;
    childRunId?: string | null;
    childRunStatus?: string | null;
  } | null;
  brief?: {
    description: string;
    objective: string;
    tone: string;
    configVersion: string;
    icp?: { targetRoleSource?: "DIRECTOR_AI" | "MANUAL"; targetRoles?: string[] };
    offer?: {
      id: string | null;
      name: string;
      description: string;
      facts: string[];
      snapshotHash?: string;
      frozenAt?: string;
      sourceVersion?: string;
      evidence?: Array<{ id: string; name: string; kind: string; url?: string; content?: string; version?: number; sha256?: string; status?: string; approvedAt?: string; frozenAt?: string; origin?: string }>;
    };
  } | null;
  currentOffer?: { id: string; name: string; description: string; facts: string[]; evidence: Array<{ id: string; name: string; kind: string; url?: string; content?: string; version?: number; sha256?: string; status?: string; approvedAt?: string; frozenAt?: string; origin?: string }>; snapshotHash: string; updatedAt: string } | null;
  hermesResult?: {
    commercial_recommendation?: "ADVANCE" | "ADVANCE_WITH_CHANGES" | "PILOT" | "NOT_RECOMMENDED";
    commercial_recommendation_reason?: string;
    execution_decision: "GO" | "BLOCK";
    execution_decision_reason: string;
    strategy_summary: string;
    positioning: string;
    value_proposition: string;
    target_company_criteria: string[];
    target_contact_roles: string[];
    target_contact_role_rationale?: Array<{ role: string; reason: string }>;
    risks: string[];
    missing_information: string[];
  } | null;
  createdAt: string;
  updatedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  campaign?: { name: string };
  config?: CampaignConfig;
  configOverriddenAt?: string | null;
  configOverrideReason?: string | null;
  configSourceVersion?: string | null;
  journeySummary?: JourneySummary;
  strategyVersions?: Array<{ id: string; version: number; status: string; reason?: string | null; strategyJson?: string | null; strategyHash?: string | null; provider?: string | null; profile?: string | null; providerTaskId?: string | null; providerRunId?: string | null; errorCode?: string | null; errorMessage?: string | null; createdAt: string; completedAt?: string | null }>;
  stageAttempts?: Array<{ id: string; stepKind: string; contactId?: string | null; companyId?: string | null; attemptNumber: number; status: string; category?: string | null; errorCode?: string | null; userMessage?: string | null; technicalCause?: string | null; provider?: string | null; agent?: string | null; profile?: string | null; providerTaskId?: string | null; providerRunId?: string | null; validator?: string | null; recommendedAction?: string | null; startedAt: string; finishedAt?: string | null }>;
  aiUsage?: AiUsageSummary;
  steps: Step[];
  events?: Array<{
    id: string;
    type: string;
    createdAt: string;
    payload: unknown;
  }>;
};

export function stageReviewCopy(run: Pick<Run, "status" | "events" | "config">, reviewedStepKind?: string) {
  const latestControlEvent = [...(run.events ?? [])]
    .filter((event) => ["RUN_STAGE_REVIEW_REQUIRED", "COMPANY_DISCOVERY_RECOVERY_BOUNDARY_REACHED"].includes(event.type))
    .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))[0];
  const discoveryRecovery = run.status === "WAITING_STAGE_REVIEW"
    && reviewedStepKind === "COMPANY_DISCOVERY"
    && latestControlEvent?.type === "COMPANY_DISCOVERY_RECOVERY_BOUNDARY_REACHED";
  const individualRecovery = discoveryRecovery && run.config?.audience.type === "B2C";

  return discoveryRecovery
    ? individualRecovery ? {
        discoveryRecovery: true,
        eyebrow: "RECUPERACIÓN DE PROSPECCIÓN",
        title: "Personas encontradas listas para continuar",
        body: "La búsqueda conservó las personas elegibles y se detuvo antes de Elegibilidad. Podés continuar con las encontradas o volver a buscar para intentar completar la meta.",
        advanceLabel: "Continuar a Elegibilidad",
        reprocessLabel: "Buscar más personas",
      } : {
        discoveryRecovery: true,
        eyebrow: "RECUPERACIÓN DE DESCUBRIMIENTO",
        title: "Empresas recuperadas listas para continuar",
        body: "La recuperación conservó las empresas válidas y se detuvo antes de Calificación. Podés continuar con las encontradas o volver a buscar para intentar completar la meta.",
        advanceLabel: "Continuar a Calificación",
        reprocessLabel: "Buscar más empresas",
      }
    : {
        discoveryRecovery: false,
        eyebrow: "MODO PASO A PASO · TEST",
        title: "Etapa detenida para revisión",
        body: "La etapa anterior quedó persistida. El sistema no ejecutará la siguiente etapa ni acciones programadas hasta que continúes manualmente.",
        advanceLabel: "Ejecutar siguiente etapa",
        reprocessLabel: "Reprocesar etapa actual",
      };
}

type DiscoveryCandidateDetail = {
  id: string; name: string; domain?: string | null; industry: string; country: string; employees: number; emailsCount?: number;
  gate: { ready: boolean; countryMatches: boolean; sizeMatches: boolean; signalReady: boolean; capabilityReady: boolean; hypothesisReady: boolean };
  assessment?: null | {
    status: "READY_FOR_QUALIFICATION" | "NEEDS_EVIDENCE" | "REJECTED";
    score: number; reasons: string[]; unknown_fields: string[]; contradictions: string[];
    public_signal?: null | { title: string; url: string; published_at: string; observed_at: string; fact: string };
    authorized_capability?: string | null; bilateral_hypothesis?: string | null;
  };
};

type DiscoveryOutput = {
  source?: string;
  sourceMode?: "SEARCH" | "IMPORTED" | "HYBRID";
  importedCandidates?: number;
  searchedCandidates?: number;
  selectedContacts?: number;
  searchSummary?: string | null;
  searchWarnings?: string[];
  discoveryPolicy?: {
    public_source_discovery?: string;
    require_explicit_published_email?: boolean;
    allow_authenticated_sources?: boolean;
    allow_sensitive_inferences?: boolean;
  };
  candidates?: number;
  candidateCompanyIds?: string[];
  agent?: { id: string; profile: string; planPromptVersion?: string; assessmentPromptVersion?: string };
  searchPlan?: { summary: string; hard_filters: { countries: string[]; min_employees: number; max_employees: number }; search_hypotheses: Array<{ id: string; label: string; rationale: string; authorized_capability: string; signal_types: string[]; hunter_query: string; preferred_industries: string[] }> };
  searches?: Array<{ hypothesisId: string; label: string; request: unknown; meta: { filters?: unknown; params?: unknown; results?: number } | null; returned: number }>;
  candidatesDetail?: DiscoveryCandidateDetail[];
  excludedCandidates?: Array<{ companyId: string; name: string; domain?: string | null; reason: string; scope: string }>;
  qualificationReadyCandidateIds?: string[];
  assessment?: { summary: string; warnings: string[] } | null;
  evaluationIncomplete?: boolean;
  completedWithPartialResults?: boolean;
  providerFailure?: { code?: string; message?: string; category?: string } | null;
  recovery?: { automatic?: boolean; preservedCandidates?: number; providerReinvoked?: boolean; manualRecoveryAvailable?: boolean } | null;
};

const stepCatalog: Record<string, { description: string; tool: string; agent: string; active: string }> = {
  HERMES_STRATEGY: {
    description: "Analiza la oferta, el objetivo y el perfil de cliente para proponer una estrategia comercial.",
    tool: "Hermes",
    agent: "Director IA",
    active: "Hermes está analizando el brief y preparando la recomendación comercial.",
  },
  COMPANY_DISCOVERY: {
    description: "Busca empresas que coincidan con país, industria, tamaño y criterios definidos.",
    tool: "Hunter Discover + Enrichment",
    agent: "Prospección IA",
    active: "Buscando y enriqueciendo empresas que coincidan con el ICP de la campaña.",
  },
  CONTACT_ENRICHMENT: {
    description: "Valida gratuitamente un candidato individual y, sólo después, resuelve su email exacto.",
    tool: "Fuentes gratuitas + Hunter Email Finder",
    agent: "Prospección IA",
    active: "Buscando responsables adecuados dentro de las empresas calificadas.",
  },
  QUALIFICATION: {
    description: "Evalúa cada empresa contra las reglas objetivas del ICP y conserva sólo las elegibles.",
    tool: "Reglas Commercial AI",
    agent: "Calificación IA",
    active: "Comparando empresas con los criterios y límites autorizados de la campaña.",
  },
  CRM_SYNC: {
    description: "Registra o actualiza empresas y contactos, evitando duplicados en el CRM.",
    tool: "HubSpot",
    agent: "RevOps IA",
    active: "Sincronizando empresas y contactos seleccionados con HubSpot.",
  },
  OUTREACH_DRAFT: {
    description: "Prepara un mensaje personalizado usando únicamente el contexto comercial autorizado.",
    tool: "Hermes",
    agent: "SDR IA",
    active: "Redactando mensajes personalizados para los contactos seleccionados.",
  },
  CLAIM_CHECK: {
    description: "Audita afirmaciones y restricciones para impedir promesas o datos sin respaldo.",
    tool: "Claim Audit + Claim Checker",
    agent: "Auditor de claims",
    active: "Validando cada afirmación del mensaje contra claims y evidencias aprobadas.",
  },
  HUMAN_APPROVAL: {
    description: "Detiene cada mensaje hasta que una persona lo revise y lo apruebe explícitamente.",
    tool: "Commercial AI · Revisión humana",
    agent: "Usuario aprobador",
    active: "Esperando la decisión humana sobre el contenido exacto del mensaje.",
  },
  SEND: {
    description: "Envía el mensaje validado respetando Claim Check, SAFE SEND y el modo de salida configurado.",
    tool: "Gmail + SAFE SEND",
    agent: "SDR IA",
    active: "Enviando únicamente mensajes aprobados por una persona y técnicamente validados.",
  },
  INBOUND_PROCESSING: {
    description: "Detecta respuestas, las vincula con el run y clasifica la intención del contacto.",
    tool: "Gmail Inbound",
    agent: "Inbound IA",
    active: "Esperando o procesando una respuesta asociada a esta conversación.",
  },
  MEETING: {
    description: "Registra una intención de reunión cuando la respuesta demuestra interés suficiente.",
    tool: "Google Calendar",
    agent: "RevOps IA",
    active: "Preparando la intención de reunión para su coordinación controlada.",
  },
  FINALIZE: {
    description: "Cierra el run y consolida resultados, estados y evidencia auditable.",
    tool: "Commercial AI",
    agent: "Director IA",
    active: "Consolidando el resultado final y la trazabilidad completa de la ejecución.",
  },
};

function stepMeta(kind: string, audienceType?: CampaignConfig["audience"]["type"], sourceMode?: CampaignConfig["acquisition"]["source"]) {
  if (audienceType !== "B2C") return stepCatalog[kind] ?? defaultStepMeta;
  if (kind === "COMPANY_DISCOVERY") {
    if (sourceMode === "IMPORTED") return {
      description: "Carga los contactos vigentes de la base CSV y omite toda búsqueda externa.",
      tool: "Base CSV de la campaña",
      agent: "Reglas Commercial AI",
      active: "Preparando los contactos activos de la base importada, sin consultar Hunter ni Hermes.",
    };
    return {
      description: sourceMode === "HYBRID"
        ? "Combina la base CSV activa con una búsqueda gobernada de personas y prioriza el conjunto por puntaje."
        : "Busca personas que coincidan con los criterios B2C y exige un email publicado explícitamente con evidencia trazable.",
      tool: sourceMode === "HYBRID" ? "Base CSV + Hermes" : "Hermes · Prospección de Personas",
      agent: "Prospección de Personas IA",
      active: "Buscando personas en fuentes públicas permitidas; Hunter permanece deshabilitado.",
    };
  }
  if (kind === "QUALIFICATION") return {
    description: "Aplica elegibilidad local, deduplicación y lista de no contactar sobre las personas encontradas o importadas.",
    tool: "Reglas Commercial AI",
    agent: "Calificación IA",
    active: "Evaluando localmente la elegibilidad de cada persona, sin inferencias sensibles.",
  };
  if (kind === "CONTACT_ENRICHMENT") return {
    description: "Conserva los emails ya aportados por la fuente B2C y omite búsqueda o verificación mediante Hunter.",
    tool: "Preparación local de contactos",
    agent: "Commercial AI",
    active: "Preparando los contactos B2C sin consultar Hunter.",
  };
  return stepCatalog[kind] ?? defaultStepMeta;
}

const reprocessableReviewSteps = new Set([
  "HERMES_STRATEGY", "COMPANY_DISCOVERY", "QUALIFICATION", "CONTACT_ENRICHMENT", "CRM_SYNC", "OUTREACH_DRAFT", "CLAIM_CHECK", "HUMAN_APPROVAL", "SEND",
]);

const defaultStepMeta = {
  description: "Ejecuta una etapa controlada del flujo comercial.",
  tool: "Commercial AI",
  agent: "Equipo comercial IA",
  active: "Procesando esta etapa de la campaña.",
};

const pipelineDisplayOrder = [
  "HERMES_STRATEGY",
  "COMPANY_DISCOVERY",
  "QUALIFICATION",
  "CONTACT_ENRICHMENT",
  "CRM_SYNC",
  "OUTREACH_DRAFT",
  "CLAIM_CHECK",
  "HUMAN_APPROVAL",
  "SEND",
  "INBOUND_PROCESSING",
  "MEETING",
  "FINALIZE",
] as const;
type Message = {
  id: string;
  subject?: string;
  body: string;
  bodyHtml?: string | null;
  status: string;
  failureCode?: string | null;
  failureMessage?: string | null;
  intendedRecipient?: string;
  actualRecipient?: string;
  direction?: string;
  classification?: string;
  aiProvider?: string;
  aiProfile?: string;
  aiAgent?: string;
  aiPromptVersion?: string;
  aiResultVersion?: string;
  hermesTaskId?: string;
  hermesRunId?: string;
  providerTimestamp?: string | null;
  firstOpenedAt?: string | null;
  lastOpenedAt?: string | null;
  openCount?: number;
  classificationJson?: string | null;
  generationMetadataJson?: string | null;
  originalGenerationJson?: string | null;
  actionKind?: string | null;
  actionSequence?: number | null;
  createdAt?: string;
  attachmentsJson?: string;
  claimsUsedJson?: string;
  claimAuditJson?: string | null;
  contentHash?: string;
  emailTemplateVersionId?: string | null;
  emailTemplateType?: CampaignEmailTemplateType | null;
  previousVersionId?: string | null;
  replyVersion?: number;
  contact: Contact;
  approval?: Approval;
  journey?: {
    id: string;
    status: string;
    lastInboundAt: string | null;
    lastOutboundAt: string | null;
    nextAction: string | null;
    nextActionAt: string | null;
    followUpCount: number;
  } | null;
};
type Approval = {
  audienceType?: "B2B" | "B2C";
  messageSource?: "AGENT" | "TEMPLATES";
  copyPolicy?: "AS_CONFIGURED" | "TEMPLATE_OR_AGENT";
  recipientDetails?: RecipientDetails | null;
  id: string;
  status: string;
  createdAt: string;
  requestedBy: string;
  reviewerId?: string | null;
  campaign: { id: string; name: string };
  emailTemplateSnapshot?: Array<{ type: CampaignEmailTemplateType; templateId: string; versionId: string; version: number; contentHash: string }>;
  message?: Message & { contact: Contact & { company: Company } };
  sourceMessage?: (Message & { contact: Contact & { company: Company } }) | null;
  payloadValid?: boolean;
  sendability?: { sendable: boolean; policyVersion: string; reasons: string[] };
  attachmentOption?: {
    eligible: boolean;
    scope: "INITIAL_ONLY" | "NEVER";
    included: boolean;
    asset: { id: string; name: string; version: number; approved: boolean; active: boolean } | null;
  };
  delivery?: { id: string; status: string; dueAt: string; attempts: number; lastError?: string | null; updatedAt: string } | null;
  regenerationRequest?: {
    id: string;
    status: "REQUESTED" | "GENERATING" | "REVIEW_REJECTED" | "FAILED" | "PENDING_APPROVAL";
    reason: string;
    errorCode?: string | null;
    errorMessage?: string | null;
    createdAt: string;
    completedAt?: string | null;
    newMessage?: (Message & { approval?: { id: string; status: string } }) | null;
  } | null;
  versionHistory?: Array<Message & { approval?: { id: string; status: string; reviewerNote?: string | null; reviewerId?: string | null; reviewedAt?: string | null; createdAt: string } | null }>;
  meeting?: {
    id: string;
    title: string;
    startsAt: string;
    endsAt?: string | null;
    timezone?: string | null;
    durationMinutes?: number | null;
    organizerEmail?: string | null;
    attendeesJson: string;
    description?: string | null;
    addGoogleMeet?: boolean;
    meetingTeamJson?: string;
    status: string;
  };
  meetingProposal?: {
    intentId: string;
    options: Array<{ id: string; startsAt: string; endsAt: string; timezone: string; expiresAt: string; source: string }>;
    meetingTeam: MeetingTeamMember[];
    availableMembers: MeetingTeamMember[];
    availabilitySource: "MANUAL_INTERNAL_COORDINATION";
  } | null;
  invitationDraft?: {
    id: string;
    version: number;
    operationKind?: "CREATE" | "UPDATE";
    calendarEventId?: string | null;
    previousDraftId?: string | null;
    contentJson: string;
    description: string;
    groundingJson: string;
    warningsJson: string;
    generationMode: "AI" | "FALLBACK" | "TEMPLATE" | "MANUAL";
    provider?: string | null;
    profile?: string | null;
    model?: string | null;
    promptVersion: string;
    contentVersion: string;
    fallbackReason?: string | null;
    baselineSource?: string | null;
    baselineSnapshotJson?: string | null;
    baselineSnapshotHash?: string | null;
    providerEtag?: string | null;
    resultProviderEtag?: string | null;
    changedFieldsJson?: string;
    calendarPayloadJson: string;
    patchPayloadJson?: string | null;
    payloadHash: string;
    status: string;
  } | null;
  kind?: string;
  payloadHash?: string | null;
  expiresAt?: string | null;
};

type CampaignEmailTemplateVersion = {
  id: string;
  version: number;
  subjectTemplate: string;
  bodyTextTemplate: string;
  bodyHtmlTemplate?: string | null;
  source: string;
  sourceConfigVersion: string;
  status: string;
  contentHash: string;
  warnings: string[];
  allowedVariables: string[];
  createdAt: string;
  publishedAt?: string | null;
};

type CampaignEmailTemplate = {
  id: string;
  campaignId: string;
  type: CampaignEmailTemplateType;
  label: string;
  status: string;
  currentVersionId?: string | null;
  currentVersion: CampaignEmailTemplateVersion | null;
  versions: CampaignEmailTemplateVersion[];
};

function recommendedTemplateTypeForMessage(message: Pick<Message, "actionKind" | "generationMetadataJson">): CampaignEmailTemplateType | null {
  if (message.actionKind === "MEETING_OPTIONS") return "MEETING_OPTIONS";
  if (message.actionKind !== "INITIAL") return null;
  const metadata = parsedObject<{ outreachContext?: { contactRole?: { purpose?: string } } }>(message.generationMetadataJson);
  if (metadata?.outreachContext?.contactRole?.purpose === "DIRECT_DECISION_MAKER") return "INITIAL_DECISION_MAKER";
  if (metadata?.outreachContext?.contactRole?.purpose === "RELEVANT_INFLUENCER") return "INITIAL_INFLUENCER";
  return "INITIAL_ROUTER";
}

type MeetingInvitationContent = {
  title: string;
  objective: string;
  agenda: string[];
  expectedOutcome: string;
  optionalPreparation?: string | null;
  description: string;
  groundingEvidence: Array<{ field: string; sourceType: string; sourceId: string }>;
  warnings: string[];
  generationMode: "AI" | "FALLBACK" | "TEMPLATE" | "MANUAL";
  provider?: string | null;
  profile?: string | null;
  model?: string | null;
  promptVersion: string;
  contentVersion: string;
};

type CalendarMeetingPayload = {
  outreachMode: "DRY_RUN" | "TEST" | "PRODUCTION";
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  timezone: string;
  calendarId: string;
  organizerEmail: string;
  logicalAttendees: string[];
  effectiveAttendees: string[];
  addGoogleMeet: boolean;
  sendUpdates: "none" | "all";
};

type InboundClassificationDetail = {
  category?: string;
  semantic_intent?: string;
  confidence?: number;
  evidence?: string[];
  rule_id?: string;
  classifier_version?: string;
  latest_inbound_text?: string;
  questions?: Array<{ id: string; topic: string; text: string }>;
  warnings?: string[];
};

type CommercialReplyMetadata = {
  generationMode?: string;
  profile?: string;
  model?: string;
  provider?: string;
  promptVersion?: string;
  questionsCovered?: Array<{ question_id: string; answer_evidence: string }>;
  warnings?: string[];
  qualityWarnings?: string[];
  outreachContext?: { contextLevel: string; contactRole: { purpose: string }; prospectEvidence: Array<{ url?: string }> };
  grounding?: unknown[];
  author?: { provider?: string; profile?: string; model?: string; taskId?: string; runId?: string; promptVersion?: string };
  review?: { approved?: boolean; reasons?: string[]; warnings?: string[]; taskId?: string; runId?: string; promptVersion?: string };
  offerContext?: { offerId?: string | null; configVersion?: string; snapshotHash?: string; pricingPolicy?: string[] };
  missingOfferInformation?: string[];
  previousVersionId?: string | null;
  regenerationRequestId?: string | null;
  rejectionFeedback?: { reason?: string } | null;
  humanEdit?: { edited?: boolean; editedAt?: string; editedBy?: string; previousPayloadHash?: string; payloadHash?: string };
  activeDraftSource?: "AI_GENERATED_DRAFT" | "CAMPAIGN_TEMPLATE" | "HUMAN_EDIT";
  aiDraftBaseline?: { subject: string; body: string; bodyHtml?: string | null; capturedAt?: string; source?: string };
  copyPolicy?: "LEGACY_V7" | "PROFESSIONAL_V2";
  qualityMode?: "OFF" | "WARN" | "ENFORCE";
  messageStrategy?: {
    policy_version?: string;
    context_mode?: string;
    objective?: string;
    focus_product_fact_id?: string | null;
    prospect_signal_id?: string | null;
    value_angle?: string;
    cta_mode?: string;
  } | null;
  qualityAssessment?: { passed?: boolean; score?: number; blockingIssues?: string[]; warnings?: string[] } | null;
  finalQualityAssessment?: { passed?: boolean; score?: number; blockingIssues?: string[]; warnings?: string[] } | null;
};

function uniqueText(values: Array<string | null | undefined>) {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))];
}

function isGroundingLimitDisclosure(value: string) {
  const normalized = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return /^(?:no\s+)?(?:cuento|contamos|tenemos|dispongo|disponemos|hay)\s+(?:todavia\s+)?(?:con\s+)?(?:informacion|detalle|detalles|datos)\s+(?:suficiente(?:s)?\s+)?(?:respaldad[oa]s?|confirmad[oa]s?|documentad[oa]s?|autorizad[oa]s?)/.test(normalized);
}

function parsedArray<T>(value?: string | null): T[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed as T[] : [];
  } catch {
    return [];
  }
}

function parsedObject<T>(value?: string | null): T | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as T : null;
  } catch {
    return null;
  }
}

function emailTextToHtml(value: string) {
  const escaped = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.split(/\n{2,}/).map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br>")}</p>`).join("");
}

function fileAsBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
}

function parsedClaimAudit(value?: string | null) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as { factual_claims?: unknown };
    return Array.isArray(parsed.factual_claims)
      ? parsed.factual_claims as Array<{ claim: string; supporting_fact?: string; supported: boolean }>
      : [];
  } catch {
    return [];
  }
}

type OutreachMode = "DRY_RUN" | "TEST" | "PRODUCTION";

function deliveryControlLabel(outreachMode?: OutreachMode) {
  if (outreachMode === "TEST") return "SAFE SEND";
  if (outreachMode === "PRODUCTION") return "destinatario real verificado";
  return "DRY RUN sin envío";
}

function approvalDecisionConfirmation(outreachMode?: OutreachMode) {
  if (outreachMode === "TEST") return "¿Confirmás que revisaste el contenido y el destinatario efectivo de SAFE SEND?";
  if (outreachMode === "PRODUCTION") return "¿Confirmás que revisaste el contenido y el destinatario real de producción?";
  return "¿Confirmás que revisaste el contenido? En DRY RUN no se realizará ningún envío.";
}

function postApprovalAction(actionKind?: string | null, outreachMode?: OutreachMode) {
  const destinationControl = deliveryControlLabel(outreachMode);
  if (actionKind === "MEETING_OPTIONS") return `Se encolará el correo con opciones de agenda; el envío respetará ${destinationControl}, ventana horaria y cuota.`;
  if (actionKind?.startsWith("FOLLOW_UP")) return "Se encolará el seguimiento; el scheduler respetará la ventana de envío y la cuota de la campaña.";
  if (actionKind === "QUESTION_RESPONSE" || actionKind === "REFERRAL_CLARIFICATION") return `Se encolará la respuesta dentro del hilo, respetando ${destinationControl} y los controles previos al envío.`;
  return `Se encolará el mensaje inicial; el scheduler respetará ${destinationControl}, la ventana horaria y la cuota de la campaña.`;
}

type JourneySummary = {
  total: number;
  terminal: number;
  active: number;
  sentToday: number;
  dailyLimit: number;
  pendingApprovals: number;
  approvedQueued: number;
  sent: number;
  waitingReply: number;
  replied: number;
  coordinatingMeeting: number;
  meetingsScheduled: number;
  sendWindow?: { state: "OPEN"; closesAt: string } | { state: "NEXT_OPEN"; opensAt: string } | { state: "UNAVAILABLE" };
  quota?: { scope: "CAMPAIGN"; campaignId: string; channel: string; quotaDay: string; timezone: string; used: number; limit: number; remaining: number; breakdown: { initial: number; followUps: number; responses: number; meetingOptions: number; total: number }; consumingStatuses: string[] };
  nextAction?: { id: string; kind: string; status: string; dueAt: string; lastError?: string | null; priority: number } | null;
  counts: Record<string, number>;
};

type Journey = {
  replyFailure?: CommercialReplyFailure | null;
  campaignId?: string;
  id: string;
  status: string;
  lastInboundAt?: string | null;
  lastOutboundAt?: string | null;
  nextAction?: string | null;
  nextActionAt?: string | null;
  replyDeadlineAt?: string | null;
  followUpCount: number;
  terminal: boolean;
  closeReason?: string | null;
  handoffStatus?: string | null;
  meetingId?: string | null;
  company: Company;
  contact: Contact;
  messages: Message[];
  actions: Array<{ id: string; kind: string; status: string; dueAt: string; updatedAt?: string; lastError?: string | null; messageId?: string | null }>;
  replacement?: { journeyId: string; companyId: string; contactId: string; companyName: string; contactName: string; keptSameCompany: boolean; selectedAt: string } | null;
  replaces?: { journeyId: string; companyId: string; contactId: string; companyName: string; contactName: string; selectedAt: string } | null;
  referralReview?: {
    sourceMessageId?: string | null;
    subject?: string | null;
    body: string;
    referredBy?: { id: string; name: string; email?: string | null } | null;
    candidateEmail?: string | null;
    companyDomain?: string | null;
    referralDomain?: string | null;
    domainMatchesCompany: boolean;
  } | null;
  referralRecovery?: {
    eligible: boolean;
    code: string;
    messageId: string;
    reason: string;
  } | null;
};
type SafeSendRecovery = {
  eligible: boolean;
  code: string;
  reasons: string[];
  recoveryKind?: "OUTREACH_PERIOD_ENDED" | "LEGACY_QUOTA";
  actionLabel?: string;
  actionId?: string;
  messageId?: string;
  journeyId?: string;
  contact?: { id: string; name: string; company: string };
  evidence?: { previousAttempts: number; previousError: string; approvalIntact: boolean; payloadHashMatches: boolean; providerInvoked: boolean; reservationExists: boolean };
};
type MeetingRecovery = {
  eligible: boolean;
  code: string;
  reasons: string[];
  actionId?: string;
  journeyId?: string;
  meetingIntentId?: string;
  round?: number;
  contact?: { name: string; company: string };
  evidence?: { previousAttempts: number; previousError?: string | null; optionCount: number; draftCount: number; calendarEventCount: number; guidance?: string };
};
type OutreachRecovery = {
  runId: string;
  campaignId?: string;
  runStatus: string;
  step?: { status: string } | null;
  snapshotFrozen: boolean;
  skippedStages: string[];
  availableActions?: string[];
  diagnosis?: {
    generatedDrafts: number;
    claimBlockedDrafts: number;
    failedContacts: number;
    redraftableContacts: number;
    canResumeFromDrafting: boolean;
    outreachStarted?: boolean;
    resumeBlocker?: string | null;
    guidance?: string | null;
    inputRejections: Array<{
      type: string;
      entityId: string;
      companyId?: string | null;
      contactId?: string | null;
      reason: string;
      createdAt: string;
    }>;
  };
  contacts: Array<{
    journeyId: string;
    contactId: string;
    companyId: string;
    company: string;
    contact: string;
    email?: string | null;
    journeyStatus: string;
    hasDraft: boolean;
    latestMessageId?: string | null;
    claimFailure?: { messageId: string; code?: string | null; reason: string; frozenTemplate: boolean } | null;
    attempts: Array<{
      id: string;
      attemptNumber: number;
      status: string;
      category?: string | null;
      errorCode?: string | null;
      userMessage?: string | null;
      technicalCause?: string | null;
      provider?: string | null;
      agent?: string | null;
      profile?: string | null;
      providerTaskId?: string | null;
      providerRunId?: string | null;
      validator?: string | null;
      recommendedAction?: string | null;
      contractPayloadJson?: string | null;
      startedAt: string;
      finishedAt?: string | null;
    }>;
  }>;
  autonomousContactResolution?: {
    policyVersion: string;
    actions: Array<{ id: string; status: string; journeyId: string; lastError?: string | null }>;
    evaluations: Array<{
      id: string; companyId: string; firstName: string; lastName: string; role: string; email?: string | null;
      status: string; decisionReason: string; identityConfidence: number; currentEmploymentConfidence: number;
      roleRelevance: number; emailConfidence: number; sourceQuality: number; contradictionPenalty: number;
      overallConfidence: number; thresholdsVersion: string; directorApproved?: boolean | null;
      evidence: Array<{ sourceType: string; url?: string; observedAt: string; fact: string }>;
      directorReasons: string[];
    }>;
  };
};
type HunterDryRun = {
  dryRun: boolean;
  providersInvoked: boolean;
  massSearchDisabled: boolean;
  maximumEstimatedCredits: number;
  preflight: {
    knownBalance: number; minimumBalance: number; authorized: number; consumed: number;
    reserved: number; available: number; perCompanyLimit: number; concurrencyLimit: number;
    emailVerifierBudget: number; balanceUpdatedAt: string;
  };
  companies: Array<{ companyId: string; company: string; contact: string; action: string; maximumCredits: number; providerCallPlannedNow: boolean }>;
  exactPaidPlan: Array<{ companyId: string; company: string; operation: string; maximumCredits: number; prerequisite: string; candidate: null }>;
  blockers: string[];
};
type HunterCreditPreflight = {
  knownBalance: number;
  minimumBalance: number;
  protectedFloor: number;
  maximumAuthorizableCredits: number;
  defaultAuthorizationCredits: number;
  authorized: number;
  consumed: number;
  reserved: number;
  available: number;
  perCompanyLimit: number;
  selectedContactLimit: number;
  concurrencyLimit: number;
  emailVerifierBudget: number;
  allowedOperations: string[];
  authorization: null | { actorId: string; reason: string; at: string };
};
type ContactEnrichmentRecovery = {
  runStatus: string;
  eligible: boolean;
  reason: string | null;
  blockedStep: null | { kind: string; status: string; errorCode: string | null; errorMessage: string | null };
  resumeFromStep: "CONTACT_ENRICHMENT";
  reusedStages: string[];
  candidateCompanyIds: string[];
  frozenSnapshotReused: boolean;
  repeatedStages: string[];
  allowedPaidOperations: string[];
  forbiddenPaidOperations: string[];
  freeCandidateValidationRequired: boolean;
  currentBudget: null | { authorized: number; consumed: number; reserved: number; remaining: number; historicallyAuthorizedOperations: unknown };
  executionStarted: boolean;
  activeRecovery: null | { id: string; status: string; attemptNumber: number; reason: string | null; requestedAt: string; startedAt: string; updatedAt: string };
  latestRecovery: null | { id: string; status: string; attemptNumber: number; reason: string | null; errorCode: string | null; errorMessage: string | null; requestedAt: string; finishedAt: string | null };
  providersInvokedByInspection: boolean;
};
type CompanyDiscoveryRecovery = {
  runStatus: string;
  eligible: boolean;
  reason: string | null;
  recoveryKind: "COMPANY_DISCOVERY" | "INDIVIDUAL_PROSPECT_SEARCH";
  sourceMode: "SEARCH" | "IMPORTED" | "HYBRID";
  actionLabel: string;
  resumeFromStep: "COMPANY_DISCOVERY";
  reusedStages: string[];
  frozenSnapshotPreserved: boolean;
  frozenStrategyPreserved: boolean;
  directionDecisionPreserved: string | null;
  calendarConfigPreserved: boolean;
  hunterBudget: null | { authorized: number; consumed: number; reserved: number; remaining: number };
  companyDiscoveryProvider: string;
  allowedPaidOperations: string[];
  forbiddenPaidOperations: string[];
  duplicateProtection: string[];
  activeRecovery: null | { id: string; status: string; attemptNumber: number; reason: string | null };
  latestRecovery: null | { id: string; status: string; errorCode: string | null; errorMessage: string | null };
};
type CompanyReplacementRecovery = {
  runStatus: string;
  eligible: boolean;
  reason: string | null;
  actionLabel: string;
  frozenStrategyPreserved: boolean;
  frozenConfigPreserved: boolean;
  candidateCompanies: Array<{ id: string; name: string }>;
  excludedCompanies: Array<{ id: string; name: string }>;
  excludedCompanyIds: string[];
  excludedContactIds: string[];
  targetContactCount: number;
  remainingCredits: number;
  freeSourcesFirst: boolean;
  allowedPaidOperations: string[];
  forbiddenPaidOperations: string[];
  duplicateProtection: string[];
  providersInvokedByInspection: boolean;
  activeRecovery: null | { id: string; status: string; attemptNumber: number; reason: string | null };
  latestRecovery: null | { id: string; status: string; errorCode: string | null; errorMessage: string | null };
};
type RunIndexItem = {
  id: string;
  campaignId: string;
  campaign: { id: string; name: string };
  status: string;
  createdAt: string;
  startedAt?: string | null;
  progress: { completed: number; total: number; percent: number };
  contacts: number;
  sent: number;
  waitingReply: number;
  coordinatingMeeting: number;
  pendingApprovals: number;
  errors: number;
  terminal: number;
  nextAction?: { id: string; kind: string; status: string; dueAt: string; lastError?: string | null } | null;
  lastActivity: string;
  attention: boolean;
  overdue: boolean;
  sendWindow?: { state: "OPEN"; closesAt: string } | { state: "NEXT_OPEN"; opensAt: string } | { state: "UNAVAILABLE" };
};
type FieldDefinition = {
  key: string;
  label: string;
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
  help?: string;
  type?: "text" | "url" | "email" | "password" | "number" | "textarea";
};
type IntegrationSetting = {
  id: string;
  provider: string;
  label: string;
  mode: "SIMULATED" | "LIVE" | "DISABLED";
  status: string;
  parameters: Record<string, unknown>;
  configuredKeys: string[];
  lastCheckAt?: string | null;
  lastError?: string | null;
  vaultReady: boolean;
  definition: {
    provider: string;
    label: string;
    description: string;
    category: string;
    secretFields: FieldDefinition[];
    parameters: FieldDefinition[];
  };
};

type IntegrationReadiness = {
  provider: string;
  mode: string;
  configured: boolean;
  connectionTested: boolean;
  lastTestAt?: string | null;
  lastTestResult?: string | null;
  requiredFieldsMissing: string[];
  capabilities: Array<{
    name: string;
    status: string;
    lastTestAt?: string | null;
    lastError?: string | null;
    operation: string;
    evidence?: unknown;
  }>;
  validationLevel: string;
};

const defaultConfig: CampaignConfig = {
  copyPolicy: "AS_CONFIGURED",
  messageSource: "AGENT",
  audience: {
    type: "B2B",
    individualCriteria: { locations: [], occupations: [], interests: [] },
  },
  acquisition: { source: "SEARCH", emailVerification: "HUNTER" },
  icp: {
    industries: ["Professional Services", "Technology, Information and Media"],
    countries: ["Argentina"],
    employeeMin: 51,
    employeeMax: 500,
    headcountRanges: ["51-200", "201-500"],
    targetRoles: ["Director Comercial", "Gerente General"],
    targetRoleSource: "DIRECTOR_AI",
    industryMatchMode: "PREFERENCE",
  },
  goal: { targetCompanies: 1, targetContacts: 1, targetMeetings: 1 },
  limits: {
    maxEmailsPerDay: 10,
    maxWhatsAppPerDay: 0,
    maxCompaniesPerRun: 25,
    maxContactsPerRun: 100,
    requireApproval: true,
  },
  objective: "",
  tone: "consultivo, profesional y breve",
  restrictions: [],
  excludedCustomers: [],
  approvedClaims: [],
  forbiddenClaims: [],
  regions: [],
  enabledChannels: ["EMAIL"],
  personalization: {
    publicSignalPolicy: "DISABLED",
    allowedPublicSignalTypes: ["PROJECT", "TRANSACTION", "ALLIANCE", "IMPLEMENTATION", "EXPANSION", "OPENING", "HIRING", "TECHNICAL_EVENT", "DOCUMENTED_INITIATIVE"],
    decisionOwner: "DIRECTOR_AI",
  },
  schedule: {
    timezone: "America/Argentina/Buenos_Aires",
    enabledWeekdays: [1, 2, 3, 4, 5],
    sendWindowStart: "00:00",
    sendWindowEnd: "23:59",
    monitoringDays: 7,
  },
  followUpPolicy: { firstBusinessDay: 3, secondBusinessDay: 7, closeBusinessDay: 12 },
  hermesFailurePolicy: "BLOCK",
};

export function scheduleFor(config?: CampaignConfig) {
  return config?.schedule ?? defaultConfig.schedule;
}

function audienceFor(config?: CampaignConfig) {
  return config?.audience ?? defaultConfig.audience;
}

function acquisitionFor(config?: CampaignConfig) {
  return config?.acquisition ?? defaultConfig.acquisition;
}

const audienceLabels = { B2B: "B2B · empresas y decisores", B2C: "B2C · personas independientes" } as const;
const sourceLabels = { SEARCH: "Búsqueda de prospectos", IMPORTED: "Base importada (CSV)", HYBRID: "Búsqueda + base importada" } as const;
const verificationLabels = { HUNTER: "Validar con Hunter", NONE: "Sin verificación externa" } as const;

function emailVerificationFor(
  audienceType: CampaignConfig["audience"]["type"],
  sourceMode: CampaignConfig["acquisition"]["source"],
  selectedMode: CampaignConfig["acquisition"]["emailVerification"],
) {
  if (audienceType === "B2C") return "NONE";
  if (sourceMode === "SEARCH") return "HUNTER";
  return selectedMode;
}

function followUpPolicyFor(config: CampaignConfig) {
  return config.followUpPolicy ?? defaultConfig.followUpPolicy;
}

const listFromText = (value: string) => value
  .split(/[\n,]/)
  .map((item) => item.trim())
  .filter(Boolean);

const localDateTimeValue = (value: string | null | undefined, timezone: string) => {
  if (!value) return "";
  try { return utcToZonedDateTimeLocal(value, timezone); } catch { return ""; }
};

const optionalIso = (value: string, timezone: string) => value
  ? zonedDateTimeLocalToUtc(value, timezone)
  : undefined;

const ianaTimezones = supportedIanaTimeZones();

function TimezoneField({ label, value, onChange, error }: { label: string; value: string; onChange: (value: string) => void; error?: string }) {
  const valid = isValidIanaTimeZone(value);
  return <label>{label}
    <input list="iana-timezones" value={value} onChange={(event) => onChange(event.target.value)} aria-invalid={!valid} />
    <datalist id="iana-timezones">{ianaTimezones.map((zone) => <option key={zone} value={zone} />)}</datalist>
    <FieldError message={error ?? (!valid ? "Seleccioná una zona horaria IANA válida." : undefined)} />
  </label>;
}

function FieldHelp({ children }: { children: string }) {
  return <small className="field-help">{children}</small>;
}

function FieldError({ message }: { message?: string }) {
  return message ? <small className="field-error" role="alert">{message}</small> : null;
}

function MeetingTeamSelector({ value, onChange, disabled = false }: { value: string[]; onChange: (value: string[]) => void; disabled?: boolean }) {
  const { data, isPending, isError } = useQuery({ queryKey: ["meeting-team", "selector"], queryFn: () => api<{ items: MeetingTeamMember[] }>("/meeting-team") });
  const members = (data?.items ?? []).filter((member) => member.active || value.includes(member.id));
  const toggle = (memberId: string) => onChange(value.includes(memberId) ? value.filter((id) => id !== memberId) : [...value, memberId]);
  return <div className="meeting-team-selector full">
    <div className="meeting-team-selector-head"><div><strong>Equipo de reuniones</strong><small>Seleccioná una o más personas para esta campaña.</small></div><span>{value.length} seleccionado{value.length === 1 ? "" : "s"}</span></div>
    {isPending ? <small>Cargando integrantes…</small> : isError ? <div className="field-error">No se pudo cargar el equipo de reuniones.</div> : members.length ? <div className="meeting-team-options">{members.map((member) => <label key={member.id} className={`${value.includes(member.id) ? "selected" : ""}${member.active ? "" : " inactive"}`}><input type="checkbox" checked={value.includes(member.id)} disabled={disabled || (!member.active && !value.includes(member.id))} onChange={() => toggle(member.id)} /><span><strong>{member.firstName} {member.lastName}</strong><small>{member.jobTitle} · {member.email}{member.active ? "" : " · Inactivo"}</small></span></label>)}</div> : <p className="muted">Todavía no hay integrantes activos. Cargalos desde «Equipo de reuniones».</p>}
  </div>;
}

function CloserAdminSelector({ value, onChange, required }: { value: string; onChange: (value: string) => void; required: boolean }) {
  const { data, isPending, isError } = useQuery({ queryKey: ["closer-users", "admins"], queryFn: () => api<{ items: Array<{ id: string; displayName: string; username: string }> }>("/closer/users?role=CLOSER_ADMIN") });
  return <label className="full">Closer Admin responsable
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">{required ? "Seleccionar antes de activar…" : "Sin asignar"}</option>
      {data?.items.map((user) => <option key={user.id} value={user.id}>{user.displayName} · {user.username}</option>)}
    </select>
    <FieldHelp>{required ? "Obligatorio para activar campañas con objetivo de reuniones. Recibirá cada reunión confirmada y podrá delegarla." : "Esta campaña no busca reuniones; podés dejarlo vacío."}</FieldHelp>
    {isPending && <small>Cargando closers…</small>}{isError && <FieldError message="No se pudo cargar el equipo de closers." />}
  </label>;
}

/** Keeps the literal edit buffer separate from the numeric value, so clearing a
 * field never turns it into an unwanted leading zero. */
function NumericInput({ value, onValueChange, onBlur, ...props }: Omit<ComponentProps<"input">, "type" | "value" | "onChange"> & {
  value: number;
  onValueChange: (value: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    if (text !== "" && Number(text) !== value) setText(String(value));
  }, [text, value]);
  return <input
    {...props}
    type="number"
    inputMode="numeric"
    value={text}
    onChange={(event) => {
      const next = event.target.value;
      setText(next);
      if (next === "") return;
      const parsed = Number(next);
      if (Number.isFinite(parsed)) onValueChange(parsed);
    }}
    onBlur={(event) => {
      if (text === "") setText(String(value));
      onBlur?.(event);
    }}
  />;
}

const foldIndustrySearch = (value: string) => value
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("es");

function HunterIndustrySelect({ value, onChange, error }: {
  value: HunterIndustry[];
  onChange: (value: HunterIndustry[]) => void;
  error?: string;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = foldIndustrySearch(query.trim());
  const filtered = useMemo(() => {
    const candidates = normalizedQuery
      ? hunterIndustries.filter((industry) => {
        const searchable = `${hunterIndustrySpanishLabel(industry)} ${industry}`;
        return foldIndustrySearch(searchable).includes(normalizedQuery);
      })
      : commonHunterIndustries;
    return candidates.slice(0, 60);
  }, [normalizedQuery]);
  const toggle = (industry: HunterIndustry, checked: boolean) => {
    const selected = checked ? [...value, industry] : value.filter((item) => item !== industry);
    onChange(hunterIndustries.filter((item) => selected.includes(item)));
  };
  return <section className="industry-catalog-control full" aria-labelledby="hunter-industry-heading">
    <div className="industry-catalog-heading">
      <div>
        <strong id="hunter-industry-heading">Industrias de Hunter</strong>
        <small>Catálogo oficial · selección exacta</small>
      </div>
      <span>{value.length} seleccionada{value.length === 1 ? "" : "s"}</span>
    </div>
    <p>Elegí una o más industrias. La plataforma enviará a Hunter exactamente los valores oficiales indicados debajo de la traducción.</p>
    {value.length > 0 && <div className="industry-selected-values" aria-label="Industrias seleccionadas">
      {value.map((industry) => <span className="industry-selected-chip" key={industry}>
        <span><strong>{hunterIndustrySpanishLabel(industry)}</strong>{hunterIndustrySpanishLabel(industry) !== industry && <small>{industry}</small>}</span>
        <button type="button" aria-label={`Quitar ${hunterIndustrySpanishLabel(industry)}`} onClick={() => toggle(industry, false)}>×</button>
      </span>)}
    </div>}
    <label className="industry-catalog-search">
      Buscar en el catálogo oficial
      <span><Search size={16} aria-hidden="true" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ej.: telecomunicaciones, energía, software…" /></span>
    </label>
    <div className="industry-catalog-subheading">
      <strong>{normalizedQuery ? `Resultados (${filtered.length}${filtered.length === 60 ? "+" : ""})` : "Opciones frecuentes"}</strong>
      {!normalizedQuery && <small>Usá el buscador para consultar las {hunterIndustries.length} opciones oficiales.</small>}
    </div>
    <div className={`industry-catalog-options ${normalizedQuery ? "search-results" : "frequent-options"}`}>
      {filtered.map((industry) => {
        const translated = hunterIndustrySpanishLabel(industry);
        return <label className="industry-option" key={industry}>
          <input type="checkbox" checked={value.includes(industry)} onChange={(event) => toggle(industry, event.target.checked)} />
          <span><strong>{translated}</strong>{translated !== industry && <small>{industry}</small>}</span>
        </label>;
      })}
      {filtered.length === 0 && <div className="industry-catalog-empty">No hay coincidencias. Probá con otra palabra en español o con el nombre oficial en inglés.</div>}
    </div>
    <FieldError message={error} />
  </section>;
}

function HunterHeadcountSelect({ minEmployees, maxEmployees, selectedRanges, onChange, error }: {
  minEmployees: number;
  maxEmployees: number;
  selectedRanges: HunterHeadcount[];
  onChange: (range: { minEmployees: number; maxEmployees: number; headcountRanges: HunterHeadcount[] }) => void;
  error?: string;
}) {
  const allSelected = selectedRanges.length === hunterHeadcountOptions.length;
  const update = (ranges: HunterHeadcount[]) => {
    const ordered = hunterHeadcountOptions.filter((option) => ranges.includes(option.value));
    if (!ordered.length) return;
    onChange({ minEmployees: ordered[0]!.minEmployees, maxEmployees: ordered.at(-1)!.maxEmployees, headcountRanges: ordered.map((option) => option.value) });
  };
  return <div className="company-size-control full">
    <div className="company-size-heading"><strong>Tamaños de empresa en Hunter</strong><button type="button" className="secondary" disabled={allSelected} onClick={() => update(hunterHeadcountOptions.map((range) => range.value))}>{allSelected ? "Todos seleccionados" : "Seleccionar todos"}</button></div>
    {!selectedRanges.length && <small className="legacy-company-size">Configuración anterior conservada: {minEmployees}–{maxEmployees} empleados. Seleccioná uno o más rangos de Hunter para reemplazarla.</small>}
    <div className="company-size-options">
      {hunterHeadcountOptions.map((range) => <label className="checkbox-row" key={range.value}><input type="checkbox" checked={selectedRanges.includes(range.value)} onChange={(event) => {
        const next = event.target.checked ? [...selectedRanges, range.value] : selectedRanges.filter((value) => value !== range.value);
        update(next);
      }} /> {range.value}</label>)}
    </div>
    <small>Podés combinar rangos. Se envían a Hunter exactamente como están seleccionados, sin ampliar la búsqueda a los intervalos intermedios.</small>
    <FieldError message={error} />
  </div>;
}

const campaignTargetRoleSuggestions = [
  "CEO", "Director/a General", "Gerente General", "COO", "Director/a de Operaciones",
  "Director/a Comercial", "Gerente Comercial", "Director/a de Ventas", "Gerente de Ventas",
  "Director/a de Desarrollo de Negocios", "Business Development", "Director/a de Alianzas",
  "Gerente de Partnerships", "Director/a de Canales", "Gerente de Canales", "Director/a de Marketing",
  "Director/a de Tecnología (CTO)", "CIO", "Director/a de Sistemas", "Gerente de IT",
  "Director/a de Infraestructura", "Gerente de Infraestructura", "CISO", "Director/a de Seguridad",
  "Gerente de Seguridad", "Director/a de Innovación", "Director/a de Transformación Digital",
  "Director/a de Ingeniería", "Gerente de Proyectos", "PMO", "Director/a de Compras",
  "Gerente de Operaciones", "Gerente de Ingeniería", "Gerente de Despliegue", "Jefe de Despliegue",
  "Responsable FTTH", "Gerente de Construcción de Red", "Jefe de Planta Externa",
  "Network Deployment Manager", "Field Operations Manager", "OSP Manager",
  "Gerente de Compras", "Abastecimiento", "Supply Chain", "Facilities", "Mantenimiento",
  "Director/a de Producto", "Gerente de Producto",
];

function localDateTimeInputValue(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function defaultCampaignStart() {
  const start = new Date();
  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() + 5);
  return localDateTimeInputValue(start);
}

function outreachEndForDuration(startsAt: string, durationDays: number) {
  const base = startsAt ? new Date(`${startsAt}:00`) : new Date();
  base.setDate(base.getDate() + durationDays);
  base.setHours(11, 59, 0, 0);
  return localDateTimeInputValue(base);
}

function campaignDurationDays(startsAt: string, endsAt: string) {
  if (!startsAt || !endsAt) return 1;
  const elapsed = new Date(`${endsAt}:00`).getTime() - new Date(`${startsAt}:00`).getTime();
  const days = Math.round(elapsed / 86_400_000);
  return [1, 7, 15, 30].includes(days) ? days : 1;
}

function ChipField({ label, value, onChange, suggestions = [], help, error }: { label: string; value: string; onChange: (value: string) => void; suggestions?: string[]; help?: string; error?: string }) {
  const [draft, setDraft] = useState("");
  const items = listFromText(value);
  const commit = (raw: string) => {
    const additions = listFromText(raw);
    if (!additions.length) return;
    const next = [...new Map([...items, ...additions].map((item) => [item.toLocaleLowerCase("es"), item])).values()];
    onChange(next.join("\n"));
    setDraft("");
  };
  return <div className="chip-field" role="group" aria-label={label}>
    <span className="chip-field-label">{label}</span>
    <div className="chip-field-values">{items.map((item) => <span className="editable-chip" key={item}>{item}<button type="button" aria-label={`Quitar ${item}`} onClick={() => onChange(items.filter((value) => value !== item).join("\n"))}>×</button></span>)}</div>
    <input list={`${label.replace(/\W+/g, "-").toLowerCase()}-suggestions`} value={draft} placeholder={`Agregar ${label.toLowerCase()}…`} onChange={(event) => setDraft(event.target.value)} onBlur={() => commit(draft)} onKeyDown={(event) => { if (["Enter", ","].includes(event.key)) { event.preventDefault(); commit(draft); } }} />
    <datalist id={`${label.replace(/\W+/g, "-").toLowerCase()}-suggestions`}>{suggestions.map((item) => <option key={item} value={item} />)}</datalist>
    {help && <small>{help}</small>}
    <FieldError message={error} />
  </div>;
}

const offerClaims = (offer?: Offer | null) => {
  try {
    const value = JSON.parse(offer?.claimsJson ?? "[]") as unknown;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
};

function parsedJson<T>(value: string | null | undefined, fallback: T): T {
  try { return value ? JSON.parse(value) as T : fallback; } catch { return fallback; }
}

function Status({ value }: { value: string }) {
  return <StatusBadge value={value} />;
}
function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Target />
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}
function PageTitle({
  eyebrow,
  title,
  body,
  action,
}: {
  eyebrow: string;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-title">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{body}</p>
      </div>
      {action}
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () =>
      api<{ user: { role: string } }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ["me"] });
      const from = (location.state as { from?: string } | null)?.from;
      const home = ["CLOSER_ADMIN", "CLOSER_OPERATOR"].includes(result.user.role) ? "/closer" : "/campaigns";
      navigate(from && !(result.user.role.startsWith("CLOSER") && !from.startsWith("/closer")) ? from : home, { replace: true });
    },
  });
  return (
    <div className="login">
      <section className="login-story">
        <BrandLogo variant="login" />
        <div>
          <span className="eyebrow">OPERACIÓN COMERCIAL RESPONSABLE</span>
          <h1>De empresas objetivo a conversaciones medibles.</h1>
          <p>
            Una plataforma para dirigir agentes, gobernar cada contacto y seguir
            la operación completa con evidencia.
          </p>
          <div className="login-points">
            <span>
              <ShieldCheck /> Control técnico
            </span>
            <span>
              <Activity /> Trazabilidad end-to-end
            </span>
            <span>
              <Bot /> Agentes coordinados
            </span>
          </div>
        </div>
        <small>Diseñado para operar con seguridad desde el primer día.</small>
      </section>
      <section className="login-form">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            login.mutate();
          }}
        >
          <div className="login-form-brand"><BrandLogo variant="login" /></div>
          <span className="eyebrow">ACCESO SEGURO</span>
          <h2>Bienvenido</h2>
          <p>Ingresá con tu usuario de Commercial AI.</p>
          <label>
            Usuario
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
          </label>
          <label>
            Contraseña
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          {login.error && (
            <div className="form-error">
              <CircleAlert size={17} />
              {login.error instanceof ApiError
                ? login.error.message
                : "No se pudo iniciar sesión"}
            </div>
          )}
          <button className="primary wide" disabled={login.isPending}>
            {login.isPending ? "Verificando…" : "Ingresar"}
            <ArrowRight size={18} />
          </button>
          <small className="help">
            Las sesiones expiran automáticamente y nunca se guardan contraseñas
            en el navegador.
          </small>
        </form>
      </section>
    </div>
  );
}

function CampaignModal({ close }: { close: () => void }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { data: offerData } = useQuery({
    queryKey: ["offers"],
    queryFn: () => api<{ items: Offer[] }>("/offers"),
  });
  const { data: health } = useQuery({
    queryKey: ["health"],
    queryFn: () => api<{ outreachMode: "DRY_RUN" | "TEST" | "PRODUCTION" }>("/health"),
  });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [offerId, setOfferId] = useState("");
  const [objective, setObjective] = useState("");
  const [audienceType, setAudienceType] = useState<CampaignConfig["audience"]["type"]>("B2B");
  const [sourceMode, setSourceMode] = useState<CampaignConfig["acquisition"]["source"]>("SEARCH");
  const [emailVerification, setEmailVerification] = useState<CampaignConfig["acquisition"]["emailVerification"]>("HUNTER");
  const [individualLocations, setIndividualLocations] = useState("");
  const [individualOccupations, setIndividualOccupations] = useState("");
  const [individualInterests, setIndividualInterests] = useState("");
  const [industries, setIndustries] = useState<HunterIndustry[]>([]);
  const [countries, setCountries] = useState("Argentina");
  const [industryMatchMode, setIndustryMatchMode] = useState<CampaignConfig["icp"]["industryMatchMode"]>("PREFERENCE");
  const [employeeMin, setEmployeeMin] = useState(defaultConfig.icp.employeeMin);
  const [employeeMax, setEmployeeMax] = useState(defaultConfig.icp.employeeMax);
  const [headcountRanges, setHeadcountRanges] = useState<HunterHeadcount[]>(() => [...(defaultConfig.icp.headcountRanges ?? [])]);
  const [targetRoles, setTargetRoles] = useState(defaultConfig.icp.targetRoles.join(", "));
  const [targetRoleSource, setTargetRoleSource] = useState<CampaignConfig["icp"]["targetRoleSource"]>(defaultConfig.icp.targetRoleSource);
  const [companies, setCompanies] = useState(1);
  const [contacts, setContacts] = useState(1);
  const [meetings, setMeetings] = useState(1);
  const [maxEmailsPerDay, setMaxEmailsPerDay] = useState(10);
  const [messageSource, setMessageSource] = useState<CampaignConfig["messageSource"]>("AGENT");
  const [copyPolicy, setCopyPolicy] = useState<CampaignConfig["copyPolicy"]>("AS_CONFIGURED");
  const [publicSignalPolicy, setPublicSignalPolicy] = useState<CampaignConfig["personalization"]["publicSignalPolicy"]>("DISABLED");
  const requireApproval = true;
  const [startsAt, setStartsAt] = useState(defaultCampaignStart);
  const [outreachDurationDays, setOutreachDurationDays] = useState(1);
  const [outreachEndsAt, setOutreachEndsAt] = useState(() => outreachEndForDuration(defaultCampaignStart(), 1));
  const [timezone, setTimezone] = useState("America/Argentina/Buenos_Aires");
  const [enabledWeekdays, setEnabledWeekdays] = useState([1, 2, 3, 4, 5]);
  const [sendWindowStart, setSendWindowStart] = useState("00:00");
  const [sendWindowEnd, setSendWindowEnd] = useState("23:59");
  const [monitoringDays, setMonitoringDays] = useState(7);
  const [initialAssetId, setInitialAssetId] = useState("");
  const [configureMeeting, setConfigureMeeting] = useState(true);
  const [calendarId, setCalendarId] = useState("sistemas@vicnet.com.ar");
  const [organizerEmail, setOrganizerEmail] = useState("sistemas@vicnet.com.ar");
  const [meetingTimezone, setMeetingTimezone] = useState("America/Argentina/Buenos_Aires");
  const [meetingWorkdayStart, setMeetingWorkdayStart] = useState("00:00");
  const [meetingWorkdayEnd, setMeetingWorkdayEnd] = useState("23:59");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [minimumNoticeHours, setMinimumNoticeHours] = useState(24);
  const [optionCount, setOptionCount] = useState(3);
  const [searchHorizonDays, setSearchHorizonDays] = useState(14);
  const [addGoogleMeet, setAddGoogleMeet] = useState(true);
  const [meetingTeamMemberIds, setMeetingTeamMemberIds] = useState<string[]>([]);
  const [closerAdminId, setCloserAdminId] = useState("");
  const selectedOffer = offerData?.items.find((offer) => offer.id === offerId);
  const approvedPdfs = selectedOffer?.assets?.filter((asset) => asset.kind === "PDF" && asset.approved && asset.status !== "RETIRED") ?? [];
  const usesB2bSearch = audienceType === "B2B" && sourceMode !== "IMPORTED";
  const effectiveEmailVerification = emailVerificationFor(audienceType, sourceMode, emailVerification);
  const targetCompanyCount = audienceType === "B2B" ? companies : contacts;
  const campaignPayload = {
    name,
    description,
    offerId: offerId || null,
    meetingTeamMemberIds,
    closerAdminId: closerAdminId || null,
    closerWorkflowEnabled: true,
    config: {
      ...defaultConfig,
      objective,
      audience: {
        type: audienceType,
        individualCriteria: {
          locations: listFromText(individualLocations),
          occupations: listFromText(individualOccupations),
          interests: listFromText(individualInterests),
        },
      },
      acquisition: { source: sourceMode, emailVerification: effectiveEmailVerification },
      icp: {
        ...defaultConfig.icp,
        industries: usesB2bSearch ? industries : [],
        countries: usesB2bSearch ? listFromText(countries) : [],
        industryMatchMode,
        employeeMin: usesB2bSearch ? employeeMin : 0,
        employeeMax: usesB2bSearch ? employeeMax : 1,
        headcountRanges: usesB2bSearch ? headcountRanges : undefined,
        targetRoles: usesB2bSearch ? listFromText(targetRoles) : [],
        targetRoleSource,
      },
      goal: {
        ...defaultConfig.goal,
        targetCompanies: targetCompanyCount,
        targetContacts: contacts,
        targetMeetings: meetings,
      },
      limits: {
        ...defaultConfig.limits,
        maxEmailsPerDay,
        maxCompaniesPerRun: campaignCompanyCapacity(targetCompanyCount, contacts),
        maxContactsPerRun: contacts,
        requireApproval,
      },
      messageSource,
      copyPolicy,
      personalization: { ...defaultConfig.personalization, publicSignalPolicy },
      schedule: {
        ...defaultConfig.schedule,
        startsAt: optionalIso(startsAt, timezone),
        outreachEndsAt: optionalIso(outreachEndsAt, timezone),
        timezone,
        enabledWeekdays,
        sendWindowStart,
        sendWindowEnd,
        monitoringDays,
      },
      initialAssetId: initialAssetId || null,
      ...(configureMeeting ? { meeting: {
        calendarId,
        organizerEmail,
        timezone: meetingTimezone,
        durationMinutes,
        workdayStart: meetingWorkdayStart,
        workdayEnd: meetingWorkdayEnd,
        minimumNoticeHours,
        optionCount,
        searchHorizonDays,
        addGoogleMeet,
      } } : {}),
    },
  };
  const parsedPayload = createCampaignSchema.safeParse(campaignPayload);
  const readinessErrors = campaignReadinessIssues({ offerId: campaignPayload.offerId, config: campaignPayload.config });
  const deferredReadinessErrors = readinessErrors.filter((message) => !["Seleccioná una oferta", "Definí un objetivo comercial"].includes(message));
  const campaignFieldErrors: Record<string, string> = {
    ...validationFieldMap(parsedPayload.success ? [] : parsedPayload.error.issues),
    ...(!offerId ? { offerId: "Seleccioná una oferta." } : {}),
    ...(!objective.trim() ? { "config.objective": "Definí un objetivo comercial." } : {}),
  };
  const formErrors = validationSummary(
    campaignFieldErrors,
    [],
  );
  const create = useMutation({
    mutationFn: () =>
      api<Campaign>("/campaigns", {
        method: "POST",
        body: JSON.stringify(campaignPayload),
      }),
    onSuccess: (campaign) => {
      client.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${campaign.id}`);
    },
  });
  return (
    <motion.div
      className="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={close}
    >
      <motion.div
        className="modal"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button className="icon-button close" onClick={close}>
          <X />
        </button>
        <span className="eyebrow">NUEVA CAMPAÑA</span>
        <h2>Definí el objetivo comercial</h2>
        <p>
          La campaña nace como borrador. Podrás revisarla antes de iniciar la
          ejecución.
        </p>
        <div className="form-grid">
          <label>
            Tipo de público
            <select value={audienceType} onChange={(event) => {
              const next = event.target.value as CampaignConfig["audience"]["type"];
              setAudienceType(next);
              if (next === "B2C") setEmailVerification("NONE");
              else if (sourceMode === "SEARCH") setEmailVerification("HUNTER");
            }}>
              <option value="B2B">B2B · empresas y decisores</option>
              <option value="B2C">B2C · personas independientes</option>
            </select>
            <FieldHelp>Esta elección adapta los datos requeridos y las fuentes permitidas.</FieldHelp>
          </label>
          <label>
            Origen de prospectos
            <select value={sourceMode} onChange={(event) => {
              const next = event.target.value as CampaignConfig["acquisition"]["source"];
              setSourceMode(next);
              if (audienceType === "B2B" && next === "SEARCH") setEmailVerification("HUNTER");
            }}>
              <option value="SEARCH">Búsqueda de prospectos</option>
              <option value="IMPORTED">Base importada (CSV)</option>
              <option value="HYBRID">Búsqueda + base importada</option>
            </select>
            <FieldHelp>{sourceMode === "SEARCH" ? "La plataforma buscará candidatos según el público elegido." : "La carga del CSV será obligatoria antes de activar la campaña."}</FieldHelp>
          </label>
          <label className="full">
            Verificación de emails
            <select
              value={effectiveEmailVerification}
              disabled={audienceType === "B2C" || sourceMode === "SEARCH"}
              onChange={(event) => setEmailVerification(event.target.value as CampaignConfig["acquisition"]["emailVerification"])}
            >
              <option value="NONE">Sin verificación externa</option>
              <option value="HUNTER">Validar con Hunter</option>
            </select>
            <FieldHelp>{audienceType === "B2C" ? "Hunter queda deshabilitado por regla de backend en campañas B2C." : sourceMode === "SEARCH" ? "La búsqueda B2B existente utiliza Hunter." : "En bases B2B importadas, la validación con Hunter es opcional."}</FieldHelp>
            <FieldError message={campaignFieldErrors["config.acquisition.emailVerification"]} />
          </label>
          <label className="full">
            Nombre
            <input
              placeholder={formCopy.campaign.namePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FieldHelp>{formCopy.campaign.nameHelp}</FieldHelp>
            <FieldError message={campaignFieldErrors.name} />
          </label>
          <label className="full">
            Descripción
            <textarea
              placeholder={formCopy.campaign.descriptionPlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <FieldHelp>{formCopy.campaign.descriptionHelp}</FieldHelp>
            <FieldError message={campaignFieldErrors.description} />
          </label>
          <label className="full">
            Oferta seleccionada
            <select value={offerId} onChange={(event) => { setOfferId(event.target.value); setInitialAssetId(""); }}>
              <option value="">Seleccionar oferta…</option>
              {offerData?.items.map((offer) => <option key={offer.id} value={offer.id}>{offer.name}</option>)}
            </select>
            <FieldHelp>Dato obligatorio. La oferta define el conocimiento comercial autorizado.</FieldHelp>
            <FieldError message={campaignFieldErrors.offerId} />
          </label>
          <label className="full">
            Objetivo
            <textarea
              placeholder={formCopy.campaign.objectivePlaceholder}
              value={objective}
              onChange={(event) => setObjective(event.target.value)}
            />
            <FieldHelp>{formCopy.campaign.objectiveHelp}</FieldHelp>
            <FieldError message={campaignFieldErrors["config.objective"]} />
          </label>
          {usesB2bSearch ? <>
            <HunterIndustrySelect value={industries} onChange={setIndustries} error={campaignFieldErrors["config.icp.industries"]} />
            <label>Uso de industrias<select value={industryMatchMode} onChange={(event) => setIndustryMatchMode(event.target.value as CampaignConfig["icp"]["industryMatchMode"])}><option value="REQUIRED">Filtro obligatorio</option><option value="PREFERENCE">Preferencia</option><option value="EXPLORATORY">Orientación exploratoria</option></select></label>
            <ChipField label="Países" value={countries} onChange={setCountries} suggestions={["Argentina", "Paraguay", "Uruguay", "Chile", "Brasil", "México"]} help={`${formCopy.campaign.defaultHelp} Usá nombres o códigos ISO.`} error={campaignFieldErrors["config.icp.countries"]} />
            <label>Origen de los roles<select value={targetRoleSource} onChange={(event) => setTargetRoleSource(event.target.value as CampaignConfig["icp"]["targetRoleSource"])}><option value="DIRECTOR_AI">Director IA según oferta y objetivo (recomendado)</option><option value="MANUAL">Definidos manualmente</option></select><small>{targetRoleSource === "DIRECTOR_AI" ? "El Director IA propondrá entre 8 y 20 roles pertinentes y justificados, cubriendo varias áreas de decisión. La lista manual queda como respaldo." : "La ejecución usará exactamente la lista indicada."}</small></label>
            <ChipField label={targetRoleSource === "DIRECTOR_AI" ? "Roles de respaldo" : "Roles objetivo"} value={targetRoles} onChange={setTargetRoles} suggestions={campaignTargetRoleSuggestions} help={formCopy.campaign.defaultHelp} error={campaignFieldErrors["config.icp.targetRoles"]} />
            <HunterHeadcountSelect minEmployees={employeeMin} maxEmployees={employeeMax} selectedRanges={headcountRanges} onChange={(range) => { setEmployeeMin(range.minEmployees); setEmployeeMax(range.maxEmployees); setHeadcountRanges(range.headcountRanges); }} error={campaignFieldErrors["config.icp.employeeMin"] ?? campaignFieldErrors["config.icp.employeeMax"]} />
          </> : audienceType === "B2C" ? <>
            <ChipField label="Ubicaciones" value={individualLocations} onChange={setIndividualLocations} suggestions={["Argentina", "Paraguay", "Uruguay", "Chile", "Brasil", "México"]} help="Opcional para bases importadas; para búsqueda sirve como criterio geográfico." error={campaignFieldErrors["config.audience.individualCriteria.locations"]} />
            <ChipField label="Ocupaciones o actividades" value={individualOccupations} onChange={setIndividualOccupations} suggestions={["Profesional independiente", "Consultor", "Comerciante", "Emprendedor"]} help="Usá categorías pertinentes para la campaña, sin datos sensibles." error={campaignFieldErrors["config.audience.individualCriteria.occupations"]} />
            <ChipField label="Intereses declarados" value={individualInterests} onChange={setIndividualInterests} suggestions={[]} help="Opcional. No incluyas categorías sensibles ni inferidas." error={campaignFieldErrors["config.audience.individualCriteria.interests"]} />
            <FieldError message={campaignFieldErrors["config.audience.individualCriteria"]} />
          </> : <div className="form-warning full"><CircleAlert size={17} />El CSV definirá el universo B2B. Industria, empresa y tamaño serán datos opcionales de cada fila.</div>}
          <label>Email máximos por día<NumericInput min="1" max="100" value={maxEmailsPerDay} onValueChange={setMaxEmailsPerDay} /><FieldHelp>{formCopy.campaign.defaultHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.limits.maxEmailsPerDay"]} /></label>
          <div className="channel-field full"><span>Aprobación humana obligatoria</span><label><input type="checkbox" checked disabled /> Todos los correos salientes se detienen para revisión</label><small>Aplica a correos iniciales, seguimientos, respuestas y coordinación. Claim Check y SAFE SEND no reemplazan la decisión humana.</small></div>
          <label className="full">Origen de todos los correos y textos de Calendar<select value={messageSource} onChange={(event) => setMessageSource(event.target.value as CampaignConfig["messageSource"])}><option value="AGENT">Agente · redacción contextual</option><option value="TEMPLATES">Plantillas · texto publicado de la campaña</option></select><FieldHelp>Aplica a contactos iniciales, seguimientos, respuestas, horarios e invitaciones y actualizaciones de Calendar. Siempre requiere los controles y aprobaciones correspondientes. Se congela al iniciar cada run.</FieldHelp></label>
          <label className="full">Redacción en todo el flujo<select value={copyPolicy} onChange={event => setCopyPolicy(event.target.value as CampaignConfig["copyPolicy"])}><option value="AS_CONFIGURED">Usar el origen configurado arriba</option><option value="TEMPLATE_OR_AGENT">Mixto · plantilla si responde, IA si no alcanza</option></select><FieldHelp>En modo mixto se evalúa la plantilla con el contexto actual. Si falta o no encaja, el agente responsable redacta. Aplica a primeros contactos, seguimientos, respuestas y Calendar. Se conservan las validaciones, la aprobación y el contexto congelado de cada ejecución.</FieldHelp></label>
          <label className="full">{audienceType === "B2C" ? "Uso de señales públicas en el mensaje" : "Uso de señales públicas"}<select value={publicSignalPolicy} onChange={(event) => setPublicSignalPolicy(event.target.value as CampaignConfig["personalization"]["publicSignalPolicy"])}><option value="DISABLED">Desactivado (predeterminado): no usar señales públicas</option><option value="OPTIONAL">Opcional: el Director IA decide y puede redactar sin señal</option><option value="REQUIRED">Obligatorio: detener si no existe una señal pertinente</option></select><FieldHelp>{audienceType === "B2C" ? "Esta opción controla únicamente la personalización del correo. No habilita ni impide la búsqueda de personas: la prospección usa su propia política de fuentes públicas permitidas y exige un email publicado explícitamente." : "Una señal puede ser un proyecto, transacción, alianza, implementación, expansión, apertura, búsqueda laboral, evento técnico o iniciativa documentada. Su ausencia nunca descarta empresas ni contactos: si elegís «Obligatorio», la ejecución se detiene para revisión."}</FieldHelp></label>
          <label>Inicio de campaña<input type="datetime-local" lang="en-GB" step="60" value={startsAt} onChange={(event) => { setStartsAt(event.target.value); setOutreachEndsAt(outreachEndForDuration(event.target.value, outreachDurationDays)); }} /><FieldHelp>Formato de 24 horas.</FieldHelp><FieldError message={campaignFieldErrors["config.schedule.startsAt"]} /></label>
          <label>Fin de primeros contactos<div className="date-duration-field"><input type="datetime-local" lang="en-GB" step="60" value={outreachEndsAt} onChange={(event) => setOutreachEndsAt(event.target.value)} /><select aria-label="Duración de primeros contactos" value={outreachDurationDays} onChange={(event) => { const days = Number(event.target.value); setOutreachDurationDays(days); setOutreachEndsAt(outreachEndForDuration(startsAt, days)); }}><option value="1">1 día</option><option value="7">7 días</option><option value="15">15 días</option><option value="30">30 días</option></select></div><FieldHelp>Al cambiar la duración, el fin se ajusta a las 11:59 (formato de 24 horas).</FieldHelp><FieldError message={campaignFieldErrors["config.schedule.outreachEndsAt"]} /></label>
          <TimezoneField label="Zona horaria" value={timezone} onChange={setTimezone} error={campaignFieldErrors["config.schedule.timezone"]} />
          <div className="full"><WeekdayPicker value={enabledWeekdays} onChange={setEnabledWeekdays} /><FieldError message={campaignFieldErrors["config.schedule.enabledWeekdays"]} /></div>
          <label>Ventana desde<input type="time" lang="en-GB" step="60" value={sendWindowStart} onChange={(event) => setSendWindowStart(event.target.value)} /><FieldError message={campaignFieldErrors["config.schedule.sendWindowStart"]} /></label>
          <label>Ventana hasta<input type="time" lang="en-GB" step="60" value={sendWindowEnd} onChange={(event) => setSendWindowEnd(event.target.value)} /><FieldError message={campaignFieldErrors["config.schedule.sendWindowEnd"]} /></label>
          <label>Días adicionales de monitoreo<NumericInput min="1" max="90" value={monitoringDays} onValueChange={setMonitoringDays} /><FieldHelp>{formCopy.campaign.defaultHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.schedule.monitoringDays"]} /></label>
          <label className="full">Adjunto del primer correo (opcional)<select value={initialAssetId} onChange={(event) => setInitialAssetId(event.target.value)}><option value="">No enviar PDF</option>{approvedPdfs.map((asset) => <option key={asset.id} value={asset.id}>Enviar {asset.originalName ?? asset.name} · v{asset.version ?? 1}</option>)}</select><FieldHelp>Si elegís un PDF, se adjunta sólo al correo inicial. No se repite en seguimientos, respuestas, opciones de reunión ni invitaciones de Calendar.</FieldHelp></label>
          <MeetingTeamSelector value={meetingTeamMemberIds} onChange={setMeetingTeamMemberIds} />
          <CloserAdminSelector value={closerAdminId} onChange={setCloserAdminId} required={meetings > 0} />
          <div className="channel-field full"><span>Coordinación de reuniones</span><label><input type="checkbox" checked={configureMeeting} onChange={(event) => setConfigureMeeting(event.target.checked)} /> Configurar Calendar para ofrecer horarios</label></div>
          {configureMeeting && <><label>Calendar ID<input value={calendarId} onChange={(event) => setCalendarId(event.target.value)} placeholder={formCopy.meeting.calendarIdPlaceholder} /><FieldHelp>{formCopy.meeting.requiredWhenEnabled}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.calendarId"]} /></label><label>Email organizador<input type="email" value={organizerEmail} onChange={(event) => setOrganizerEmail(event.target.value)} placeholder={formCopy.meeting.organizerPlaceholder} /><FieldHelp>{formCopy.meeting.requiredWhenEnabled}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.organizerEmail"]} /></label><TimezoneField label="Zona horaria de agenda" value={meetingTimezone} onChange={setMeetingTimezone} error={campaignFieldErrors["config.meeting.timezone"]} /><label>Jornada desde<input type="time" lang="en-GB" step="60" value={meetingWorkdayStart} onChange={(event) => setMeetingWorkdayStart(event.target.value)} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.workdayStart"]} /></label><label>Jornada hasta<input type="time" lang="en-GB" step="60" value={meetingWorkdayEnd} onChange={(event) => setMeetingWorkdayEnd(event.target.value)} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.workdayEnd"]} /></label><label>Duración (minutos)<NumericInput min="15" max="240" value={durationMinutes} onValueChange={setDurationMinutes} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.durationMinutes"]} /></label><label>Anticipación mínima (horas)<NumericInput min="1" max="720" value={minimumNoticeHours} onValueChange={setMinimumNoticeHours} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.minimumNoticeHours"]} /></label><label>Cantidad de opciones<select value={optionCount} onChange={(event) => setOptionCount(Number(event.target.value))}><option value="2">2</option><option value="3">3</option></select><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.optionCount"]} /></label><label>Horizonte (días)<NumericInput min="1" max="60" value={searchHorizonDays} onValueChange={setSearchHorizonDays} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.searchHorizonDays"]} /></label><label className="checkbox-row"><input type="checkbox" checked={addGoogleMeet} onChange={(event) => setAddGoogleMeet(event.target.checked)} /> Agregar Google Meet</label></>}
          {audienceType === "B2B" && <label>
            Empresas objetivo
            <NumericInput min="1" value={companies} onValueChange={setCompanies} />
            <FieldError message={campaignFieldErrors["config.goal.targetCompanies"]} />
          </label>}
          <label>{audienceType === "B2C" ? "Personas objetivo" : "Contactos objetivo"}<NumericInput min="1" value={contacts} onValueChange={setContacts} /><FieldHelp>{audienceType === "B2B" ? "La capacidad de búsqueda se ajusta automáticamente a la meta y agrega empresas de reserva; no hay un tope oculto de contactos." : "Cantidad de personas que la campaña buscará contactar."}</FieldHelp><FieldError message={campaignFieldErrors["config.goal.targetContacts"]} /></label>
          <label>
            Reuniones meta
            <NumericInput min="0" value={meetings} onValueChange={setMeetings} />
            <FieldError message={campaignFieldErrors["config.goal.targetMeetings"]} />
          </label>
          <label>
            Modo de salida
            <input
              value={health ? `${statusText[health.outreachMode]} · Claim Check + SAFE SEND` : "Claim Check + SAFE SEND"}
              disabled
            />
          </label>
        </div>
        {create.error && (
          <div className="form-error">
            {visibleErrorMessage(create.error, "No se pudo crear la campaña.")}
          </div>
        )}
        {deferredReadinessErrors.length > 0 && <div className="form-warning"><CircleAlert size={17} />La campaña se guardará como borrador. Antes de activarla: {deferredReadinessErrors.join(" · ")}</div>}
        {formErrors.length > 0 && <div className="form-error"><CircleAlert size={17} />{formErrors.join(" · ")}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={close}>
            Cancelar
          </button>
          <button
            className="primary"
            disabled={create.isPending || formErrors.length > 0}
            onClick={() => create.mutate()}
          >
            Crear campaña
            <ArrowRight size={17} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function CampaignsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [copySource, setCopySource] = useState<Campaign | null>(null);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<ListingSortField>("updatedAt");
  const [sortDirection, setSortDirection] = useState<ListingSortDirection>("desc");
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me") });
  const { data, isPending } = useQuery({
    queryKey: ["campaigns", search],
    queryFn: () =>
      api<{ items: Campaign[] }>(
        `/campaigns${search ? `?q=${encodeURIComponent(search)}` : ""}`,
      ),
  });
  const campaigns = data?.items ?? [];
  const sortedCampaigns = useMemo(() => sortListingItems(campaigns, sortField, sortDirection), [campaigns, sortField, sortDirection]);
  const active = campaigns.filter((item) =>
    ["READY", "RUNNING", "PAUSED"].includes(item.status),
  ).length;
  const approvals = campaigns.reduce(
    (sum, item) => sum + (item._count?.approvals ?? 0),
    0,
  );
  return (
    <>
      <PageTitle
        eyebrow="CAMPAÑAS"
        title="Centro de mando comercial"
        body="Diseñá, activá y auditá cada iniciativa desde una única vista."
        action={
          <button className="primary" onClick={() => setShowCreate(true)}>
            <Plus size={18} />
            Nueva campaña
          </button>
        }
      />
      <div className="metrics">
        <div>
          <span>CAMPAÑAS TOTALES</span>
          <strong>{campaigns.length}</strong>
          <small>en el espacio de trabajo</small>
        </div>
        <div>
          <span>EN OPERACIÓN</span>
          <strong>{active}</strong>
          <small>listas, activas o pausadas</small>
        </div>
        <div>
          <span>CONTACTOS TRABAJADOS</span>
          <strong>
            {campaigns.reduce(
              (sum, item) => sum + (item._count?.contacts ?? 0),
              0,
            )}
          </strong>
          <small>con trazabilidad</small>
        </div>
        <div>
          <span>APROBACIONES</span>
          <strong>{approvals}</strong>
          <small>historial acumulado</small>
        </div>
      </div>
      <div className="toolbar listing-toolbar">
        <div className="input-search">
          <Search size={18} />
          <input
            placeholder="Buscar campañas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ListingSortControls field={sortField} direction={sortDirection} onFieldChange={setSortField} onDirectionChange={setSortDirection} />
        <span>{campaigns.length} resultados</span>
      </div>
      {isPending ? (
        <div className="loading">Cargando campañas…</div>
      ) : campaigns.length === 0 ? (
        <Empty
          title="Tu primera campaña empieza acá"
          body="Definí el ICP, los límites y el resultado esperado. La plataforma se ocupa del flujo controlado."
        />
      ) : (
        <div className="campaign-grid">
          {sortedCampaigns.map((campaign, index) => (
            <motion.div
              key={campaign.id}
              className="campaign-card-wrapper"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
            >
              <Link className="campaign-card" to={`/campaigns/${campaign.id}`}>
                <div className="card-top">
                  <span className="campaign-icon">
                    <Target />
                  </span>
                  <Status value={campaign.status} />
                </div>
                <h3 title={campaign.name}>{campaign.name}</h3>
                <p title={campaign.description || "Sin descripción"}>{campaign.description || "Sin descripción"}</p>
                <div className="card-stats">
                  <span>
                    <Building2 />{" "}
                    <strong>{campaign._count?.companies ?? 0}</strong> empresas
                  </span>
                  <span>
                    <Users /> <strong>{campaign._count?.contacts ?? 0}</strong>{" "}
                    contactos
                  </span>
                  <span>
                    <Activity /> <strong>{campaign._count?.runs ?? 0}</strong>{" "}
                    runs
                  </span>
                </div>
                <div className="card-foot">
                  <small>Actualizada {formatDate(campaign.updatedAt)}</small>
                  <ChevronRight />
                </div>
              </Link>
              {me?.user.role && me.user.role !== "VIEWER" && <button type="button" className="secondary campaign-duplicate-button" onClick={() => setCopySource(campaign)}>Duplicar campaña</button>}
            </motion.div>
          ))}
        </div>
      )}
      <AnimatePresence>
        {showCreate && <CampaignModal close={() => setShowCreate(false)} />}
        {copySource && <CampaignEditorModal key={copySource.id} campaign={copySource} mode="duplicate" close={() => setCopySource(null)} />}
      </AnimatePresence>
    </>
  );
}

function OfferModal({ close }: { close: () => void }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [claimsText, setClaimsText] = useState("");
  const claims = linesFromText(claimsText);
  const validation = createOfferSchema.safeParse({ name, description, claims, active: true });
  const offerFieldErrors = validationFieldMap(validation.success ? [] : validation.error.issues);
  const offerErrors = validationSummary(offerFieldErrors);
  const create = useMutation({
    mutationFn: () => api<Offer>("/offers", {
      method: "POST",
      body: JSON.stringify({ name, description, claims, active: true }),
    }),
    onSuccess: async (offer) => {
      await client.invalidateQueries({ queryKey: ["offers"] });
      navigate(`/offers/${offer.id}`);
    },
  });
  return (
    <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={close}>
      <motion.div className="modal" initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onMouseDown={(event) => event.stopPropagation()}>
        <button className="icon-button close" onClick={close}><X /></button>
        <span className="eyebrow">NUEVA OFERTA</span>
        <h2>Registrar producto o servicio</h2>
        <p>Definí la propuesta y los únicos claims comerciales que podrán utilizarse como base en nuevas campañas.</p>
        <div className="form-grid">
          <label className="full">Nombre<input value={name} onChange={(event) => setName(event.target.value)} placeholder={formCopy.offer.namePlaceholder} /><FieldHelp>{formCopy.offer.nameHelp}</FieldHelp><FieldError message={offerFieldErrors.name} /></label>
          <label className="full">Descripción<textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder={formCopy.offer.descriptionPlaceholder} /><FieldHelp>{formCopy.offer.descriptionHelp}</FieldHelp><FieldError message={offerFieldErrors.description} /></label>
          <label className="full">Claims autorizados<textarea className="claims-editor" value={claimsText} onChange={(event) => setClaimsText(event.target.value)} placeholder={formCopy.offer.claimsPlaceholder} /><FieldHelp>{formCopy.offer.claimsHelp}</FieldHelp><FieldError message={offerFieldErrors.claims} /></label>
        </div>
        <div className="governance-notice"><ShieldCheck /><p>Los claims autorizan el lenguaje comercial. La evidencia que los respalda se carga y aprueba en el expediente de la oferta.</p></div>
        {create.error && <div className="form-error">{visibleErrorMessage(create.error, "No se pudo crear la oferta.")}</div>}
        {!validation.success && (name || description || claimsText) && <div className="form-error"><CircleAlert size={17} />Revisá los campos señalados: {offerErrors.join(" · ")}</div>}
        <div className="modal-actions"><button className="secondary" onClick={close}>Cancelar</button><button className="primary" disabled={create.isPending || !validation.success} onClick={() => create.mutate()}><Plus size={17} />{create.isPending ? "Creando…" : "Crear oferta"}</button></div>
      </motion.div>
    </motion.div>
  );
}

export function OffersPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<ListingSortField>("updatedAt");
  const [sortDirection, setSortDirection] = useState<ListingSortDirection>("desc");
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me") });
  const { data, isPending } = useQuery({ queryKey: ["offers", "all"], queryFn: () => api<{ items: Offer[] }>("/offers?includeInactive=true") });
  const offers = (data?.items ?? []).filter((offer) => `${offer.name} ${offer.description}`.toLowerCase().includes(search.trim().toLowerCase()));
  const sortedOffers = useMemo(() => sortListingItems(offers, sortField, sortDirection), [offers, sortField, sortDirection]);
  const canEdit = Boolean(me?.user.role && me.user.role !== "VIEWER");
  return <>
    <PageTitle eyebrow="OFERTAS" title="Catálogo comercial" body="Administrá productos, claims autorizados y la evidencia que recibirá Hermes." action={canEdit ? <button className="primary" onClick={() => setShowCreate(true)}><Plus size={18} />Nueva oferta</button> : undefined} />
    <div className="metrics offer-metrics">
      <div><span>OFERTAS</span><strong>{data?.items.length ?? 0}</strong><small>registradas</small></div>
      <div><span>ACTIVAS</span><strong>{data?.items.filter((offer) => offer.active).length ?? 0}</strong><small>disponibles para campañas</small></div>
      <div><span>CLAIMS</span><strong>{data?.items.reduce((total, offer) => total + offerClaims(offer).length, 0) ?? 0}</strong><small>afirmaciones autorizadas</small></div>
      <div><span>EVIDENCIAS APROBADAS</span><strong>{data?.items.reduce((total, offer) => total + (offer.assets?.filter((asset) => asset.approved).length ?? 0), 0) ?? 0}</strong><small>incluidas en nuevos briefs</small></div>
    </div>
    <div className="toolbar listing-toolbar"><div className="input-search"><Search size={18} /><input placeholder="Buscar ofertas…" value={search} onChange={(event) => setSearch(event.target.value)} /></div><ListingSortControls field={sortField} direction={sortDirection} onFieldChange={setSortField} onDirectionChange={setSortDirection} /><span>{offers.length} resultados</span></div>
    {isPending ? <div className="loading" role="status">Cargando ofertas…</div> : offers.length === 0 ? <Empty title="No hay ofertas" body="Registrá el primer producto o servicio para poder asociarlo a una campaña." /> : <div className="campaign-grid offer-grid">{sortedOffers.map((offer, index) => <motion.div key={offer.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * .04 }}><Link className="campaign-card" to={`/offers/${offer.id}`}><div className="card-top"><span className="campaign-icon"><PackageOpen /></span><Status value={offer.active ? "ACTIVE" : "INACTIVE"} /></div><h3 title={offer.name}>{offer.name}</h3><p title={offer.description}>{offer.description}</p><div className="card-stats"><span><CheckCircle2 /><strong>{offerClaims(offer).length}</strong> claims</span><span><ShieldCheck /><strong>{offer.assets?.filter((asset) => asset.approved).length ?? 0}</strong> aprobadas</span><span><Target /><strong>{offer._count?.campaigns ?? 0}</strong> campañas</span></div><div className="card-foot"><small>{offer.updatedAt ? `Actualizada ${formatDate(offer.updatedAt)}` : "Oferta registrada"}</small><ChevronRight /></div></Link></motion.div>)}</div>}
    <AnimatePresence>{showCreate && <OfferModal close={() => setShowCreate(false)} />}</AnimatePresence>
  </>;
}

function OfferDetailEditor({ offer, canEdit }: { offer: Offer; canEdit: boolean }) {
  const client = useQueryClient();
  const [name, setName] = useState(offer.name);
  const [description, setDescription] = useState(offer.description);
  const [claimsText, setClaimsText] = useState(offerClaims(offer).join("\n"));
  const claims = linesFromText(claimsText);
  const validation = createOfferSchema.safeParse({ name, description, claims, active: offer.active });
  const offerFieldErrors = validationFieldMap(validation.success ? [] : validation.error.issues);
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["offer", offer.id] });
    await client.invalidateQueries({ queryKey: ["offers"] });
    await client.invalidateQueries({ queryKey: ["campaigns"] });
    await client.invalidateQueries({ queryKey: ["campaign"] });
  };
  const update = useMutation<Offer, Error, Record<string, unknown>>({
    mutationFn: (payload) => api<Offer>(`/offers/${offer.id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: refresh,
  });
  return <>
    <div className="detail-head"><div><Link className="back" to="/offers">← Ofertas</Link><div className="title-row"><h1>{offer.name}</h1><Status value={offer.active ? "ACTIVE" : "INACTIVE"} /></div><p>Oferta, claims y expediente técnico utilizados para construir CampaignBrief.</p></div>{canEdit && <button className="secondary" disabled={update.isPending} onClick={() => update.mutate({ active: !offer.active })}>{offer.active ? "Desactivar oferta" : "Activar oferta"}</button>}</div>
    <div className="overview-grid">
      <section className="panel span-3"><span className="eyebrow">DEFINICIÓN COMERCIAL</span><h2>Producto y claims autorizados</h2>{canEdit ? <><div className="form-grid offer-editor"><label>Nombre<input value={name} onChange={(event) => setName(event.target.value)} /><FieldHelp>{formCopy.offer.nameHelp}</FieldHelp><FieldError message={offerFieldErrors.name} /></label><label className="full">Descripción<textarea value={description} onChange={(event) => setDescription(event.target.value)} /><FieldHelp>{formCopy.offer.descriptionHelp}</FieldHelp><FieldError message={offerFieldErrors.description} /></label><label className="full">Claims autorizados<textarea className="claims-editor" value={claimsText} onChange={(event) => setClaimsText(event.target.value)} placeholder={formCopy.offer.claimsPlaceholder} /><FieldHelp>{formCopy.offer.claimsHelp}</FieldHelp><FieldError message={offerFieldErrors.claims} /></label></div>{update.error && <div className="form-error">{visibleErrorMessage(update.error, "No se pudo guardar la oferta.")}</div>}<div className="panel-actions"><p className="muted">Modificar la oferta afecta únicamente briefs de runs futuros; los runs existentes conservan su snapshot.</p><button className="primary" disabled={update.isPending || !validation.success} onClick={() => update.mutate({ name, description, claims })}><Save size={16} />{update.isPending ? "Guardando…" : "Guardar oferta"}</button></div></> : <><p>{offer.description}</p><div className="tags">{offerClaims(offer).map((claim) => <span key={claim}>{claim}</span>)}</div></>}</section>
      <OfferAssetsManager offer={offer} canEdit={canEdit} onChanged={refresh} />
    </div>
  </>;
}

export function OfferDetailPage() {
  const { id: offerId } = useParams();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me") });
  const { data: offer, isPending } = useQuery({ queryKey: ["offer", offerId], queryFn: () => api<Offer>(`/offers/${offerId}`), enabled: Boolean(offerId) });
  if (isPending || !offer) return <div className="loading">Cargando oferta…</div>;
  return <OfferDetailEditor key={`${offer.id}:${offer.updatedAt}`} offer={offer} canEdit={Boolean(me?.user.role && me.user.role !== "VIEWER")} />;
}

function MeetingModal({
  campaign,
  close,
}: {
  campaign: Campaign;
  close: () => void;
}) {
  const client = useQueryClient();
  const [title, setTitle] = useState(`Reunión · ${campaign.name}`);
  const [startsAt, setStartsAt] = useState("");
  const meetingTimezone = campaign.config.meeting?.timezone ?? scheduleFor(campaign.config).timezone;
  const [attendee, setAttendee] = useState("");
  const attendeeError = attendee && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendee)
    ? "Ingresá un email válido."
    : undefined;
  const pendingIntents = campaign.meetingIntents?.filter(
    (intent) => intent.status === "PENDING_REVIEW",
  ) ?? [];
  const [meetingIntentId, setMeetingIntentId] = useState(
    pendingIntents[0]?.id ?? "",
  );
  const create = useMutation({
    mutationFn: () =>
      api(`/campaigns/${campaign.id}/meetings`, {
        method: "POST",
        body: JSON.stringify({
          title,
          startsAt: zonedDateTimeLocalToUtc(startsAt, meetingTimezone),
          attendee: attendee || undefined,
          durationMinutes: 30,
          meetingIntentId,
        }),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["campaign", campaign.id] });
      close();
    },
  });
  return (
    <motion.div
      className="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={close}
    >
      <motion.div
        className="modal"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="icon-button close" onClick={close}>
          <X />
        </button>
        <span className="eyebrow">GOOGLE CALENDAR</span>
        <h2>Agendar una reunión</h2>
        <p>
          Se crea en Calendar cuando Google está en vivo; en modo simulado queda
          registrada localmente.
        </p>
        <div className="form-grid">
          <label className="full">
            Intención confirmada por el contacto
            <select
              value={meetingIntentId}
              onChange={(event) => setMeetingIntentId(event.target.value)}
            >
              {pendingIntents.map((intent) => (
                <option key={intent.id} value={intent.id}>
                  {intent.contact.firstName} {intent.contact.lastName} · {intent.note}
                </option>
              ))}
            </select>
          </label>
          <label className="full">
            Título
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
            <FieldHelp>Dato obligatorio. Se utilizará como título de la reunión.</FieldHelp>
            <FieldError message={title.trim() ? undefined : "Ingresá un título."} />
          </label>
          <label>
            Fecha y hora
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
            />
            <FieldHelp>Dato obligatorio en la zona horaria indicada.</FieldHelp>
            <FieldError message={startsAt ? undefined : "Seleccioná una fecha y hora."} />
          </label>
          <TimezoneField label="Zona horaria" value={meetingTimezone} onChange={() => undefined} />
          <label>
            Invitado opcional
            <input
              type="email"
              placeholder={formCopy.meeting.attendeePlaceholder}
              value={attendee}
              onChange={(event) => setAttendee(event.target.value)}
            />
            <FieldHelp>Dato opcional. No se completa automáticamente.</FieldHelp>
            <FieldError message={attendeeError} />
          </label>
        </div>
        {create.error && (
          <div className="form-error">
            <CircleAlert size={17} />
            {visibleErrorMessage(create.error, "No se pudo crear la reunión.")}
          </div>
        )}
        <div className="modal-actions">
          <button className="secondary" onClick={close}>
            Cancelar
          </button>
          <button
            className="primary"
            disabled={create.isPending || !title.trim() || !startsAt || !meetingIntentId || Boolean(attendeeError)}
            onClick={() => create.mutate()}
          >
            <CalendarPlus size={17} />
            {create.isPending ? "Agendando…" : "Agendar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function CampaignEditorModal({ campaign, close, mode = "edit" }: { campaign: Campaign; close: () => void; mode?: "edit" | "duplicate" }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const duplicating = mode === "duplicate";
  const { data: offerData } = useQuery({ queryKey: ["offers"], queryFn: () => api<{ items: Offer[] }>("/offers") });
  const existingSchedule = scheduleFor(campaign.config);
  const existingFollowUpPolicy = followUpPolicyFor(campaign.config);
  const [name, setName] = useState(duplicating ? `${campaign.name} (copia)` : campaign.name);
  const [description, setDescription] = useState(campaign.description);
  const [offerId, setOfferId] = useState(campaign.offer?.id ?? campaign.offerId ?? "");
  const [objective, setObjective] = useState(campaign.config.objective);
  const existingAudience = audienceFor(campaign.config);
  const existingAcquisition = acquisitionFor(campaign.config);
  const [audienceType, setAudienceType] = useState<CampaignConfig["audience"]["type"]>(existingAudience.type);
  const [sourceMode, setSourceMode] = useState<CampaignConfig["acquisition"]["source"]>(existingAcquisition.source);
  const [emailVerification, setEmailVerification] = useState<CampaignConfig["acquisition"]["emailVerification"]>(existingAcquisition.emailVerification);
  const [individualLocations, setIndividualLocations] = useState(existingAudience.individualCriteria.locations.join(", "));
  const [individualOccupations, setIndividualOccupations] = useState(existingAudience.individualCriteria.occupations.join(", "));
  const [individualInterests, setIndividualInterests] = useState(existingAudience.individualCriteria.interests.join(", "));
  const [industries, setIndustries] = useState<HunterIndustry[]>(() => normalizeHunterIndustries(campaign.config.icp.industries));
  const [countries, setCountries] = useState(campaign.config.icp.countries.join(", "));
  const [regions, setRegions] = useState(campaign.config.regions.join(", "));
  const [targetRoles, setTargetRoles] = useState(campaign.config.icp.targetRoles.join(", "));
  const [targetRoleSource, setTargetRoleSource] = useState<CampaignConfig["icp"]["targetRoleSource"]>(campaign.config.icp.targetRoleSource ?? "MANUAL");
  const [industryMatchMode, setIndustryMatchMode] = useState(campaign.config.icp.industryMatchMode ?? "PREFERENCE");
  const [employeeMin, setEmployeeMin] = useState(campaign.config.icp.employeeMin);
  const [employeeMax, setEmployeeMax] = useState(campaign.config.icp.employeeMax);
  const [headcountRanges, setHeadcountRanges] = useState<HunterHeadcount[]>(campaign.config.icp.headcountRanges ?? []);
  const [targetCompanies, setTargetCompanies] = useState(campaign.config.goal.targetCompanies);
  const [targetContacts, setTargetContacts] = useState(campaign.config.goal.targetContacts);
  const [targetMeetings, setTargetMeetings] = useState(campaign.config.goal.targetMeetings);
  const [maxEmailsPerDay, setMaxEmailsPerDay] = useState(campaign.config.limits.maxEmailsPerDay);
  const [messageSource, setMessageSource] = useState<CampaignConfig["messageSource"]>(campaign.config.messageSource ?? "AGENT");
  const [copyPolicy, setCopyPolicy] = useState<CampaignConfig["copyPolicy"]>(campaign.config.copyPolicy ?? "AS_CONFIGURED");
  const [publicSignalPolicy, setPublicSignalPolicy] = useState<CampaignConfig["personalization"]["publicSignalPolicy"]>(campaign.config.personalization?.publicSignalPolicy ?? "DISABLED");
  const requireApproval = true;
  const [timezone, setTimezone] = useState(existingSchedule.timezone);
  const [startsAt, setStartsAt] = useState(localDateTimeValue(existingSchedule.startsAt, existingSchedule.timezone));
  const [outreachEndsAt, setOutreachEndsAt] = useState(localDateTimeValue(existingSchedule.outreachEndsAt, existingSchedule.timezone));
  const [outreachDurationDays, setOutreachDurationDays] = useState(() => campaignDurationDays(
    localDateTimeValue(existingSchedule.startsAt, existingSchedule.timezone),
    localDateTimeValue(existingSchedule.outreachEndsAt, existingSchedule.timezone),
  ));
  const [enabledWeekdays, setEnabledWeekdays] = useState(existingSchedule.enabledWeekdays);
  const [sendWindowStart, setSendWindowStart] = useState(existingSchedule.sendWindowStart);
  const [sendWindowEnd, setSendWindowEnd] = useState(existingSchedule.sendWindowEnd);
  const [monitoringDays, setMonitoringDays] = useState(existingSchedule.monitoringDays);
  const [firstBusinessDay, setFirstBusinessDay] = useState(existingFollowUpPolicy.firstBusinessDay);
  const [secondBusinessDay, setSecondBusinessDay] = useState(existingFollowUpPolicy.secondBusinessDay);
  const [closeBusinessDay, setCloseBusinessDay] = useState(existingFollowUpPolicy.closeBusinessDay);
  const [initialAssetId, setInitialAssetId] = useState(campaign.config.initialAssetId ?? "");
  const [configureMeeting, setConfigureMeeting] = useState(Boolean(campaign.config.meeting));
  const [calendarId, setCalendarId] = useState(campaign.config.meeting?.calendarId ?? "");
  const [organizerEmail, setOrganizerEmail] = useState(campaign.config.meeting?.organizerEmail ?? "");
  const [meetingTimezone, setMeetingTimezone] = useState(campaign.config.meeting?.timezone ?? existingSchedule.timezone);
  const [meetingWorkdayStart, setMeetingWorkdayStart] = useState(campaign.config.meeting?.workdayStart ?? "00:00");
  const [meetingWorkdayEnd, setMeetingWorkdayEnd] = useState(campaign.config.meeting?.workdayEnd ?? "23:59");
  const [addGoogleMeet, setAddGoogleMeet] = useState(campaign.config.meeting?.addGoogleMeet ?? true);
  const [meetingTeamMemberIds, setMeetingTeamMemberIds] = useState<string[]>(() => campaign.meetingTeamMembers?.map((member) => member.id) ?? []);
  const [closerAdminId, setCloserAdminId] = useState(campaign.closerAdminId ?? "");
  const [durationMinutes, setDurationMinutes] = useState(campaign.config.meeting?.durationMinutes ?? 30);
  const [minimumNoticeHours, setMinimumNoticeHours] = useState(campaign.config.meeting?.minimumNoticeHours ?? 24);
  const [optionCount, setOptionCount] = useState(campaign.config.meeting?.optionCount ?? 3);
  const [searchHorizonDays, setSearchHorizonDays] = useState(campaign.config.meeting?.searchHorizonDays ?? 14);
  const [tone, setTone] = useState(campaign.config.tone);
  const [restrictions, setRestrictions] = useState(campaign.config.restrictions.join("\n"));
  const [excludedCustomers, setExcludedCustomers] = useState(campaign.config.excludedCustomers.join("\n"));
  const [approvedClaims, setApprovedClaims] = useState(campaign.config.approvedClaims.join("\n"));
  const [forbiddenClaims, setForbiddenClaims] = useState(campaign.config.forbiddenClaims.join("\n"));
  const [enabledChannels, setEnabledChannels] = useState(campaign.config.enabledChannels);
  const [hermesFailurePolicy, setHermesFailurePolicy] = useState(campaign.config.hermesFailurePolicy);
  const acquisitionLocked = !duplicating && (campaign._count?.runs ?? campaign.runs?.length ?? 0) > 0;
  const usesB2bSearch = audienceType === "B2B" && sourceMode !== "IMPORTED";
  const effectiveEmailVerification = emailVerificationFor(audienceType, sourceMode, emailVerification);
  const effectiveTargetCompanies = audienceType === "B2B" ? targetCompanies : targetContacts;

  const config: CampaignConfig = {
    ...campaign.config,
    objective,
    audience: {
      type: audienceType,
      individualCriteria: {
        locations: listFromText(individualLocations),
        occupations: listFromText(individualOccupations),
        interests: listFromText(individualInterests),
      },
    },
    acquisition: { source: sourceMode, emailVerification: effectiveEmailVerification },
    tone,
    regions: listFromText(regions),
    restrictions: listFromText(restrictions),
    excludedCustomers: listFromText(excludedCustomers),
    approvedClaims: linesFromText(approvedClaims),
    forbiddenClaims: linesFromText(forbiddenClaims),
    enabledChannels,
    hermesFailurePolicy,
    icp: {
      industries: usesB2bSearch ? industries : [],
      countries: usesB2bSearch ? listFromText(countries) : [],
      employeeMin: usesB2bSearch ? employeeMin : 0,
      employeeMax: usesB2bSearch ? employeeMax : 1,
      ...(usesB2bSearch && headcountRanges.length ? { headcountRanges } : {}),
      targetRoles: usesB2bSearch ? listFromText(targetRoles) : [],
      targetRoleSource,
      industryMatchMode,
    },
    goal: { targetCompanies: effectiveTargetCompanies, targetContacts, targetMeetings },
    limits: {
      ...campaign.config.limits,
      maxEmailsPerDay,
      maxCompaniesPerRun: campaignCompanyCapacity(effectiveTargetCompanies, targetContacts),
      maxContactsPerRun: targetContacts,
      requireApproval,
    },
    messageSource,
    copyPolicy,
    personalization: { ...(campaign.config.personalization ?? defaultConfig.personalization), publicSignalPolicy, decisionOwner: "DIRECTOR_AI" },
    schedule: {
      ...existingSchedule,
      startsAt: optionalIso(startsAt, timezone),
      outreachEndsAt: optionalIso(outreachEndsAt, timezone),
      timezone,
      enabledWeekdays,
      sendWindowStart,
      sendWindowEnd,
      monitoringDays,
    },
    followUpPolicy: { firstBusinessDay, secondBusinessDay, closeBusinessDay },
    initialAssetId: initialAssetId || null,
    meeting: configureMeeting ? {
      calendarId,
      organizerEmail,
      timezone: meetingTimezone,
      durationMinutes,
      workdayStart: meetingWorkdayStart,
      workdayEnd: meetingWorkdayEnd,
      minimumNoticeHours,
      optionCount,
      searchHorizonDays,
      addGoogleMeet,
    } : undefined,
  };
  const payload = {
    name,
    description,
    offerId: offerId || null,
    meetingTeamMemberIds,
    closerAdminId: closerAdminId || null,
    closerWorkflowEnabled: true,
    config,
    ...(duplicating ? { sourceCampaignId: campaign.id } : {}),
  };
  const parsed = createCampaignSchema.safeParse(payload);
  const readinessErrors = campaignReadinessIssues({
    offerId: payload.offerId,
    config,
    hasImportedContacts: campaign.importSummary?.hasImportedContacts,
  });
  const deferredReadinessErrors = readinessErrors.filter((message) => !["Seleccioná una oferta", "Definí un objetivo comercial"].includes(message));
  const campaignFieldErrors: Record<string, string> = {
    ...validationFieldMap(parsed.success ? [] : parsed.error.issues),
    ...(!offerId ? { offerId: "Seleccioná una oferta." } : {}),
    ...(!objective.trim() ? { "config.objective": "Definí un objetivo comercial." } : {}),
  };
  const errors = validationSummary(
    campaignFieldErrors,
    [],
  );
  const save = useMutation({
    mutationFn: () => api<Campaign>(duplicating ? "/campaigns" : `/campaigns/${campaign.id}`, { method: duplicating ? "POST" : "PATCH", body: JSON.stringify(payload) }),
    onSuccess: async (saved) => {
      await client.invalidateQueries({ queryKey: ["campaign", campaign.id] });
      await client.invalidateQueries({ queryKey: ["campaigns"] });
      close();
      if (duplicating) navigate(`/campaigns/${saved.id}`);
    },
  });
  const toggleChannel = (channel: "EMAIL" | "WHATSAPP") => setEnabledChannels((current) =>
    current.includes(channel) ? current.filter((item) => item !== channel) : [...current, channel],
  );
  const selectedOffer = offerData?.items.find((offer) => offer.id === offerId);
  const approvedPdfs = selectedOffer?.assets?.filter((asset) => asset.kind === "PDF" && asset.approved && asset.status !== "RETIRED") ?? [];

  return (
    <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={close}>
      <motion.div className="modal campaign-editor" initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onMouseDown={(event) => event.stopPropagation()}>
        <button className="icon-button close" onClick={close}><X /></button>
        <span className="eyebrow">{duplicating ? "NUEVA CAMPAÑA · COPIA SIN GUARDAR" : "CONFIGURACIÓN DE CAMPAÑA"}</span>
        <h2>{duplicating ? "Duplicar campaña" : "Editar brief y límites"}</h2>
        <p>{duplicating ? "Revisá los datos copiados. Al crear el borrador se copiarán también las últimas versiones publicadas de las plantillas. No se copian la base importada, los contactos, mensajes, ejecuciones ni aprobaciones." : "Estos datos forman la versión que recibirá Hermes al iniciar el próximo run."}</p>
        <div className="form-grid">
          <label>Tipo de público<select disabled={acquisitionLocked} value={audienceType} onChange={(event) => {
            const next = event.target.value as CampaignConfig["audience"]["type"];
            setAudienceType(next);
            if (next === "B2C") setEmailVerification("NONE");
            else if (sourceMode === "SEARCH") setEmailVerification("HUNTER");
          }}><option value="B2B">B2B · empresas y decisores</option><option value="B2C">B2C · personas independientes</option></select><FieldHelp>{acquisitionLocked ? "El público y el origen quedan congelados después del primer run." : "Adapta los criterios y proveedores permitidos."}</FieldHelp></label>
          <label>Origen de prospectos<select disabled={acquisitionLocked} value={sourceMode} onChange={(event) => {
            const next = event.target.value as CampaignConfig["acquisition"]["source"];
            setSourceMode(next);
            if (audienceType === "B2B" && next === "SEARCH") setEmailVerification("HUNTER");
          }}><option value="SEARCH">Búsqueda de prospectos</option><option value="IMPORTED">Base importada (CSV)</option><option value="HYBRID">Búsqueda + base importada</option></select><FieldHelp>{sourceMode === "SEARCH" ? "La plataforma buscará candidatos según el público elegido." : "El CSV será obligatorio antes de activar la campaña."}</FieldHelp></label>
          <label className="full">Verificación de emails<select value={effectiveEmailVerification} disabled={acquisitionLocked || audienceType === "B2C" || sourceMode === "SEARCH"} onChange={(event) => setEmailVerification(event.target.value as CampaignConfig["acquisition"]["emailVerification"])}><option value="NONE">Sin verificación externa</option><option value="HUNTER">Validar con Hunter</option></select><FieldHelp>{audienceType === "B2C" ? "Hunter queda deshabilitado por regla de backend en campañas B2C." : sourceMode === "SEARCH" ? "La búsqueda B2B existente utiliza Hunter." : "En bases B2B importadas, la validación con Hunter es opcional."}</FieldHelp><FieldError message={campaignFieldErrors["config.acquisition.emailVerification"]} /></label>
          <label>Nombre<input value={name} onChange={(event) => setName(event.target.value)} /><FieldHelp>{formCopy.campaign.nameHelp}</FieldHelp><FieldError message={campaignFieldErrors.name} /></label>
          <label>Oferta<select value={offerId} onChange={(event) => { setOfferId(event.target.value); setInitialAssetId(""); }}><option value="">Seleccionar…</option>{offerData?.items.map((offer) => <option key={offer.id} value={offer.id}>{offer.name}</option>)}</select><FieldHelp>Dato obligatorio.</FieldHelp><FieldError message={campaignFieldErrors.offerId} /></label>
          <label className="full">Descripción<textarea value={description} onChange={(event) => setDescription(event.target.value)} /><FieldHelp>{formCopy.campaign.descriptionHelp}</FieldHelp><FieldError message={campaignFieldErrors.description} /></label>
          <label className="full">Objetivo<textarea value={objective} onChange={(event) => setObjective(event.target.value)} /><FieldHelp>{formCopy.campaign.objectiveHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.objective"]} /></label>
          {usesB2bSearch ? <>
            <HunterIndustrySelect value={industries} onChange={setIndustries} error={campaignFieldErrors["config.icp.industries"]} />
            <label>Uso de industrias<select value={industryMatchMode} onChange={(event) => setIndustryMatchMode(event.target.value as CampaignConfig["icp"]["industryMatchMode"])}><option value="REQUIRED">Filtro obligatorio</option><option value="PREFERENCE">Preferencia</option><option value="EXPLORATORY">Orientación exploratoria</option></select></label>
            <ChipField label="Países" value={countries} onChange={setCountries} suggestions={["Argentina", "Paraguay", "Uruguay", "Chile", "Brasil", "México"]} error={campaignFieldErrors["config.icp.countries"]} />
            <ChipField label="Regiones" value={regions} onChange={setRegions} suggestions={["Cono Sur", "LATAM", "Mercosur"]} />
            <label>Origen de los roles<select value={targetRoleSource} onChange={(event) => setTargetRoleSource(event.target.value as CampaignConfig["icp"]["targetRoleSource"])}><option value="DIRECTOR_AI">Director IA según oferta y objetivo (recomendado)</option><option value="MANUAL">Definidos manualmente</option></select><small>{targetRoleSource === "DIRECTOR_AI" ? "El Director IA propondrá entre 8 y 20 roles pertinentes y justificados para ampliar la cobertura de Hunter en el próximo run." : "El próximo run usará exactamente la lista indicada."}</small></label>
            <ChipField label={targetRoleSource === "DIRECTOR_AI" ? "Roles de respaldo" : "Roles objetivo"} value={targetRoles} onChange={setTargetRoles} suggestions={campaignTargetRoleSuggestions} error={campaignFieldErrors["config.icp.targetRoles"]} />
            <HunterHeadcountSelect minEmployees={employeeMin} maxEmployees={employeeMax} selectedRanges={headcountRanges} onChange={(range) => { setEmployeeMin(range.minEmployees); setEmployeeMax(range.maxEmployees); setHeadcountRanges(range.headcountRanges); }} error={campaignFieldErrors["config.icp.employeeMin"] ?? campaignFieldErrors["config.icp.employeeMax"]} />
          </> : audienceType === "B2C" ? <>
            <ChipField label="Ubicaciones" value={individualLocations} onChange={setIndividualLocations} suggestions={["Argentina", "Paraguay", "Uruguay", "Chile", "Brasil", "México"]} error={campaignFieldErrors["config.audience.individualCriteria.locations"]} />
            <ChipField label="Ocupaciones o actividades" value={individualOccupations} onChange={setIndividualOccupations} suggestions={["Profesional independiente", "Consultor", "Comerciante", "Emprendedor"]} error={campaignFieldErrors["config.audience.individualCriteria.occupations"]} />
            <ChipField label="Intereses declarados" value={individualInterests} onChange={setIndividualInterests} suggestions={[]} help="No incluyas categorías sensibles ni inferidas." error={campaignFieldErrors["config.audience.individualCriteria.interests"]} />
            <FieldError message={campaignFieldErrors["config.audience.individualCriteria"]} />
          </> : <div className="form-warning full"><CircleAlert size={17} />El CSV definirá el universo B2B. Industria, empresa y tamaño serán datos opcionales de cada fila.</div>}
          {audienceType === "B2B" && <label>Empresas objetivo<NumericInput min="1" value={targetCompanies} onValueChange={setTargetCompanies} /><FieldError message={campaignFieldErrors["config.goal.targetCompanies"]} /></label>}
          <label>{audienceType === "B2C" ? "Personas objetivo" : "Contactos objetivo"}<NumericInput min="1" value={targetContacts} onValueChange={setTargetContacts} /><FieldHelp>{audienceType === "B2B" ? "La capacidad operativa y la reserva de empresas se recalculan con esta meta, sin un límite fijo oculto." : "Cantidad de personas que la campaña buscará contactar."}</FieldHelp><FieldError message={campaignFieldErrors["config.goal.targetContacts"]} /></label>
          <label>Reuniones meta<NumericInput min="0" value={targetMeetings} onValueChange={setTargetMeetings} /><FieldError message={campaignFieldErrors["config.goal.targetMeetings"]} /></label>
          <label>Hard cap total de correos por día<NumericInput min="1" max="100" value={maxEmailsPerDay} onValueChange={setMaxEmailsPerDay} /><FieldError message={campaignFieldErrors["config.limits.maxEmailsPerDay"]} /></label>
          <div className="channel-field full"><span>Aprobación humana obligatoria</span><label><input type="checkbox" checked disabled /> Todos los correos salientes se detienen para revisión</label><small>Esta política global no puede desactivarse desde una campaña.</small></div>
          <label className="full">Origen de todos los correos y textos de Calendar<select value={messageSource} onChange={(event) => setMessageSource(event.target.value as CampaignConfig["messageSource"])}><option value="AGENT">Agente · redacción contextual</option><option value="TEMPLATES">Plantillas · texto publicado de la campaña</option></select><FieldHelp>Aplica a contactos iniciales, seguimientos, respuestas, horarios e invitaciones y actualizaciones de Calendar. Siempre requiere los controles y aprobaciones correspondientes. Se congela al iniciar cada run.</FieldHelp></label>
          <label className="full">Redacción en todo el flujo<select value={copyPolicy} onChange={event => setCopyPolicy(event.target.value as CampaignConfig["copyPolicy"])}><option value="AS_CONFIGURED">Usar el origen configurado arriba</option><option value="TEMPLATE_OR_AGENT">Mixto · plantilla si responde, IA si no alcanza</option></select><FieldHelp>En modo mixto se evalúa la plantilla con el contexto actual. Si falta o no encaja, el agente responsable redacta. Aplica a primeros contactos, seguimientos, respuestas y Calendar. Se conservan las validaciones, la aprobación y el contexto congelado de cada ejecución.</FieldHelp></label>
          <label className="full">{audienceType === "B2C" ? "Uso de señales públicas en el mensaje" : "Uso de señales públicas"}<select value={publicSignalPolicy} onChange={(event) => setPublicSignalPolicy(event.target.value as CampaignConfig["personalization"]["publicSignalPolicy"])}><option value="DISABLED">Desactivado (predeterminado): no usar señales públicas</option><option value="OPTIONAL">Opcional: el Director IA decide y puede redactar sin señal</option><option value="REQUIRED">Obligatorio: detener si no existe una señal pertinente</option></select><FieldHelp>{audienceType === "B2C" ? "Esta opción sólo afecta cómo se personaliza el correo. La búsqueda B2C permanece separada, usa fuentes públicas permitidas y nunca consulta Hunter." : "La decisión editorial pertenece al Director IA, no a un operador humano. La ausencia de señal nunca reemplaza al prospecto; si es obligatoria, el run se detiene para revisión."}</FieldHelp></label>
          <label>Inicio de campaña<input type="datetime-local" lang="en-GB" step="60" value={startsAt} onChange={(event) => { setStartsAt(event.target.value); setOutreachEndsAt(outreachEndForDuration(event.target.value, outreachDurationDays)); }} /><FieldHelp>Formato de 24 horas.</FieldHelp><FieldError message={campaignFieldErrors["config.schedule.startsAt"]} /></label>
          <label>Fin de primeros contactos<div className="date-duration-field"><input type="datetime-local" lang="en-GB" step="60" value={outreachEndsAt} onChange={(event) => setOutreachEndsAt(event.target.value)} /><select aria-label="Duración de primeros contactos" value={outreachDurationDays} onChange={(event) => { const days = Number(event.target.value); setOutreachDurationDays(days); setOutreachEndsAt(outreachEndForDuration(startsAt, days)); }}><option value="1">1 día</option><option value="7">7 días</option><option value="15">15 días</option><option value="30">30 días</option></select></div><FieldHelp>Al cambiar la duración, el fin se ajusta a las 11:59 (formato de 24 horas).</FieldHelp><FieldError message={campaignFieldErrors["config.schedule.outreachEndsAt"]} /></label>
          <TimezoneField label="Zona horaria" value={timezone} onChange={setTimezone} error={campaignFieldErrors["config.schedule.timezone"]} />
          <div className="full"><WeekdayPicker value={enabledWeekdays} onChange={setEnabledWeekdays} /><FieldError message={campaignFieldErrors["config.schedule.enabledWeekdays"]} /></div>
          <label>Ventana desde<input type="time" lang="en-GB" step="60" value={sendWindowStart} onChange={(event) => setSendWindowStart(event.target.value)} /><FieldError message={campaignFieldErrors["config.schedule.sendWindowStart"]} /></label>
          <label>Ventana hasta<input type="time" lang="en-GB" step="60" value={sendWindowEnd} onChange={(event) => setSendWindowEnd(event.target.value)} /><FieldError message={campaignFieldErrors["config.schedule.sendWindowEnd"]} /></label>
          <label>Días de monitoreo<NumericInput min="1" max="90" value={monitoringDays} onValueChange={setMonitoringDays} /><FieldError message={campaignFieldErrors["config.schedule.monitoringDays"]} /></label>
          <label>Follow-up 1 · día hábil<NumericInput min="1" value={firstBusinessDay} onValueChange={setFirstBusinessDay} /></label>
          <label>Follow-up 2 · día hábil<NumericInput min="2" value={secondBusinessDay} onValueChange={setSecondBusinessDay} /></label>
          <label>Cierre sin respuesta · día hábil<NumericInput min="3" value={closeBusinessDay} onValueChange={setCloseBusinessDay} /></label>
          <label className="full">Adjunto del primer correo (opcional)<select value={initialAssetId} onChange={(event) => setInitialAssetId(event.target.value)}><option value="">No enviar PDF</option>{approvedPdfs.map((asset) => <option key={asset.id} value={asset.id}>Enviar {asset.originalName ?? asset.name} · v{asset.version ?? 1}</option>)}</select><FieldHelp>Se adjunta únicamente al correo inicial. Seguimientos, respuestas, opciones de reunión e invitaciones de Calendar se envían sin este PDF.</FieldHelp></label>
          <MeetingTeamSelector value={meetingTeamMemberIds} onChange={setMeetingTeamMemberIds} />
          <CloserAdminSelector value={closerAdminId} onChange={setCloserAdminId} required={targetMeetings > 0} />
          <div className="channel-field full"><span>Coordinación de reuniones</span><label><input type="checkbox" checked={configureMeeting} onChange={(event) => setConfigureMeeting(event.target.checked)} /> Usar disponibilidad de Calendar</label></div>
          {configureMeeting && <><label>Calendar ID<input value={calendarId} onChange={(event) => setCalendarId(event.target.value)} placeholder={formCopy.meeting.calendarIdPlaceholder} /><FieldHelp>{formCopy.meeting.requiredWhenEnabled}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.calendarId"]} /></label><label>Organizador<input type="email" value={organizerEmail} onChange={(event) => setOrganizerEmail(event.target.value)} placeholder={formCopy.meeting.organizerPlaceholder} /><FieldHelp>{formCopy.meeting.requiredWhenEnabled}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.organizerEmail"]} /></label><TimezoneField label="Zona horaria de agenda" value={meetingTimezone} onChange={setMeetingTimezone} error={campaignFieldErrors["config.meeting.timezone"]} /><label>Jornada desde<input type="time" lang="en-GB" step="60" value={meetingWorkdayStart} onChange={(event) => setMeetingWorkdayStart(event.target.value)} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.workdayStart"]} /></label><label>Jornada hasta<input type="time" lang="en-GB" step="60" value={meetingWorkdayEnd} onChange={(event) => setMeetingWorkdayEnd(event.target.value)} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.workdayEnd"]} /></label><label>Duración (minutos)<NumericInput min="15" max="240" value={durationMinutes} onValueChange={setDurationMinutes} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.durationMinutes"]} /></label><label>Anticipación mínima (horas)<NumericInput min="1" value={minimumNoticeHours} onValueChange={setMinimumNoticeHours} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.minimumNoticeHours"]} /></label><label>Cantidad de opciones<select value={optionCount} onChange={(event) => setOptionCount(Number(event.target.value))}><option value="2">2</option><option value="3">3</option></select><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.optionCount"]} /></label><label>Horizonte de búsqueda (días)<NumericInput min="1" max="60" value={searchHorizonDays} onValueChange={setSearchHorizonDays} /><FieldHelp>{formCopy.meeting.defaultsHelp}</FieldHelp><FieldError message={campaignFieldErrors["config.meeting.searchHorizonDays"]} /></label><label className="checkbox-row"><input type="checkbox" checked={addGoogleMeet} onChange={(event) => setAddGoogleMeet(event.target.checked)} /> Agregar Google Meet</label></>}
          <label className="full">Tono<input value={tone} onChange={(event) => setTone(event.target.value)} /></label>
          <label>Restricciones<textarea value={restrictions} onChange={(event) => setRestrictions(event.target.value)} placeholder="Una por línea" /></label>
          <label>Clientes excluidos<textarea value={excludedCustomers} onChange={(event) => setExcludedCustomers(event.target.value)} placeholder="Uno por línea" /></label>
          <label>Claims autorizados<textarea value={approvedClaims} onChange={(event) => setApprovedClaims(event.target.value)} placeholder="Uno por línea" /></label>
          <label>Claims prohibidos<textarea value={forbiddenClaims} onChange={(event) => setForbiddenClaims(event.target.value)} placeholder="Uno por línea" /></label>
          <label>Política si Hermes falla<select value={hermesFailurePolicy} onChange={(event) => setHermesFailurePolicy(event.target.value as CampaignConfig["hermesFailurePolicy"])}><option value="BLOCK">Bloquear</option><option value="DEGRADED">Degradar sólo en modo simulado</option></select></label>
          <div className="channel-field"><span>Canales habilitados</span><label><input type="checkbox" checked={enabledChannels.includes("EMAIL")} onChange={() => toggleChannel("EMAIL")} /> Email</label><label><input type="checkbox" checked={enabledChannels.includes("WHATSAPP")} onChange={() => toggleChannel("WHATSAPP")} /> WhatsApp</label></div>
        </div>
        {deferredReadinessErrors.length > 0 && <div className="form-warning"><CircleAlert size={17} />{duplicating ? "La copia se guardará como borrador." : "Podés guardar los cambios, pero la campaña no podrá activarse todavía."} Antes de activarla: {deferredReadinessErrors.join(" · ")}</div>}
        {errors.length > 0 && <div className="form-error"><CircleAlert size={17} />{errors.join(" · ")}</div>}
        {save.error && <div className="form-error">{visibleErrorMessage(save.error, "No se pudo guardar la campaña.")}</div>}
        <div className="modal-actions"><button className="secondary" onClick={close}>Cancelar</button><button className="primary" disabled={save.isPending || errors.length > 0} onClick={() => save.mutate()}><Save size={17} />{save.isPending ? "Guardando…" : duplicating ? "Crear campaña en borrador" : "Guardar cambios"}</button></div>
      </motion.div>
    </motion.div>
  );
}

function AssetEditor({ asset, canEdit, refresh }: { asset: Asset; canEdit: boolean; refresh: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(asset.name);
  const [kind, setKind] = useState(asset.kind);
  const [url, setUrl] = useState(asset.url ?? "");
  const [content, setContent] = useState(asset.content ?? "");
  const update = useMutation<unknown, Error, Record<string, unknown>>({
    mutationFn: (payload) => api(`/assets/${asset.id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
    onSuccess: () => { setEditing(false); refresh(); },
  });
  const pdfAction = useMutation({
    mutationFn: (action: "approve" | "retire") => api(`/assets/${asset.id}/${action}`, { method: "POST" }),
    onSuccess: refresh,
  });
  if (asset.kind === "PDF") {
    const claims = (() => { try { return JSON.parse(asset.claimsJson ?? "[]") as string[]; } catch { return []; } })();
    return <article className="asset-card pdf-asset-card">
      <div className="asset-head"><div><strong>{asset.originalName ?? asset.name}</strong><small>PDF · v{asset.version ?? 1} · {asset.sizeBytes ? `${Math.round(asset.sizeBytes / 1024)} KB` : "tamaño no disponible"}</small></div><Status value={asset.status === "RETIRED" ? "RETIRED" : asset.approved ? "APPROVED" : "PENDING"} /></div>
      <p>{asset.description || "Material comercial PDF"}</p>
      <small className="asset-hash">SHA-256: {asset.sha256?.slice(0, 16)}…</small>
      {claims.length > 0 && <div className="tags">{claims.map((claim) => <span key={claim}>{claim}</span>)}</div>}
      {pdfAction.error && <div className="form-error">{visibleErrorMessage(pdfAction.error, "No se pudo actualizar el PDF.")}</div>}
      {canEdit && asset.status !== "RETIRED" && <div className="actions">{!asset.approved && <button className="primary" disabled={pdfAction.isPending} onClick={() => pdfAction.mutate("approve")}>Aprobar PDF</button>}{asset.approved && <button className="secondary" disabled={pdfAction.isPending} onClick={() => pdfAction.mutate("retire")}>Retirar versión</button>}</div>}
      {asset.status === "RETIRED" && <p className="immutability-note">Versión retirada e inmutable. Cargá una nueva versión para reemplazarla.</p>}
    </article>;
  }
  return (
    <article className="asset-card">
      <div className="asset-head"><div><strong>{asset.name}</strong><small>{asset.kind} · {formatDate(asset.createdAt)}</small></div><Status value={asset.approved ? "APPROVED" : asset.everApproved ? "WITHDRAWN" : "PENDING"} /></div>
      {editing ? <div className="asset-edit"><input aria-label="Nombre de la evidencia" value={name} onChange={(event) => setName(event.target.value)} /><input aria-label="Tipo de evidencia" value={kind} onChange={(event) => setKind(event.target.value)} /><input aria-label="URL de la evidencia" placeholder={formCopy.evidence.urlPlaceholder} value={url} onChange={(event) => setUrl(event.target.value)} /><textarea aria-label="Contenido de la evidencia" placeholder={formCopy.evidence.contentPlaceholder} value={content} onChange={(event) => setContent(event.target.value)} /><FieldHelp>{formCopy.evidence.sourceHelp}</FieldHelp><div className="actions"><button className="secondary" onClick={() => setEditing(false)}>Cancelar</button><button className="primary" disabled={update.isPending || (!url.trim() && !content.trim())} onClick={() => update.mutate({ name, kind, url: url.trim() || null, content: content.trim() || null })}><Save size={15} />Guardar</button></div></div> : <><p>{asset.content || "Evidencia enlazada"}</p>{asset.url && <a className="asset-link" href={asset.url} target="_blank" rel="noreferrer"><ExternalLink size={14} />Abrir evidencia</a>}</>}
      {update.error && <div className="form-error">{visibleErrorMessage(update.error, "No se pudo actualizar la evidencia.")}</div>}
      {canEdit && !editing && !asset.everApproved && <div className="actions"><button className="secondary" onClick={() => setEditing(true)}>Editar</button><button className="primary" disabled={update.isPending} onClick={() => update.mutate({ approved: true })}>Aprobar evidencia</button></div>}
      {canEdit && !editing && asset.approved && <div className="actions"><button className="secondary" disabled={update.isPending} onClick={() => update.mutate({ approved: false })}>Retirar aprobación</button></div>}
      {asset.everApproved && !asset.approved && <p className="immutability-note">Esta versión fue aprobada y permanece inmutable. Para corregirla, creá una evidencia nueva.</p>}
    </article>
  );
}

function OfferAssetsManager({ offer, canEdit, onChanged }: { offer: Offer; canEdit: boolean; onChanged: () => void | Promise<void> }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("PRODUCT_FACTS");
  const [url, setUrl] = useState("");
  const [content, setContent] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfDescription, setPdfDescription] = useState("");
  const [pdfClaims, setPdfClaims] = useState("");
  const evidenceNameError = name.length > 0 && name.trim().length < 2 ? "Ingresá al menos 2 caracteres." : undefined;
  const evidenceSourceError = (name || url || content) && !url.trim() && !content.trim()
    ? "Completá la URL, el contenido o ambos."
    : undefined;
  const refresh = () => { void onChanged(); };
  const create = useMutation({
    mutationFn: () => api(`/offers/${offer.id}/assets`, { method: "POST", body: JSON.stringify({ name, kind, ...(url.trim() ? { url: url.trim() } : {}), ...(content.trim() ? { content: content.trim() } : {}) }) }),
    onSuccess: () => { setName(""); setUrl(""); setContent(""); refresh(); },
  });
  const uploadPdf = useMutation({
    mutationFn: async () => {
      if (!pdfFile) throw new Error("Seleccioná un PDF");
      const contentBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("No se pudo leer el PDF"));
        reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
        reader.readAsDataURL(pdfFile);
      });
      return api(`/offers/${offer.id}/assets/pdf`, { method: "POST", body: JSON.stringify({ filename: pdfFile.name, mimeType: pdfFile.type || "application/pdf", contentBase64, description: pdfDescription, claims: linesFromText(pdfClaims) }) });
    },
    onSuccess: () => { setPdfFile(null); setPdfDescription(""); setPdfClaims(""); refresh(); },
  });
  return <>
    {canEdit && <section className="panel span-2"><span className="eyebrow">NUEVA EVIDENCIA</span><h2>Agregar al expediente</h2><div className="form-grid evidence-form"><label>Nombre<input value={name} onChange={(event) => setName(event.target.value)} placeholder={formCopy.evidence.namePlaceholder} /><FieldHelp>{formCopy.evidence.nameHelp}</FieldHelp><FieldError message={evidenceNameError} /></label><label>Tipo<select value={kind} onChange={(event) => setKind(event.target.value)}><option value="PRODUCT_FACTS">Datos de producto</option><option value="TECHNICAL_DOCUMENT">Documento técnico</option><option value="CASE_STUDY">Caso de uso</option><option value="POLICY">Política o alcance</option><option value="OTHER">Otro</option></select><FieldHelp>Dato obligatorio. Elegí la categoría que mejor describe la fuente.</FieldHelp></label><label className="full">URL opcional<input type="url" placeholder={formCopy.evidence.urlPlaceholder} value={url} onChange={(event) => setUrl(event.target.value)} /><FieldError message={evidenceSourceError} /></label><label className="full">Contenido opcional<textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder={formCopy.evidence.contentPlaceholder} /><FieldHelp>{formCopy.evidence.sourceHelp}</FieldHelp><FieldError message={evidenceSourceError} /></label></div>{create.error && <div className="form-error">{visibleErrorMessage(create.error, "No se pudo crear la evidencia.")}</div>}<button className="primary" disabled={create.isPending || name.trim().length < 2 || (!url.trim() && !content.trim())} onClick={() => create.mutate()}><Plus size={16} />Agregar evidencia</button></section>}
    <section className={canEdit ? "panel span-3" : "panel span-2"}><div className="panel-title"><div><span className="eyebrow">EXPEDIENTE</span><h2>{offer.assets?.length ?? 0} evidencias</h2></div><ShieldCheck /></div><p className="muted">Sólo las evidencias actualmente aprobadas se incluyen en nuevos CampaignBrief. Aprobar una evidencia vuelve inmutable esa versión.</p><div className="asset-grid">{offer.assets?.map((asset) => <AssetEditor key={asset.id} asset={asset} canEdit={canEdit} refresh={refresh} />)}</div>{!offer.assets?.length && <Empty title="Expediente vacío" body="Agregá documentación, enlaces o notas verificables del producto." />}<small className="muted">Las evidencias no se eliminan: se retira su aprobación para conservar la trazabilidad.</small></section>
    {canEdit && <section className="panel span-3 evidence-pdf-section"><span className="eyebrow">MATERIAL COMERCIAL · PASO FINAL</span><h2>Cargar PDF gobernado</h2><p className="muted">Máximo 10 MB. Queda fuera del directorio público, con hash SHA-256 y aprobación explícita antes de poder adjuntarlo.</p><div className="form-grid evidence-form"><div className="full"><PdfFilePicker file={pdfFile} onChange={setPdfFile} loading={uploadPdf.isPending} success={uploadPdf.isSuccess} error={uploadPdf.error ? visibleErrorMessage(uploadPdf.error, "No se pudo cargar el PDF.") : undefined} /></div><label className="full">Descripción<input value={pdfDescription} onChange={(event) => setPdfDescription(event.target.value)} placeholder={formCopy.evidence.pdfDescriptionPlaceholder} /><FieldHelp>{formCopy.evidence.pdfDescriptionHelp}</FieldHelp></label><label className="full">Claims autorizados del material<textarea value={pdfClaims} onChange={(event) => setPdfClaims(event.target.value)} placeholder={formCopy.evidence.pdfClaimsPlaceholder} /><FieldHelp>No amplían automáticamente los claims de la oferta.</FieldHelp></label></div><button className="primary" disabled={uploadPdf.isPending || !pdfFile} onClick={() => uploadPdf.mutate()}>{uploadPdf.isPending ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />}{uploadPdf.isPending ? "Cargando…" : "Cargar PDF"}</button></section>}
  </>;
}

function OfferEvidenceView({ campaign, canEdit }: { campaign: Campaign; canEdit: boolean }) {
  const client = useQueryClient();
  const offerId = campaign.offer?.id;
  const { data: offer } = useQuery({ queryKey: ["offer", offerId], queryFn: () => api<Offer>(`/offers/${offerId}`), enabled: !!offerId });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ["offer", offerId] });
    client.invalidateQueries({ queryKey: ["campaign", campaign.id] });
  };
  if (!offerId) return <Empty title="Sin oferta seleccionada" body="Editá la campaña y elegí una oferta antes de administrar su expediente." />;
  const selectedOffer = offer ?? campaign.offer;
  if (!selectedOffer) return <div className="loading">Cargando oferta…</div>;
  return <div className="overview-grid evidence-layout">
    <section className="panel span-3 offer-summary"><div><span className="eyebrow">RESUMEN DEL EXPEDIENTE</span><h2>{selectedOffer.name}</h2><p>{selectedOffer.description}</p><div className="tags">{offerClaims(selectedOffer).map((claim) => <span key={claim}>{claim}</span>)}</div></div><Link className="asset-link" to={`/offers/${selectedOffer.id}`}><ExternalLink size={14} />Administrar oferta</Link></section>
    <OfferAssetsManager offer={selectedOffer} canEdit={canEdit} onChanged={refresh} />
  </div>;
}

function CampaignMeetingTeamPanel({ campaign, canEdit }: { campaign: Campaign; canEdit: boolean }) {
  const client = useQueryClient();
  const [memberIds, setMemberIds] = useState<string[]>(() => campaign.meetingTeamMembers?.map((member) => member.id) ?? []);
  useEffect(() => setMemberIds(campaign.meetingTeamMembers?.map((member) => member.id) ?? []), [campaign.meetingTeamMembers]);
  const originalIds = campaign.meetingTeamMembers?.map((member) => member.id) ?? [];
  const changed = memberIds.join("|") !== originalIds.join("|");
  const save = useMutation({
    mutationFn: () => api<{ items: MeetingTeamMember[] }>(`/campaigns/${campaign.id}/meeting-team`, { method: "PUT", body: JSON.stringify({ memberIds }) }),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ["campaign", campaign.id] }); await client.invalidateQueries({ queryKey: ["meeting-team"] }); },
  });
  return <div className="overview-grid"><section className="panel span-3"><div className="panel-title"><div><span className="eyebrow">PARTICIPANTES INTERNOS</span><h2>Equipo de reuniones de la campaña</h2></div><Users /></div><p className="muted">Esta selección será la base para presentar e invitar a las personas que realizarán las reuniones. Más adelante podrá ajustarse en cada propuesta.</p><MeetingTeamSelector value={memberIds} onChange={setMemberIds} disabled={!canEdit} />{save.error && <div className="form-error">{visibleErrorMessage(save.error, "No se pudo actualizar el equipo de reuniones.")}</div>}{save.isSuccess && !changed && <div className="resume-banner"><CheckCircle2 size={17} /><p>Equipo actualizado.</p></div>}{canEdit && <div className="panel-actions"><button className="primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}><Save size={16} />{save.isPending ? "Guardando…" : "Guardar equipo"}</button></div>}</section></div>;
}

export function CampaignDetailPage() {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [tab, setTab] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("section") === "templates" ? "templates" : localStorage.getItem(`vicnet:campaign-section:${campaignId}`) || "overview"; } catch { return "overview"; }
  });
  const [showEdit, setShowEdit] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me") });
  const { data: campaign, isPending, isFetching, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: () => api<Campaign>(`/campaigns/${campaignId}`),
    refetchInterval: 5000,
  });
  const changeStatus = useMutation({
    mutationFn: (status: string) =>
      api(`/campaigns/${campaignId}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["campaign", campaignId] }),
  });
  const campaignControl = useMutation({
    mutationFn: (action: "pause" | "resume-outreach" | "cancel-outreach") =>
      api(`/campaigns/${campaignId}/${action}`, { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["campaign", campaignId] }),
  });
  const start = useMutation({
    mutationFn: () =>
      api<Run>(`/campaigns/${campaignId}/runs`, { method: "POST" }),
    onSuccess: (run) => {
      client.invalidateQueries({ queryKey: ["campaign", campaignId] });
      navigate(`/operation/${run.id}`);
    },
  });
  useEffect(() => {
    try { localStorage.setItem(`vicnet:campaign-section:${campaignId}`, tab); } catch { /* storage may be disabled */ }
  }, [campaignId, tab]);
  if (isPending) return <div className="loading" role="status">Cargando campaña…</div>;
  if (isError) return <div className="query-state error"><CircleAlert /><strong>No se pudo cargar la campaña</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal."}</p><button className="secondary" onClick={() => void refetch()}><RefreshCcw size={15} />Reintentar</button></div>;
  if (!campaign) return <Empty title="Campaña no disponible" body="No encontramos datos para esta campaña." />;
  const latestRun = campaign.runs?.[0];
  const activeRun = campaign.runs?.find((run) =>
    ["PENDING_AUTHORIZATION", "PENDING", "RUNNING", "WAITING_DIRECTION", "WAITING_STAGE_REVIEW", "WAITING_APPROVAL", "ACTION_REQUIRED", "BLOCKED", "DEGRADED"].includes(run.status)
    && !run.events?.some((event) => event.type === "RUN_ACQUISITION_CLOSED"));
  const canEdit = Boolean(me?.user.role && me.user.role !== "VIEWER");
  const campaignAudience = audienceFor(campaign.config);
  const campaignAcquisition = acquisitionFor(campaign.config);
  const configurationErrors = campaignReadinessIssues({
    offerId: campaign.offer?.id,
    config: campaign.config,
    hasImportedContacts: campaign.importSummary?.hasImportedContacts,
  });
  if (campaign.closerWorkflowEnabled && campaign.config.goal.targetMeetings > 0 && !campaign.closerAdminId)
    configurationErrors.push("Seleccioná un Closer Admin responsable antes de activar la campaña");
  const tabs: CampaignTabItem[] = [
    { value: "overview", label: "Configuración" },
    ...(campaignAcquisition.source !== "SEARCH" ? [{ value: "imports", label: "Base importada", count: campaign.importSummary?.acceptedRows ?? 0 }] : []),
    { value: "evidence", label: "Expediente", count: campaign.offer?.assets?.length ?? 0 },
    { value: "strategy", label: "Estrategia Hermes" },
    { value: "templates", label: "Plantillas", count: campaignEmailTemplateTypes.length },
    { value: "meeting-team", label: "Equipo de reuniones", count: campaign.meetingTeamMembers?.length ?? 0 },
    { value: "prospects", label: "Prospectos", count: `${campaign.companies?.filter((item) => item.accepted !== false || item.status === "READY_FOR_QUALIFICATION").length ?? 0}/${campaign.companies?.length ?? 0}` },
    { value: "contacts", label: "Contactos", count: campaign.contactResolution?.items.length ?? campaign.contactProgress?.historicalTotal ?? campaign.contacts?.length ?? 0 },
    { value: "outreach", label: "Outreach", count: campaign.messages?.filter((message) => message.direction === "OUTBOUND").length ?? 0 },
    { value: "inbound", label: "Inbound", count: campaign.messages?.filter((message) => message.direction === "INBOUND").length ?? 0 },
    { value: "meetings", label: "Reuniones", count: campaign.meetings?.length ?? 0 },
    { value: "runs", label: "Ejecuciones", count: campaign.runs?.length ?? 0 },
    { value: "activity", label: "Actividad" },
  ];
  return (
    <>
      <section className="campaign-detail-head">
        <div className="detail-head">
          <div>
            <Link className="back" to="/campaigns">Campañas / Detalle</Link>
            <div className="title-row"><h1>{campaign.name}</h1><Status value={campaign.status} /></div>
            <p title={campaign.description || "Campaña comercial controlada"}>{campaign.description || "Campaña comercial controlada"}</p>
            <span className="campaign-update-state">{isFetching ? <><RefreshCcw className="spin" size={12} />Actualizando datos…</> : <>Actualizado {formatDate(new Date(dataUpdatedAt).toISOString())}</>}</span>
          </div>
          <div className="actions">
          {canEdit && <button className="secondary" onClick={() => setShowDuplicate(true)}><Plus size={17} />Duplicar campaña</button>}
          {canEdit && campaign.status !== "RUNNING" && (
            <button className="secondary" onClick={() => setShowEdit(true)}><Settings2 size={17} />Editar campaña</button>
          )}
          {campaign.status === "DRAFT" && (
            <button
              className="secondary"
              disabled={configurationErrors.length > 0}
              onClick={() => changeStatus.mutate("READY")}
            >
              <Check size={17} />
              Marcar lista
            </button>
          )}
          {campaign.status === "RUNNING" && (
            <button
              className="secondary"
              disabled={campaignControl.isPending}
              onClick={() => campaignControl.mutate("pause")}
            >
              <Pause size={17} />
              Pausar
            </button>
          )}
          {campaign.status === "PAUSED" && (
            <button className="secondary" disabled={campaignControl.isPending} onClick={() => campaignControl.mutate("resume-outreach")}>
              <Play size={17} />Reanudar salidas
            </button>
          )}
          {canEdit && me?.user.role === "ADMIN" && !["CANCELLED", "COMPLETED", "ARCHIVED"].includes(campaign.status) && (
            <button className="secondary danger-outline" disabled={campaignControl.isPending} onClick={() => window.confirm("¿Cancelar esta campaña? Se conservará toda la trazabilidad y se detendrán las acciones futuras.") && campaignControl.mutate("cancel-outreach")}>
              <XCircle size={17} />Cancelar campaña
            </button>
          )}
          {activeRun ? (
            <button className="primary" onClick={() => navigate(`/operation/${activeRun.id}`)}><Activity size={17} />Abrir ejecución activa</button>
          ) : (
            <button
              className="primary"
              disabled={start.isPending || configurationErrors.length > 0 || (campaign.contactProgress?.remaining ?? campaign.config.goal.targetContacts) <= 0}
              onClick={() => start.mutate()}
            >
              {start.isPending ? <LoaderCircle className="spin" size={17} /> : <Play size={17} />}
              {start.isPending ? "Iniciando primera etapa…" : campaign.contactProgress?.coveredTotal ? `Buscar ${campaign.contactProgress.remaining} contactos faltantes` : "Iniciar ejecución"}
            </button>
          )}
          </div>
        </div>
        <section className="campaign-executive-summary" aria-label="Resumen ejecutivo de la campaña">
          <div><small>Estado</small><Status value={campaign.status} /></div>
          <div><small>Público</small><strong>{audienceLabels[campaignAudience.type]}</strong></div>
          <div><small>Origen</small><strong>{sourceLabels[campaignAcquisition.source]}</strong></div>
          <div className="wide"><small>Objetivo</small><strong title={campaign.config.objective || "Pendiente"}>{campaign.config.objective || "Pendiente"}</strong></div>
          <div><small>Hard cap total</small><strong>{campaign.config.limits.maxEmailsPerDay} correos/día</strong></div>
          <div><small>Objetivo acumulado de contactos</small><strong>{campaign.contactProgress?.coveredTotal ?? campaign.contactProgress?.validForReferenceRun ?? 0} / {campaign.contactProgress?.target ?? campaign.config.goal.targetContacts}</strong><span>{campaign.contactProgress?.remaining ?? campaign.config.goal.targetContacts} faltantes · {campaign.contactProgress?.historicalTotal ?? campaign.contacts?.length ?? 0} históricos preservados</span></div>
          <div><small>Reuniones</small><strong>{campaign.meetings?.length ?? 0} / {campaign.config.goal.targetMeetings}</strong></div>
        </section>
        <details className="campaign-more-details">
          <summary><ChevronRight size={14} />Ver detalles de campaña</summary>
          <div className="config-grid">
            <div><span>Oferta</span><strong>{campaign.offer?.name ?? "Pendiente"}</strong></div>
            <div><span>Verificación</span><strong>{verificationLabels[campaignAcquisition.emailVerification]}</strong></div>
            <div><span>Inicio</span><strong>{scheduleFor(campaign.config).startsAt ? formatDate(scheduleFor(campaign.config).startsAt) : "Al activar"}</strong></div>
            <div><span>Último run</span><strong>{latestRun ? latestRun.id : "Sin ejecución"}</strong></div>
            <div><span>Closer Admin</span><strong>{campaign.closerAdmin?.displayName ?? "Pendiente"}</strong></div>
          </div>
        </details>
      </section>
      {start.error && (
        <div className="form-error">
          {start.error instanceof ApiError
            ? start.error.message
            : "No se pudo iniciar"}
        </div>
      )}
      {configurationErrors.length > 0 && (
        <div className="warning-banner"><CircleAlert /><div><strong>Configuración incompleta</strong><p>{configurationErrors.join(" · ")}</p></div></div>
      )}
      <CampaignTabs items={tabs} value={tab} onChange={setTab} />
      <div id={`campaign-section-${tab}`} role="tabpanel">
      {tab === "overview" && (
        <div className="overview-grid">
          <section className="panel span-3">
            <div className="panel-title">
              <div>
                <span className="eyebrow">CONFIGURACIÓN</span>
                <h2>Objetivo y límites</h2>
              </div>
              <ShieldCheck />
            </div>
            <div className="config-sections">
              <details className="config-section" open>
                <summary><span><strong>Público y origen</strong><small>{audienceLabels[campaignAudience.type]} · {sourceLabels[campaignAcquisition.source]}</small></span><ChevronRight size={17} /></summary>
                <div className="config-grid"><div><span>Tipo de público</span><strong>{audienceLabels[campaignAudience.type]}</strong></div><div><span>Origen de prospectos</span><strong>{sourceLabels[campaignAcquisition.source]}</strong></div><div><span>Verificación de emails</span><strong>{verificationLabels[campaignAcquisition.emailVerification]}</strong></div>{campaignAudience.type === "B2C" && <div className="config-wide"><span>Criterios personales no sensibles</span><div className="tags">{[...campaignAudience.individualCriteria.locations, ...campaignAudience.individualCriteria.occupations, ...campaignAudience.individualCriteria.interests].map((criterion, index) => <span key={`${criterion}-${index}`}>{criterion}</span>)}{campaignAudience.individualCriteria.locations.length + campaignAudience.individualCriteria.occupations.length + campaignAudience.individualCriteria.interests.length === 0 && <span>La base importada definirá el universo</span>}</div></div>}</div>
              </details>
              <details className="config-section" open>
                <summary><span><strong>Objetivo y límites</strong><small>{campaignAudience.type === "B2B" ? `${campaign.config.goal.targetCompanies} empresas · ` : ""}{campaign.config.goal.targetContacts} {campaignAudience.type === "B2C" ? "personas" : "contactos"} · {campaign.config.goal.targetMeetings} reuniones</small></span><ChevronRight size={17} /></summary>
                <div className="config-grid"><div className="config-wide"><span>Oferta seleccionada</span><strong>{campaign.offer?.name ?? "Pendiente"}</strong></div><div className="config-wide"><span>Objetivo</span><strong>{campaign.config.objective || "Pendiente"}</strong></div>{campaignAudience.type === "B2B" && <div><span>Empresas objetivo</span><strong>{campaign.config.goal.targetCompanies}</strong></div>}<div><span>{campaignAudience.type === "B2C" ? "Personas objetivo" : "Contactos objetivo"}</span><strong>{campaign.config.goal.targetContacts}</strong></div><div><span>Reuniones meta</span><strong>{campaign.config.goal.targetMeetings}</strong></div></div>
              </details>
              <details className="config-section">
                <summary><span><strong>Programación y ventana de envío</strong><small>{weekdayNames(scheduleFor(campaign.config).enabledWeekdays)} · {scheduleFor(campaign.config).sendWindowStart}–{scheduleFor(campaign.config).sendWindowEnd}</small></span><ChevronRight size={17} /></summary>
                <div className="config-grid"><div><span>Zona horaria</span><strong>{scheduleFor(campaign.config).timezone}</strong></div><div className="config-wide"><span>Días habilitados</span><strong>{weekdayNames(scheduleFor(campaign.config).enabledWeekdays)}</strong></div><div><span>Ventana</span><strong>{scheduleFor(campaign.config).sendWindowStart}–{scheduleFor(campaign.config).sendWindowEnd}</strong></div><div><span>Inicio</span><strong>{formatDate(scheduleFor(campaign.config).startsAt)}</strong></div><div><span>Primeros contactos hasta</span><strong>{formatDate(scheduleFor(campaign.config).outreachEndsAt)}</strong></div><div><span>Monitoreo</span><strong>{scheduleFor(campaign.config).monitoringDays} días</strong></div></div>
              </details>
              <details className="config-section">
                <summary><span><strong>Cuota y seguimientos</strong><small>Hard cap total: {campaign.config.limits.maxEmailsPerDay} correos por día</small></span><ChevronRight size={17} /></summary>
                <div className="config-grid"><div><span>Hard cap total</span><strong>{campaign.config.limits.maxEmailsPerDay} correos/día</strong></div><div><span>Aprobación humana</span><strong>Siempre requerida</strong></div><div><span>Primer seguimiento</span><strong>Día hábil {followUpPolicyFor(campaign.config).firstBusinessDay}</strong></div><div><span>Segundo seguimiento</span><strong>Día hábil {followUpPolicyFor(campaign.config).secondBusinessDay}</strong></div><div><span>Cierre sin respuesta</span><strong>Día hábil {followUpPolicyFor(campaign.config).closeBusinessDay}</strong></div></div><p className="hard-cap-note">El hard cap incluye contactos iniciales, seguimientos, respuestas y coordinación de agenda. No representa la cantidad de contactos.</p>
              </details>
              <details className="config-section">
                <summary><span><strong>Reuniones</strong><small>{campaign.config.meeting ? `${campaign.config.meeting.durationMinutes} min · ${campaign.config.meeting.optionCount} opciones` : "Sin configurar"}</small></span><ChevronRight size={17} /></summary>
                {campaign.config.meeting ? <div className="config-grid"><div><span>Agenda</span><strong>{campaign.config.meeting.calendarId}</strong></div><div><span>Organizador</span><strong>{campaign.config.meeting.organizerEmail}</strong></div><div><span>Zona horaria</span><strong>{campaign.config.meeting.timezone}</strong></div><div><span>Jornada</span><strong>{campaign.config.meeting.workdayStart}–{campaign.config.meeting.workdayEnd}</strong></div><div><span>Anticipación</span><strong>{campaign.config.meeting.minimumNoticeHours} horas</strong></div><div><span>Horizonte</span><strong>{campaign.config.meeting.searchHorizonDays} días</strong></div><div><span>Videollamada</span><strong>{campaign.config.meeting.addGoogleMeet ? "Google Meet habilitado" : "Sin Google Meet"}</strong></div></div> : <p className="muted">La coordinación de reuniones no está configurada para esta campaña.</p>}
              </details>
              <details className="config-section">
                <summary><span><strong>Gobierno y configuración avanzada</strong><small>{campaignAudience.type === "B2B" ? `${campaign.config.icp.industries.length} industrias · ` : ""}{offerClaims(campaign.offer).length + campaign.config.approvedClaims.length} claims</small></span><ChevronRight size={17} /></summary>
                <div className="config-grid">{campaignAudience.type === "B2B" && <div className="config-wide"><span>ICP</span><div className="tags">{[...campaign.config.icp.industries, ...campaign.config.icp.countries].map((tag) => <span key={tag}>{tag}</span>)}</div></div>}<div className="config-wide"><span>Claims autorizados</span><div className="tags">{[...new Set([...offerClaims(campaign.offer), ...campaign.config.approvedClaims])].map((claim) => <span key={claim}>{claim}</span>)}{offerClaims(campaign.offer).length + campaign.config.approvedClaims.length === 0 && <span>Ninguno</span>}</div></div><div><span>Expediente</span><strong>{campaign.offer?.assets?.length ?? 0} evidencias · {campaign.offer?.assets?.filter((asset) => asset.approved).length ?? 0} aprobadas</strong></div><div><span>Señales públicas</span><strong>{campaign.config.personalization?.publicSignalPolicy === "REQUIRED" ? "Obligatorias" : campaign.config.personalization?.publicSignalPolicy === "DISABLED" ? "Desactivadas" : "Opcionales · decide Director IA"}</strong></div><div><span>Política Hermes</span><strong>{presentationText(campaign.config.hermesFailurePolicy)}</strong></div></div>
              </details>
            </div>
          </section>
          <section className="panel span-3 compact-governance">
            <span className="eyebrow">GOBERNANZA</span>
            <h2>Salida protegida</h2>
            <div className="safe-list">
              <span>
                <CheckCircle2 /> Claim Check obligatorio
              </span>
              <span>
                <CheckCircle2 /> SAFE SEND controlado
              </span>
              <span>
                <CheckCircle2 /> Claims determinísticos
              </span>
              <span>
                <CheckCircle2 /> Auditoría append-only
              </span>
            </div>
          </section>
          <section className="panel span-3">
            <div className="panel-title">
              <div>
                <span className="eyebrow">ÚLTIMA EJECUCIÓN</span>
                <h2>
                  {latestRun
                    ? `Run ${latestRun.id.slice(-8)}`
                    : "Todavía no hay ejecuciones"}
                </h2>
              </div>
              {latestRun && <Status value={latestRun.status} />}
            </div>
            {latestRun ? (
              <StepRail steps={latestRun.steps} audienceType={latestRun.config?.audience.type} sourceMode={latestRun.config?.acquisition.source} />
            ) : (
              <p className="muted">
                Cuando inicies la campaña, acá vas a ver cada etapa y su
                evidencia.
              </p>
            )}
          </section>
        </div>
      )}
      {tab === "imports" && <CampaignImportPanel campaign={campaign} canEdit={canEdit} />}
      {tab === "evidence" && <OfferEvidenceView campaign={campaign} canEdit={canEdit} />}
      {tab === "strategy" && <StrategyView run={latestRun} />}
      {tab === "templates" && <CampaignEmailTemplatesPanel campaign={campaign} canEdit={canEdit} />}
      {tab === "meeting-team" && <CampaignMeetingTeamPanel campaign={campaign} canEdit={canEdit} />}
      {tab === "prospects" && <Prospects campaign={campaign} />}{" "}
      {tab === "contacts" && <Contacts campaign={campaign} />}{" "}
      {tab === "outreach" && <Outreach campaign={campaign} />}{" "}
      {tab === "inbound" && <InboundMessages campaign={campaign} />}{" "}
      {tab === "meetings" && <MeetingsView campaign={campaign} />}{" "}
      {tab === "runs" && <Runs campaign={campaign} />}{" "}
      {tab === "activity" && <ActivityFeed campaignId={campaign.id} runs={campaign.runs ?? []} />}
      </div>
      <AnimatePresence>
        {showEdit && <CampaignEditorModal campaign={campaign} close={() => setShowEdit(false)} />}
        {showDuplicate && <CampaignEditorModal campaign={campaign} mode="duplicate" close={() => setShowDuplicate(false)} />}
      </AnimatePresence>
    </>
  );
}

const templateVariableLabels: Record<string, string> = {
  "contact.first_name": "Nombre del contacto",
  "contact.last_name": "Apellido del contacto",
  "contact.role": "Cargo del contacto",
  "company.name": "Empresa",
  "offer.name": "Oferta",
  "offer.summary": "Información autorizada de la oferta",
  "sender.company": "Empresa remitente",
  "sender.name": "Nombre del remitente",
  "sender.role": "Cargo del remitente",
  "meeting.date_time": "Fecha y hora de la reunión",
  "meeting.duration": "Duración de la reunión",
  "meeting.options": "Horarios propuestos",
  "meeting.team_names": "Nombres del equipo de reuniones",
  "meeting.team_with_roles": "Equipo de reuniones con cargos",
  "meeting.team_emails": "Emails del equipo de reuniones",
};

export function CampaignImportPanel({ campaign, canEdit }: { campaign: Campaign; canEdit: boolean }) {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const summary = campaign.importSummary ?? {
    hasImportedContacts: false,
    importCount: 0,
    activeImportCount: 0,
    excludedImportCount: 0,
    totalRows: 0,
    acceptedRows: 0,
    invalidRows: 0,
    duplicateRows: 0,
    items: [],
  };
  const [exclusionFile, setExclusionFile] = useState<File | null>(null);
  const exclusions = useMutation({
    mutationFn: async () => {
      if (!exclusionFile) throw new Error("Seleccioná un CSV de exclusiones");
      if (exclusionFile.size > 5 * 1024 * 1024) throw new Error("El CSV no puede superar 5 MB");
      return api<{ added: number; matched: number; unmatched: number; invalid: number; duplicates: number }>(`/campaigns/${campaign.id}/exclusions`, { method: "POST", body: JSON.stringify({ filename: exclusionFile.name, content: await exclusionFile.text() }) }, { timeoutMs: 30000 });
    },
    onSuccess: async () => { setExclusionFile(null); await client.invalidateQueries({ queryKey: ["campaign", campaign.id] }); await client.invalidateQueries({ queryKey: ["approvals"] }); },
  });
  const locked = (campaign._count?.runs ?? campaign.runs?.length ?? 0) > 0;
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Seleccioná un archivo CSV");
      if (file.size > 5 * 1024 * 1024) throw new Error("El CSV no puede superar 5 MB");
      return api(`/campaigns/${campaign.id}/imports`, {
        method: "POST",
        body: JSON.stringify({ filename: file.name, content: await file.text() }),
      }, { timeoutMs: 30_000 });
    },
    onSuccess: async () => {
      setFile(null);
      await client.invalidateQueries({ queryKey: ["campaign", campaign.id] });
    },
  });
  const changeImportState = useMutation({
    mutationFn: ({ importId, excluded, reason }: { importId: string; excluded: boolean; reason: string }) => api(`/campaigns/${campaign.id}/imports/${importId}`, {
      method: "PATCH",
      body: JSON.stringify({ excluded, reason }),
    }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["campaign", campaign.id] });
    },
  });
  const revalidateNotes = useMutation({
    mutationFn: (importId: string) => api<{ recoveredRows: number; duplicateRows: number; excludedRows: number; stillInvalidRows: number }>(`/campaigns/${campaign.id}/imports/${importId}/revalidate-notes`, { method: "POST" }, { timeoutMs: 70000 }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["campaign", campaign.id] });
      await client.invalidateQueries({ queryKey: ["prospects"] });
      await client.invalidateQueries({ queryKey: ["commercial-dashboard"] });
    },
  });
  return <section className="panel span-3 campaign-import-panel">
    <div className="panel-title">
      <div>
        <span className="eyebrow">BASE PROPIA</span>
        <h2>Importar contactos desde CSV</h2>
        <p>El email es el único dato obligatorio. Los demás campos enriquecen la personalización cuando están disponibles.</p>
      </div>
      <a className="secondary button-link" href="/api/campaign-imports/template" download="vicnet-contactos-template.csv"><Download size={16} />Descargar plantilla</a>
    </div>
    <div className="import-summary-grid" aria-label="Resumen de importación">
      <div><small>Contactos activos</small><strong>{summary.acceptedRows}</strong></div>
      <div><small>Contactos excluidos</small><strong>{summary.excludedRows ?? 0}</strong></div>
      <div><small>Con errores</small><strong>{summary.invalidRows}</strong></div>
      <div><small>Duplicadas</small><strong>{summary.duplicateRows}</strong></div>
      <div><small>Cargas realizadas</small><strong>{summary.importCount}</strong></div>
      <div><small>Cargas excluidas</small><strong>{summary.excludedImportCount}</strong></div>
    </div>
    <div className="import-columns">
      <strong>Columnas admitidas</strong>
      <div className="tags"><span>email · obligatorio</span><span>nombre y apellido</span><span>empresa y dominio</span><span>cargo</span><span>teléfono</span><span>LinkedIn</span><span>ubicación</span><span>industria y país</span><span>empleados</span><span>notas · hasta 20.000 caracteres</span></div>
    </div>
    {canEdit && <div className="import-upload-box">
      <label>Archivo CSV
        <input type="file" accept=".csv,text/csv" disabled={locked || upload.isPending} onChange={(event) => {
          upload.reset();
          setFile(event.target.files?.[0] ?? null);
        }} />
      </label>
      <button className="primary" disabled={!file || locked || upload.isPending} onClick={() => upload.mutate()}>
        {upload.isPending ? <LoaderCircle className="spin" size={16} /> : <Upload size={16} />}
        {upload.isPending ? "Validando e importando…" : "Importar CSV"}
      </button>
      {file && <span className="muted">{file.name} · {Math.max(1, Math.ceil(file.size / 1024))} KB</span>}
      {locked && <div className="form-warning full"><CircleAlert size={16} />La base queda congelada después del primer run para preservar la auditoría.</div>}
      {upload.error && <div className="form-error full">{upload.error instanceof ApiError ? upload.error.message : upload.error instanceof Error ? upload.error.message : "No se pudo importar el CSV"}</div>}
      {upload.isSuccess && <div className="upload-state success full">CSV procesado correctamente.</div>}
    </div>}
    {canEdit && <section className="import-exclusions" aria-label="Correos a excluir">
      <h3>Correos a excluir</h3><p>Subí un CSV con la columna <b>email</b>. Sólo afecta a esta campaña. Podés cargarlo antes o después de los contactos; las exclusiones se conservan para futuras cargas.</p>
      <p><strong>{summary.exclusionCount ?? 0}</strong> correos en la lista de exclusiones. Se detienen nuevos envíos a esos contactos, incluidos los pendientes de aprobación o envío.</p>
      <div className="import-upload-box"><label>CSV de exclusiones<input type="file" accept=".csv,text/csv" disabled={exclusions.isPending} onChange={event => { exclusions.reset(); setExclusionFile(event.target.files?.[0] ?? null); }} /></label>
      <button className="secondary" disabled={!exclusionFile || exclusions.isPending} onClick={() => exclusions.mutate()}>{exclusions.isPending ? "Aplicando exclusiones…" : "Cargar exclusiones"}</button></div>
      {exclusions.error && <p className="form-error" role="alert">{exclusions.error.message}</p>}
      {exclusions.data && <p role="status">{exclusions.data.added} exclusiones nuevas · {exclusions.data.matched} coincidencias · {exclusions.data.unmatched} sin coincidencia todavía (se conservan) · {exclusions.data.duplicates} repetidas · {exclusions.data.invalid} inválidas.</p>}
    </section>}
    {revalidateNotes.error && <p className="form-error" role="alert">{revalidateNotes.error.message}</p>}
    {revalidateNotes.data && <p role="status">Revalidación de notas: {revalidateNotes.data.recoveredRows} filas recuperadas (incluye {revalidateNotes.data.excludedRows} excluidas por la campaña) · {revalidateNotes.data.duplicateRows} duplicadas · {revalidateNotes.data.stillInvalidRows} siguen con errores. No se inició ninguna ejecución ni envío.</p>}
    {changeImportState.error && <div className="form-error" role="alert">{changeImportState.error instanceof ApiError ? changeImportState.error.message : "No se pudo modificar la importación"}</div>}
    {summary.items.length ? <div className="import-history">
      <div><span className="eyebrow">HISTORIAL DE CARGAS</span><h3>Importaciones de esta campaña</h3><p className="muted">Excluir una carga es reversible y no elimina sus registros.</p></div>
      {summary.items.map((item, index) => {
        const excluded = item.status === "EXCLUDED";
        const canToggle = item.acceptedRows > 0 && !locked;
        return <article className={`import-history-entry${excluded ? " excluded" : ""}`} key={item.id}>
          <div className="panel-title import-history-heading">
            <div><strong>{item.filename}</strong><p>{formatDate(item.createdAt)} · {item.acceptedRows} válidas, {item.invalidRows} con errores y {item.duplicateRows} duplicadas.</p></div>
            <div className="import-history-actions">
              <Status value={item.status} />
              {canEdit && !excluded && (item.notesRevalidationRows ?? 0) > 0 && <button className="secondary" disabled={locked || revalidateNotes.isPending || changeImportState.isPending || upload.isPending}
                onClick={() => { if (window.confirm("Se revisarán sólo las filas rechazadas por el antiguo límite de notas. Se conservarán los datos, las exclusiones y los controles de duplicados. No se iniciará la campaña. ¿Continuar?")) revalidateNotes.mutate(item.id); }}>
                {revalidateNotes.isPending && revalidateNotes.variables === item.id ? <LoaderCircle className="spin" size={16} /> : <RefreshCcw size={16} />}Revalidar notas ({item.notesRevalidationRows})</button>}

              {canEdit && item.status !== "REJECTED" && <button
                className={excluded ? "secondary" : "secondary danger-outline"}
                disabled={!canToggle || changeImportState.isPending}
                onClick={() => {
                  const reason = window.prompt(`Motivo para ${excluded ? "reactivar" : "excluir"} ${item.filename}:`);
                  if (reason?.trim() && window.confirm(`¿${excluded ? "Reactivar" : "Excluir"} la carga ${item.filename}? ${excluded ? "Sus contactos volverán a participar en futuros runs." : "Sus contactos dejarán de participar; los datos no se borrarán."}`))
                    changeImportState.mutate({ importId: item.id, excluded: !excluded, reason: reason.trim() });
                }}
              >{changeImportState.isPending && changeImportState.variables?.importId === item.id ? <LoaderCircle className="spin" size={16} /> : excluded ? <RefreshCcw size={16} /> : <XCircle size={16} />}{excluded ? "Reactivar carga" : "Excluir carga"}</button>}
            </div>
          </div>
          {locked && <div className="form-warning"><LockKeyhole size={16} />Esta carga está congelada porque la campaña ya tiene una ejecución.</div>}
          <details open={index === 0}>
            <summary>Ver registros de la carga</summary>
            <div className="table-wrap"><table><thead><tr><th>Fila</th><th>Email</th><th>Nombre</th><th>Empresa</th><th>Estado</th><th>Detalle</th></tr></thead><tbody>
              {item.rows.map((row) => <tr key={row.id}><td>{row.rowNumber}</td><td>{row.email || "—"}</td><td>{[row.data.first_name, row.data.last_name].filter(Boolean).join(" ") || "—"}</td><td>{row.data.company_name || row.data.domain || "—"}</td><td><Status value={row.status} /></td><td>{row.exclusionReason || row.errors.join(" · ") || (row.status === "DUPLICATE" ? "Email ya incorporado" : excluded ? "Excluida de futuros runs" : "Lista para usar")}</td></tr>)}
            </tbody></table></div>
            {item.totalRows > item.rows.length && <p className="muted">Se muestran las primeras {item.rows.length} filas de esta carga.</p>}
          </details>
        </article>;
      })}
    </div> : <div className="empty-state"><FileText size={28} /><strong>Todavía no hay archivos importados</strong><p>Descargá la plantilla, completala y cargala para habilitar la base propia.</p></div>}
  </section>;
}

export function CampaignEmailTemplatesPanel({ campaign, canEdit }: { campaign: Campaign; canEdit: boolean }) {
  const client = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["campaign-email-templates", campaign.id],
    queryFn: () => api<{ items: CampaignEmailTemplate[] }>(`/campaigns/${campaign.id}/email-templates`),
  });
  const generate = useMutation({
    mutationFn: () => api(`/campaigns/${campaign.id}/email-templates/generate`, { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["campaign-email-templates", campaign.id] }),
  });
  const update = useMutation({
    mutationFn: ({ template, current }: { template: CampaignEmailTemplate; current: CampaignEmailTemplateVersion }) => api(`/campaigns/${campaign.id}/email-templates/${template.id}`, {
      method: "PATCH",
      body: JSON.stringify({ subjectTemplate: subject, bodyTextTemplate: body, bodyHtmlTemplate: bodyHtml, expectedContentHash: current.contentHash }),
    }),
    onSuccess: async () => {
      setEditingId(null);
      await client.invalidateQueries({ queryKey: ["campaign-email-templates", campaign.id] });
    },
  });
  const items = templatesInFlowOrder(data?.items ?? []);
  const begin = (template: CampaignEmailTemplate) => {
    const current = template.currentVersion;
    if (!current) return;
    update.reset();
    setSubject(current.subjectTemplate);
    setBody(current.bodyTextTemplate);
    setBodyHtml(current.bodyHtmlTemplate ?? emailTextToHtml(current.bodyTextTemplate));
    setEditingId(template.id);
  };
  if (isLoading) return <div className="loading" role="status">Preparando plantillas…</div>;
  if (isError) return <div className="query-state error"><CircleAlert /><strong>No se pudieron cargar las plantillas</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal."}</p><button className="secondary" onClick={() => void refetch()}><RefreshCcw />Reintentar</button></div>;
  return <section className="email-templates-panel">
    <div className="panel-title email-templates-heading"><div><span className="eyebrow">REDACCIÓN DE OUTREACH</span><h2>Plantillas de la campaña</h2><p>{campaign.config.messageSource === "TEMPLATES" ? "Esta campaña usa las plantillas publicadas para redactar correos y textos de Calendar." : "Esta campaña usa al agente para redactar; las plantillas están disponibles como alternativas."} {campaign.config.copyPolicy === "TEMPLATE_OR_AGENT" && "En todo el flujo, IA evalúa la plantilla y redacta cuando falta o no alcanza."} Están ordenadas por el flujo; las variantes y ramas se usan según la conversación. Ninguna plantilla aprueba ni envía por sí sola.</p></div>{canEdit && items.length < campaignEmailTemplateTypes.length && <button className="secondary" disabled={generate.isPending} onClick={() => generate.mutate()}>{generate.isPending ? <LoaderCircle className="spin" /> : <RefreshCcw />}Generar faltantes</button>}</div>
    {generate.error && <div className="form-error">{generate.error instanceof ApiError ? generate.error.message : "No se pudieron generar las plantillas"}</div>}
    <ol className="email-template-list">
      {items.map((template) => {
        const current = template.currentVersion;
        const editing = editingId === template.id;
        const stale = Boolean(current && campaign.configVersion && current.sourceConfigVersion !== campaign.configVersion);
        return <li key={template.id}><details className={`email-template-card${editing ? " editing" : ""}`}><summary><ChevronRight size={18} aria-hidden="true" /><div><span className="eyebrow">{campaignTemplateFlow[template.type]?.stage}</span><h3>{template.label}</h3></div><div className="template-version"><Status value={template.status} />{current && <small>v{current.version}</small>}</div></summary><div className="email-template-content">
          {stale && <div className="template-stale"><CircleAlert />La campaña cambió después de publicar esta versión. Podés revisarla; los runs existentes conservan su snapshot.</div>}
          {!editing && current && <div className="template-preview-meta"><span>Asunto</span><strong>{current.subjectTemplate}</strong></div>}
          {current ? editing ? <ApprovalMessageEditor
            editing
            subject={subject}
            body={body}
            bodyHtml={bodyHtml}
            attachments={[]}
            approvalId={template.id}
            pending={update.isPending}
            error={update.error}
            variableTokens={current.allowedVariables.map((value) => ({ value, label: templateVariableLabels[value] ?? value }))}
            showAttachments={false}
            footerNote="Guardar publica una nueva versión de la plantilla. No modifica mensajes ya creados ni dispara envíos."
            onSubjectChange={setSubject}
            onBodyChange={setBody}
            onBodyHtmlChange={setBodyHtml}
            onUploadAttachment={() => undefined}
            onRemoveAttachment={() => undefined}
            onCancel={() => setEditingId(null)}
            onSave={() => update.mutate({ template, current })}
          /> : current.bodyHtmlTemplate
            ? <div className="template-email-preview" dangerouslySetInnerHTML={{ __html: current.bodyHtmlTemplate }} />
            : <pre className="template-email-preview">{current.bodyTextTemplate}</pre>
          : <p className="muted">La plantilla todavía no tiene una versión publicada.</p>}
          {!editing && current && <>{current.warnings.length > 0 && <ul className="template-warnings">{current.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>}{canEdit && <button className="secondary" onClick={() => begin(template)}><Settings2 />Editar plantilla</button>}</>}
        </div></details></li>;
      })}
      {!items.length && <li><Empty title="Todavía no hay plantillas" body="Generá las plantillas de contactos, seguimientos, respuestas y Calendar de esta campaña." /></li>}
    </ol>
  </section>;
}

function StrategyView({ run }: { run?: Run }) {
  if (!run)
    return <Empty title="Sin estrategia" body="Iniciá una ejecución para enviar el brief a Hermes." />;
  const recommendation = run.hermesResult?.commercial_recommendation
    ?? (run.hermesResult?.execution_decision === "GO" ? "ADVANCE" : "NOT_RECOMMENDED");
  const recommendationReason = run.hermesResult?.commercial_recommendation_reason
    ?? run.hermesResult?.execution_decision_reason;
  const recommendationLabel = ({ ADVANCE: "Avanzar", ADVANCE_WITH_CHANGES: "Avanzar con ajustes", PILOT: "Realizar piloto", NOT_RECOMMENDED: "No recomendado" } as Record<string, string>)[recommendation] ?? recommendation;
  return (
    <div className="overview-grid strategy-view">
      <section className="panel span-2">
        <span className="eyebrow">BRIEF ORIGINAL · V{run.brief?.configVersion ?? "—"}</span>
        <h2>{run.brief?.objective || "Objetivo pendiente"}</h2>
        <p>{run.brief?.description}</p>
        <div className="tags"><span>{run.brief?.tone ?? "Tono pendiente"}</span></div>
      </section>
      <section className="panel">
        <span className="eyebrow">HERMES</span>
        <h2>{run.provider === "HERMES" ? "Resultado remoto" : "SIMULATED"}</h2>
        <p>{run.hermesResult?.strategy_summary ?? run.degradedReason ?? "Esperando resultado estructurado…"}</p>
        {run.hermesResult && <div className="decision-box"><span className="eyebrow">RECOMENDACIÓN IA</span><strong>{recommendationLabel}</strong><p>{recommendationReason}</p></div>}
        {run.hermesTaskId && <details className="technical-disclosure"><summary>Ver identificadores de Hermes</summary><small>Task {run.hermesTaskId}{run.hermesRunId ? ` · Run ${run.hermesRunId}` : ""}</small></details>}
      </section>
      {(run.strategyVersions?.length ?? 0) > 0 && <section className="panel span-3"><span className="eyebrow">VERSIONES DE ESTRATEGIA</span><h2>{run.strategyVersions?.at(-1)?.status === "RUNNING" ? "Reprocesando estrategia" : (run.strategyVersions?.length ?? 0) > 1 ? "Historial comparable" : "Resultado técnico completo"}</h2><div className="strategy-version-list">{run.strategyVersions?.slice().reverse().map((version) => <details key={version.id} open={version === run.strategyVersions?.at(-1)}><summary><strong>Versión {version.version}</strong><Status value={version.status} /><small>{version.reason ?? "Estrategia inicial"}</small></summary>{version.strategyJson ? <pre>{JSON.stringify(JSON.parse(version.strategyJson), null, 2)}</pre> : version.errorMessage ? <div className="form-error">{version.errorMessage}</div> : <p>Esperando resultado del Director IA…</p>}<small>{version.profile ?? "director-general-ia"}{version.providerTaskId ? ` · Task ${version.providerTaskId}` : ""}</small></details>)}</div></section>}
      {run.hermesResult && (
        <section className="panel span-3">
          <span className="eyebrow">RESULTADO HERMES</span>
          <h2>{run.hermesResult.positioning}</h2>
          <p>{run.hermesResult.value_proposition}</p>
          <div className="tags">{run.hermesResult.target_company_criteria.map((item) => <span key={item}>{item}</span>)}</div>
          <div className="role-rationale-list"><h3>Roles efectivos</h3><p>{run.brief?.icp?.targetRoleSource === "DIRECTOR_AI" ? "Propuestos por Director IA según la oferta y el objetivo." : "Definidos manualmente en la campaña."}</p>{run.hermesResult.target_contact_roles.map((role) => <article key={role}><strong>{role}</strong><small>{run.hermesResult?.target_contact_role_rationale?.find((item) => item.role === role)?.reason ?? "Rol efectivo del snapshot de esta ejecución."}</small></article>)}</div>
          <details className="technical-disclosure">
            <summary>Ver riesgos, faltantes y etapas pendientes</summary>
            {run.hermesResult.missing_information.length > 0 && <div className="warning-banner"><CircleAlert /><div><strong>Información faltante</strong><p>{run.hermesResult.missing_information.join(" · ")}</p></div></div>}
            {run.hermesResult.risks.length > 0 && <div className="warning-banner"><CircleAlert /><div><strong>Riesgos señalados por Hermes</strong><p>{run.hermesResult.risks.join(" · ")}</p></div></div>}
            {run.steps.some((step) => step.status === "PENDING") && <div className="pending-steps"><strong>Etapas que no comenzaron</strong><div className="tags">{run.steps.filter((step) => step.status === "PENDING").map((step) => <span key={step.id}>{step.label}</span>)}</div></div>}
          </details>
        </section>
      )}
    </div>
  );
}

type RunEvent = NonNullable<Run["events"]>[number];
type LiveDiscoveryCandidate = { key: string; name: string; domain: string; industry?: string; country?: string; employees?: number; status: "FOUND" | "ACCEPTED" | "EXCLUDED"; reason?: string };

export function liveDiscoveryState(events: RunEvent[]) {
  const candidates = new Map<string, LiveDiscoveryCandidate>();
  let activeSearch: { label: string; requested?: number } | null = null;
  let completedSearches = 0;
  let estimatedCreditsMin = 0;
  let estimatedCreditsMax = 0;
  let meteredCalls = 0;
  let recovery: { active: boolean; preservedCandidates: number; providerReinvoked: boolean; message: string } | null = null;
  const payloadOf = (event: RunEvent) => event.payload && typeof event.payload === "object" && !Array.isArray(event.payload) ? event.payload as Record<string, unknown> : {};
  const text = (value: unknown) => typeof value === "string" ? value : undefined;
  const number = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? value : undefined;
  for (const event of [...events].sort((left, right) => left.createdAt.localeCompare(right.createdAt))) {
    const payload = payloadOf(event);
    if (event.type === "COMPANY_DISCOVERY_AUTO_RECOVERY_STARTED") recovery = {
      active: true,
      preservedCandidates: number(payload.preservedCandidates) ?? 0,
      providerReinvoked: false,
      message: "Recuperación automática iniciada; se conserva el progreso ya validado.",
    };
    if (event.type === "COMPANY_DISCOVERY_HUNTER_RETRY_EXECUTED") recovery = {
      active: true,
      preservedCandidates: number(payload.preservedCandidates) ?? recovery?.preservedCandidates ?? 0,
      providerReinvoked: true,
      message: "Hunter será consultado nuevamente con un reintento acotado y auditable.",
    };
    if (event.type === "COMPANY_DISCOVERY_PARTIAL_RESULTS_PRESERVED") recovery = {
      active: true,
      preservedCandidates: number(payload.preservedCandidates) ?? recovery?.preservedCandidates ?? 0,
      providerReinvoked: payload.providerReinvoked === true,
      message: "El universo existente alcanza el objetivo; no se harán más búsquedas y Discovery cerrará con esos resultados.",
    };
    if (event.type === "COMPANY_DISCOVERY_COMPLETED_WITH_PARTIAL_RESULTS") recovery = recovery ? { ...recovery, active: false, message: "Discovery terminó de forma degradada pero segura con los resultados preservados." } : null;
    if (event.type === "COMPANY_DISCOVERY_AUTO_RECOVERY_FAILED") recovery = recovery ? { ...recovery, active: false, message: "La recuperación automática se agotó; el progreso quedó preservado para revisión o recuperación manual." } : null;
    if (event.type === "COMPANY_DISCOVERY_SEARCH_STARTED") activeSearch = { label: text(payload.label) ?? text(payload.route) ?? "Búsqueda Hunter", requested: number(payload.requested) };
    if (["COMPANY_DISCOVERY_INCREMENTAL_BATCH_COMPLETED", "COMPANY_DISCOVERY_SEARCH_EXPANDED"].includes(event.type)) completedSearches += 1;
    const operation = text(payload.operation);
    if (event.type === "HUNTER_USAGE_METERED" && ["DISCOVER", "COMPANY_ENRICHMENT"].includes(operation ?? "")) {
      meteredCalls += 1;
      estimatedCreditsMin += number(payload.estimatedCreditsMin) ?? 0;
      estimatedCreditsMax += number(payload.estimatedCreditsMax) ?? 0;
    }
    if (!["COMPANY_DISCOVERY_CANDIDATE_FOUND", "COMPANY_DISCOVERY_FIRMOGRAPHICS_CONFIRMED", "COMPANY_DISCOVERY_FIRMOGRAPHICS_NOT_FOUND", "COMPANY_DISCOVERY_FIRMOGRAPHICS_FAILED", "COMPANY_DISCOVERY_CANDIDATE_ACCEPTED", "COMPANY_DISCOVERY_CANDIDATE_EXCLUDED"].includes(event.type)) continue;
    const domain = text(payload.domain) ?? "";
    const companyId = text(payload.companyId) ?? "";
    const key = domain || companyId;
    if (!key) continue;
    const previous = candidates.get(domain) ?? candidates.get(companyId) ?? candidates.get(key);
    const status = event.type === "COMPANY_DISCOVERY_CANDIDATE_ACCEPTED" ? "ACCEPTED" : event.type === "COMPANY_DISCOVERY_CANDIDATE_EXCLUDED" ? "EXCLUDED" : previous?.status ?? "FOUND";
    const next: LiveDiscoveryCandidate = {
      key,
      name: text(payload.name) ?? previous?.name ?? domain ?? "Empresa encontrada",
      domain: domain || previous?.domain || "",
      industry: text(payload.industry) ?? previous?.industry,
      country: text(payload.country) ?? previous?.country,
      employees: number(payload.employees) ?? previous?.employees,
      status,
      reason: text(payload.reason) ?? previous?.reason,
    };
    if (previous && previous.key !== key) candidates.delete(previous.key);
    candidates.set(key, next);
  }
  return { candidates: [...candidates.values()], activeSearch, completedSearches, meteredCalls, estimatedCreditsMin, estimatedCreditsMax, recovery };
}

export function discoveryDisplayMetrics(output: Pick<DiscoveryOutput, "candidateCompanyIds" | "candidatesDetail" | "qualificationReadyCandidateIds" | "excludedCandidates">, liveCandidates: LiveDiscoveryCandidate[]) {
  const hasPersistedResult = Array.isArray(output.candidateCompanyIds) || Array.isArray(output.candidatesDetail);
  return {
    hasPersistedResult,
    candidates: hasPersistedResult ? (output.candidatesDetail?.length ?? output.candidateCompanyIds?.length ?? 0) : liveCandidates.length,
    ready: hasPersistedResult ? (output.qualificationReadyCandidateIds?.length ?? 0) : liveCandidates.filter((candidate) => candidate.status === "ACCEPTED").length,
    excluded: hasPersistedResult ? (output.excludedCandidates?.length ?? 0) : liveCandidates.filter((candidate) => candidate.status === "EXCLUDED").length,
  };
}

function DiscoveryLiveActivity({ events, active }: { events: RunEvent[]; active: boolean }) {
  const state = liveDiscoveryState(events);
  if (!state.candidates.length && !state.activeSearch && !state.meteredCalls) return null;
  const accepted = state.candidates.filter((candidate) => candidate.status === "ACCEPTED").length;
  const excluded = state.candidates.filter((candidate) => candidate.status === "EXCLUDED").length;
  return <section className="discovery-live" aria-live="polite">
    <header><div><span className="eyebrow">SEGUIMIENTO EN VIVO</span><strong>{active ? state.activeSearch?.label ?? "Evaluando empresas" : "Actividad registrada durante la búsqueda"}</strong><small>{active ? "La lista se actualiza sin esperar a que finalice la etapa." : `${state.completedSearches} búsquedas procesadas.`}</small></div>{active && <LoaderCircle className="spin" />}</header>
    {state.recovery && <div className="warning-banner"><CircleAlert /><div><strong>{state.recovery.active ? "Recuperación automática en curso" : "Recuperación automática registrada"}</strong><p>{state.recovery.message} {state.recovery.preservedCandidates} candidatas preservadas · Hunter {state.recovery.providerReinvoked ? "fue consultado nuevamente" : "no fue reinvocado"}.</p></div></div>}
    <div className="discovery-live-metrics"><span><strong>{state.candidates.length}</strong><small>encontradas</small></span><span><strong>{accepted}</strong><small>seleccionadas</small></span><span><strong>{excluded}</strong><small>descartadas</small></span><span><strong>{state.meteredCalls}</strong><small>consultas Hunter</small></span></div>
    {state.meteredCalls > 0 && <div className="discovery-usage"><strong>Consumo Hunter estimado: {state.estimatedCreditsMin.toFixed(1)}–{state.estimatedCreditsMax.toFixed(1)} créditos</strong><small>Hunter no informa el débito exacto en estas respuestas; el valor definitivo se concilia en su panel de consumo.</small></div>}
    <div className="discovery-live-list">{state.candidates.map((candidate) => <article key={candidate.key} className={candidate.status.toLowerCase()}><div><strong>{candidate.name}</strong><small>{candidate.domain || "Dominio pendiente"}{candidate.industry ? ` · ${candidate.industry}` : ""}{candidate.country ? ` · ${candidate.country}` : ""}{candidate.employees ? ` · ${candidate.employees} empleados` : ""}</small>{candidate.reason && <p>{candidate.reason}</p>}</div><span>{candidate.status === "ACCEPTED" ? "Seleccionada" : candidate.status === "EXCLUDED" ? "Descartada" : "Encontrada"}</span></article>)}</div>
  </section>;
}

function IndividualDiscoveryReview({ step, sourceMode }: { step: Step; sourceMode: CampaignConfig["acquisition"]["source"] }) {
  const output = parsedJson<DiscoveryOutput>(step.outputJson, {});
  const searched = output.searchedCandidates ?? 0;
  const imported = output.importedCandidates ?? 0;
  const selected = output.selectedContacts ?? 0;
  const warnings = output.searchWarnings ?? [];
  const agent = output.agent?.id ?? (sourceMode === "IMPORTED" ? null : "prospeccion-personas");
  return <details className="panel discovery-review" aria-labelledby="individual-discovery-review-title" open={["RUNNING", "BLOCKED", "FAILED"].includes(step.status)}>
    <summary className="discovery-review-summary section-head">
      <div>
        <span className="eyebrow">RESULTADO DE LA ETAPA 2</span>
        <h2 id="individual-discovery-review-title">Prospección de personas auditable</h2>
        <p>{output.searchSummary ?? (sourceMode === "IMPORTED" ? "La etapa utilizó exclusivamente la base CSV activa de la campaña." : "Búsqueda gobernada de personas con email público explícito y evidencia trazable.")}</p>
      </div>
      <div className="section-head-metrics">
        {sourceMode !== "SEARCH" && <span><strong>{imported}</strong><small>importadas</small></span>}
        {sourceMode !== "IMPORTED" && <span><strong>{searched}</strong><small>encontradas</small></span>}
        <span><strong>{selected}</strong><small>seleccionadas</small></span>
      </div>
    </summary>
    <div className="discovery-review-content">
      <div className="discovery-agent-strip"><Bot /><div><strong>{agent ? `Agente ejecutado: ${agent}` : "Fuente ejecutada: base propia"}</strong><small>{sourceMode === "HYBRID" ? "Base CSV + búsqueda pública" : sourceMode === "SEARCH" ? "Fuentes públicas permitidas · Hunter deshabilitado" : "Sin proveedores externos · Hunter deshabilitado"}</small></div></div>
      {output.discoveryPolicy && <div className="info-banner"><ShieldCheck /><div><strong>Política separada de búsqueda</strong><p>La búsqueda pública está {output.discoveryPolicy.public_source_discovery === "ALLOWED" ? "permitida" : "fuera de alcance"}; exige email publicado explícitamente, no usa fuentes autenticadas y no admite inferencias sensibles. La política editorial del correo no modifica estas reglas.</p></div></div>}
      {step.errorCode === "DIRECT_CONTACT_SOURCE_EMPTY" && <div className="warning-banner"><CircleAlert /><div><strong>La búsqueda terminó sin contactos elegibles</strong><p>El agente respondió y el resultado quedó preservado. Podés revisar sus advertencias y reintentar únicamente esta búsqueda, sin repetir Estrategia ni consultar Hunter.</p></div></div>}
      {warnings.length > 0 && <details className="discovery-section" open><summary>Advertencias del agente ({warnings.length})</summary><div className="discovery-warnings">{warnings.map((warning, index) => <p key={index}>{warning}</p>)}</div></details>}
    </div>
  </details>;
}

function DiscoveryReview({ step, events = [] }: { step: Step; events?: RunEvent[] }) {
  const output = parsedJson<DiscoveryOutput>(step.outputJson, {});
  const candidates = output.candidatesDetail ?? [];
  const excluded = output.excludedCandidates ?? [];
  const ready = new Set(output.qualificationReadyCandidateIds ?? []);
  const live = liveDiscoveryState(events);
  const metrics = discoveryDisplayMetrics(output, live.candidates);
  const { hasPersistedResult } = metrics;
  if (!output.searchPlan && !candidates.length && !live.candidates.length && !live.activeSearch) return null;
  return <details className="panel discovery-review" aria-labelledby="discovery-review-title" open={step.status === "RUNNING" ? true : undefined}>
    <summary className="discovery-review-summary section-head">
      <div><span className="eyebrow">RESULTADO DE LA ETAPA 2</span><h2 id="discovery-review-title">Prospección auditable</h2><p>{output.assessment?.summary ?? output.searchPlan?.summary ?? "Plan, búsquedas, evidencia y decisiones de Prospección IA."}</p></div>
      <div className="section-head-metrics"><span><strong>{metrics.candidates}</strong><small>{hasPersistedResult ? "evaluadas" : "encontradas"}</small></span><span><strong>{metrics.ready}</strong><small>listas</small></span><span><strong>{metrics.excluded}</strong><small>excluidas</small></span></div>
    </summary>
    <div className="discovery-review-content">
    <DiscoveryLiveActivity events={events} active={step.status === "RUNNING"} />
    {(output.searchPlan || candidates.length > 0) && <div className="discovery-agent-strip"><Bot /><div><strong>Agente ejecutado: {output.agent?.id ?? "prospeccion-leads"}</strong><small>Perfil {output.agent?.profile ?? "prospeccion-leads"} · plan {output.agent?.planPromptVersion ?? "—"} · evaluación {output.agent?.assessmentPromptVersion ?? "—"}</small></div></div>}
    {output.evaluationIncomplete && <div className="warning-banner"><CircleAlert /><div><strong>Evaluación incompleta por una falla del proveedor</strong><p>Se preservaron {output.recovery?.preservedCandidates ?? candidates.length} empresas. {output.providerFailure?.message ?? "La búsqueda no pudo finalizar."} La recuperación manual sigue disponible y no repetirá Estrategia.</p></div></div>}
    {output.completedWithPartialResults && <div className="warning-banner"><CircleAlert /><div><strong>Descubrimiento completado con resultados preservados</strong><p>Hunter no pudo ampliar una búsqueda posterior, pero ya había {output.recovery?.preservedCandidates ?? candidates.length} candidatas suficientes para continuar. {output.recovery?.providerReinvoked ? "Se ejecutaron reintentos acotados." : "No fue necesario volver a invocar al proveedor."}</p></div></div>}
    {step.status !== "RUNNING" && hasPersistedResult && !output.evaluationIncomplete && ready.size === 0 && <div className="warning-banner"><CircleAlert /><div><strong>Calificación permanece bloqueada</strong><p>Ninguna candidata cumple todavía los filtros obligatorios de país, tamaño y dominio. La señal pública, la capacidad vinculada y la hipótesis mejoran la puntuación, pero no descartan empresas por sí solas.</p></div></div>}
    {output.searchPlan && <details className="discovery-section"><summary>Plan de búsqueda del agente</summary><div className="discovery-plan-grid">{output.searchPlan.search_hypotheses.map((hypothesis) => <article key={hypothesis.id}><span className="eyebrow">{hypothesis.label}</span><p>{hypothesis.rationale}</p><small><b>Capacidad:</b> {hypothesis.authorized_capability}</small><small><b>Consulta Hunter:</b> {hypothesis.hunter_query}</small><small><b>Señales buscadas:</b> {hypothesis.signal_types.join(" · ")}</small></article>)}</div></details>}
    {(output.searches?.length ?? 0) > 0 && <details className="discovery-section"><summary>Búsquedas y filtros realmente aplicados por Hunter</summary><div className="discovery-search-list">{output.searches?.map((search) => <article key={search.hypothesisId}><div><strong>{search.label}</strong><span>{search.returned} resultados devueltos</span></div><small>Solicitud enviada</small><pre>{JSON.stringify(search.request, null, 2)}</pre><small>Filtros informados por Hunter (`meta.filters`)</small><pre>{JSON.stringify(search.meta?.filters ?? "No informado", null, 2)}</pre></article>)}</div></details>}
    <div className="discovery-candidate-list">
      {candidates.map((candidate) => <details key={candidate.id} className={`discovery-candidate ${ready.has(candidate.id) ? "ready" : candidate.assessment?.status === "REJECTED" ? "rejected" : "pending"}`}>
        <summary><div><span className="eyebrow">{ready.has(candidate.id) ? "LISTA PARA CALIFICAR" : candidate.assessment?.status === "REJECTED" ? "DESCARTADA" : "REQUIERE EVIDENCIA"}</span><h3>{candidate.name}</h3><small>{candidate.domain ?? "Dominio desconocido"} · {candidate.country} · {candidate.employees || "Tamaño desconocido"} empleados</small></div><span className="score">{candidate.assessment?.score ?? 0}</span></summary>
        <div className="discovery-candidate-content">
        <div className="candidate-facts"><span><b>País</b>{candidate.country}</span><span><b>Empleados</b>{candidate.employees || "Desconocido"}</span><span><b>Industria</b>{candidate.industry}</span><span><b>Emails Hunter</b>{candidate.emailsCount ?? 0}</span></div>
        <div className="candidate-gates"><span className={candidate.gate.countryMatches ? "ok" : "fail"}>{candidate.gate.countryMatches ? <Check /> : <X />}País</span><span className={candidate.gate.sizeMatches ? "ok" : "fail"}>{candidate.gate.sizeMatches ? <Check /> : <X />}Tamaño</span><span className={candidate.gate.signalReady ? "ok" : "optional"}>{candidate.gate.signalReady ? <Check /> : "—"}Señal (puntaje)</span><span className={candidate.gate.capabilityReady ? "ok" : "optional"}>{candidate.gate.capabilityReady ? <Check /> : "—"}Capacidad (puntaje)</span><span className={candidate.gate.hypothesisReady ? "ok" : "optional"}>{candidate.gate.hypothesisReady ? <Check /> : "—"}Hipótesis (puntaje)</span></div>
        {candidate.assessment?.public_signal && <div className="candidate-signal"><strong>{candidate.assessment.public_signal.title}</strong><p>{candidate.assessment.public_signal.fact}</p><a href={candidate.assessment.public_signal.url} target="_blank" rel="noreferrer"><ExternalLink />Abrir fuente pública</a><small>Publicada {formatDate(candidate.assessment.public_signal.published_at)} · verificada {formatDate(candidate.assessment.public_signal.observed_at)}</small></div>}
        {candidate.assessment?.authorized_capability && <p><b>Capacidad autorizada:</b> {candidate.assessment.authorized_capability}</p>}
        {candidate.assessment?.bilateral_hypothesis && <p><b>Hipótesis bilateral:</b> {candidate.assessment.bilateral_hypothesis}</p>}
        <p><b>Evaluación:</b> {candidate.assessment?.reasons.join(" · ") ?? "Sin evaluación del agente"}</p>
        {(candidate.assessment?.unknown_fields.length ?? 0) > 0 && <small>Campos desconocidos: {candidate.assessment?.unknown_fields.join(" · ")}</small>}
        {(candidate.assessment?.contradictions.length ?? 0) > 0 && <small className="inline-error">Contradicciones: {candidate.assessment?.contradictions.join(" · ")}</small>}
        </div>
      </details>)}
    </div>
    {excluded.length > 0 && <details className="discovery-section"><summary>Candidatas excluidas antes de evaluar</summary><div className="discovery-excluded-list">{excluded.map((candidate) => <article key={candidate.companyId}><strong>{candidate.name}</strong><small>{candidate.domain ?? "Sin dominio"} · {candidate.scope}</small><p>{candidate.reason}</p></article>)}</div></details>}
    {(output.assessment?.warnings.length ?? 0) > 0 && <details className="discovery-section"><summary>Advertencias del agente ({output.assessment?.warnings.length})</summary><div className="discovery-warnings">{output.assessment?.warnings.map((warning, index) => <p key={index}>{warning}</p>)}</div></details>}
    </div>
  </details>;
}

function Contacts({ campaign }: { campaign: Campaign }) {
  const [expanded, setExpanded] = useState<string>();
  const contacts = campaign.contacts ?? [];
  const resolution = campaign.contactResolution;
  return <section>
    <div className="section-head"><div><span className="eyebrow">RESOLUCIÓN DE CONTACTOS</span><h2>Resultado por empresa del último run</h2><p>Cada empresa aceptada aparece aunque Hunter no haya encontrado una persona válida. Los descartes quedan visibles con su motivo.</p></div><div className="section-head-metrics"><span><strong>{campaign.contactProgress?.validForReferenceRun ?? 0}/{campaign.contactProgress?.target ?? campaign.config.goal.targetContacts}</strong><small>válidos del último run</small></span><span><strong>{contacts.length}</strong><small>históricos seleccionados</small></span></div></div>
    {resolution?.items.length ? <div className="table-card contact-resolution-table"><table><thead><tr><th>Empresa</th><th>Resultado</th><th>Contacto seleccionado</th><th>Candidatos revisados</th><th aria-label="Detalle" /></tr></thead><tbody>
      {resolution.items.map((item) => <Fragment key={item.companyId}><tr><td><strong>{item.company}</strong><small>{item.domain ?? "Sin dominio"}</small></td><td><Status value={item.status} /><small>{item.status === "NO_VERIFIED_CONTACT" ? "Hunter respondió, pero ningún rol superó la validación" : item.status === "NOT_EVALUATED" ? "Todavía no se evaluaron personas" : "Contacto válido para este run"}</small></td><td>{item.selectedContact ? <><strong>{item.selectedContact.firstName} {item.selectedContact.lastName}</strong><small>{item.selectedContact.role || "Rol no informado"} · {item.selectedContact.email ?? "Sin email"}</small></> : <span className="no-action">Sin contacto válido</span>}</td><td><strong>{item.candidatesEvaluated}</strong><small>{item.rejectedCandidates.length} descartados</small></td><td>{item.rejectedCandidates.length > 0 && <button className="icon-button table-expand-button" aria-label={`Ver candidatos descartados de ${item.company}`} aria-expanded={expanded === `resolution:${item.companyId}`} onClick={() => setExpanded((current) => current === `resolution:${item.companyId}` ? undefined : `resolution:${item.companyId}`)}><ChevronRight size={16} /></button>}</td></tr>{expanded === `resolution:${item.companyId}` && <tr className="expandable-detail"><td colSpan={5}><div className="contact-rejection-list">{item.rejectedCandidates.map((candidate) => <article key={candidate.id}><div><strong>{`${candidate.firstName} ${candidate.lastName}`.trim() || "Nombre no informado"}</strong><small>{candidate.role || "Rol no informado"} · {candidate.email ?? "Sin email"}</small></div><span className="score">{candidate.confidence}</span><p>{candidate.reason}</p></article>)}</div></td></tr>}</Fragment>)}
    </tbody></table></div> : <Empty title="Sin resolución del último run" body="Las empresas y sus resultados aparecerán después del enriquecimiento de contactos." />}
    <div className="section-head contact-history-head"><div><span className="eyebrow">HISTORIAL</span><h2>Contactos seleccionados anteriormente</h2><p>El historial se conserva, pero un run nuevo comienza sin contactos válidos heredados.</p></div></div>
    {contacts.length ? <div className="table-card"><table><thead><tr><th>Contacto</th><th>Rol</th><th>Empresa</th><th>Score de contacto</th><th>Estado</th><th aria-label="Detalle" /></tr></thead><tbody>
      {contacts.map((row) => <Fragment key={row.id}><tr><td><strong>{row.contact.firstName} {row.contact.lastName}</strong><small title={row.contact.email}>{row.contact.email}</small></td><td>{row.contact.role}</td><td>{row.contact.company.name}</td><td><span className="score">{row.score}</span></td><td><Status value={row.status ?? "SELECTED"} /><small>No heredable automáticamente</small></td><td><button className="icon-button table-expand-button" aria-label={`Ver detalle de ${row.contact.firstName}`} aria-expanded={expanded === row.id} onClick={() => setExpanded((current) => current === row.id ? undefined : row.id)}><ChevronRight size={16} /></button></td></tr>{expanded === row.id && <tr className="expandable-detail"><td colSpan={6}><div className="expandable-detail-content"><div><small>Email histórico</small><strong>{row.contact.email ?? "No disponible"}</strong></div><div><small>Empresa</small><strong>{row.contact.company.name}</strong></div><div><small>Dominio</small><strong>{row.contact.company.domain ?? "No disponible"}</strong></div><div><small>Industria</small><strong>{row.contact.company.normalizedIndustry ?? row.contact.company.industry}</strong></div><div><small>País</small><strong>{row.contact.company.country}</strong></div><div><small>Score histórico del contacto</small><strong>{row.score} · no es el score de empresa</strong></div><div className="wide"><small>Elegibilidad para un run nuevo</small><strong>{row.reuseEligibility?.exclusion?.reason ?? "Debe evaluarse nuevamente dentro del run; no se hereda como contacto válido."}</strong></div><div className="wide"><small>Alcance de la exclusión</small><strong>{row.reuseEligibility?.exclusion?.scope ?? "CAMPAÑA Y ASOCIACIÓN CON EMPRESA"}{row.reuseEligibility?.exclusion?.allowsReinstatementWithNewEvidence ? " · admite evidencia pública posterior" : " · no admite reactivación automática"}</strong></div><div className="wide"><small>Desglose y fundamento histórico</small><strong>{Object.entries(parsedJson<Record<string, number | string>>(row.scoreBreakdownJson, {})).map(([key, value]) => `${key}: ${value}`).join(" · ") || row.qualificationReason || "No disponible en este run legado"}</strong></div></div></td></tr>}</Fragment>)}
    </tbody></table></div> : <Empty title="Sin contactos" body="Los contactos aparecerán después del enriquecimiento." />}
  </section>;
}

function InboundMessages({ campaign }: { campaign: Campaign }) {
  const messages = campaign.messages?.filter((message) => message.direction === "INBOUND") ?? [];
  return <section><div className="section-head"><div><span className="eyebrow">INBOUND</span><h2>Respuestas recibidas</h2><p>Clasificación humana primero; payload y código técnico disponibles al expandir.</p></div><div className="section-head-metrics"><span><strong>{messages.length}</strong><small>respuestas</small></span></div></div>{messages.length ? (
    <div className="message-list">{messages.map((message) => <article key={message.id} className="message-card"><div className="message-person"><span>{message.contact.firstName.slice(0, 1)}</span><div><strong>{message.contact.firstName} {message.contact.lastName}</strong><small>{message.createdAt ? formatDate(message.createdAt) : "Respuesta vinculada"}</small></div><Status value={message.classification ?? "UNKNOWN"} /></div><h3>{message.subject || "Respuesta sin asunto"}</h3><p className="message-preview">{message.body}</p><div className="message-meta"><span>Estado: {presentationText(message.status)}</span><span>Próxima acción: {message.actionKind ? presentationText(message.actionKind, "action") : "Revisión según clasificación"}</span></div><details className="message-detail"><summary>Ver mensaje y detalle técnico</summary><div className="message-detail-body">{message.body}</div><code>Código: {message.classification ?? "UNKNOWN"} · {message.status}</code></details></article>)}</div>
  ) : <Empty title="Sin respuestas" body="Las respuestas vinculadas aparecerán aquí con su clasificación y próxima acción." />}</section>;
}

function MeetingsView({ campaign }: { campaign: Campaign }) {
  const hasContent = Boolean(campaign.meetingIntents?.length || campaign.meetings?.length);
  const intents = campaign.meetingIntents ?? [];
  const meetings = campaign.meetings ?? [];
  const pending = intents.filter((intent) => ["PENDING_REVIEW", "PENDING_SCHEDULING", "MEETING_INTENT_NO_SLOT"].includes(intent.status)).length;
  const noAvailability = intents.filter((intent) => intent.status === "MEETING_INTENT_NO_SLOT").length;
  return <section><div className="section-head"><div><span className="eyebrow">REUNIONES</span><h2>Coordinación de agenda</h2><p>Intenciones, opciones y reuniones confirmadas con el detalle técnico en segundo nivel.</p></div></div><div className="meeting-summary"><div><strong>{intents.length}</strong><small>intenciones</small></div><div><strong>{pending}</strong><small>por coordinar</small></div><div><strong>{intents.filter((item) => item.status === "SLOT_OPTIONS_PENDING_APPROVAL").length}</strong><small>opciones propuestas</small></div><div><strong>{meetings.filter((item) => ["CONFIRMED", "MEETING_SCHEDULED"].includes(item.status)).length}</strong><small>confirmadas</small></div><div><strong>{noAvailability}</strong><small>sin disponibilidad</small></div></div>{hasContent ? (
    <div className="run-list">
      {intents.map((intent) => <article key={intent.id}><span className="run-icon"><CalendarPlus size={17} /></span><div><strong>{intent.contact.firstName} {intent.contact.lastName}</strong><small>{intent.note || "Intención de reunión registrada"}</small></div><Status value={intent.status} /><details className="message-detail"><summary>Ver detalle</summary><code>ID: {intent.id}</code></details></article>)}
      {meetings.map((meeting) => <article key={meeting.id}><span className="run-icon"><CalendarPlus size={17} /></span><div><strong>{meeting.title}</strong><small>{formatDate(meeting.startsAt)}</small></div><Status value={meeting.status} /><details className="message-detail"><summary>Ver detalle</summary><code>ID: {meeting.id}</code></details></article>)}
    </div>
  ) : <Empty title="Sin reuniones" body="Sólo se habilitarán luego de una intención inbound y revisión humana." />}</section>;
}

export function stepStatusText(status: string, kind?: string, runStatus?: string) {
  if (kind === "INBOUND_PROCESSING" && status === "MONITORING") return "Monitoreando respuestas";
  if (status === "RUNNING" && runStatus === "ACTION_REQUIRED") return "Detenida · requiere una acción";
  if (status === "RUNNING") return "Trabajando ahora";
  if (status === "WAITING_APPROVAL") return "Requiere una acción humana";
  if (status === "BLOCKED") return "Detenida";
  if (status === "FAILED") return "Falló";
  if (status === "SEND_UNCERTAIN") return "Envío por confirmar";
  if (status === "SKIPPED") return "Omitida por configuración";
  return presentationText(status);
}

function stepWasReused(step: Step) {
  if (!step.outputJson) return false;
  try {
    const output = JSON.parse(step.outputJson) as { checkpoint?: { reused?: boolean } };
    return output.checkpoint?.reused === true;
  } catch {
    return false;
  }
}

function StepMarker({ step, displayNumber = step.ordinal + 1 }: { step: Step; displayNumber?: number }) {
  if (step.status === "COMPLETED") return <Check />;
  if (step.status === "SKIPPED") return <Check />;
  if (step.status === "RUNNING") return <Activity />;
  if (step.status === "WAITING_APPROVAL") return <Clock3 />;
  if (["BLOCKED", "FAILED", "SEND_UNCERTAIN"].includes(step.status)) return <X />;
  return <span>{displayNumber}</span>;
}

const tokenNumber = new Intl.NumberFormat("es-AR");
const aiCapableSteps = new Set(["HERMES_STRATEGY", "COMPANY_DISCOVERY", "CONTACT_ENRICHMENT", "OUTREACH_DRAFT", "CLAIM_CHECK", "INBOUND_PROCESSING", "MEETING"]);

export function currentRunActivity(
  steps: Step[],
  runStatus?: string,
  audienceType?: CampaignConfig["audience"]["type"],
  sourceMode?: CampaignConfig["acquisition"]["source"],
) {
  if (["BLOCKED", "FAILED", "DEGRADED"].includes(runStatus ?? "")) {
    const stopped = steps.find((step) => ["BLOCKED", "FAILED", "SEND_UNCERTAIN"].includes(step.status));
    return { step: stopped, message: stopped ? `La ejecución se detuvo en ${stopped.label}. Revisá el problema informado; las etapas pendientes no se están ejecutando.` : "La ejecución está detenida. Revisá el problema informado; las etapas pendientes no se están ejecutando.", tone: "stopped" };
  }
  if (runStatus === "PENDING_AUTHORIZATION") return { step: undefined, message: "Este run fue preparado con la versión anterior. Puede iniciar directamente, sin motivo ni confirmación adicional.", tone: "waiting" };
  if (runStatus === "ACTION_REQUIRED") {
    const stopped = steps.find((step) => ["BLOCKED", "FAILED", "SEND_UNCERTAIN", "RUNNING"].includes(step.status));
    return { step: stopped, message: stopped ? `La ejecución está detenida en ${stopped.label} y requiere una acción. No hay una tarea ejecutándose en segundo plano.` : "La ejecución requiere una acción antes de continuar.", tone: "stopped" };
  }
  const running = steps.find((step) => step.status === "RUNNING");
  if (running) return { step: running, message: stepMeta(running.kind, audienceType, sourceMode).active, tone: "running" };
  const waiting = steps.find((step) => step.status === "WAITING_APPROVAL");
  if (waiting) return { step: waiting, message: stepMeta(waiting.kind, audienceType, sourceMode).active, tone: "waiting" };
  const stopped = steps.find((step) => ["BLOCKED", "FAILED", "SEND_UNCERTAIN"].includes(step.status));
  if (stopped) return { step: stopped, message: `La ejecución se detuvo en ${stopped.label}. Revisá el motivo antes de iniciar un nuevo run.`, tone: "stopped" };
  if (runStatus === "WAITING_DIRECTION") return { step: undefined, message: "Hermes terminó el análisis. La estrategia está detenida para revisión antes de ejecutar la etapa siguiente.", tone: "waiting" };
  if (runStatus === "WAITING_STAGE_REVIEW") return { step: steps.find((step) => step.status === "PENDING"), message: "La etapa anterior terminó y el flujo está detenido para revisión. Nada posterior se ejecutará hasta continuar manualmente.", tone: "waiting" };
  if (runStatus === "WAITING_INBOUND") return { step: steps.find((step) => step.kind === "INBOUND_PROCESSING"), message: "El mensaje fue enviado. Commercial AI está esperando una respuesta vinculada a la conversación.", tone: "waiting" };
  if (runStatus === "COMPLETED") return { step: undefined, message: "La ejecución terminó y toda la evidencia quedó registrada.", tone: "completed" };
  const next = steps.find((step) => step.status === "PENDING");
  return { step: next, message: next ? `Próxima etapa: ${next.label}.` : "Preparando la ejecución.", tone: "pending" };
}

export function elapsedLabel(startedAt?: string, finishedAt?: string, nowMs = Date.now()) {
  if (!startedAt) return "Sin iniciar";
  const started = Date.parse(startedAt);
  const finished = finishedAt ? Date.parse(finishedAt) : nowMs;
  if (!Number.isFinite(started) || !Number.isFinite(finished)) return "No disponible";
  const totalSeconds = Math.max(0, Math.floor((finished - started) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours} h ${String(minutes).padStart(2, "0")} min ${String(seconds).padStart(2, "0")} s`
    : minutes > 0 ? `${minutes} min ${String(seconds).padStart(2, "0")} s` : `${seconds} s`;
}

function StepRail({ steps, detailed = false, runStatus, runStartedAt, runFinishedAt, aiUsage, audienceType, sourceMode, nowMs = Date.now() }: { steps: Step[]; detailed?: boolean; runStatus?: string; runStartedAt?: string; runFinishedAt?: string; aiUsage?: AiUsageSummary; audienceType?: CampaignConfig["audience"]["type"]; sourceMode?: CampaignConfig["acquisition"]["source"]; nowMs?: number }) {
  const orderedSteps = steps.map((step) => runStatus === "RUNNING" ? normalizePartialStep(step) : step).sort((left, right) => {
    const leftIndex = pipelineDisplayOrder.indexOf(left.kind as typeof pipelineDisplayOrder[number]);
    const rightIndex = pipelineDisplayOrder.indexOf(right.kind as typeof pipelineDisplayOrder[number]);
    return (leftIndex < 0 ? left.ordinal : leftIndex) - (rightIndex < 0 ? right.ordinal : rightIndex);
  });
  const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(() => new Set(steps.map((step) => step.id)));
  const orderedStepIds = orderedSteps.map((step) => step.id).join("|");
  useEffect(() => {
    setExpandedStepIds((current) => {
      const missing = orderedSteps.filter((step) => !current.has(step.id));
      if (!missing.length) return current;
      return new Set([...current, ...missing.map((step) => step.id)]);
    });
  }, [orderedStepIds]);
  const completed = orderedSteps.filter((step) => ["COMPLETED", "SKIPPED"].includes(step.status)).length;
  const progress = orderedSteps.length ? Math.round((completed / orderedSteps.length) * 100) : 0;
  const activity = currentRunActivity(orderedSteps, runStatus, audienceType, sourceMode);
  const activityNumber = activity.step ? orderedSteps.findIndex((step) => step.id === activity.step?.id) + 1 : undefined;
  return (
    <div className={`pipeline-view${detailed ? " detailed" : " compact-view"}`}>
      <div className={`current-activity ${activity.tone}`}>
        <span className="activity-pulse">{activity.tone === "running" ? <RefreshCcw className="spin" /> : <Activity />}</span>
        <div>
          <span className="eyebrow">{activity.step ? `ETAPA ${activityNumber} DE ${orderedSteps.length}` : "ESTADO ACTUAL"}</span>
          <strong>{activity.step?.label ?? (runStatus === "COMPLETED" ? "Ejecución completada" : "Coordinación del run")}</strong>
          <p>{activity.message}</p>
          {aiUsage && <div className="ai-run-usage">
            <span><Bot /> <b>Modelos efectivos</b> {aiUsage.byModel.length ? aiUsage.byModel.map((item) => item.model === "NO_INFORMADO" ? "No informado" : item.model).join(" · ") : "Aún no informados"}</span>
            <span><Activity /> <b>IA acumulada</b> {tokenNumber.format(aiUsage.totals.inputTokens)} entrada · {tokenNumber.format(aiUsage.totals.outputTokens)} salida</span>
          </div>}
        </div>
        <div className="progress-number"><strong>{progress}%</strong><small>{completed} de {orderedSteps.length} etapas</small>{runStartedAt && <small className="elapsed-time"><Clock3 />Tiempo total {elapsedLabel(runStartedAt, runFinishedAt, nowMs)}</small>}</div>
      </div>
      <div className="progress-meter" aria-label={`${progress}% completado`}><span style={{ width: `${progress}%` }} /></div>
      <div className="step-track" aria-label="Etapas del pipeline">
        {orderedSteps.map((step, index) => (
          <div key={step.id} className={`track-node ${step.status.toLowerCase()}`} title={`${step.label}: ${stepStatusText(step.status, step.kind, runStatus)}`}>
            <span><StepMarker step={step} displayNumber={index + 1} /></span>
            <small>{index + 1}</small>
          </div>
        ))}
      </div>
      {detailed && (
        <div className="step-cards">
          {orderedSteps.map((step, index) => {
            const meta = stepMeta(step.kind, audienceType, sourceMode);
            const stageUsage = aiUsage?.byStep.find((entry) => entry.stepKind === step.kind);
            return (
              <details
                key={step.id}
                className={`step-card ${step.status.toLowerCase()}${stepWasReused(step) ? " reused" : ""}`}
                open={expandedStepIds.has(step.id)}
                onToggle={(event) => {
                  const isOpen = event.currentTarget.open;
                  setExpandedStepIds((current) => {
                    if (current.has(step.id) === isOpen) return current;
                    const next = new Set(current);
                    if (isOpen) next.add(step.id);
                    else next.delete(step.id);
                    return next;
                  });
                }}
              >
                <summary className="step-card-summary">
                  <span className="step-card-marker"><StepMarker step={step} displayNumber={index + 1} /></span>
                  <div className="step-card-title">
                    <div><small>ETAPA {index + 1}</small><strong>{step.label}</strong></div>
                    <div className="step-card-state">
                      {stepAggregateProgress(step) && <b>{stepAggregateProgress(step)}</b>}
                      <span>{stepWasReused(step) ? "Reutilizada" : stepStatusText(step.status, step.kind, runStatus)}</span>
                    </div>
                  </div>
                </summary>
                <div className="step-card-copy">
                  <p>{meta.description}</p>
                  <div className="step-resources">
                    <span><Settings2 /> <b>Herramienta</b> {meta.tool}</span>
                    <span><Bot /> <b>Responsable</b> {meta.agent}</span>
                    <span><Clock3 /> <b>Tiempo insumido</b> {elapsedLabel(step.startedAt, step.finishedAt, nowMs)}</span>
                    {stageUsage ? <>
                      <span><Bot /> <b>Modelo efectivo</b> {stageUsage.models.length ? stageUsage.models.join(" · ") : "No informado"}</span>
                      <span><Settings2 /> <b>Perfil</b> {stageUsage.profiles.length ? stageUsage.profiles.join(" · ") : "No informado"}</span>
                      <span><Activity /> <b>Tokens exactos</b> {tokenNumber.format(stageUsage.inputTokens)} entrada · {tokenNumber.format(stageUsage.outputTokens)} salida</span>
                      {(stageUsage.cacheReadTokens > 0 || stageUsage.cacheWriteTokens > 0 || stageUsage.reasoningTokens > 0) && <span><Activity /> <b>Detalle</b> caché leída {tokenNumber.format(stageUsage.cacheReadTokens)} · escrita {tokenNumber.format(stageUsage.cacheWriteTokens)} · razonamiento {tokenNumber.format(stageUsage.reasoningTokens)}</span>}
                    </> : <span><Bot /> <b>Modelo y tokens</b> {aiCapableSteps.has(step.kind) ? "Aún no informados por Hermes" : "Etapa sin modelo de IA"}</span>}
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

function pipelineStepsForPresentation(run: Run): Step[] {
  const normalizedSteps = run.steps.map((step) => step.kind === "HUMAN_APPROVAL" ? { ...step, label: "Aprobación humana" } : step);
  if (run.errorCode !== "NO_APPROVABLE_DRAFTS") return normalizedSteps;
  const outreach = normalizedSteps.find((step) => step.kind === "OUTREACH_DRAFT");
  let generatedDrafts: number | undefined;
  try {
    const output = JSON.parse(outreach?.outputJson ?? "{}") as { drafts?: unknown };
    generatedDrafts = typeof output.drafts === "number" ? output.drafts : undefined;
  } catch {
    generatedDrafts = undefined;
  }
  if (generatedDrafts !== 0) return normalizedSteps;
  return normalizedSteps.map((step) => {
    if (step.kind === "OUTREACH_DRAFT") return { ...step, status: "BLOCKED", errorCode: "NO_DRAFTS_GENERATED", errorMessage: "No existían contactos elegibles para generar borradores." };
    if (["CLAIM_CHECK", "HUMAN_APPROVAL"].includes(step.kind)) return { ...step, status: "PENDING", errorCode: undefined, errorMessage: undefined, outputJson: undefined };
    return step;
  });
}
function Prospects({ campaign }: { campaign: Campaign }) {
  const [expanded, setExpanded] = useState<string>();
  const companies = campaign.companies ?? [];
  const ready = companies.filter((row) => row.accepted !== false || row.status === "READY_FOR_QUALIFICATION").length;
  return <section><div className="section-head"><div><span className="eyebrow">PROSPECTOS</span><h2>Universo de empresas</h2><p>Empresas encontradas, preservadas y calificadas según el ICP de la campaña. Si una ejecución se cancela, este universo queda disponible para el siguiente run.</p></div><div className="section-head-metrics"><span><strong>{companies.length}</strong><small>registradas</small></span><span><strong>{ready}</strong><small>listas</small></span></div></div>{companies.length ? (
    <div className="table-card">
      <table>
        <thead>
          <tr>
            <th>Empresa</th>
            <th>Industria</th>
            <th>País</th>
            <th>Tamaño</th>
            <th>Score de empresa</th>
            <th>Fundamento</th>
            <th aria-label="Detalle" />
          </tr>
        </thead>
        <tbody>
          {companies.map((row) => {
            const evidence = parsedJson<Array<{ sourceId?: string; source?: string; fact?: string; sourceUrl?: string; observedAt?: string; criterion?: string; inference?: string; uncertainty?: string | number }>>(row.evidenceJson, []);
            const breakdown = parsedJson<Record<string, number | string>>(row.scoreBreakdownJson, {});
            return <Fragment key={row.id}>
            <tr>
              <td>
                <strong>{row.company.name}</strong>
                <small>{row.company.domain}</small>
                {(row.company.source === "REFERENCE" || row.company.domain?.endsWith(".example")) && (
                  <small className="simulated-label">DEMO / SIMULATED</small>
                )}
              </td>
              <td>{row.company.industry}</td>
              <td>{row.company.country}</td>
              <td>{row.company.employees}</td>
              <td>
                <span className="score">{row.score}</span>
              </td>
              <td>
                <Status value={row.accepted !== false ? "ACCEPTED" : row.status === "READY_FOR_QUALIFICATION" ? "READY" : row.status ?? "DISCOVERED"} />
                <small title={row.rejectionReason ?? row.rationale}>{row.rejectionReason ?? row.rationale}</small>
              </td>
              <td><button className="icon-button table-expand-button" aria-label={`Ver detalle de ${row.company.name}`} aria-expanded={expanded === row.id} onClick={() => setExpanded((current) => current === row.id ? undefined : row.id)}><ChevronRight size={16} /></button></td>
            </tr>
            {expanded === row.id && <tr className="expandable-detail"><td colSpan={7}><div className="expandable-detail-content"><div><small>Dominio</small><strong>{row.company.domain ?? "No disponible"}</strong></div><div><small>Industria del proveedor</small><strong>{row.company.providerIndustry ?? row.company.industry}</strong></div><div><small>Industria normalizada</small><strong>{row.company.normalizedIndustry ?? "Sin normalizar"} · confianza {Math.round((row.company.industryConfidence ?? 0) * 100)}%</strong></div><div><small>País / dominio</small><strong>{row.company.providerCountry ?? row.company.country} · {row.company.domainCountryStatus ?? "No verificado"}</strong></div><div><small>Origen</small><strong>{row.company.source ?? "No disponible"}</strong></div><div><small>Empleados</small><strong>{row.company.employees}</strong></div><div className="wide"><small>Desglose del score de empresa</small><strong>{Object.entries(breakdown).map(([key, value]) => `${key}: ${value}`).join(" · ") || "No disponible en este run legado"}</strong></div><div className="wide"><small>Fundamento completo</small><strong>{row.rejectionReason ?? row.rationale}</strong></div><div className="wide qualification-evidence"><small>Evidencia específica y fuentes</small>{evidence.length ? evidence.map((item, index) => <article key={`${item.sourceId ?? item.sourceUrl ?? "evidence"}:${index}`}><strong>{item.fact ?? "Hecho sin texto"}</strong><span>Fuente {item.source ?? "no registrada"} · ID {item.sourceId ?? "no registrado"}</span>{item.sourceUrl ? <a href={item.sourceUrl} target="_blank" rel="noreferrer">{item.sourceUrl}</a> : <span>URL no registrada</span>}<span>{item.observedAt ? `Observado ${formatDate(item.observedAt)}` : "Fecha no registrada"} · criterio: {item.criterion ?? "no indicado"}</span>{item.inference && <em>Inferencia: {item.inference}</em>}{item.uncertainty !== undefined && <em>Incertidumbre: {typeof item.uncertainty === "number" ? `${Math.round(item.uncertainty * 100)}%` : item.uncertainty}</em>}</article>) : <strong>No existe evidencia trazable persistida para esta evaluación.</strong>}</div></div></td></tr>}
          </Fragment>})}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty
      title="Sin prospectos todavía"
      body="Iniciá una ejecución para seleccionar y puntuar empresas del universo de prueba."
    />
  )}</section>;
}
function OutreachContextSummary({ metadata }: { metadata?: string | null }) {
  const context = parsedObject<CommercialReplyMetadata>(metadata);
  if (!context?.outreachContext) return null;
  const level = { RESEARCHED: "Sitio oficial consultado", CONTEXTUAL: "Contexto del proveedor", MINIMAL: "Información mínima" }[context.outreachContext.contextLevel] ?? "Contexto disponible";
  const purpose = { DIRECT_DECISION_MAKER: "Proponer conversación", RELEVANT_INFLUENCER: "Consultar por el área", POSSIBLE_ROUTER: "Pedir orientación o derivación" }[context.outreachContext.contactRole.purpose] ?? "Consultar contacto";
  const urls = [...new Set(context.outreachContext.prospectEvidence.map((item) => item.url).filter((url): url is string => Boolean(url && /^https:\/\//i.test(url))))];
  return <div className="message-meta"><span>{level} · {purpose}</span>{urls.slice(0, 2).map((url) => <a key={url} href={url} target="_blank" rel="noreferrer">Fuente consultada</a>)}{context.qualityWarnings?.map((warning) => <small key={warning}>{warning}</small>)}</div>;
}

function messageTimestamp(message: Message) {
  return new Date(message.providerTimestamp ?? message.createdAt ?? 0).getTime();
}

function followUpCountdown(value: string) {
  const milliseconds = new Date(value).getTime() - Date.now();
  const absoluteMinutes = Math.max(1, Math.round(Math.abs(milliseconds) / 60_000));
  const amount = absoluteMinutes < 60
    ? `${absoluteMinutes} min`
    : absoluteMinutes < 1_440
      ? `${Math.round(absoluteMinutes / 60)} h`
      : `${Math.round(absoluteMinutes / 1_440)} ${Math.round(absoluteMinutes / 1_440) === 1 ? "día" : "días"}`;
  return milliseconds >= 0 ? `faltan ${amount}` : `vencido hace ${amount}`;
}

export function conversationState(message: Message, messages: Message[]) {
  if (message.direction !== "INBOUND" && !["SENT", "DRY_RUN"].includes(message.status)) {
    const label = message.status === "BLOCKED_CLAIM_CHECK" ? "No enviado: requiere revisión de claims"
      : message.status === "PENDING_APPROVAL" ? "No enviado: pendiente de aprobación"
      : ["QUEUED", "APPROVED"].includes(message.status) ? "No enviado: en cola de envío"
      : message.status.startsWith("SUPERSEDED") ? "Borrador reemplazado; no fue enviado"
      : "No enviado";
    return { tone: "neutral", label, received: null };
  }
  const sentAt = messageTimestamp(message);
  const sameContact = messages.filter((item) => item.contact.id === message.contact.id);
  const nextOutbound = sameContact
    .filter((item) => item.direction !== "INBOUND" && messageTimestamp(item) > sentAt)
    .sort((left, right) => messageTimestamp(left) - messageTimestamp(right))[0];
  const received = sameContact
    .filter((item) => item.direction === "INBOUND" && messageTimestamp(item) > sentAt && (!nextOutbound || messageTimestamp(item) < messageTimestamp(nextOutbound)))
    .sort((left, right) => messageTimestamp(left) - messageTimestamp(right))[0];
  if (received) return { tone: "answered", label: `Respondido · ${formatDate(received.providerTimestamp ?? received.createdAt ?? "")}`, received };
  if (nextOutbound) return { tone: "continued", label: "Continuado en un mensaje posterior", received: null };
  if (message.actionKind === "MANUAL_NO_FOLLOW_UP") return { tone: "neutral", label: "Sin seguimiento automático", received: null };
  const nextAction = message.journey?.nextAction;
  const dueAt = message.journey?.nextActionAt;
  const actionLabel = nextAction === "FOLLOW_UP_1"
    ? "Follow-up 1"
    : nextAction === "FOLLOW_UP_2"
      ? "Follow-up 2"
      : nextAction === "MEETING_OPTIONS_FOLLOW_UP"
        ? "Recordatorio de reunión"
        : nextAction === "NO_RESPONSE"
          ? "Cierre sin respuesta"
          : null;
  if (actionLabel && dueAt) return { tone: new Date(dueAt).getTime() <= Date.now() ? "overdue" : "waiting", label: `${actionLabel}: ${followUpCountdown(dueAt)}`, received: null };
  return { tone: "waiting", label: "Esperando respuesta", received: null };
}

function Outreach({ campaign }: { campaign: Campaign }) {
  const messages = campaign.messages ?? [];
  const outbound = messages.filter(
    (message) => message.direction !== "INBOUND",
  );
  const opened = outbound.filter((message) => (message.openCount ?? 0) > 0);
  return <section><div className="section-head"><div><span className="eyebrow">OUTREACH</span><h2>Mensajes salientes</h2><p>Estado de cada conversación. El historial completo y las respuestas manuales se gestionan desde Prospectos.</p></div><div className="section-head-metrics"><span><strong>{outbound.length}</strong><small>mensajes</small></span><span><strong>{opened.length}</strong><small>con apertura detectada</small></span></div></div>{outbound.length ? (
    <div className="message-list">
      {outbound.map((message) => { const state = conversationState(message, messages); const prospectUrl = `/prospects?${new URLSearchParams({ tab: "contacts", campaignId: campaign.id, q: message.intendedRecipient ?? `${message.contact.firstName} ${message.contact.lastName}`, contactId: message.contact.id })}`; return (
        <article key={message.id} className="message-card">
          <div className="message-person">
            <span>{message.contact.firstName.slice(0, 1)}</span>
            <div>
              <strong>
                {message.contact.firstName} {message.contact.lastName}
              </strong>
              <small>{message.intendedRecipient}</small>
            </div>
            <Status value={message.status} />
          </div>
          <h3>{message.subject}</h3>
          <p className="message-preview">{message.body}</p>
          <div className="message-meta"><span>{message.createdAt ? formatDate(message.createdAt) : "Fecha no disponible"}</span><span>Acción: {message.actionKind ? presentationText(message.actionKind, "action") : "Envío"}</span></div>
          <div className={`message-conversation-state ${state.tone}`}><Clock3 size={16} /><strong>{state.label}</strong></div>
          {state.received && <div className="message-inbound-summary"><Mail size={16} /><div><strong>Respuesta recibida: {state.received.subject || "Sin asunto"}</strong><span>{state.received.body}</span></div></div>}
          <div className="message-actions"><Link className="secondary" to={prospectUrl}>Ver conversación en Prospectos <ArrowRight size={15} /></Link></div>
          {(message.openCount ?? 0) > 0 && <div className="email-open-signal"><Eye /><div><strong>Apertura detectada: indicio débil</strong><span>Primera {formatDate(message.firstOpenedAt ?? message.lastOpenedAt ?? "")} · última {formatDate(message.lastOpenedAt ?? message.firstOpenedAt ?? "")} · {message.openCount} {message.openCount === 1 ? "detección" : "detecciones"}</span><small>{message.actualRecipient && message.intendedRecipient && message.actualRecipient.toLowerCase() !== message.intendedRecipient.toLowerCase() ? `La detección corresponde al destinatario efectivo de prueba (${message.actualRecipient}), no al prospecto.` : "La carga de la imagen invisible indica apertura probable; no confirma que la persona haya leído el mensaje."}</small></div></div>}
          <details className="message-detail"><summary>Ver mensaje completo y trazabilidad</summary><div className="message-detail-body">{message.body}</div><OutreachContextSummary metadata={message.generationMetadataJson} />{message.aiProvider && <small>Proveedor: {message.aiProvider} · Agente: {message.aiAgent}</small>}<br /><code>ID: {message.id} · Código: {message.status}{message.actionKind ? ` / ${message.actionKind}` : ""}</code></details>
          {message.actualRecipient && (
            <div className="delivery">
              <Send />
              Salida segura: <strong>{message.actualRecipient}</strong>
            </div>
          )}
        </article>
      ); })}
    </div>
  ) : (
    <Empty
      title="No hay mensajes preparados"
      body="El agente SDR redactará mensajes basados en claims permitidos y los enviará a aprobación."
    />
  )}</section>;
}
function Runs({ campaign }: { campaign: Campaign }) {
  return campaign.runs?.length ? (
    <div className="run-list">
      {campaign.runs.map((run) => (
        <Link to={`/operation/${run.id}`} key={run.id}>
          <span className="run-icon">
            <Activity />
          </span>
          <div>
            <strong>Ejecución {run.id.slice(-8)}</strong>
            <small>Creada {formatDate(run.createdAt)}</small>
          </div>
          <Status value={run.status} />
          <ChevronRight />
        </Link>
      ))}
    </div>
  ) : (
    <Empty
      title="Sin ejecuciones"
      body="Iniciá la campaña para crear el primer run inmutable."
    />
  );
}
type ActivityItem = NonNullable<Run["events"]>[number] & { runId?: string | null; entityType: string; entityId: string; actorType: string; actor?: { displayName: string; username: string } | null };

function eventSeverity(event: ActivityItem) {
  if (/FAILED|BLOCKED|UNCERTAIN|ERROR/.test(event.type)) return "error";
  if (/WAITING|PENDING|LIMIT|PAUSED|REVIEW/.test(event.type)) return "warning";
  if (/COMPLETED|APPROVED|SENT|CREATED/.test(event.type)) return "success";
  return "info";
}

function eventCategory(event: ActivityItem) {
  if (/MESSAGE|OUTREACH|INBOUND|FOLLOW_UP|CLAIM/.test(event.type)) return "Mensajes";
  if (/MEETING|SLOT|CALENDAR/.test(event.type)) return "Reuniones";
  if (/CAMPAIGN|DIRECTION|HERMES/.test(event.type)) return "Campaña";
  if (/RUN|STEP|CHECKPOINT/.test(event.type)) return "Ejecución";
  return "Sistema";
}

function ActivityFeed({ campaignId, runs }: { campaignId: string; runs: Run[] }) {
  const [runId, setRunId] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const query = new URLSearchParams({ page: String(page), limit: "30", ...(runId ? { runId } : {}), ...(type ? { type } : {}), ...(status ? { status } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) });
  const { data, isLoading, isFetching, isError, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["campaign-activity", campaignId, runId, type, status, from, to, page],
    queryFn: () => api<{ items: ActivityItem[]; page: number; total: number; hasMore: boolean }>(`/campaigns/${campaignId}/activity?${query}`),
    refetchInterval: 5000,
  });
  const updateFilter = (setter: (value: string) => void, value: string) => { setter(value); setPage(1); };
  const actors = [...new Set((data?.items ?? []).map((event) => event.actor?.displayName ?? event.actor?.username ?? event.actorType))].sort();
  const visibleItems = (data?.items ?? []).filter((event) => (!category || eventCategory(event) === category) && (!severity || eventSeverity(event) === severity) && (!actor || (event.actor?.displayName ?? event.actor?.username ?? event.actorType) === actor));
  const grouped = visibleItems.reduce<Record<string, ActivityItem[]>>((result, event) => {
    const day = new Intl.DateTimeFormat("es-AR", { dateStyle: "full" }).format(new Date(event.createdAt));
    (result[day] ??= []).push(event);
    return result;
  }, {});
  return <section className="activity-section">
    <div className="section-head"><div><span className="eyebrow">ACTIVIDAD</span><h2>Historial auditable</h2><p>Eventos agrupados por día, con descripción humana y detalle técnico bajo demanda.</p></div><span className="operation-refresh">{isFetching && !isLoading ? <><RefreshCcw className="spin" size={12} />Actualizando…</> : <>Actualizado {dataUpdatedAt ? formatDate(new Date(dataUpdatedAt).toISOString()) : "—"}</>}</span></div>
    <div className="activity-filters"><label>Ejecución<select value={runId} onChange={(event) => updateFilter(setRunId, event.target.value)}><option value="">Todas</option>{runs.map((run) => <option key={run.id} value={run.id}>…{run.id.slice(-8)}</option>)}</select></label><label>Categoría<select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">Todas</option>{["Campaña", "Ejecución", "Mensajes", "Reuniones", "Sistema"].map((value) => <option key={value}>{value}</option>)}</select></label><label>Severidad<select value={severity} onChange={(event) => setSeverity(event.target.value)}><option value="">Todas</option><option value="info">Información</option><option value="success">Correcto</option><option value="warning">Atención</option><option value="error">Error</option></select></label><label>Responsable<select value={actor} onChange={(event) => setActor(event.target.value)}><option value="">Todos</option>{actors.map((value) => <option key={value}>{value}</option>)}</select></label><label>Tipo técnico<input value={type} onChange={(event) => updateFilter(setType, event.target.value)} placeholder="Ej. MESSAGE_SENT" /></label><label>Estado técnico<input value={status} onChange={(event) => updateFilter(setStatus, event.target.value)} placeholder="Ej. SENT" /></label><label>Desde<input type="date" value={from} onChange={(event) => updateFilter(setFrom, event.target.value)} /></label><label>Hasta<input type="date" value={to} onChange={(event) => updateFilter(setTo, event.target.value)} /></label></div>
    {isLoading ? <div className="loading" role="status">Cargando actividad…</div> : isError ? <div className="query-state error"><strong>No se pudo cargar la actividad</strong><button className="secondary" onClick={() => void refetch()}>Reintentar</button></div> : visibleItems.length ? <div className="activity-timeline">{Object.entries(grouped).map(([day, events]) => <section className="activity-day" key={day}><h3>{day}</h3>{events.map((event) => { const [title, detail] = eventDescription(event); const safe = safeEventDetail(event); return <details key={event.id} className={`activity-event severity-${eventSeverity(event)}`}><summary><span className="activity-event-title"><strong>{title}</strong><p>{detail}</p></span><span className="activity-event-meta">{formatDate(event.createdAt)}<br />{event.actor?.displayName ?? event.actor?.username ?? event.actorType}</span></summary><div className="activity-event-detail"><div className="message-meta"><span>Categoría: {eventCategory(event)}</span><span>Entidad: {event.entityType} {event.entityId}</span>{event.runId && <Link to={`/operation/${event.runId}`}>Abrir ejecución</Link>}</div><details className="technical-disclosure"><summary>Detalle técnico controlado</summary><code>{event.type}</code>{Object.keys(safe).length > 0 && <pre>{JSON.stringify(safe, null, 2)}</pre>}</details></div></details>; })}</section>)}</div> : <Empty title="Sin actividad" body="No hay eventos para estos filtros." />}
    <div className="index-pagination"><button className="secondary" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>Anterior</button><span>Página {page} · {data?.total ?? 0} eventos</span><button className="secondary" disabled={!data?.hasMore} onClick={() => setPage((value) => value + 1)}>Siguiente</button></div>
  </section>;
}

function eventDescription(event: NonNullable<Run["events"]>[number]) {
  const payload = event.payload && typeof event.payload === "object" && !Array.isArray(event.payload)
    ? event.payload as Record<string, unknown>
    : {};
  const label = typeof payload.label === "string" ? payload.label : undefined;
  const descriptions: Record<string, [string, string]> = {
    RUN_CREATED: ["Ejecución creada", "Se congeló la configuración que usará este run."],
    RUN_STARTED: ["Ejecución iniciada", "Comenzó la coordinación automática del pipeline."],
    RUN_WAITING_DIRECTION: ["Estrategia lista para revisar", "El run legado quedó detenido antes de la etapa siguiente."],
    DIRECTION_DECISION_RECORDED: ["Decisión de Dirección registrada", "La decisión quedó vinculada de forma auditable a este run."],
    STRATEGY_REFORMULATION_REQUESTED: ["Dirección solicitó reformular", "El motivo quedó registrado y el mismo run está preparando una nueva versión de estrategia."],
    STRATEGY_REFORMULATION_COMPLETED: ["Estrategia reformulada", "La nueva versión está lista para comparar y espera otra decisión de Dirección."],
    OUTREACH_DRAFT_GENERATED: ["Mensaje preparado", "El SDR IA generó un borrador para validación."],
    OUTREACH_DRAFT_CONTACT_FAILED: ["Falló el borrador de un contacto", "El contacto, el intento, el proveedor y la causa quedaron identificados; los demás pueden continuar."],
    JOURNEY_OUTREACH_DRAFT_FAILED: ["Contacto detenido en redacción", "Sólo este recorrido quedó en revisión y puede reintentarse sin repetir prospección ni CRM."],
    OUTREACH_RECOVERY_QUEUED: ["Recuperación de redacción en cola", "Se reutiliza el snapshot congelado y se omiten las etapas ya completadas."],
    OUTREACH_CONTACT_EXCLUDED_REPLACEMENT_QUEUED: ["Reemplazo de contacto solicitado", "El contacto fue excluido de forma auditable y la búsqueda queda limitada al universo congelado."],
    OUTREACH_REPLACEMENT_SELECTED: ["Contacto de reemplazo seleccionado", "Se eligió un contacto verificable sin repetir el resto del pipeline."],
    CLAIM_AUDIT_COMPLETED: ["Auditoría de claims completada", "Se analizaron el asunto y el cuerpo completos del mensaje."],
    MESSAGE_BLOCKED_CLAIM_CHECK: ["Mensaje detenido por claims", "El borrador no se envió. Los motivos del auditor están disponibles en el diagnóstico de redacción y claims."],
    APPROVALS_REQUESTED: ["Aprobación humana solicitada", "Los mensajes habilitados están listos para revisión."],
    APPROVAL_MESSAGE_EDITED: ["Correo editado por una persona", "La nueva versión permanece detenida hasta que una persona la apruebe."],
    APPROVAL_APPROVED: ["Mensaje aprobado", "Un usuario autorizó el contenido exacto del envío."],
    MESSAGE_QUEUED: ["Mensaje aprobado en cola", "La autorización quedó registrada; el scheduler respetará prioridad, ventana y cupo diario."],
    MESSAGE_SENT: ["Mensaje enviado", "El envío respetó el modo de salida y SAFE SEND."],
    SEND_FAILED: ["Envío fallido", "El mensaje no se confirmó como enviado y requiere revisión segura."],
    SEND_DEFERRED_OUTSIDE_WINDOW: ["Envío diferido", "El mensaje permanece en cola hasta la próxima ventana habilitada."],
    RUN_WAITING_INBOUND: ["Esperando respuesta", "El sistema aguarda actividad entrante vinculada a la conversación."],
    CONTACT_JOURNEY_CREATED: ["Seguimiento individual creado", "El contacto tiene su propio estado, acciones y trazabilidad dentro del run."],
    CONTACT_JOURNEY_STATE_CHANGED: ["Cambió el estado de un contacto", "Sólo avanzó el seguimiento individual asociado; los demás contactos permanecen independientes."],
    GMAIL_INBOUND_CLASSIFIED: ["Respuesta clasificada", "El inbound quedó vinculado al contacto y fue evaluado sin cerrar otros seguimientos."],
    FOLLOW_UP_GENERATED: ["Seguimiento preparado", "Se creó un borrador gobernado; todavía requiere auditoría y aprobación humana."],
    FOLLOW_UP_CANCELLED: ["Seguimientos futuros cancelados", "Una respuesta o cierre individual anuló las acciones pendientes de ese contacto."],
    DAILY_SEND_LIMIT_REACHED: ["Cupo diario alcanzado", "Los mensajes restantes permanecen aprobados en cola para la próxima ventana."],
    SLOT_OPTIONS_GENERATED: ["Opciones de horario preparadas", "Se creó una propuesta pendiente de aprobación con horarios y participantes definidos manualmente."],
    MEETING_INTENT_DETECTED: ["Intención de reunión detectada", "La respuesta indica interés en reunirse y quedó registrada para coordinación."],
    MEETING_COORDINATION_COMPLETED: ["Coordinación de agenda preparada", "Las opciones disponibles quedaron listas para la siguiente acción gobernada."],
    MEETING_COORDINATION_RECOVERY_AVAILABLE: ["Recuperación de agenda disponible", "La coordinación puede reintentarse sin repetir etapas anteriores."],
    MEETING_COORDINATION_RECOVERY_QUEUED: ["Recuperación de agenda en cola", "La consulta de disponibilidad quedó preparada para un nuevo intento controlado."],
    CONVERSATION_DRAFT_CREATED: ["Conversación preparada", "Se creó un borrador vinculado al contacto y a su recorrido individual."],
    MEETING_PENDING_APPROVAL: ["Reunión pendiente de aprobación", "Calendar aún no fue llamado; se debe revisar horario, organizador y asistentes."],
    MEETING_AVAILABILITY_REVALIDATED: ["Disponibilidad revalidada", "El horario seguía libre inmediatamente antes de crear el evento aprobado."],
    MEETING_CREATED: ["Reunión creada", "La aprobación humana fue consumida y el evento quedó registrado una sola vez."],
    REFERRAL_DETECTED: ["Derivación detectada", "El contacto original fue transferido y la referencia quedó preservada."],
    REFERRAL_CONTACT_CREATED: ["Contacto referido pendiente", "Se creó un seguimiento independiente, sin enviarle nada automáticamente."],
    INBOUND_PARTICIPANT_DETECTED: ["Participante detectado en copia", "La dirección quedó vinculada como contexto; no recibirá salidas ni invitaciones sin aprobación."],
    CAMPAIGN_PAUSED: ["Campaña pausada", "Se detuvieron nuevas salidas; el inbound continúa registrándose."],
    CAMPAIGN_RESUMED: ["Campaña reanudada", "La cola puede continuar dentro de la ventana y los límites configurados."],
    CAMPAIGN_CANCELLED: ["Campaña cancelada", "Se cancelaron acciones futuras y se conservó toda la evidencia."],
    RUN_AGGREGATE_UPDATED: ["Resumen del run actualizado", "El estado global se recalculó a partir de todos los contactos y acciones pendientes."],
    RUN_RESUMED: ["Nuevo intento desde checkpoint", "Se creó un run hijo con contexto congelado y sin repetir las etapas costosas ya completadas."],
    COMPANY_DISCOVERY_SEARCH_STARTED: ["Búsqueda de empresas iniciada", "Hunter recibió los filtros congelados de país, industria y tamaño para esta ruta."],
    COMPANY_DISCOVERY_CANDIDATE_FOUND: ["Empresa encontrada", "La candidata apareció en la búsqueda y todavía debe superar los filtros y la evaluación."],
    COMPANY_DISCOVERY_CANDIDATE_ACCEPTED: ["Empresa seleccionada", "La candidata cumplió los controles del descubrimiento y quedó disponible para las etapas siguientes."],
    COMPANY_DISCOVERY_CANDIDATE_EXCLUDED: ["Empresa descartada", "La causa de descarte quedó registrada sin bloquear su uso futuro en campañas con otro perfil."],
    INDIVIDUAL_PROSPECTING_COMPLETED: ["Búsqueda de personas completada", "El agente devolvió candidatos trazables y Hunter permaneció deshabilitado."],
    INDIVIDUAL_PROSPECTING_EMPTY: ["Búsqueda de personas sin candidatos", "El agente finalizó correctamente y sus advertencias quedaron preservadas para revisión o reintento."],
    DIRECT_CONTACT_SOURCE_EMPTY: ["Fuente B2C sin contactos elegibles", "No se avanzó a elegibilidad, CRM ni redacción; la evidencia de la búsqueda quedó preservada."],
    STEP_REUSED: ["Etapa reutilizada", "El resultado aprobado del run anterior se conservó sin volver a llamar al proveedor."],
    CHECKPOINT_EXECUTION_STARTED: ["Reanudación segura iniciada", "El flujo retomó desde Validación de claims sin consultar Hunter ni HubSpot."],
    CONTACT_ENRICHMENT_FAILED: ["Falló el enriquecimiento", "La validación de contactos se detuvo antes de crear borradores; no se repetirá una búsqueda amplia."],
    CONTACT_EVIDENCE_CONTRACT_INVALID: ["Evidencia de contacto inválida", "Una fuente devolvió evidencia incompleta. El run quedó bloqueado de forma segura y puede recuperarse desde enriquecimiento."],
    HUNTER_OPERATION_NOT_AUTHORIZED: ["Operación Hunter bloqueada", "La política sólo permite Email Finder después de validar gratuitamente un candidato individual."],
    HUNTER_EMAIL_FINDER_RESPONSE_INVALID: ["Respuesta Hunter inválida", "Email Finder respondió con una estructura incompleta; el run quedó detenido sin usar ese resultado."],
    NO_DRAFTS_GENERATED: ["No se generaron borradores", "La redacción no recibió contactos elegibles; claims y aprobación humana no comenzaron."],
    NO_APPROVABLE_DRAFTS: ["Claims detuvo los borradores", "Los borradores existentes no superaron la validación y no llegaron a aprobación humana."],
    OUTREACH_AUTHORIZATION_BLOCKED: ["El control técnico detuvo el envío", "Los claims fueron aprobados. Revisá la validación del contacto y la enviabilidad del mensaje."],
  };
  if (event.type === "STEP_RUNNING") return [`Comenzó ${label ?? "una etapa"}`, "La herramienta y el agente responsables están trabajando."];
  if (event.type === "STEP_COMPLETED") return [`Completó ${label ?? "una etapa"}`, "El resultado quedó registrado y el flujo puede continuar."];
  if (event.type === "HUNTER_USAGE_METERED")
    return ["Uso estimado de Hunter registrado", "La operación terminó correctamente. El detalle seguro muestra la operación y el rango estimado de créditos consumidos."];
  if (event.type === "STEP_PENDING") return [`Preparó ${label ?? "la siguiente etapa"}`, "La etapa está lista y espera su momento de ejecución."];
  if (event.type === "STEP_WAITING_APPROVAL") return [`${label ?? "La etapa"} requiere aprobación`, "El flujo no continuará sin una acción humana."];
  if (event.type === "STEP_BLOCKED") return [`Se detuvo ${label ?? "una etapa"}`, "El flujo preservó lo realizado y no avanzó a las etapas siguientes."];
  return descriptions[event.type] ?? ["Actividad registrada", "El evento quedó preservado en la auditoría; su código técnico está disponible al expandir."];
}

function safeEventDetail(event: NonNullable<Run["events"]>[number]) {
  const payload = event.payload && typeof event.payload === "object" && !Array.isArray(event.payload)
    ? event.payload as Record<string, unknown>
    : {};
  const allowed = new Set([
    "campaignId", "contactId", "companyId", "journeyId", "messageId", "meetingId", "actionId",
    "taskId", "runId", "hermesTaskId", "hermesRunId", "kind", "status", "previousStatus", "category",
    "reason", "confidence", "sequence", "count", "total", "active", "terminal", "pendingMessages",
    "pendingActions", "quotaDay", "limit", "optionCount", "timezone", "expiresAt", "startsAt", "endsAt",
    "outreachMode", "requiresApproval", "autoSent", "available", "calendarId", "provider", "agent", "profile",
    "providerRunId", "attempt", "stage", "code", "category", "message", "technicalCause", "validator",
    "recommendedAction", "action", "requestKey", "reusedSnapshot", "priorStagesRepeated", "skippedStages",
    "failedContacts", "partialFailure", "reasons",
    "sourceMode", "importedCandidates", "searchedCandidates", "selectedContacts", "searchSummary", "searchWarnings",
    "recoveryKind", "hunterInvoked", "companyDiscoveryProvider",
  ]);
  return Object.fromEntries(Object.entries(payload).filter(([key, value]) => allowed.has(key) && value !== undefined));
}

function runProblemMessage(run: Run) {
  const messages: Record<string, string> = {
    CONTACT_ENRICHMENT_FAILED: "La validación de contactos se detuvo sin crear borradores. La recuperación debe retomar desde esta etapa usando fuentes gratuitas y, sólo para un candidato validado, Email Finder.",
    CONTACT_EVIDENCE_CONTRACT_INVALID: "Una fuente de contactos devolvió evidencia incompleta. El resultado fue rechazado antes de puntuarlo y el run permanece recuperable desde enriquecimiento.",
    HUNTER_OPERATION_NOT_AUTHORIZED: "Hunter bloqueó una operación no autorizada antes de llamar al proveedor. En este flujo sólo se permite Email Finder sobre una persona previamente validada.",
    HUNTER_EMAIL_FINDER_RESPONSE_INVALID: "Hunter Email Finder respondió con un contrato incompleto. El resultado no fue utilizado y el run permanece detenido de forma segura.",
    COMPANY_DISCOVERY_FAILED: "No se pudo completar la búsqueda de empresas. El run se detuvo antes de crear contactos o mensajes.",
    DIRECT_CONTACT_SOURCE_EMPTY: run.config?.audience.type === "B2C"
      ? "La fuente B2C terminó sin contactos nuevos y elegibles. Si se utilizó el agente, su resultado y sus advertencias quedaron preservados; podés reintentar sólo la búsqueda sin usar Hunter."
      : "La fuente configurada no produjo contactos nuevos y elegibles.",
    INDIVIDUAL_PROSPECTING_RESULT_NOT_PUBLISHED: "Hermes completó la búsqueda, pero no publicó la lista estructurada de prospectos. El resumen quedó auditado, no se crearon contactos y podés reintentar únicamente esta etapa.",
    INDIVIDUAL_PROSPECTING_RESULT_INVALID: "Hermes devolvió una lista de prospectos incompleta o inválida. El resultado fue rechazado antes de crear contactos y podés reintentar únicamente esta etapa.",
    OUTREACH_DRAFT_CONTACTS_FAILED: "No se pudo preparar ningún borrador. Cada contacto fallido muestra su causa, intento y recuperación disponible sin repetir discovery ni CRM.",
    HERMES_RUN_BLOCKED: "Hermes detuvo la tarea por una inconsistencia concreta. Revisá el contacto y el diagnóstico del intento antes de reintentar esa etapa.",
    NO_DRAFTS_GENERATED: "La redacción no recibió contactos elegibles. No se ejecutaron claims ni se solicitó aprobación humana.",
    NO_ELIGIBLE_CONTACTS: "Se agotaron los contactos disponibles y no quedan borradores pendientes. Revisá los motivos individuales antes de reintentar.",
    NO_APPROVABLE_DRAFTS: "Los borradores existentes fueron detenidos en Validación de claims, antes de la aprobación humana. No se envió ningún mensaje.",
    OUTREACH_AUTHORIZATION_BLOCKED: "La auditoría de claims fue aprobada, pero un control técnico de contacto o enviabilidad detuvo el proceso. No se envió ningún mensaje.",
    HERMES_UNAVAILABLE: "Hermes no estuvo disponible. El flujo se detuvo antes de consultar Hunter.",
  };
  const presentedFailure = pipelineStepsForPresentation(run).find((step) => step.status === "BLOCKED" && step.errorCode)?.errorCode;
  const effectiveCode = presentedFailure ?? run.errorCode;
  return (effectiveCode && messages[effectiveCode]) || run.errorMessage || run.degradedReason || "La ejecución se detuvo de forma segura.";
}

export function journeyStatusText(status: string) {
  const labels: Record<string, string> = {
    QUEUED: "En cola",
    DRAFT_PENDING: "Preparando mensaje",
    PENDING_APPROVAL: "Espera aprobación",
    APPROVED_QUEUED: "Aprobado en cola",
    WAITING_REPLY: "Esperando respuesta",
    FOLLOW_UP_DUE: "Seguimiento vencido",
    FOLLOW_UP_PENDING_APPROVAL: "Seguimiento por aprobar",
    PENDING_SCHEDULING: "Preparando horarios",
    SLOT_OPTIONS_PENDING_APPROVAL: "Opciones por aprobar",
    WAITING_SLOT_CONFIRMATION: "Esperando confirmación de horario",
    MEETING_PENDING_APPROVAL: "Reunión por aprobar",
    MEETING_SCHEDULED: "Reunión confirmada",
    HUMAN_HANDOFF: "Transferido a comercial",
    NOT_INTERESTED: "Sin interés",
    DO_NOT_CONTACT: "No contactar",
    NO_RESPONSE: "Sin respuesta",
    MANUAL_REVIEW: "Revisión manual",
    REFERRED: "Derivó a otro contacto",
    INBOUND_RETRY_PENDING: "Reintento automático programado",
    FAILED_REQUIRES_REVIEW: "Fallo para revisar",
    BLOCKED_NON_SENDABLE: "Contacto no elegible",
    SENT: "Mensaje enviado",
    CANCELLED: "Cancelado",
  };
  return labels[status] ?? status.replaceAll("_", " ");
}

export function replacementCloseReason(reason?: string | null) {
  return Boolean(reason?.startsWith("AUTO_CONTACT_REJECTED:") || reason?.startsWith("EXCLUDED_FOR_REPLACEMENT:"));
}

export function journeyCloseReasonText(reason?: string | null) {
  if (!reason) return null;
  if (reason.startsWith("AUTO_CONTACT_REJECTED:")) return reason.slice("AUTO_CONTACT_REJECTED:".length).trim();
  if (reason.startsWith("EXCLUDED_FOR_REPLACEMENT:")) return reason.slice("EXCLUDED_FOR_REPLACEMENT:".length).trim();
  return presentationText(reason, "cause");
}

export function journeyOperationalState(journey: Pick<Journey, "status" | "messages" | "closeReason">) {
  const latestOutbound = journey.messages
    .filter((message) => message.direction === "OUTBOUND")
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))[0];
  if (journey.status === "NOT_INTERESTED" || journey.status === "DO_NOT_CONTACT")
    return { value: journey.status, label: journeyStatusText(journey.status) };
  if (latestOutbound?.status === "BLOCKED_NON_SENDABLE")
    return { value: "BLOCKED_NON_SENDABLE", label: "Contacto no elegible" };
  if (latestOutbound?.status === "BLOCKED_CLAIM_CHECK")
    return { value: "BLOCKED_CLAIM_CHECK", label: "El mensaje necesita reformulación" };
  if (journey.status === "CANCELLED" && replacementCloseReason(journey.closeReason))
    return { value: "BLOCKED_NON_SENDABLE", label: "Descartado para reemplazo" };
  if (journey.status === "FAILED_REQUIRES_REVIEW")
    return { value: "FAILED_REQUIRES_REVIEW", label: "Fallo técnico para revisar" };
  if (journey.status === "PENDING_APPROVAL" || latestOutbound?.status === "PENDING_APPROVAL")
    return { value: "PENDING_APPROVAL", label: "Borrador esperando aprobación" };
  if (journey.status === "APPROVED_QUEUED" || latestOutbound?.status === "APPROVED_QUEUED")
    return { value: "APPROVED_QUEUED", label: "Mensaje aprobado" };
  if (["QUESTION_PENDING_RESPONSE", "PENDING_SCHEDULING", "SLOT_OPTIONS_PENDING_APPROVAL", "WAITING_SLOT_CONFIRMATION", "MEETING_PENDING_APPROVAL", "MEETING_SCHEDULED", "HUMAN_HANDOFF", "REFERRED"].includes(journey.status))
    return { value: journey.status, label: journeyStatusText(journey.status) };
  if (latestOutbound?.status === "SENT")
    return { value: "INITIAL_MESSAGE_SENT", label: "Mensaje inicial enviado" };
  return { value: journey.status, label: journeyStatusText(journey.status) };
}

export function normalizePartialStep(step: Step): Step {
  const keys: Record<string, string> = { OUTREACH_DRAFT: "prepared", CLAIM_CHECK: "checked" };
  const output = parsedJson<Record<string, number>>(step.outputJson, {});
  const key = keys[step.kind];
  if (key && step.status === "COMPLETED" && output.total && (output[key] ?? 0) < output.total) return { ...step, status: "RUNNING", finishedAt: undefined };
  return step;
}

function stepAggregateProgress(step: Step) {
  if (!step.outputJson) return null;
  try {
    const output = JSON.parse(step.outputJson) as Record<string, unknown>;
    const total = typeof output.total === "number" ? output.total : undefined;
    if (!total) return null;
    const completedByKind: Record<string, string> = {
      OUTREACH_DRAFT: "prepared",
      CLAIM_CHECK: "checked",
      HUMAN_APPROVAL: "approved",
      SEND: "sent",
      INBOUND_PROCESSING: "replied",
      MEETING: "scheduled",
    };
    const key = completedByKind[step.kind];
    const explicit = key && typeof output[key] === "number" ? Number(output[key]) : undefined;
    if (step.kind === "HUMAN_APPROVAL" && explicit === undefined) return null;
    const completed = explicit;
    return completed === undefined ? null : `${completed}/${total}`;
  } catch {
    return null;
  }
}

function journeyResponsible(status: string) {
  if (["QUEUED", "DRAFT_PENDING"].includes(status)) return "SDR IA";
  if (["PENDING_APPROVAL", "APPROVED_QUEUED", "FOLLOW_UP_PENDING_APPROVAL", "SLOT_OPTIONS_PENDING_APPROVAL", "MEETING_PENDING_APPROVAL"].includes(status)) return "Usuario aprobador";
  if (["PENDING_SCHEDULING", "WAITING_SLOT_CONFIRMATION", "MEETING_SCHEDULED"].includes(status)) return "RevOps IA";
  if (["WAITING_REPLY", "FOLLOW_UP_DUE", "SNOOZED"].includes(status)) return "Inbound IA";
  if (["MANUAL_REVIEW", "FAILED_REQUIRES_REVIEW", "QUESTION_PENDING_RESPONSE"].includes(status)) return "Equipo comercial";
  return "Commercial AI";
}

export function journeyActionText(action?: string | null) {
  const labels: Record<string, string> = {
    HUMAN_APPROVAL: "Registrar aprobación humana",
    SEND: "Enviar en la ventana habilitada",
    SEND_APPROVED: "Enviar mensaje aprobado",
    FOLLOW_UP_1: "Preparar primer seguimiento",
    FOLLOW_UP_2: "Preparar segundo seguimiento",
    NO_RESPONSE: "Cerrar por falta de respuesta",
    MANUAL_REVIEW: "Revisión manual requerida",
    VALIDATE_REFERRAL: "Validar contacto referido",
    GENERATE_REFERRAL_OUTREACH: "Preparar mensaje para el referido",
    REVIEW_REJECTED_DRAFT: "Revisar borrador rechazado",
    REVIEW_MONITORING_EXPIRED: "Revisar monitoreo vencido",
    GENERATE_FOLLOW_UP: "Preparar seguimiento programado",
    PREPARE_MEETING_COORDINATION: "Consultar agenda y preparar opciones",
    RETRY_MEETING_COORDINATION: "Reintentar consulta de agenda",
    RETRY_COMMERCIAL_REPLY: "Recuperar respuesta comercial",
    RETRY_INBOUND: "Recuperar interpretación del mensaje",
    REVIEW_INBOUND: "Revisar interpretación del mensaje",
    GENERATE_INITIAL: "Reformular mensaje y repetir Claim Check",
    PREPARE_COMMERCIAL_REPLY: "Preparar respuesta comercial",
    REGENERATING_INITIAL_OUTREACH: "Preparar nueva versión del correo inicial",
    REGENERATING_RESPONSE: "Preparar nueva versión de la respuesta",
    REGENERATING_MEETING_OPTIONS: "Preparar nuevas opciones de horario",
    APPROVE_MEETING_OPTIONS: "Revisar opciones de horario",
    APPROVE_MEETING: "Aprobar creación de reunión",
    ESCALATE_MONITORING: "Revisar conversación sin respuesta",
    CLOSE_NO_RESPONSE: "Cerrar por falta de respuesta",
  };
  return action ? labels[action] ?? action.replaceAll("_", " ") : null;
}

export function sendWindowText(window?: JourneySummary["sendWindow"]) {
  if (!window || window.state === "UNAVAILABLE") return "Sin ventana disponible";
  if (window.state === "OPEN") return `Ventana abierta hasta ${formatDate(window.closesAt)}`;
  return `Próxima apertura ${formatDate(window.opensAt)}`;
}

function ReferralReviewAction({ journey, canReview, pending, onDecide }: { journey: Journey; canReview: boolean; pending: boolean; onDecide: (journeyId: string, decision: "APPROVE" | "REJECT") => void }) {
  const review = journey.referralReview;
  if (!review) return <span className="no-action">Falta la evidencia del referido</span>;
  const referredBy = review.referredBy?.name || review.referredBy?.email || "el contacto original";
  const domainLabel = review.domainMatchesCompany
    ? `El dominio coincide con ${review.companyDomain ?? "la empresa"}`
    : `Dominio externo: ${review.referralDomain ?? "sin identificar"}`;
  const approve = () => {
    if (window.confirm(`Se validará ${review.candidateEmail ?? "el contacto referido"} y se preparará un borrador. No se enviará nada sin pasar por Claim Check, aprobación y SAFE SEND. ¿Continuar?`)) onDecide(journey.id, "APPROVE");
  };
  const reject = () => {
    if (window.confirm(`Se rechazará ${review.candidateEmail ?? "el contacto referido"} y se cerrará este recorrido sin enviar ningún mensaje. ¿Continuar?`)) onDecide(journey.id, "REJECT");
  };
  return <div className="referral-review-action">
    <strong>Referido por {referredBy}</strong>
    <small className={review.domainMatchesCompany ? "domain-match" : "domain-warning"}>{domainLabel}</small>
    <details>
      <summary>Ver mensaje que originó la derivación</summary>
      {review.subject && <small>Asunto: {review.subject}</small>}
      <p>{review.body || "El mensaje original no está disponible."}</p>
    </details>
    {canReview ? <div className="referral-review-buttons">
      <button className="primary" disabled={pending} onClick={approve}><CheckCircle2 size={13} />Validar y preparar</button>
      <button className="secondary danger" disabled={pending} onClick={reject}><XCircle size={13} />Rechazar</button>
    </div> : <small>Necesitás permisos de edición para resolver esta revisión.</small>}
  </div>;
}

export function runContactTargets(run: Pick<Run, "config" | "steps">) {
  const configured = run.config?.goal.targetContacts ?? 0;
  const enrichment = run.steps.find((step) => step.kind === "CONTACT_ENRICHMENT");
  const output = parsedJson<{ target?: number; contacts?: number; selected?: number; shortfallAccepted?: boolean }>(enrichment?.outputJson, {});
  const original = output.target ?? configured;
  const found = output.selected ?? output.contacts ?? 0;
  return {
    configured: original,
    effective: output.shortfallAccepted === true && found > 0 ? found : original,
  };
}

function JourneyDashboard({ run, journeys, recoveries, meetingRecoveries, frozenTemplateContactIds = [], onRecover, onRecoverClaim, onRecoverMeeting, onRecoverReply, onDecideReferral, onRecoverReferral, canRecoverClaim, canRecoverReply, canReviewReferral, recovering }: { run: Run; journeys: Journey[]; recoveries: SafeSendRecovery[]; meetingRecoveries: MeetingRecovery[]; frozenTemplateContactIds?: string[]; onRecover: (actionId: string) => void; onRecoverClaim: (contactId: string) => void; onRecoverMeeting: (journeyId: string) => void; onRecoverReply: (journeyId: string, revision?: ReplyTemplateRevision) => void; onDecideReferral: (journeyId: string, decision: "APPROVE" | "REJECT") => void; onRecoverReferral: (journeyId: string) => void; canRecoverClaim: boolean; canRecoverReply: boolean; canReviewReferral: boolean; recovering: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const summary = run.journeySummary;
  const schedule = scheduleFor(run.config);
  const individualAudience = run.config?.audience.type === "B2C";
  const targets = runContactTargets(run);
  const discardedForReplacement = journeys.filter((journey) => replacementCloseReason(journey.closeReason)).length;
  const replacementsSelected = journeys.filter((journey) => journey.replaces).length;
  const replacementsPending = journeys.filter((journey) => replacementCloseReason(journey.closeReason) && !journey.replacement).length;
  const activeContacts = journeys.length - discardedForReplacement;
  const filtered = journeys.filter((journey) =>
    (!statusFilter || journey.status === statusFilter)
    && (!companyFilter || (individualAudience
      ? [journey.contact.firstName, journey.contact.lastName, journey.contact.email, journey.contact.role, journey.company.name]
        .filter(Boolean).join(" ").toLowerCase().includes(companyFilter.toLowerCase())
      : journey.company.name.toLowerCase().includes(companyFilter.toLowerCase())))
    && (!actionFilter || (journey.nextAction ?? "").toLowerCase().includes(actionFilter.toLowerCase())),
  );
  const metrics = [
    ["Contactos", summary?.total ?? journeys.length],
    ["En cola", (summary?.counts.QUEUED ?? 0) + (summary?.counts.DRAFT_PENDING ?? 0)],
    ["Pendientes de validación", summary?.pendingApprovals ?? 0],
    ["Validados en cola", summary?.approvedQueued ?? 0],
    ["Enviados", summary?.sent ?? 0],
    ["Esperando respuesta", summary?.waitingReply ?? 0],
    ["Respondieron", summary?.replied ?? 0],
    ["Coordinando reunión", summary?.coordinatingMeeting ?? 0],
    ["Reuniones confirmadas", summary?.meetingsScheduled ?? 0],
    ["No interesados", summary?.counts.NOT_INTERESTED ?? 0],
    ["DNC", summary?.counts.DO_NOT_CONTACT ?? 0],
    ["Rebotes", summary?.counts.BOUNCED ?? 0],
    ["Sin respuesta", summary?.counts.NO_RESPONSE ?? 0],
    ["Revisión / fallos", (summary?.counts.MANUAL_REVIEW ?? 0) + (summary?.counts.FAILED_REQUIRES_REVIEW ?? 0)],
  ] as const;
  return (
    <section className="journey-dashboard">
      {journeys.filter((journey) => journey.replyFailure).map((journey) => <CommercialReplyFailureNotice key={journey.id} contactName={`${journey.contact.firstName} ${journey.contact.lastName}`.trim()} campaignId={run.campaignId} failure={journey.replyFailure!} canRecover={canRecoverReply && !journey.terminal} pending={recovering} onRetry={(revision) => onRecoverReply(journey.id, revision)} />)}
      {journeys.filter((journey) => journey.nextAction === "DRAFT_RESPONSE" && !journey.terminal).map((journey) => <div key={journey.id} className="resume-banner" role="status"><LoaderCircle className="spin" size={17} /><p>Preparando la respuesta a {journey.contact.firstName || journey.contact.email || "este contacto"}. Después se validará antes de solicitar aprobación.</p></div>)}
      <div className="journey-overview">
        <div className="journey-heading">
          <div>
            <span className="eyebrow">OPERACIÓN MULTICONTACTO</span>
            <h2>Estado individual y capacidad diaria</h2>
            <p>Cada contacto avanza de manera independiente; una respuesta o reunión no detiene a los demás.</p>
            <p className="journey-status-summary">{summary?.total ?? journeys.length} contactos · {summary?.pendingApprovals ?? 0} pendientes de validación · {summary?.sent ?? 0} enviados · {summary?.replied ?? 0} respondieron</p>
          </div>
          <button
            type="button"
            className="secondary journey-toggle"
            aria-expanded={expanded}
            aria-controls="multicontact-operation-details"
            onClick={() => setExpanded((current) => !current)}
          >
            <ChevronRight size={16} />
            {expanded ? "Contraer" : "Expandir"}
          </button>
        </div>
        <div className="quota-card">
          <strong>{summary?.sentToday ?? 0} de {summary?.dailyLimit ?? run.config?.limits.maxEmailsPerDay ?? 0}</strong>
          <small>outbound de esta campaña · {summary?.quota?.quotaDay ?? "día local actual"}</small>
          <span>{summary?.quota?.channel ?? "EMAIL"} · {summary?.quota?.timezone ?? schedule.timezone}</span>
          <span>{sendWindowText(summary?.sendWindow)}</span>
          <div className="quota-breakdown"><small>Iniciales {summary?.quota?.breakdown.initial ?? 0}</small><small>Seguimientos {summary?.quota?.breakdown.followUps ?? 0}</small><small>Respuestas {summary?.quota?.breakdown.responses ?? 0}</small><small>Agenda {summary?.quota?.breakdown.meetingOptions ?? 0}</small><small>Restante {summary?.quota?.remaining ?? 0}</small></div>
          {summary?.nextAction && <span>Próxima acción real: {journeyActionText(summary.nextAction.kind)} · {formatDate(summary.nextAction.dueAt)}</span>}
        </div>
      </div>
      {expanded && <div className="journey-details" id="multicontact-operation-details">
        <div className="replacement-summary" role="status">
          <strong>Objetivo y reemplazos de este run</strong>
          <span>Meta configurada: {targets.configured} · lote aceptado: {targets.effective} · contactos vigentes: {activeContacts} · reemplazos elegidos: {replacementsSelected} · pendientes: {replacementsPending}</span>
          <small>{replacementsPending > 0
            ? "El sistema todavía debe buscar reemplazos dentro de este mismo run. No inicies otra ejecución mientras existan reemplazos pendientes."
            : activeContacts < targets.effective
              ? "El universo congelado no alcanzó el lote aceptado. Ampliá roles o alcance de la campaña y luego iniciá otro run sólo para completar los faltantes."
              : "El lote aceptado está cubierto; los descartes ya fueron sustituidos o no reducen la cantidad vigente."}</small>
        </div>
        <div className="campaign-period">
          <span><Clock3 /> Inicio: {schedule.startsAt ? formatDate(schedule.startsAt) : "al activar"}</span>
          <span><Send /> Primeros mensajes hasta: {schedule.outreachEndsAt ? formatDate(schedule.outreachEndsAt) : "sin fecha definida"}</span>
          <span><Activity /> Zona horaria: {schedule.timezone}</span>
          <span><RefreshCcw /> Monitoreo: {schedule.monitoringDays} días</span>
        </div>
        <div className="journey-metrics">
          {metrics.map(([label, value]) => <div key={label}><strong>{value}</strong><small>{label}</small></div>)}
        </div>
        <div className="journey-filters">
          <label>Estado<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">Todos</option>{[...new Set(journeys.map((item) => item.status))].sort().map((status) => <option key={status} value={status}>{journeyStatusText(status)}</option>)}</select></label>
          <label>{individualAudience ? "Persona" : "Empresa"}<input value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)} placeholder={individualAudience ? "Buscar persona" : "Buscar empresa"} /></label>
          <label>Acción pendiente<input value={actionFilter} onChange={(event) => setActionFilter(event.target.value)} placeholder="Ej. aprobación o follow-up" /></label>
        </div>
        {filtered.length ? <div className="table-card journey-table"><table><thead><tr><th>{individualAudience ? "Persona" : "Empresa / contacto"}</th><th>Estado</th><th>Última actividad</th><th>Próxima acción</th><th>Vencimiento</th><th title="Correos de seguimiento enviados después del mensaje inicial; máximo dos si no hay respuesta.">Seguimientos enviados</th><th>Responsable / trazabilidad</th><th>Acción manual</th></tr></thead><tbody>{filtered.map((journey) => {
        const recovery = recoveries.find((item) => item.journeyId === journey.id && item.eligible && item.actionId);
        const meetingRecovery = meetingRecoveries.find((item) => item.journeyId === journey.id && item.eligible);
        const reformulateMeetingInvitation = meetingRecovery?.evidence?.previousError === "MEETING_INVITATION_SEMANTIC_REJECTED";
        const latestOutbound = journey.messages.filter((message) => message.direction === "OUTBOUND").sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))[0];
        const claimRecoveryAvailable = latestOutbound?.status === "BLOCKED_CLAIM_CHECK";
        const latestInbound = journey.messages.filter((message) => message.direction === "INBOUND").sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))[0];
        const inboundFailure = parsedObject<{ inboundRecovery?: { message?: string } }>(latestInbound?.classificationJson)?.inboundRecovery;
        const meetingCoordinationActive = ["PREPARE_MEETING_COORDINATION", "RETRY_MEETING_COORDINATION"].includes(journey.nextAction ?? "");
        const actionError = journey.replyFailure?.reasons.join(" · ") || (journey.actions.find((action) => action.lastError && (action.status === "FAILED_REQUIRES_REVIEW" || (meetingCoordinationActive && action.kind === "PREPARE_MEETING_COORDINATION")))?.lastError ?? inboundFailure?.message);
        const inboundRetry = ["RETRY_INBOUND", "REVIEW_INBOUND"].includes(journey.nextAction ?? "");
        const operationalState = journeyOperationalState(journey);
        const closeReason = journeyCloseReasonText(journey.closeReason);
        const nextActionLabel = journey.replacement
          ? `Reemplazado por ${journey.replacement.companyName} · ${journey.replacement.contactName}`
          : journeyActionText(journey.nextAction) ?? (journey.terminal ? "Ciclo cerrado" : journey.status === "QUEUED" ? "Preparación de mensaje en cola" : "En espera de la próxima acción");
        return <tr key={journey.id}><td><div className="journey-contact-cell">{!individualAudience && <span className="journey-company-name">{journey.company.name}</span>}<strong className="journey-contact-name">{journey.contact.firstName} {journey.contact.lastName}</strong>{journey.replaces && <span className="replacement-badge">Reemplazo de {journey.replaces.companyName} · {journey.replaces.contactName}</span>}<small>{journey.contact.role?.trim() || "Puesto no informado"}</small><small>{journey.contact.email ?? "Sin email"}</small>{journey.contact.phone && <small>Tel: {journey.contact.phone}</small>}{journey.contact.linkedinUrl && <small><a href={journey.contact.linkedinUrl} target="_blank" rel="noreferrer">Ver LinkedIn</a></small>}</div></td><td><Status value={inboundRetry ? (journey.nextActionAt ? "INBOUND_RETRY_PENDING" : "INBOUND_RECOVERY_FAILED") : journey.nextAction === "RETRY_COMMERCIAL_REPLY" ? "COMMERCIAL_REPLY_RECOVERY" : operationalState.value} /><small>{(inboundRetry || journey.nextAction === "RETRY_COMMERCIAL_REPLY") ? (journey.nextActionAt ? "Reintento automático programado" : journey.replyFailure ? "La consulta está guardada; revisá el motivo y las opciones de recuperación" : "La consulta está guardada; podés reintentar") : operationalState.label}</small>{replacementCloseReason(journey.closeReason) && closeReason && <small className="cell-error">{closeReason}</small>}</td><td>{formatDate(journey.lastInboundAt ?? journey.lastOutboundAt)}</td><td><strong>{nextActionLabel}</strong><small>{journey.replacement ? `Seleccionado ${formatDate(journey.replacement.selectedAt)}` : journey.nextActionAt ? formatDate(journey.nextActionAt) : "—"}</small>{journey.referralRecovery?.reason ? <small className="cell-error">{journey.referralRecovery.reason}</small> : actionError ? <small className="cell-error">{presentationText(actionError, "cause")}</small> : null}{meetingRecovery?.evidence?.guidance && <small>{meetingRecovery.evidence.guidance}</small>}</td><td>{journey.replyDeadlineAt ? formatDate(journey.replyDeadlineAt) : "—"}</td><td title="Cuenta sólo seguimientos enviados, no mensajes iniciales, respuestas ni propuestas de reunión.">{journey.followUpCount} / 2</td><td><strong>{journeyResponsible(journey.status)}</strong><details className="journey-trace"><summary>Ver trazabilidad</summary><small>{journey.messages.length} mensajes · {journey.messages.filter((message) => message.approval).length} aprobaciones · {journey.actions.length} acciones{journey.meetingId ? " · reunión vinculada" : ""}</small>{journey.replacement && <small>Reemplazo: {journey.replacement.companyName} · {journey.replacement.contactName}</small>}{journey.replaces && <small>Reemplaza a: {journey.replaces.companyName} · {journey.replaces.contactName}</small>}<a href="#run-evidence">Ir a eventos seguros</a></details></td><td>{journey.nextAction === "VALIDATE_REFERRAL" ? <ReferralReviewAction journey={journey} canReview={canReviewReferral} pending={recovering} onDecide={onDecideReferral} /> : claimRecoveryAvailable && canRecoverClaim && frozenTemplateContactIds.includes(journey.contact.id) ? <Link className="secondary compact-action" to={`/campaigns/${run.campaignId}`}>Revisar oferta y plantilla</Link> : claimRecoveryAvailable && canRecoverClaim ? <button className="secondary compact-action" disabled={recovering} onClick={() => onRecoverClaim(journey.contact.id)}><RefreshCcw size={13} />Reformular mensaje</button> : journey.referralRecovery?.eligible && canReviewReferral ? <button className="secondary compact-action" disabled={recovering} onClick={() => onRecoverReferral(journey.id)}><RefreshCcw size={13} />Reintentar preparación</button> : journey.replyFailure && templateReplyNeedsCorrection(journey.replyFailure) ? <a className="secondary compact-action" href={`#${replyFailureAnchor(journey.replyFailure.sourceMessageId)}`}>Ver motivo y resolver respuesta</a> : canRecoverReply && (journey.nextAction === "RETRY_COMMERCIAL_REPLY" || inboundRetry && !journey.nextActionAt) && !journey.terminal ? <button className="secondary compact-action" disabled={recovering} onClick={() => onRecoverReply(journey.id)}><RefreshCcw size={13} />Reintentar respuesta</button> : meetingRecovery ? <button className="secondary compact-action" disabled={recovering} onClick={() => onRecoverMeeting(journey.id)}>{reformulateMeetingInvitation ? <RefreshCcw size={13} /> : <CalendarPlus size={13} />}{reformulateMeetingInvitation ? "Reformular invitación segura" : "Reintentar consulta de agenda"}</button> : recovery ? <button className="secondary compact-action" disabled={recovering} onClick={() => onRecover(recovery.actionId!)}><RefreshCcw size={13} />{recovery.actionLabel ?? "Reintentar envío seguro"}</button> : <span className="no-action">Sin acciones disponibles</span>}</td></tr>;
        })}</tbody></table></div> : <Empty title="Sin contactos para este filtro" body="Ajustá los filtros o esperá a que finalice el descubrimiento y enriquecimiento." />}
      </div>}
    </section>
  );
}

function OperationIndex() {
  const [group, setGroup] = useState("");
  const [sortBy, setSortBy] = useState<"attention" | "recent" | "campaign">("attention");
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(() => new Set());
  const cursor = cursorStack.at(-1);
  const { data, isLoading, isFetching, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["runs-index", group, cursor],
    queryFn: () => api<{ items: RunIndexItem[]; nextCursor?: string | null; heavyDetailsIncluded: boolean }>(`/runs?limit=20${group ? `&group=${group}` : ""}${cursor ? `&cursor=${cursor}` : ""}`),
    refetchInterval: cursor ? false : 5000,
  });
  const filters: Array<[string, string]> = [
    ["", "Todas"], ["attention", "Requieren atención"], ["active", "En curso"],
    ["monitoring", "Monitoreando respuestas"], ["finished", "Finalizadas"], ["failed", "Fallidas / canceladas"],
  ];
  const sortedItems = useMemo(() => [...(data?.items ?? [])].sort((left, right) => {
    if (sortBy === "campaign") return left.campaign.name.localeCompare(right.campaign.name, "es");
    if (sortBy === "recent") return Date.parse(right.lastActivity) - Date.parse(left.lastActivity);
    return Number(right.attention) - Number(left.attention)
      || Number(right.overdue) - Number(left.overdue)
      || Date.parse(right.lastActivity) - Date.parse(left.lastActivity);
  }), [data?.items, sortBy]);
  return <>
    <PageTitle eyebrow="OPERACIÓN" title="Sala de control" body="Prioriza intervenciones, vencimientos y ejecuciones activas sin cargar la evidencia pesada de cada run." />
    <div className="operation-index-toolbar">
      <div className="operation-index-filters" role="tablist" aria-label="Filtrar ejecuciones">
        {filters.map(([value, label]) => <button key={value} role="tab" aria-selected={group === value} className={group === value ? "active" : ""} onClick={() => { setGroup(value); setCursorStack([]); }}>{label}</button>)}
      </div>
      <label>Ordenar por<select value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)}><option value="attention">Atención primero</option><option value="recent">Actividad reciente</option><option value="campaign">Campaña A–Z</option></select></label>
    </div>
    <div className="operation-refresh" role="status">{isFetching && !isLoading ? <><RefreshCcw className="spin" size={12} />Actualizando sin interrumpir la vista…</> : <>Última actualización {dataUpdatedAt ? formatDate(new Date(dataUpdatedAt).toISOString()) : "—"}</>}</div>
    {isLoading ? <div className="loading" role="status">Cargando ejecuciones…</div> : isError ? <div className="query-state error"><CircleAlert /><strong>No se pudieron cargar las operaciones</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal."}</p><button className="secondary" onClick={() => void refetch()}><RefreshCcw size={15} />Reintentar</button></div> : data?.items.length ? <div className={`operation-index${isFetching ? " refreshing" : ""}`}>
      {sortedItems.map((item) => {
        const expanded = expandedRuns.has(item.id);
        const attentionSignals = item.pendingApprovals + item.errors;
        return <article className={`run-control-card${item.attention ? " attention" : ""}${item.overdue ? " overdue" : ""}`} key={item.id}>
          <div className="run-control-summary">
            <div className="run-control-identity"><strong title={item.campaign.name}>{item.campaign.name}</strong><small>Run …{item.id.slice(-8)}</small></div>
            <div className="run-control-cell run-status-cell"><small>Estado</small><Status value={item.status} /></div>
            <div className="run-control-cell run-progress-cell"><small>Progreso real</small><div className="run-control-progress-inline"><span><i style={{ width: `${item.progress.percent}%` }} /></span><small>{item.progress.percent}% · {item.progress.completed}/{item.progress.total} etapas</small></div></div>
            <div className="run-control-cell next-action-cell"><small>Próxima acción</small><strong>{item.nextAction ? journeyActionText(item.nextAction.kind) : item.status === "COMPLETED" ? "Ejecución finalizada" : "Monitoreo sin acción inmediata"}</strong></div>
            <div className="run-control-cell window-cell"><small>Próxima ventana real</small><strong>{sendWindowText(item.sendWindow)}</strong></div>
            <button className="secondary run-control-expand" aria-label={`${expanded ? "Contraer" : "Expandir"} operación ${item.campaign.name}`} aria-expanded={expanded} aria-controls={`run-details-${item.id}`} onClick={() => setExpandedRuns((current) => { const next = new Set(current); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); return next; })}><ChevronRight size={17} /></button>
          </div>
          {expanded && <div className="run-control-details" id={`run-details-${item.id}`}>
            <div className="run-control-details-grid"><div><small>ID del run</small><strong title={item.id}>…{item.id.slice(-12)}</strong></div><div><small>Inicio</small><strong>{formatDate(item.startedAt ?? item.createdAt)}</strong></div><div><small>Contactos procesados</small><strong>{item.contacts}</strong></div><div><small>En espera</small><strong>{item.waitingReply}</strong></div><div><small>Requieren atención</small><strong className={attentionSignals ? "attention-count" : ""}>{attentionSignals} señales</strong></div><div><small>Última actividad</small><strong>{formatDate(item.lastActivity)}</strong></div><div><small>Enviados</small><strong>{item.sent}</strong></div><div><small>Coordinando reunión</small><strong>{item.coordinatingMeeting}</strong></div><div><small>Aprobaciones</small><strong>{item.pendingApprovals}</strong></div><div><small>Alertas</small><strong>{item.errors}</strong></div><div><small>Completados</small><strong>{item.terminal}</strong></div><div><small>Ventana</small><strong>{sendWindowText(item.sendWindow)}</strong></div></div>
            {item.nextAction?.lastError && <div className="warning-banner"><CircleAlert size={17} /><div><strong>Motivo que requiere atención</strong><p>{presentationText(item.nextAction.lastError, "cause")}</p></div></div>}
            <div className="run-control-detail-actions"><span className="operation-refresh">Última actividad registrada: {formatDate(item.lastActivity)}</span><Link className="secondary" to={`/operation/${item.id}`}>Abrir detalle del run <ArrowRight size={15} /></Link></div>
          </div>}
        </article>;
      })}
    </div> : <Empty title="No hay ejecuciones en este grupo" body="Probá otro filtro o iniciá una campaña." />}
    <div className="index-pagination"><button className="secondary" disabled={!cursorStack.length} onClick={() => setCursorStack((current) => current.slice(0, -1))}>Anterior</button><span>Página {cursorStack.length + 1}</span><button className="secondary" disabled={!data?.nextCursor} onClick={() => data?.nextCursor && setCursorStack((current) => [...current, data.nextCursor!])}>Siguiente</button></div>
  </>;
}

function RunConfigOverrideModal({ run, close }: { run: Run; close: () => void }) {
  const client = useQueryClient();
  const base = run.config ?? defaultConfig;
  const [config, setConfig] = useState<CampaignConfig>(base);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const parsed = createCampaignSchema.shape.config.safeParse(config);
  const update = useMutation({
    mutationFn: () => api(`/runs/${run.id}/config`, { method: "PUT", body: JSON.stringify({ config, reason, confirm: confirmed }) }),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ["run", run.id] }); close(); },
  });
  const schedule = config.schedule;
  const meeting = config.meeting;
  return <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onMouseDown={close}><motion.div className="modal campaign-editor" onMouseDown={(event) => event.stopPropagation()}>
    <button className="icon-button close" onClick={close}><X /></button><span className="eyebrow">OVERRIDE AUDITABLE</span><h2>Editar configuración de esta ejecución</h2><p>Este cambio no modifica la campaña. Quedará registrado como una excepción del snapshot activo.</p>
    <div className="form-grid">
      <label>Hard cap diario<NumericInput min="1" max="100" value={config.limits.maxEmailsPerDay} onValueChange={(maxEmailsPerDay) => setConfig({ ...config, limits: { ...config.limits, maxEmailsPerDay } })} /></label>
      <TimezoneField label="Zona horaria" value={schedule.timezone} onChange={(timezone) => setConfig({ ...config, schedule: { ...schedule, timezone } })} />
      <div className="full"><WeekdayPicker value={schedule.enabledWeekdays} onChange={(enabledWeekdays) => setConfig({ ...config, schedule: { ...schedule, enabledWeekdays } })} /></div>
      <label>Horario diario desde<input type="time" lang="en-GB" step="60" value={schedule.sendWindowStart} onChange={(event) => setConfig({ ...config, schedule: { ...schedule, sendWindowStart: event.target.value } })} /></label>
      <label>Horario diario hasta<input type="time" lang="en-GB" step="60" value={schedule.sendWindowEnd} onChange={(event) => setConfig({ ...config, schedule: { ...schedule, sendWindowEnd: event.target.value } })} /></label>
      <label>Inicio<input type="datetime-local" lang="en-GB" step="60" value={localDateTimeValue(schedule.startsAt, schedule.timezone)} onChange={(event) => setConfig({ ...config, schedule: { ...schedule, startsAt: optionalIso(event.target.value, schedule.timezone) } })} /></label>
      <label>Fecha límite para primeros contactos<input type="datetime-local" lang="en-GB" step="60" value={localDateTimeValue(schedule.outreachEndsAt, schedule.timezone)} onChange={(event) => setConfig({ ...config, schedule: { ...schedule, outreachEndsAt: optionalIso(event.target.value, schedule.timezone) } })} /><FieldHelp>Esta fecha debe quedar vigente para reencolar mensajes iniciales cancelados.</FieldHelp></label>
      <label>Monitoreo (días)<NumericInput min="1" max="90" value={schedule.monitoringDays} onValueChange={(monitoringDays) => setConfig({ ...config, schedule: { ...schedule, monitoringDays } })} /></label>
      <label className="checkbox-row full"><input type="checkbox" checked={Boolean(meeting)} onChange={(event) => setConfig({ ...config, meeting: event.target.checked ? { calendarId: "sistemas@vicnet.com.ar", organizerEmail: "sistemas@vicnet.com.ar", timezone: schedule.timezone, workdayStart: "00:00", workdayEnd: "23:59", durationMinutes: 30, minimumNoticeHours: 24, optionCount: 3, searchHorizonDays: 14, addGoogleMeet: true } : undefined })} /> Coordinar reuniones con Calendar</label>
      {meeting && <><label>Calendar ID<input value={meeting.calendarId} onChange={(event) => setConfig({ ...config, meeting: { ...meeting, calendarId: event.target.value } })} /></label><label>Organizador<input type="email" value={meeting.organizerEmail} onChange={(event) => setConfig({ ...config, meeting: { ...meeting, organizerEmail: event.target.value } })} /></label><TimezoneField label="Zona de agenda" value={meeting.timezone} onChange={(timezone) => setConfig({ ...config, meeting: { ...meeting, timezone } })} /><label>Jornada desde<input type="time" lang="en-GB" step="60" value={meeting.workdayStart} onChange={(event) => setConfig({ ...config, meeting: { ...meeting, workdayStart: event.target.value } })} /></label><label>Jornada hasta<input type="time" lang="en-GB" step="60" value={meeting.workdayEnd} onChange={(event) => setConfig({ ...config, meeting: { ...meeting, workdayEnd: event.target.value } })} /></label><label>Duración (minutos)<NumericInput min="15" max="240" value={meeting.durationMinutes} onValueChange={(durationMinutes) => setConfig({ ...config, meeting: { ...meeting, durationMinutes } })} /></label><label>Anticipación mínima (horas)<NumericInput min="1" max="720" value={meeting.minimumNoticeHours} onValueChange={(minimumNoticeHours) => setConfig({ ...config, meeting: { ...meeting, minimumNoticeHours } })} /></label><label>Opciones<NumericInput min="2" max="3" value={meeting.optionCount} onValueChange={(optionCount) => setConfig({ ...config, meeting: { ...meeting, optionCount } })} /></label><label>Horizonte (días)<NumericInput min="1" max="60" value={meeting.searchHorizonDays} onValueChange={(searchHorizonDays) => setConfig({ ...config, meeting: { ...meeting, searchHorizonDays } })} /></label><label className="checkbox-row"><input type="checkbox" checked={meeting.addGoogleMeet} onChange={(event) => setConfig({ ...config, meeting: { ...meeting, addGoogleMeet: event.target.checked } })} /> Agregar Google Meet</label></>}
      <label className="full">Motivo del cambio<textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Explicá por qué este run necesita una excepción" /></label>
      <label className="checkbox-row full"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> Confirmo que este cambio aplica sólo a esta ejecución activa.</label>
    </div>
    {!parsed.success && <div className="form-error">{[...new Set(parsed.error.issues.map((issue) => issue.message))].join(" · ")}</div>}
    {update.error && <div className="form-error">{update.error instanceof ApiError ? update.error.message : "No se pudo actualizar el snapshot"}</div>}
    <div className="modal-actions"><button className="secondary" onClick={close}>Cancelar</button><button className="primary" disabled={!parsed.success || !reason.trim() || !confirmed || update.isPending} onClick={() => update.mutate()}><Save size={16} />{update.isPending ? "Guardando…" : "Guardar override"}</button></div>
  </motion.div></motion.div>;
}

export function outreachRecoveryVisible(runStatus: string, outreachStepStatus: string | null | undefined, failedContacts: number, blockedDrafts: number, canResumeFromDrafting: boolean) {
  const outreachStarted = Boolean(outreachStepStatus && !["PENDING", "SKIPPED", "CANCELLED"].includes(outreachStepStatus));
  return ["BLOCKED", "ACTION_REQUIRED", "WAITING_STAGE_REVIEW", "WAITING_APPROVAL"].includes(runStatus)
    && outreachStarted
    && (failedContacts > 0 || blockedDrafts > 0 || canResumeFromDrafting);
}

function OutreachRecoveryPanel({ recovery, hunterPlan, isAdmin, pending, error, onRecover }: {
  recovery: OutreachRecovery;
  hunterPlan?: HunterDryRun;
  isAdmin: boolean;
  pending: boolean;
  error?: unknown;
  onRecover: (input: { action: "RETRY_STAGE" | "RETRY_CONTACT" | "EXCLUDE_AND_REPLACE"; contactId?: string; reason: string; requestKey: string }) => void;
}) {
  const client = useQueryClient();
  const [reason, setReason] = useState("");
  const [showHunterBudget, setShowHunterBudget] = useState(false);
  const [hunterCredits, setHunterCredits] = useState(1);
  const [hunterReason, setHunterReason] = useState("");
  const authorizeHunter = useMutation({
    mutationFn: () => api(`/runs/${recovery.runId}/hunter-credit-budget`, {
      method: "POST",
      headers: { "x-vicnet-intent": "hunter-credit-budget-write" },
      body: JSON.stringify({
        authorizedCredits: (hunterPlan?.preflight.authorized ?? 0) + hunterCredits,
        knownBalance: hunterPlan?.preflight.knownBalance,
        minimumBalance: hunterPlan?.preflight.minimumBalance,
        reason: hunterReason.trim(),
      }),
    }),
    onSuccess: async () => {
      setShowHunterBudget(false);
      setHunterReason("");
      await client.invalidateQueries({ queryKey: ["hunter-reconciliation-dry-run", recovery.runId] });
    },
  });
  const failed = recovery.contacts.filter((item) => item.attempts.some((attempt) => attempt.status === "FAILED") && !item.hasDraft);
  const automatic = recovery.autonomousContactResolution;
  const diagnosis = recovery.diagnosis;
  const hasContractFailures = failed.length > 0 && (diagnosis?.claimBlockedDrafts ?? 0) === 0;
  const showHunterPlan = Boolean(hunterPlan && hunterPlan.maximumEstimatedCredits > 0);
  const hunterBudgetExhausted = showHunterPlan && hunterPlan?.preflight.available === 0;
  if (!outreachRecoveryVisible(recovery.runStatus, recovery.step?.status, failed.length, diagnosis?.claimBlockedDrafts ?? 0, diagnosis?.canResumeFromDrafting ?? false)) return null;
  const submit = (action: "RETRY_STAGE" | "RETRY_CONTACT" | "EXCLUDE_AND_REPLACE", contactId?: string) => {
    if (reason.trim().length < 8 || pending) return;
    onRecover({ action, contactId, reason: reason.trim(), requestKey: crypto.randomUUID() });
  };
  return <details className="outreach-recovery-panel">
    <summary className="panel-title"><div><span className="eyebrow">RECUPERACIÓN DESDE ETAPA 6</span><h2>Revisar problemas de redacción</h2></div><span className="outreach-recovery-summary-status"><Status value={recovery.runStatus} /><ChevronRight size={18} aria-hidden="true" /></span></summary>
    <p>El snapshot del run se conserva. La plataforma verifica candidatos, descarta contradicciones y busca reemplazos sin ampliar países, industrias ni el universo congelado.</p>
    {diagnosis && <section className="outreach-root-cause" aria-label="Diagnóstico de redacción y claims">
      <header><div><span className="eyebrow">CAUSA PERSISTIDA</span><h3>{hasContractFailures ? "Fallas contractuales de redacción" : diagnosis.generatedDrafts === 0 ? "No hubo borradores para validar" : "Borradores detenidos en claims"}</h3></div><Status value={hasContractFailures || diagnosis.generatedDrafts === 0 ? "OUTREACH_DRAFT" : "CLAIM_CHECK"} /></header>
      <p>{hasContractFailures
        ? `${failed.length} contacto(s) recibieron un resultado de redacción que no cumplió el contrato. No es un rechazo del control de claims y puede reintentarse sin repetir descubrimiento ni CRM.`
        : diagnosis.generatedDrafts === 0
        ? "No se rechazó ningún email: la redacción recibió cero contactos elegibles. Claims procesó una lista vacía y la aprobación humana nunca comenzó."
        : `${diagnosis.claimBlockedDrafts} borrador(es) quedaron bloqueados por claims. Revisá los motivos y la fuente del texto antes de reintentar.`}</p>
      {diagnosis.inputRejections.length > 0 && <details><summary>Ver causas por contacto o empresa</summary><ul>{diagnosis.inputRejections.map((item, index) => <li key={`${item.type}-${item.entityId}-${index}`}><strong>{item.type.replaceAll("_", " ")}</strong><span>{item.reason}</span></li>)}</ul></details>}
      {recovery.contacts.filter((item) => item.claimFailure).map((item) => <details key={item.journeyId} className="outreach-failure-card"><summary>{item.contact || item.email} · {item.claimFailure!.code === "CLAIM_AUDIT_FAILED" ? "Auditoría no completada" : "Claims rechazados"}</summary><p>{item.claimFailure!.reason}</p><small>Mensaje {item.claimFailure!.messageId} · {item.claimFailure!.code ?? "BLOCKED_CLAIM_CHECK"}</small></details>)}
      {diagnosis.guidance && <p className="recommended-action" role="status">{diagnosis.guidance}</p>}
      {diagnosis.guidance && recovery.campaignId && <Link className="secondary" to={`/campaigns/${recovery.campaignId}`}>Revisar oferta y plantillas de esta campaña</Link>}
      <label className="recovery-reason">Motivo auditable de reanudación<textarea value={reason} maxLength={1000} onChange={(event) => setReason(event.target.value)} placeholder="Indicá por qué corresponde reformular los mensajes." /><small>{reason.length}/1000 · mínimo 8 caracteres.</small></label>
      <div className="recovery-stage-action"><button className="primary" disabled={pending || reason.trim().length < 8 || !diagnosis.canResumeFromDrafting} onClick={() => submit("RETRY_STAGE")}><RefreshCcw className={pending ? "spin" : ""} size={16} />{pending ? "Encolando…" : "Reanudar desde redacción"}</button></div>
      {!diagnosis.canResumeFromDrafting && !diagnosis.guidance && <p className="form-help" role="status">La acción permanece visible, pero no puede ejecutarse: este run no conserva un contacto validado ni un borrador bloqueado para reformular. Debe resolverse primero la selección autónoma de contacto, sin pedir una aprobación humana ficticia.</p>}
    </section>}
    {showHunterPlan && hunterPlan && <section className="hunter-credit-preflight" aria-label="Preflight de créditos Hunter">
      <header><div><span className="eyebrow">FIREWALL DE CRÉDITOS</span><h3>Plan sin llamadas a proveedores</h3></div><Status value={hunterPlan.providersInvoked ? "PROVIDER_INVOKED" : "DRY_RUN"} /></header>
      <div className="detail-grid"><div><small>Saldo conocido</small><strong>{hunterPlan.preflight.knownBalance}</strong></div><div><small>Piso protegido</small><strong>{hunterPlan.preflight.minimumBalance}</strong></div><div><small>Presupuesto del run</small><strong>{hunterPlan.preflight.authorized}</strong></div><div><small>Consumido / reservado</small><strong>{hunterPlan.preflight.consumed} / {hunterPlan.preflight.reserved}</strong></div><div><small>Disponible</small><strong>{hunterPlan.preflight.available}</strong></div><div><small>Máximo estimado</small><strong>{hunterPlan.maximumEstimatedCredits}</strong></div></div>
      <p className="recommended-action"><strong>Sin búsquedas masivas</strong><span>Primero se validará gratuitamente un único candidato. Sólo esa persona podrá habilitar Email Finder, con máximo un crédito por empresa.</span></p>
      <ul>{hunterPlan.companies.map((item) => <li key={item.companyId}><strong>{item.company}</strong> · {item.action === "REUSE_EXISTING_CONTACT_AND_DRAFT" ? "reutilización, costo 0" : item.maximumCredits ? "fase gratuita y, sólo si hay candidato elegible, máximo 1 crédito" : "sin consumo previsto"}</li>)}</ul>
      {hunterPlan.blockers.map((blocker) => <p className="form-help" key={blocker}>{blocker}</p>)}
      {hunterBudgetExhausted && <p className="recommended-action"><strong>Presupuesto agotado</strong><span>No se llamará a Hunter. La única recuperación habilitada es autorizar créditos adicionales para este run.</span></p>}
      {isAdmin && hunterBudgetExhausted && !showHunterBudget && <button className="secondary" onClick={() => setShowHunterBudget(true)}>Autorizar créditos adicionales</button>}
      {showHunterBudget && <div className="hunter-budget-form"><label>Créditos adicionales<NumericInput min="1" max="4" value={hunterCredits} onValueChange={setHunterCredits} /><small>Máximo total por run: 4 · máximo uno por empresa · piso protegido: 34.</small></label><label>Motivo auditable<textarea value={hunterReason} minLength={12} maxLength={500} onChange={(event) => setHunterReason(event.target.value)} /></label><div className="actions"><button className="secondary" onClick={() => setShowHunterBudget(false)}>Cancelar</button><button className="primary" disabled={authorizeHunter.isPending || hunterCredits < 1 || (hunterPlan?.preflight.authorized ?? 0) + hunterCredits > 4 || hunterReason.trim().length < 12} onClick={() => authorizeHunter.mutate()}>{authorizeHunter.isPending ? "Autorizando…" : "Confirmar presupuesto"}</button></div>{authorizeHunter.error && <div className="form-error" role="alert">{authorizeHunter.error instanceof ApiError ? authorizeHunter.error.message : "No se pudo autorizar el presupuesto."}</div>}</div>}
    </section>}
    <div className="outreach-failure-list">{failed.map((item) => {
      const attempt = item.attempts.find((candidate) => candidate.status === "FAILED")!;
      return <article key={item.journeyId} className="outreach-failure-card">
        <header><div><strong>{item.contact} · {item.company}</strong><small>{item.email ?? "Sin email"} · intento {attempt.attemptNumber} · {formatDate(attempt.finishedAt ?? attempt.startedAt)}</small></div><Status value={attempt.status} /></header>
        <p className="failure-summary">{attempt.userMessage ?? "La redacción no pudo completarse."}</p>
        <dl><div><dt>Código / categoría</dt><dd>{attempt.errorCode ?? "—"} · {attempt.category ?? "—"}</dd></div><div><dt>Agente</dt><dd>{attempt.agent ?? "sdr-outreach"} · {attempt.profile ?? "—"}</dd></div><div><dt>Proveedor</dt><dd>{attempt.provider ?? "—"}{attempt.providerTaskId ? ` · Task ${attempt.providerTaskId}` : ""}{attempt.providerRunId ? ` · Run ${attempt.providerRunId}` : ""}</dd></div><div><dt>Validador</dt><dd>{attempt.validator ?? "Revisión del proveedor"}</dd></div></dl>
        <details><summary>Ver causa técnica y payload contractual sanitizado</summary><p>{attempt.technicalCause ?? "Sin detalle técnico persistido."}</p>{attempt.contractPayloadJson && <pre>{attempt.contractPayloadJson}</pre>}</details>
        <div className="recommended-action"><strong>Acción recomendada</strong><span>{attempt.recommendedAction ?? "Reintentar sólo este contacto."}</span></div>
        {attempt.category === "PROVIDER_REVIEW_BLOCK"
          ? <div className="recommended-action"><strong>Recuperación autónoma</strong><span>Este contacto se descarta automáticamente. El sistema evalúa otros candidatos de la misma empresa y, sólo si los agota, otra empresa del snapshot. No requiere una decisión humana.</span></div>
          : !hunterBudgetExhausted && <div className="failure-actions"><button className="secondary" disabled={pending || reason.trim().length < 8} onClick={() => submit("RETRY_CONTACT", item.contactId)}><RefreshCcw size={15} />Reintentar este contacto</button></div>}
      </article>;
    })}</div>
    {!diagnosis && !hunterBudgetExhausted && failed.some((item) => item.attempts.some((attempt) => attempt.status === "FAILED" && attempt.category !== "PROVIDER_REVIEW_BLOCK")) && <><label className="recovery-reason">Motivo auditable de reintento técnico<textarea value={reason} maxLength={1000} onChange={(event) => setReason(event.target.value)} placeholder="Describí por qué corresponde reintentar la etapa técnica." /><small>{reason.length}/1000 · mínimo 8 caracteres.</small></label><div className="recovery-stage-action"><button className="primary" disabled={pending || reason.trim().length < 8} onClick={() => submit("RETRY_STAGE")}><RefreshCcw className={pending ? "spin" : ""} size={16} />{pending ? "Encolando…" : "Reintentar fallos técnicos"}</button></div></>}
    {automatic?.evaluations.length ? <details className="contact-resolution-audit"><summary>Ver candidatos evaluados y fuentes · política {automatic.policyVersion}</summary><div className="outreach-failure-list">{automatic.evaluations.map((evaluation) => <article key={evaluation.id} className="outreach-failure-card"><header><div><strong>{evaluation.firstName} {evaluation.lastName}</strong><small>{evaluation.role || "Rol no informado"} · {evaluation.email ?? "Sin correo"}</small></div><Status value={evaluation.status} /></header><p>{evaluation.decisionReason}</p><dl><div><dt>Confianza global</dt><dd>{evaluation.overallConfidence}/100</dd></div><div><dt>Identidad / empleo</dt><dd>{evaluation.identityConfidence} / {evaluation.currentEmploymentConfidence}</dd></div><div><dt>Rol / correo</dt><dd>{evaluation.roleRelevance} / {evaluation.emailConfidence}</dd></div><div><dt>Fuentes / penalización</dt><dd>{evaluation.sourceQuality} / {evaluation.contradictionPenalty}</dd></div></dl>{evaluation.evidence.map((source, index) => <p key={`${evaluation.id}-${index}`}><strong>{source.sourceType}</strong> · {source.fact} {source.url && <a href={source.url} target="_blank" rel="noreferrer">Ver fuente</a>}</p>)}</article>)}</div></details> : null}
    {Boolean(error) && <div className="form-error" role="alert"><CircleAlert size={17} />{error instanceof ApiError ? error.message : "No se pudo encolar la recuperación."}</div>}
  </details>;
}

function ContactEnrichmentRecoveryModal({ remainingCredits, reason, confirmed, pending, error, onReason, onConfirmed, onCancel, onSubmit }: {
  remainingCredits: number;
  reason: string;
  confirmed: boolean;
  pending: boolean;
  error?: unknown;
  onReason: (value: string) => void;
  onConfirmed: (value: boolean) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const validReason = usefulRejectionReason(reason);
  useEffect(() => {
    reasonRef.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onCancel();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onCancel, pending]);
  return <motion.div className="rejection-modal-backdrop" role="presentation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(event) => { if (event.target === event.currentTarget && !pending) onCancel(); }}>
    <motion.section className="rejection-modal contact-recovery-modal" role="dialog" aria-modal="true" aria-labelledby="contact-recovery-title" aria-describedby="contact-recovery-description" initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}>
      <header><div><span className="eyebrow">RECUPERACIÓN CONTROLADA</span><h2 id="contact-recovery-title">Buscar más contactos</h2></div><button className="icon-button" aria-label="Cerrar confirmación" disabled={pending} onClick={onCancel}><X /></button></header>
      <div className="contact-recovery-explanation" id="contact-recovery-description">
        <p>Esta operación crea un intento nuevo y auditable dentro del mismo run:</p>
        <ul>
          <li>Se conservarán estrategia, discovery y calificación.</li>
          <li>Se conservarán los contactos válidos ya encontrados y se retomará únicamente la etapa 4.</li>
          <li>Quedan <strong>{remainingCredits} créditos autorizados</strong>; no se ampliará el presupuesto.</li>
          <li>Primero se utilizarán fuentes gratuitas.</li>
          <li>Hunter consultará <strong>Domain Search</strong> por decisores del área; si hace falta ampliará por seniority.</li>
          <li><strong>Email Finder</strong> sólo se usará después de validar una persona pertinente.</li>
          <li>No se duplicarán empresas, contactos, journeys, mensajes ni approvals.</li>
        </ul>
      </div>
      <label className="rejection-reason"><span>Motivo auditable <b>*</b></span><textarea ref={reasonRef} value={reason} disabled={pending} maxLength={500} rows={4} onChange={(event) => onReason(event.target.value)} placeholder="Explicá por qué corresponde retomar esta etapa técnica." aria-invalid={reason.length > 0 && !validReason} aria-describedby="contact-recovery-reason-help" /><small id="contact-recovery-reason-help" className={reason.length > 0 && !validReason ? "invalid" : ""}>{reason.length}/500 · mínimo 12 caracteres y 3 palabras significativas.</small></label>
      <label className="contact-recovery-confirm"><input type="checkbox" checked={confirmed} disabled={pending} onChange={(event) => onConfirmed(event.target.checked)} /><span>Confirmo que se retomará sólo el enriquecimiento y que se conservarán los contactos válidos existentes.</span></label>
      {Boolean(error) && <div className="query-state error compact" role="alert"><CircleAlert /><strong>No se pudo encolar la recuperación</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal. Podés reintentar sin duplicar la solicitud."}</p></div>}
      {pending && <div className="regeneration-progress" role="status"><LoaderCircle className="spin" /><div><strong>Registrando el intento…</strong><p>La acción queda bloqueada para evitar solicitudes duplicadas.</p></div></div>}
      <footer><button className="secondary" disabled={pending} onClick={onCancel}>Cancelar</button><button className="primary" disabled={pending || !confirmed || !validReason} onClick={onSubmit}>{pending ? <LoaderCircle className="spin" /> : <RefreshCcw />}Confirmar búsqueda</button></footer>
    </motion.section>
  </motion.div>;
}

function CompanyReplacementRecoveryModal({ recovery, reason, confirmed, pending, error, onReason, onConfirmed, onCancel, onSubmit }: {
  recovery: CompanyReplacementRecovery;
  reason: string;
  confirmed: boolean;
  pending: boolean;
  error?: unknown;
  onReason: (value: string) => void;
  onConfirmed: (value: boolean) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const validReason = usefulRejectionReason(reason);
  useEffect(() => {
    reasonRef.current?.focus();
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape" && !pending) onCancel(); };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onCancel, pending]);
  return <motion.div className="rejection-modal-backdrop" role="presentation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(event) => { if (event.target === event.currentTarget && !pending) onCancel(); }}>
    <motion.section className="rejection-modal contact-recovery-modal" role="dialog" aria-modal="true" aria-labelledby="company-replacement-title" aria-describedby="company-replacement-description" initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}>
      <header><div><span className="eyebrow">RECUPERACIÓN AUTÓNOMA</span><h2 id="company-replacement-title">Buscar empresas de reemplazo</h2></div><button className="icon-button" aria-label="Cerrar confirmación" disabled={pending} onClick={onCancel}><X /></button></header>
      <div className="contact-recovery-explanation" id="company-replacement-description">
        <p>La operación continuará dentro del mismo run y con sus límites actuales:</p>
        <ul>
          <li>Se conservarán la estrategia y la configuración congeladas.</li>
          <li>Se excluirán Pluspetrol, ARSAT y SION, además de los {recovery.excludedContactIds.length} contactos históricos ya descartados.</li>
          <li>Se evaluarán hasta {recovery.candidateCompanies.length} empresas del universo local preservado.</li>
          <li>Una empresa sólo será aceptada después de verificar un contacto vigente con rol objetivo.</li>
          <li>Primero se utilizarán fuentes gratuitas.</li>
          <li>Quedan <strong>{recovery.remainingCredits} créditos autorizados</strong>; la única operación paga permitida será Hunter <strong>EMAIL_FINDER</strong>.</li>
          <li><strong>Domain Search permanecerá bloqueado.</strong></li>
          <li>Luego continuarán CRM, redacción y Claim Check; el envío permanecerá controlado por el pipeline.</li>
          <li>No se duplicarán empresas, contactos, journeys, mensajes ni approvals.</li>
        </ul>
      </div>
      <label className="rejection-reason"><span>Motivo auditable <b>*</b></span><textarea ref={reasonRef} value={reason} disabled={pending} maxLength={500} rows={4} onChange={(event) => onReason(event.target.value)} placeholder="Explicá por qué corresponde buscar empresas de reemplazo." aria-invalid={reason.length > 0 && !validReason} aria-describedby="company-replacement-reason-help" /><small id="company-replacement-reason-help" className={reason.length > 0 && !validReason ? "invalid" : ""}>{reason.length}/500 · mínimo 12 caracteres y 3 palabras significativas.</small></label>
      <label className="contact-recovery-confirm"><input type="checkbox" checked={confirmed} disabled={pending} onChange={(event) => onConfirmed(event.target.checked)} /><span>Confirmo las exclusiones, el límite de 3 créditos y que sólo EMAIL_FINDER puede consumir presupuesto.</span></label>
      {Boolean(error) && <div className="query-state error compact" role="alert"><CircleAlert /><strong>No se pudo encolar la recuperación</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal. Podés reintentar sin duplicar la solicitud."}</p></div>}
      {pending && <div className="regeneration-progress" role="status"><LoaderCircle className="spin" /><div><strong>Registrando la búsqueda…</strong><p>La acción queda bloqueada para evitar solicitudes duplicadas.</p></div></div>}
      <footer><button className="secondary" disabled={pending} onClick={onCancel}>Cancelar</button><button className="primary" disabled={pending || !confirmed || !validReason} onClick={onSubmit}>{pending ? <LoaderCircle className="spin" /> : <RefreshCcw />}Confirmar búsqueda</button></footer>
    </motion.section>
  </motion.div>;
}

export function OperationPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [showConfigOverride, setShowConfigOverride] = useState(false);
  const [showContactRecovery, setShowContactRecovery] = useState(false);
  const [contactRecoveryReason, setContactRecoveryReason] = useState("");
  const [contactRecoveryConfirmed, setContactRecoveryConfirmed] = useState(false);
  const [contactRecoveryRequestKey, setContactRecoveryRequestKey] = useState("");
  const [contactRecoveryRequested, setContactRecoveryRequested] = useState(false);
  const [showCompanyReplacementRecovery, setShowCompanyReplacementRecovery] = useState(false);
  const [companyReplacementReason, setCompanyReplacementReason] = useState("");
  const [companyReplacementConfirmed, setCompanyReplacementConfirmed] = useState(false);
  const [companyReplacementRequestKey, setCompanyReplacementRequestKey] = useState("");
  const [companyReplacementRequested, setCompanyReplacementRequested] = useState(false);
  const [companyDiscoveryRequested, setCompanyDiscoveryRequested] = useState(false);
  const [contactShortfallReason, setContactShortfallReason] = useState("");
  const { data: run, isLoading: runLoading, isFetching: runFetching, isError: runError, error: runQueryError, refetch: refetchRun, dataUpdatedAt: runUpdatedAt } = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api<Run>(`/runs/${runId}`),
    enabled: !!runId,
    refetchInterval: 2500,
  });
  const [elapsedClock, setElapsedClock] = useState(() => Date.now());
  useEffect(() => {
    if (!run || (terminalRunStatuses as readonly string[]).includes(run.status)) return;
    const timer = window.setInterval(() => setElapsedClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [run?.status]);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me") });
  const startPrepared = useMutation({
    mutationFn: () => api<Run>(`/runs/${runId}/start`, {
      method: "POST",
    }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["run", runId] });
    },
  });
  const advanceStage = useMutation({
    mutationFn: () => api(`/runs/${runId}/advance-stage`, { method: "POST" }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["run", runId] });
      await client.invalidateQueries({ queryKey: ["run-journeys", runId] });
    },
  });
  const reprocessStage = useMutation({
    mutationFn: () => api(`/runs/${runId}/reprocess-current-stage`, { method: "POST" }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["run", runId] });
      await client.invalidateQueries({ queryKey: ["run-journeys", runId] });
    },
  });
  const revalidateClaims = useMutation({
    mutationFn: () => api<{ attemptId: string }>(`/runs/${runId}/revalidate-claims`, { method: "POST" }),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ["run", runId] }); },
  });
  const latestClaimRevalidation = [...(run?.stageAttempts ?? [])].filter((attempt) => attempt.stepKind === "CLAIM_CHECK_REVALIDATION").sort((left, right) => right.startedAt.localeCompare(left.startedAt))[0];
  const { data: claimRevalidationResult } = useQuery({
    queryKey: ["claim-revalidation", runId, latestClaimRevalidation?.id],
    queryFn: () => api<{ status: string; userMessage?: string | null; result?: { allowed: boolean; messages: Array<{ messageId: string; allowed: boolean; violations: string[] }> } | null }>(`/runs/${runId}/claims-revalidation/${latestClaimRevalidation!.id}`),
    enabled: Boolean(runId && latestClaimRevalidation),
    refetchInterval: (query) => !query.state.data || query.state.data.status === "RUNNING" ? 2500 : false,
  });
  const { data: journeyData } = useQuery({
    queryKey: ["run-journeys", runId],
    queryFn: () => api<{ items: Journey[]; summary: JourneySummary }>(`/runs/${runId}/journeys`),
    enabled: !!runId,
    refetchInterval: 2500,
  });
  const { data: recoveryData } = useQuery({
    queryKey: ["safe-send-recoveries", runId],
    queryFn: () => api<{ items: SafeSendRecovery[] }>(`/runs/${runId}/safe-send-recoveries`),
    enabled: !!runId,
    refetchInterval: 5000,
  });
  const { data: meetingRecoveryData } = useQuery({
    queryKey: ["meeting-recoveries", runId],
    queryFn: () => api<{ items: MeetingRecovery[] }>(`/runs/${runId}/meeting-coordination-recoveries`),
    enabled: !!runId,
    refetchInterval: 5000,
  });
  const { data: outreachRecovery } = useQuery({
    queryKey: ["outreach-recovery", runId],
    queryFn: () => api<OutreachRecovery>(`/runs/${runId}/outreach-recovery`),
    enabled: !!runId,
    refetchInterval: 5000,
  });
  const { data: hunterDryRun } = useQuery({
    queryKey: ["hunter-reconciliation-dry-run", runId],
    queryFn: () => api<HunterDryRun>(`/runs/${runId}/hunter-reconciliation-dry-run`),
    enabled: !!runId && run?.status !== "PENDING_AUTHORIZATION" && run?.config?.audience.type !== "B2C",
    refetchInterval: 5000,
  });
  const { data: contactEnrichmentRecovery } = useQuery({
    queryKey: ["contact-enrichment-recovery", runId],
    queryFn: () => api<ContactEnrichmentRecovery>(`/runs/${runId}/contact-enrichment-recovery`),
    enabled: !!runId && (contactRecoveryRequested || (run?.status === "ACTION_REQUIRED" && run?.errorCode === "CONTACT_TARGET_SHORTFALL") || (run?.status === "BLOCKED" && Boolean(run?.errorCode?.includes("CONTACT") || run?.steps.some((step) => step.kind === "CONTACT_ENRICHMENT" && step.status === "BLOCKED")))),
    refetchInterval: contactRecoveryRequested ? 2500 : false,
  });
  const { data: companyDiscoveryRecovery } = useQuery({
    queryKey: ["company-discovery-recovery", runId],
    queryFn: () => api<CompanyDiscoveryRecovery>(`/runs/${runId}/company-discovery-recovery`),
    enabled: !!runId && (companyDiscoveryRequested || (run?.status === "BLOCKED" && run?.steps.some((step) => step.kind === "COMPANY_DISCOVERY" && step.status === "BLOCKED"))),
    refetchInterval: companyDiscoveryRequested ? 2500 : false,
  });
  const recoverCompanyDiscovery = useMutation({
    mutationFn: (input: { reason: string; requestKey: string }) => api<{ attemptId: string; status: string; replayed: boolean }>(`/runs/${runId}/company-discovery-recovery`, {
      method: "POST",
      headers: { "x-vicnet-intent": "company-discovery-recovery-start" },
      body: JSON.stringify({ confirm: true, reason: input.reason, requestKey: input.requestKey }),
    }),
    onSuccess: async () => {
      setCompanyDiscoveryRequested(true);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["company-discovery-recovery", runId] }),
        client.invalidateQueries({ queryKey: ["run", runId] }),
        client.invalidateQueries({ queryKey: ["run-journeys", runId] }),
      ]);
    },
  });
  const recoverContactEnrichment = useMutation({
    mutationFn: () => api<{ attemptId: string; status: string; replayed: boolean }>(`/runs/${runId}/contact-enrichment-recovery`, {
      method: "POST",
      headers: { "x-vicnet-intent": "contact-enrichment-recovery-start" },
      body: JSON.stringify({ confirm: true, reason: contactRecoveryReason.trim(), requestKey: contactRecoveryRequestKey }),
    }),
    onSuccess: async () => {
      setContactRecoveryRequested(true);
      setShowContactRecovery(false);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["contact-enrichment-recovery", runId] }),
        client.invalidateQueries({ queryKey: ["run", runId] }),
        client.invalidateQueries({ queryKey: ["run-journeys", runId] }),
      ]);
    },
  });
  const { data: companyReplacementRecovery } = useQuery({
    queryKey: ["company-replacement-recovery", runId],
    queryFn: () => api<CompanyReplacementRecovery>(`/runs/${runId}/company-replacement-recovery`),
    enabled: !!runId && (companyReplacementRequested || (run?.status === "BLOCKED" && (run?.errorCode === "NO_APPROVABLE_DRAFTS" || run?.errorCode === "NO_DRAFTS_GENERATED"))),
    refetchInterval: companyReplacementRequested ? 2500 : false,
  });
  const recoverCompanyReplacement = useMutation({
    mutationFn: () => api<{ attemptId: string; status: string; replayed: boolean }>(`/runs/${runId}/company-replacement-recovery`, {
      method: "POST",
      headers: { "x-vicnet-intent": "company-replacement-recovery-start" },
      body: JSON.stringify({ confirm: true, reason: companyReplacementReason.trim(), requestKey: companyReplacementRequestKey }),
    }),
    onSuccess: async () => {
      setCompanyReplacementRequested(true);
      setShowCompanyReplacementRecovery(false);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["company-replacement-recovery", runId] }),
        client.invalidateQueries({ queryKey: ["run", runId] }),
        client.invalidateQueries({ queryKey: ["run-journeys", runId] }),
      ]);
    },
  });
  const openCompanyReplacementRecovery = () => {
    setCompanyReplacementReason("");
    setCompanyReplacementConfirmed(false);
    setCompanyReplacementRequestKey(globalThis.crypto.randomUUID().replaceAll("-", ""));
    setShowCompanyReplacementRecovery(true);
  };
  const openContactRecovery = () => {
    setContactRecoveryReason("");
    setContactRecoveryConfirmed(false);
    setContactRecoveryRequestKey(globalThis.crypto.randomUUID().replaceAll("-", ""));
    setShowContactRecovery(true);
  };
  const openCompanyDiscoveryRecovery = () => {
    const individualSearch = companyDiscoveryRecovery?.recoveryKind === "INDIVIDUAL_PROSPECT_SEARCH";
    const reason = window.prompt(
      individualSearch
        ? "Indicá el motivo auditable para reintentar únicamente la búsqueda de personas (mínimo 12 caracteres):"
        : "Indicá el motivo auditable para retomar este run desde descubrimiento (mínimo 12 caracteres):",
      individualSearch ? "Reintentar prospección B2C con la política de búsqueda corregida" : "Corregir el recorrido técnico desde discovery local",
    )?.trim() ?? "";
    if (reason.length < 12) return;
    const accepted = window.confirm(individualSearch
      ? "Se conservarán estrategia, decisión y snapshot. Se ejecutará una nueva tarea de Prospección de Personas y el flujo se detendrá antes de Elegibilidad. No se consultará Hunter ni se reutilizará una base CSV. ¿Confirmás?"
      : "Se conservarán estrategia, decisión, snapshot, presupuesto y configuración de Calendar. Descubrimiento volverá a ejecutar el plan de Prospección IA, Hunter Discover, el enriquecimiento firmográfico y la evaluación por lotes. El flujo se detendrá antes de Calificación. ¿Confirmás?");
    if (!accepted) return;
    recoverCompanyDiscovery.mutate({ reason, requestKey: globalThis.crypto.randomUUID().replaceAll("-", "") });
  };
  const cancel = useMutation({
    mutationFn: () => api(`/runs/${runId}/cancel`, { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["run", runId] }),
  });
  const closeAcquisition = useMutation({
    mutationFn: () => api(`/runs/${runId}/close-acquisition`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["run", runId] });
      void client.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
  const resume = useMutation({
    mutationFn: () => api<Run>(`/runs/${runId}/resume`, { method: "POST" }),
    onSuccess: (resumedRun) => {
      void client.invalidateQueries({ queryKey: ["run", runId] });
      navigate(`/operation/${resumedRun.id}`);
    },
  });
  const continueWithContactShortfall = useMutation({
    mutationFn: () => api(`/runs/${runId}/contact-shortfall-decision`, {
      method: "POST",
      headers: { "x-vicnet-intent": "continue-with-found-contacts" },
      body: JSON.stringify({ action: "CONTINUE_WITH_FOUND_CONTACTS", reason: contactShortfallReason.trim() }),
    }),
    onSuccess: async () => {
      setContactShortfallReason("");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["run", runId] }),
        client.invalidateQueries({ queryKey: ["run-journeys", runId] }),
      ]);
    },
  });
  const recoverSend = useMutation({
    mutationFn: (actionId: string) => api(`/scheduled-actions/${actionId}/retry-safe`, { method: "POST" }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["safe-send-recoveries", runId] });
      client.invalidateQueries({ queryKey: ["run-journeys", runId] });
      client.invalidateQueries({ queryKey: ["run", runId] });
    },
  });
  const recoverMeeting = useMutation({
    mutationFn: (journeyId: string) => api(`/journeys/${journeyId}/retry-meeting-coordination`, { method: "POST" }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["meeting-recoveries", runId] });
      client.invalidateQueries({ queryKey: ["run-journeys", runId] });
      client.invalidateQueries({ queryKey: ["run", runId] });
    },
  });
  const recoverReply = useMutation({
    mutationFn: ({ journeyId, revision }: { journeyId: string; revision?: ReplyTemplateRevision }) => api(`/journeys/${journeyId}/retry-commercial-reply`, { method: "POST", ...(revision ? { body: JSON.stringify(revision) } : {}) }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["run-journeys", runId] });
      client.invalidateQueries({ queryKey: ["run", runId] });
    },
  });
  const decideReferral = useMutation({
    mutationFn: ({ journeyId, decision }: { journeyId: string; decision: "APPROVE" | "REJECT" }) => api(`/journeys/${journeyId}/referral-decision`, { method: "POST", body: JSON.stringify({ decision }) }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["run-journeys", runId] });
      client.invalidateQueries({ queryKey: ["run", runId] });
      client.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
  const recoverReferral = useMutation({
    mutationFn: (journeyId: string) => api(`/journeys/${journeyId}/retry-referral-outreach`, { method: "POST" }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["run-journeys", runId] });
      client.invalidateQueries({ queryKey: ["run", runId] });
      client.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
  const recoverOutreach = useMutation({
    mutationFn: (input: { action: "RETRY_STAGE" | "RETRY_CONTACT" | "EXCLUDE_AND_REPLACE"; contactId?: string; reason: string; requestKey: string }) => api(`/runs/${runId}/outreach-recovery`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["outreach-recovery", runId] });
      client.invalidateQueries({ queryKey: ["run-journeys", runId] });
      client.invalidateQueries({ queryKey: ["run", runId] });
    },
  });
  if (!runId) return <OperationIndex />;
  if (runLoading) return <div className="loading" role="status">Cargando ejecución y actividad reciente…</div>;
  if (runError) return <div className="query-state error"><CircleAlert /><strong>No se pudo cargar la ejecución</strong><p>{runQueryError instanceof ApiError ? runQueryError.message : "Ocurrió un problema temporal."}</p><button className="secondary" onClick={() => void refetchRun()}><RefreshCcw size={15} />Reintentar</button></div>;
  if (!run) return <Empty title="Ejecución no disponible" body="No encontramos datos para esta ejecución." />;
  const currentReviewStep = [...run.steps].reverse().find((step) => ["COMPLETED", "FAILED"].includes(step.status));
  const reviewCopy = stageReviewCopy(run, currentReviewStep?.kind);
  const canReprocessCurrentStage = Boolean(currentReviewStep && reprocessableReviewSteps.has(currentReviewStep.kind));
  const discoveryStep = run.steps.find((step) => step.kind === "COMPANY_DISCOVERY");
  const individualSearchRecovery = companyDiscoveryRecovery?.recoveryKind === "INDIVIDUAL_PROSPECT_SEARCH";
  const discoveryReviewStep = discoveryStep?.status === "COMPLETED" ? discoveryStep : undefined;
  const nextPendingStep = run.steps.find((step) => step.status === "PENDING");
  const discoveryOutput = discoveryReviewStep ? parsedJson<DiscoveryOutput>(discoveryReviewStep.outputJson, {}) : {};
  const qualificationBlockedByEvidence = nextPendingStep?.kind === "QUALIFICATION"
    && Boolean(discoveryReviewStep)
    && discoveryOutput.evaluationIncomplete !== true
    && Array.isArray(discoveryOutput.qualificationReadyCandidateIds)
    && discoveryOutput.qualificationReadyCandidateIds.length === 0;
  const sendStep = run.steps.find((step) => step.kind === "SEND" && step.status === "COMPLETED");
  const sendOutput = sendStep ? parsedJson<{ sent?: number }>(sendStep.outputJson, {}) : {};
  const contactEnrichmentStep = run.steps.find((step) => step.kind === "CONTACT_ENRICHMENT");
  const contactEnrichmentOutput = parsedJson<{ target?: number; contacts?: number; selected?: number; shortfall?: number }>(contactEnrichmentStep?.outputJson, {});
  const contactShortfallPending = run.status === "ACTION_REQUIRED" && run.errorCode === "CONTACT_TARGET_SHORTFALL";
  const contactShortfallTarget = contactEnrichmentOutput.target ?? run.config?.goal.targetContacts ?? 0;
  const contactShortfallFound = contactEnrichmentOutput.selected ?? contactEnrichmentOutput.contacts ?? journeyData?.summary.total ?? 0;
  const acquisitionClosed = run.events?.some((event) => event.type === "RUN_ACQUISITION_CLOSED") ?? false;
  const canCloseAcquisition = contactEnrichmentStep?.status === "COMPLETED";
  const expiredOutreach = journeyData?.items.some((journey) => journey.closeReason === "OUTREACH_PERIOD_ENDED");
  const expiredSendRecoveries = recoveryData?.items.filter((item) => item.recoveryKind === "OUTREACH_PERIOD_ENDED") ?? [];
  const blockedExpiredSendRecoveries = expiredSendRecoveries.filter((item) => !item.eligible);
  return (
    <>
      <PageTitle
        eyebrow="OPERACIÓN EN TIEMPO REAL"
        title={run.campaign?.name ?? "Ejecución comercial"}
        body={`Run ${run.id} · iniciado ${formatDate(run.startedAt ?? run.createdAt)}`}
        action={
          <div className="actions">
            {canCloseAcquisition && !(terminalRunStatuses as readonly string[]).includes(run.status) && (
              <button
                className="secondary"
                disabled={closeAcquisition.isPending || acquisitionClosed}
                onClick={() => window.confirm("¿Cerrar la captación de este run? Se detendrán nuevas búsquedas y reemplazos, pero continuarán aprobaciones, envíos ya preparados, respuestas e inbound.") && closeAcquisition.mutate()}
              >
                <ShieldCheck size={16} />
                {acquisitionClosed ? "Captación cerrada" : closeAcquisition.isPending ? "Cerrando captación…" : "Cerrar captación"}
              </button>
            )}
            {(!(terminalRunStatuses as readonly string[]).includes(run.status) || run.status === "BLOCKED") && (
              <button
                className="secondary"
                disabled={cancel.isPending}
                onClick={() => window.confirm("¿Cancelar este run? Se conservarán los resultados y la evidencia ya registrados.") && cancel.mutate()}
              >
                <XCircle size={16} />
                {cancel.isPending ? "Cancelando…" : "Cancelar run"}
              </button>
            )}
            {run.resumePlan?.eligible && me?.user.role !== "VIEWER" && (
              <button className="primary" disabled={resume.isPending} onClick={() => resume.mutate()}>
                <RefreshCcw size={16} />
                {resume.isPending ? "Reanudando…" : "Reanudar sin repetir Hunter"}
              </button>
            )}
            {run.resumePlan?.childRunId && (
              <button className="secondary" onClick={() => navigate(`/operation/${run.resumePlan?.childRunId}`)}>
                Abrir intento reanudado
              </button>
            )}
            {me?.user.role === "ADMIN" && !(terminalRunStatuses as readonly string[]).includes(run.status) && <button className="secondary" onClick={() => setShowConfigOverride(true)}><Settings2 size={16} />Editar configuración de esta ejecución</button>}
            <Status value={run.status} />
          </div>
        }
      />
      {run.status === "RUNNING" && <div className="run-working-banner" role="status" aria-live="polite"><RefreshCcw size={18} className="spin" /><div><strong>La ejecución sigue trabajando</strong><p>{(() => {
        const attempt = [...(run.stageAttempts ?? [])].filter((item) => item.status === "RUNNING").sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
        const person = journeyData?.items.find((item) => item.contact.id === attempt?.contactId)?.contact;
        return attempt ? `${stepMeta(attempt.stepKind, run.config?.audience.type, run.config?.acquisition.source).agent}${person ? ` · ${person.firstName || person.email || "Contacto sin nombre"} ${person.lastName || ""}` : ""}. Inicio: ${formatDate(attempt.startedAt)}. Esperando el resultado de la tarea.` : "Procesando la cola del run. Los contactos avanzan por separado; las aprobaciones pendientes se muestran en su sección.";
      })()}</p></div></div>}
      <div className="operation-refresh" role="status">{runFetching && !runLoading ? <><RefreshCcw className="spin" size={12} />Actualizando progreso sin ocultar el contenido…</> : <>Última actualización {runUpdatedAt ? formatDate(new Date(runUpdatedAt).toISOString()) : "—"}</>}</div>
      {(["BLOCKED", "FAILED", "DEGRADED"].includes(run.status) || (run.status === "ACTION_REQUIRED" && Boolean(run.errorCode || run.errorMessage || run.degradedReason))) && <RunProblemNotice
        status={run.status} code={run.errorCode} message={runProblemMessage(run)}
        sent={journeyData?.summary.sent ?? run.journeySummary?.sent ?? 0}
        discarded={journeyData?.summary.counts.CANCELLED ?? run.journeySummary?.counts.CANCELLED ?? 0}
      />}
      {run.status === "PENDING_AUTHORIZATION" && (
        <section className="run-authorization-panel" aria-labelledby="run-authorization-title">
          <header><div><span className="eyebrow">RUN PREPARADO ANTES DEL CAMBIO</span><h2 id="run-authorization-title">Iniciar primera etapa</h2></div><Status value="PENDING_AUTHORIZATION" /></header>
          <p>Este run conserva el snapshot ya creado. No necesita motivo, checkbox ni otra autorización: el botón inicia directamente la primera etapa y el modo de prueba se detendrá al terminarla.</p>
          {me?.user.role !== "VIEWER" ? <>
            <button className="primary" disabled={startPrepared.isPending} onClick={() => startPrepared.mutate()}>{startPrepared.isPending ? <><LoaderCircle className="spin" size={17} />Iniciando…</> : <><Play size={17} />Iniciar primera etapa</>}</button>
            {startPrepared.error && <div className="form-error" role="alert">{startPrepared.error instanceof ApiError ? startPrepared.error.message : "No se pudo iniciar la ejecución."}</div>}
          </> : <p className="form-help">No tenés permisos para iniciar el run.</p>}
        </section>
      )}
      {(run.status === "WAITING_STAGE_REVIEW" || run.status === "WAITING_DIRECTION") && (
        <section className="run-authorization-panel" aria-labelledby="stage-review-title">
          <header><div><span className="eyebrow">{reviewCopy.eyebrow}</span><h2 id="stage-review-title">{reviewCopy.title}</h2></div><Status value="WAITING_STAGE_REVIEW" /></header>
          <p>{reviewCopy.body}</p>
          {currentReviewStep && <p><strong>Etapa disponible para revisar:</strong> {currentReviewStep.label}</p>}
          {sendStep && sendOutput.sent === 0 && <div className="form-error" role="alert">La etapa de envío terminó con 0 correos enviados{expiredOutreach ? ": venció el período de outreach y se canceló la acción pendiente" : ""}. Revalidar claims no reabre el envío.</div>}
          {run.steps.find((step) => step.status === "PENDING") && <p><strong>Próxima etapa:</strong> {run.steps.find((step) => step.status === "PENDING")?.label}</p>}
          {me?.user.role !== "VIEWER" ? <div className="actions">
            {canReprocessCurrentStage && <button className="secondary" disabled={advanceStage.isPending || reprocessStage.isPending || revalidateClaims.isPending || latestClaimRevalidation?.status === "RUNNING"} onClick={() => {
              if (window.confirm(`Se volverá a ejecutar ${currentReviewStep?.label ?? "la etapa actual"}. Puede repetir consumo de proveedores y acciones externas, pero no iniciará la etapa siguiente. ¿Confirmás?`)) reprocessStage.mutate();
            }}>{reprocessStage.isPending ? <><LoaderCircle className="spin" size={17} />Reprocesando…</> : <><RefreshCcw size={17} />{reviewCopy.reprocessLabel}</>}</button>}
            {run.steps.some((step) => step.kind === "CLAIM_CHECK" && step.status === "COMPLETED") && <button className="secondary" disabled={revalidateClaims.isPending || latestClaimRevalidation?.status === "RUNNING"} onClick={() => {
              if (window.confirm("Se repetirá la auditoría de claims del borrador guardado. No cambiará aprobaciones, no reabrirá la ventana vencida y no enviará correos. ¿Confirmás?")) revalidateClaims.mutate();
            }}>{revalidateClaims.isPending || latestClaimRevalidation?.status === "RUNNING" ? <><LoaderCircle className="spin" size={17} />Revalidando claims…</> : <><RefreshCcw size={17} />Reejecutar etapa 7 (sin enviar)</>}</button>}
            <button className="primary" disabled={advanceStage.isPending || reprocessStage.isPending || revalidateClaims.isPending || latestClaimRevalidation?.status === "RUNNING" || qualificationBlockedByEvidence} onClick={() => advanceStage.mutate()}>{advanceStage.isPending ? <><LoaderCircle className="spin" size={17} />Iniciando etapa…</> : <><Play size={17} />{reviewCopy.advanceLabel}</>}</button>
          </div> : <p className="form-help">No tenés permisos para continuar el run.</p>}
          {qualificationBlockedByEvidence && <div className="form-error" role="alert">{run.config?.audience.type === "B2C"
            ? "Elegibilidad no puede comenzar: la búsqueda no produjo personas elegibles. Volvé a buscar personas."
            : "Calificación no puede comenzar: ninguna candidata cumple todavía país, tamaño y dominio. Reprocesá Descubrimiento de empresas."}</div>}
          {advanceStage.error && <div className="form-error" role="alert">{advanceStage.error instanceof ApiError ? advanceStage.error.message : "No se pudo iniciar la siguiente etapa."}</div>}
          {reprocessStage.error && <div className="form-error" role="alert">{reprocessStage.error instanceof ApiError ? reprocessStage.error.message : "No se pudo reprocesar la etapa actual."}</div>}
          {revalidateClaims.error && <div className="form-error" role="alert">{revalidateClaims.error instanceof ApiError ? revalidateClaims.error.message : "No se pudo solicitar la revalidación de claims."}</div>}
          {latestClaimRevalidation?.status === "FAILED" && <div className="form-error" role="alert">La revalidación de claims falló: {claimRevalidationResult?.userMessage ?? latestClaimRevalidation.errorCode ?? "revisá la evidencia técnica"}.</div>}
          {claimRevalidationResult?.status === "COMPLETED" && <p role="status">Última revalidación de claims: {claimRevalidationResult.result?.allowed ? "aprobada" : "con observaciones"}. {claimRevalidationResult.result?.messages.map((item) => item.violations.join("; ")).filter(Boolean).join("; ") || "Sin observaciones."} No se modificaron aprobaciones ni envíos.</p>}
        </section>
      )}
      {cancel.error && (
        <div className="form-error">
          <CircleAlert size={17} />
          {cancel.error instanceof ApiError
            ? cancel.error.message
            : "No se pudo cancelar la ejecución"}
        </div>
      )}
      {closeAcquisition.error && <div className="form-error"><CircleAlert size={17} />{closeAcquisition.error instanceof ApiError ? closeAcquisition.error.message : "No se pudo cerrar la captación"}</div>}
      {acquisitionClosed && <div className="info-banner"><ShieldCheck size={17} /><div><strong>Captación cerrada</strong><p>No se buscarán nuevos contactos ni reemplazos. Las aprobaciones, los envíos ya preparados y el inbound continúan. Ya podés iniciar un run de completado desde la campaña si todavía faltan contactos.</p></div></div>}
      {resume.error && (
        <div className="form-error">
          <CircleAlert size={17} />
          {resume.error instanceof ApiError ? resume.error.message : "No se pudo reanudar la ejecución"}
        </div>
      )}
      {run.resumedFromRunId && (
        <div className="resume-banner">
          <RefreshCcw />
          <div>
            <strong>Intento {run.attemptNumber ?? 2} reanudado desde Validación de claims</strong>
            <p>Se reutilizaron estrategia, empresas, calificación, contactos, CRM y borradores del run {run.resumedFromRunId}. Hunter y HubSpot no se ejecutaron otra vez.</p>
          </div>
        </div>
      )}
      <details className={`run-config-snapshot${run.configOverriddenAt ? " overridden" : ""}`}>
        <summary className="snapshot-heading"><span className="eyebrow">SNAPSHOT DE EJECUCIÓN</span><strong>{run.configOverriddenAt ? "Configuración sobrescrita para este run" : `Configuración de campaña · versión ${run.configSourceVersion ?? "original"}`}</strong><small>{scheduleFor(run.config).timezone} · {run.config?.limits.maxEmailsPerDay ?? 0} correos/día</small>{run.configOverrideReason && <small>Motivo: {run.configOverrideReason}</small>}</summary>
        <div className="snapshot-grid">
          <div><small>Zona</small><strong>{scheduleFor(run.config).timezone}</strong></div><div><small>Días</small><strong>{weekdayNames(scheduleFor(run.config).enabledWeekdays)}</strong></div><div><small>Ventana</small><strong>{scheduleFor(run.config).sendWindowStart}–{scheduleFor(run.config).sendWindowEnd}</strong></div><div><small>Hard cap</small><strong>{run.config?.limits.maxEmailsPerDay ?? 0} correos/día</strong></div>
          <div><small>Inicio</small><strong>{scheduleFor(run.config).startsAt ? formatDate(scheduleFor(run.config).startsAt) : "Al activar"}</strong></div><div><small>Primeros contactos hasta</small><strong>{scheduleFor(run.config).outreachEndsAt ? formatDate(scheduleFor(run.config).outreachEndsAt) : "Sin fecha definida"}</strong></div><div><small>Monitoreo</small><strong>{scheduleFor(run.config).monitoringDays} días</strong></div>
          {run.config?.meeting ? <><div><small>Agenda</small><strong>{run.config.meeting.calendarId}</strong></div><div><small>Organizador</small><strong>{run.config.meeting.organizerEmail}</strong></div><div><small>Jornada de reuniones</small><strong>{run.config.meeting.workdayStart}–{run.config.meeting.workdayEnd} · {run.config.meeting.timezone}</strong></div><div><small>Opciones</small><strong>{run.config.meeting.optionCount} de {run.config.meeting.durationMinutes} min · {run.config.meeting.minimumNoticeHours} h de anticipación</strong></div><div><small>Horizonte y Meet</small><strong>{run.config.meeting.searchHorizonDays} días · {run.config.meeting.addGoogleMeet ? "con Google Meet" : "sin Google Meet"}</strong></div></> : <div><small>Coordinación de reuniones</small><strong>Sin configurar</strong></div>}
        </div>
      </details>
      {run.brief?.offer && <details className="run-offer-snapshot">
        <summary><span><span className="eyebrow">OFERTA CONGELADA DE ESTE RUN</span><strong>{run.brief.offer.name}</strong><small>{run.brief.offer.snapshotHash === run.currentOffer?.snapshotHash ? "Coincide con la oferta actual" : "La oferta actual cambió; este run conserva su versión original"}</small></span><ChevronRight size={17} /></summary>
        <div className="snapshot-comparison"><div><small>Snapshot de este run</small><strong>Hash {run.brief.offer.snapshotHash?.slice(0, 16) ?? "legado sin hash"}</strong><span>Congelado {run.brief.offer.frozenAt ? formatDate(run.brief.offer.frozenAt) : "al iniciar el run"}</span></div><div><small>Oferta actual</small><strong>Hash {run.currentOffer?.snapshotHash.slice(0, 16) ?? "no disponible"}</strong><span>{run.currentOffer?.updatedAt ? `Actualizada ${formatDate(run.currentOffer.updatedAt)}` : "Sin oferta actual"}</span></div></div>
        <div className="snapshot-evidence-grid"><section><h3>Claims completos congelados</h3>{run.brief.offer.facts.length ? <ul>{run.brief.offer.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul> : <p>No había claims autorizados.</p>}</section><section><h3>Evidencias completas usadas</h3>{run.brief.offer.evidence?.length ? run.brief.offer.evidence.map((evidence) => <article key={evidence.id}><strong>{evidence.name}</strong><small>ID {evidence.id} · v{evidence.version ?? "—"} · {evidence.origin ?? "OFFER"}</small>{evidence.sha256 && <small>SHA {evidence.sha256}</small>}{evidence.url && <a href={evidence.url} target="_blank" rel="noreferrer">Abrir fuente</a>}{evidence.content && <p>{evidence.content}</p>}</article>) : <p>No había evidencias activas y aprobadas.</p>}</section></div>
        <p className="snapshot-note">Los cambios posteriores de la oferta afectan sólo runs nuevos. Varias campañas pueden reutilizar la misma oferta sin alterar este snapshot.</p>
      </details>}
      {run.resumePlan?.eligible && (
        <div className="resume-banner available recovery-notice" role="status">
          <CircleAlert />
          <div>
            <strong>Este run puede retomarse sin gastar nuevamente créditos de Hunter</strong>
            <p>Se creará un intento nuevo y auditable desde Validación de claims. El run original y sus resultados permanecerán intactos.</p>
          </div>
        </div>
      )}
      {contactShortfallPending && (
        <section className="run-authorization-panel contact-shortfall-decision" aria-labelledby="contact-shortfall-title">
          <header>
            <div>
              <span className="eyebrow">DECISIÓN REQUERIDA</span>
              <h2 id="contact-shortfall-title">Se encontraron {contactShortfallFound} de {contactShortfallTarget} contactos válidos</h2>
            </div>
            <Status value="ACTION_REQUIRED" />
          </header>
          <p>La búsqueda agotó el universo que cumplía el alcance actual. Los contactos encontrados quedaron preservados y no se ejecutarán CRM, redacción ni envíos hasta que decidas.</p>
          {me?.user.role !== "VIEWER" ? <>
            <label>
              Motivo auditable para continuar con menos contactos
              <textarea
                value={contactShortfallReason}
                disabled={continueWithContactShortfall.isPending}
                maxLength={500}
                rows={3}
                onChange={(event) => setContactShortfallReason(event.target.value)}
                placeholder={`Ej.: continuar este run con los ${contactShortfallFound} contactos válidos encontrados.`}
              />
              <small>{contactShortfallReason.trim().length}/500 · mínimo 8 caracteres.</small>
            </label>
            <div className="actions">
              <button
                className="primary"
                disabled={continueWithContactShortfall.isPending || contactShortfallReason.trim().length < 8 || contactShortfallFound < 1}
                onClick={() => window.confirm(`El objetivo de la campaña seguirá siendo ${contactShortfallTarget}. Este run continuará únicamente con ${contactShortfallFound} contactos y la excepción quedará auditada. ¿Confirmás?`) && continueWithContactShortfall.mutate()}
              >
                {continueWithContactShortfall.isPending ? <><LoaderCircle className="spin" size={17} />Continuando…</> : <><Play size={17} />Continuar con {contactShortfallFound}</>}
              </button>
              <span className="form-help">Si no continuás, el run permanece pausado para que puedas ampliar roles o alcance y crear una nueva ejecución.</span>
            </div>
            {continueWithContactShortfall.error && <div className="form-error" role="alert">{continueWithContactShortfall.error instanceof ApiError ? continueWithContactShortfall.error.message : "No se pudo registrar la decisión."}</div>}
          </> : <p className="form-help">Un operador debe decidir si este run continúa con los contactos encontrados.</p>}
        </section>
      )}
      {(companyDiscoveryRecovery?.eligible || companyDiscoveryRecovery?.activeRecovery || (companyDiscoveryRequested && !companyDiscoveryRecovery?.latestRecovery)) && (
        <div className="resume-banner available recovery-notice company-discovery-recovery" role="status">
          <CircleAlert />
          <div>
            <strong>{companyDiscoveryRecovery?.activeRecovery || (companyDiscoveryRequested && !companyDiscoveryRecovery?.latestRecovery)
              ? individualSearchRecovery ? "Reintento de búsqueda de personas en curso" : "Recuperación desde descubrimiento en curso"
              : individualSearchRecovery ? "Reintento seguro disponible para la búsqueda de personas" : "Recuperación técnica disponible desde Descubrimiento de empresas"}</strong>
            <p>{companyDiscoveryRecovery?.activeRecovery || (companyDiscoveryRequested && !companyDiscoveryRecovery?.latestRecovery)
              ? "El intento fue registrado y el progreso se actualizará con el estado real del pipeline."
              : individualSearchRecovery
                ? "Se conservarán estrategia, decisión y snapshot. Se repetirá únicamente Prospección de Personas con una nueva tarea idempotente; no se utilizarán el CSV ni Hunter cuando la fuente sea sólo Búsqueda. El flujo se detendrá antes de Elegibilidad."
                : "Se conservarán estrategia, decisión, snapshot, presupuesto y configuración de Calendar. Se reutilizarán primero las empresas y firmografías ya persistidas; Hunter sólo volverá a consultarse si no existe un universo recuperable. El flujo se detendrá antes de Calificación."}</p>
            <small>{individualSearchRecovery ? "0 créditos Hunter · fuentes públicas permitidas · sin repetir Estrategia" : `${companyDiscoveryRecovery?.hunterBudget?.remaining ?? 0} créditos restantes · uso de Discover y enriquecimiento auditado · sin repetir Dirección`}</small>
            {companyDiscoveryRecovery?.eligible && run.status === "BLOCKED" && me?.user.role === "ADMIN" && <button className="primary contact-recovery-button" disabled={recoverCompanyDiscovery.isPending} onClick={openCompanyDiscoveryRecovery}><RefreshCcw size={16} />{companyDiscoveryRecovery.actionLabel}</button>}
            {recoverCompanyDiscovery.error && <small className="inline-error" role="alert">{recoverCompanyDiscovery.error instanceof ApiError ? recoverCompanyDiscovery.error.message : "No se pudo registrar la recuperación."}</small>}
          </div>
        </div>
      )}
      {(contactEnrichmentRecovery?.eligible || contactEnrichmentRecovery?.executionStarted || contactRecoveryRequested) && (
        <div className="resume-banner available recovery-notice contact-enrichment-recovery" role="status">
          <CircleAlert />
          <div>
            <strong>{contactEnrichmentRecovery?.executionStarted || contactRecoveryRequested ? "Recuperación de enriquecimiento en curso" : "Recuperación técnica disponible desde Enriquecimiento de contactos"}</strong>
            <p>{contactEnrichmentRecovery?.executionStarted || contactRecoveryRequested ? "El intento fue registrado y el progreso se actualizará con el estado real del pipeline." : "Se conservarán los contactos válidos y se volverán a revisar únicamente las empresas calificadas que no aportaron contacto. Hunter buscará primero decisores del área y luego ampliará por seniority si hace falta."}</p>
            <small>{contactEnrichmentRecovery?.candidateCompanyIds.length ?? 0} empresas calificadas · {contactEnrichmentRecovery?.currentBudget?.remaining ?? 0} créditos restantes · Domain Search y Email Finder auditados{contactEnrichmentRecovery?.activeRecovery ? ` · intento ${contactEnrichmentRecovery.activeRecovery.attemptNumber} ${contactEnrichmentRecovery.activeRecovery.status.toLowerCase()}` : ""}</small>
            {contactEnrichmentRecovery?.eligible && ["BLOCKED", "ACTION_REQUIRED"].includes(run.status) && me?.user.role === "ADMIN" && <button className="primary contact-recovery-button" disabled={recoverContactEnrichment.isPending} onClick={openContactRecovery}><RefreshCcw size={16} />Buscar más contactos</button>}
            {recoverContactEnrichment.error && <small className="inline-error" role="alert">{recoverContactEnrichment.error instanceof ApiError ? recoverContactEnrichment.error.message : "No se pudo registrar la recuperación."}</small>}
          </div>
        </div>
      )}
      {(companyReplacementRecovery?.eligible || companyReplacementRecovery?.activeRecovery || companyReplacementRequested) && (
        <div className="resume-banner available recovery-notice company-replacement-recovery" role="status">
          <CircleAlert />
          <div>
            <strong>{companyReplacementRecovery?.activeRecovery || companyReplacementRequested ? "Búsqueda de empresas de reemplazo en curso" : "Recuperación disponible: faltan contactos elegibles"}</strong>
            <p>{companyReplacementRecovery?.activeRecovery || companyReplacementRequested ? "El intento quedó registrado. El progreso real se actualizará sin repetir estrategia ni configuración." : "Podés buscar reemplazos dentro del universo preservado. Cada empresa será calificada y sólo se incorporará si antes se verifica un contacto vigente con rol objetivo."}</p>
            <small>{companyReplacementRecovery?.candidateCompanies.length ?? 0} empresas candidatas · {companyReplacementRecovery?.remainingCredits ?? 0} créditos restantes · fuentes gratuitas primero · sólo EMAIL_FINDER</small>
            {companyReplacementRecovery?.eligible && run.status === "BLOCKED" && me?.user.role === "ADMIN" && <button className="primary contact-recovery-button" disabled={recoverCompanyReplacement.isPending} onClick={openCompanyReplacementRecovery}><RefreshCcw size={16} />Buscar empresas de reemplazo</button>}
            {recoverCompanyReplacement.error && <small className="inline-error" role="alert">{recoverCompanyReplacement.error instanceof ApiError ? recoverCompanyReplacement.error.message : "No se pudo registrar la búsqueda."}</small>}
          </div>
        </div>
      )}
      {(recoveryData?.items.some((item) => item.eligible) || recoverSend.error) && (
        <div className="human-action-banner recovery-notice" role="status">
          <CircleAlert />
          <div>
            <strong>{expiredSendRecoveries.some((item) => item.eligible) ? "Acción disponible: reencolar el envío cancelado" : "Acción humana disponible: recuperar un envío seguro"}</strong>
            <p>La evidencia confirma que Gmail no fue invocado, no existe reserva y la aprobación conserva su hash. Expandí “Estado individual y capacidad diaria” y usá el botón del contacto. La recuperación sólo vuelve a encolar; no envía por sí sola.</p>
            {recoverSend.error && <small>{recoverSend.error instanceof ApiError ? recoverSend.error.message : "No se pudo recuperar la acción"}</small>}
          </div>
        </div>
      )}
      {blockedExpiredSendRecoveries.length > 0 && !expiredSendRecoveries.some((item) => item.eligible) && (
        <div className="warning-banner" role="status">
          <CircleAlert size={17} />
          <div><strong>Los envíos cancelados todavía no se pueden reencolar</strong><p>{blockedExpiredSendRecoveries[0]?.reasons.join(" · ")}. En “Editar configuración de esta ejecución” cambiá “Fecha límite para primeros contactos”; “Horario diario hasta” es un control diferente.</p></div>
        </div>
      )}
      {(meetingRecoveryData?.items.some((item) => item.eligible) || recoverMeeting.error) && (
        <div className="human-action-banner recovery-notice meeting-recovery-banner" role="status">
          <CircleAlert />
          <div><strong>La coordinación de agenda necesita intervención</strong><p>El inbound y la intención de reunión están preservados. Podés volver a encolar solamente la consulta FreeBusy, sin repetir Hermes, Hunter, CRM ni clasificación.</p>{recoverMeeting.error && <small>{recoverMeeting.error instanceof ApiError ? recoverMeeting.error.message : "No se pudo recuperar la coordinación"}</small>}</div>
        </div>
      )}
      {(run.status === "WAITING_DIRECTION" || (run.status === "WAITING_STAGE_REVIEW" && currentReviewStep?.kind === "HERMES_STRATEGY") || ["RUNNING", "FAILED"].includes(run.strategyVersions?.at(-1)?.status ?? "")) && (
        <StrategyView run={run} />
      )}
      {discoveryStep && (run.config?.audience.type === "B2C"
        ? <IndividualDiscoveryReview step={discoveryStep} sourceMode={run.config.acquisition.source} />
        : <DiscoveryReview step={discoveryStep} events={run.events ?? []} />)}
      {outreachRecovery && run.errorCode !== "CONTACT_TARGET_SHORTFALL" && me?.user.role !== "VIEWER" && <OutreachRecoveryPanel recovery={outreachRecovery} hunterPlan={hunterDryRun} isAdmin={me?.user.role === "ADMIN"} pending={recoverOutreach.isPending} error={recoverOutreach.error} onRecover={(input) => recoverOutreach.mutate(input)} />}
      <JourneyDashboard
        run={{ ...run, journeySummary: journeyData?.summary ?? run.journeySummary }}
        journeys={journeyData?.items ?? []}
        recoveries={recoveryData?.items ?? []}
        meetingRecoveries={meetingRecoveryData?.items ?? []}
        frozenTemplateContactIds={outreachRecovery?.contacts.filter((item) => item.claimFailure?.frozenTemplate).map((item) => item.contactId) ?? []}
        onRecover={(actionId) => recoverSend.mutate(actionId)}
        onRecoverClaim={(contactId) => recoverOutreach.mutate({ action: "RETRY_CONTACT", contactId, reason: "Reformular automáticamente después del bloqueo de Claim Check", requestKey: crypto.randomUUID() })}
        onRecoverMeeting={(journeyId) => recoverMeeting.mutate(journeyId)}
        onRecoverReply={(journeyId, revision) => recoverReply.mutate({ journeyId, revision })}
        onDecideReferral={(journeyId, decision) => decideReferral.mutate({ journeyId, decision })}
        onRecoverReferral={(journeyId) => recoverReferral.mutate(journeyId)}
        canRecoverClaim={Boolean(me && me.user.role !== "VIEWER")}
        canRecoverReply={Boolean(me && me.user.role !== "VIEWER")}
        canReviewReferral={Boolean(me && me.user.role !== "VIEWER")}
        recovering={recoverSend.isPending || recoverOutreach.isPending || recoverMeeting.isPending || recoverReply.isPending || decideReferral.isPending || recoverReferral.isPending}
      />
      {recoverReply.error && <p role="alert">{recoverReply.error instanceof ApiError ? recoverReply.error.message : "No se pudo recuperar la respuesta"}</p>}
      {decideReferral.error && <p role="alert">{decideReferral.error instanceof ApiError ? decideReferral.error.message : "No se pudo resolver la revisión del referido"}</p>}
      {recoverReferral.error && <p role="alert">{recoverReferral.error instanceof ApiError ? recoverReferral.error.message : "No se pudo recuperar la preparación del referido"}</p>}
      <div className="operation-layout">
        <section className="panel operation-main">
          <div className="panel-title">
            <div>
              <span className="eyebrow">PIPELINE</span>
              <h2>Progreso end-to-end</h2>
            </div>
            <RefreshCcw size={18} />
          </div>
          <StepRail
            steps={pipelineStepsForPresentation(run)}
            detailed
            runStatus={run.status}
            audienceType={run.config?.audience.type}
            sourceMode={run.config?.acquisition.source}
            runStartedAt={run.startedAt ?? run.createdAt}
            runFinishedAt={run.finishedAt ?? (["COMPLETED", "FAILED", "CANCELLED"].includes(run.status) ? run.updatedAt : undefined)}
            aiUsage={run.aiUsage}
            nowMs={elapsedClock}
          />
          <div className="operator-note">
            <Bot />
            <div>
              <strong>
                Director IA ·{" "}
                {run.provider === "HERMES"
                  ? "Hermes remoto"
                  : run.provider === "MOCK_HERMES"
                    ? "modo simulado"
                    : "proveedor pendiente"}
              </strong>
              <p>
                {run.provider === "HERMES"
                  ? "La ejecución está vinculada con el perfil Director de Hermes y cada resultado se registra en Commercial AI."
                  : run.provider === "MOCK_HERMES"
                    ? "La plataforma está ejecutando el flujo vertical con datos controlados y trazabilidad completa."
                    : "La ejecución todavía no seleccionó un proveedor de estrategia."}
              </p>
              {run.hermesTaskId && (
                <small>
                  Estrategia · Task {run.hermesTaskId}{run.hermesRunId ? ` · Run ${run.hermesRunId}` : ""}
                </small>
              )}
            </div>
          </div>
        </section>
        <details className="panel evidence-panel" id="run-evidence">
          <summary><span><span className="eyebrow">EVIDENCIA TÉCNICA</span><strong>Actividad y eventos auditables</strong></span><ChevronRight size={17} /></summary>
          <p className="timeline-intro">Cada mensaje explica qué avanzó y por qué quedó registrado.</p>
          <div className="timeline compact">
            {run.events
              ?.slice()
              .reverse()
              .map((event) => {
                const [title, detail] = eventDescription(event);
                const safeDetail = safeEventDetail(event);
                return (
                  <div key={event.id}>
                    <span />
                    <div>
                      <strong>{title}</strong>
                      <p>{detail}</p>
                      <small>{formatDate(event.createdAt)}</small>
                      {Object.keys(safeDetail).length > 0 && <details className="safe-event-detail"><summary>Ver detalle seguro</summary><pre>{JSON.stringify(safeDetail, null, 2)}</pre></details>}
                    </div>
                  </div>
                );
              })}
          </div>
        </details>
      </div>
      <AnimatePresence>{showConfigOverride && <RunConfigOverrideModal run={run} close={() => setShowConfigOverride(false)} />}</AnimatePresence>
      <AnimatePresence>{showContactRecovery && contactEnrichmentRecovery && <ContactEnrichmentRecoveryModal remainingCredits={contactEnrichmentRecovery.currentBudget?.remaining ?? 0} reason={contactRecoveryReason} confirmed={contactRecoveryConfirmed} pending={recoverContactEnrichment.isPending} error={recoverContactEnrichment.error} onReason={setContactRecoveryReason} onConfirmed={setContactRecoveryConfirmed} onCancel={() => { if (!recoverContactEnrichment.isPending) setShowContactRecovery(false); }} onSubmit={() => recoverContactEnrichment.mutate()} />}</AnimatePresence>
      <AnimatePresence>{showCompanyReplacementRecovery && companyReplacementRecovery && <CompanyReplacementRecoveryModal recovery={companyReplacementRecovery} reason={companyReplacementReason} confirmed={companyReplacementConfirmed} pending={recoverCompanyReplacement.isPending} error={recoverCompanyReplacement.error} onReason={setCompanyReplacementReason} onConfirmed={setCompanyReplacementConfirmed} onCancel={() => { if (!recoverCompanyReplacement.isPending) setShowCompanyReplacementRecovery(false); }} onSubmit={() => recoverCompanyReplacement.mutate()} />}</AnimatePresence>
    </>
  );
}

export function usefulRejectionReason(value: string) {
  const usefulWords = value.match(/[\p{L}\p{N}]{2,}/gu)?.length ?? 0;
  return value.trim().length >= 12 && usefulWords >= 3;
}

function CommercialRejectionModal({ approval, pending, error, onCancel, onSubmit }: {
  approval: Approval;
  pending: boolean;
  error?: unknown;
  onCancel: () => void;
  onSubmit: (mode: "MANUAL_REVIEW" | "REGENERATE", reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const valid = usefulRejectionReason(reason);
  useEffect(() => {
    textareaRef.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onCancel();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onCancel, pending]);
  return (
    <motion.div className="rejection-modal-backdrop" role="presentation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(event) => { if (event.target === event.currentTarget && !pending) onCancel(); }}>
      <motion.section className="rejection-modal" role="dialog" aria-modal="true" aria-labelledby="rejection-title" aria-describedby="rejection-description" initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}>
        <header>
          <div><span className="eyebrow">RECHAZAR PROPUESTA</span><h2 id="rejection-title">Indicá qué debe corregirse</h2></div>
          <button className="icon-button" aria-label="Cerrar rechazo" disabled={pending} onClick={onCancel}><X /></button>
        </header>
        <div className="rejection-summary" id="rejection-description">
          <Mail size={18} />
          <div><strong>{approval.message?.subject ?? "Respuesta comercial"}</strong><p>{approval.message?.body.slice(0, 220)}{(approval.message?.body.length ?? 0) > 220 ? "…" : ""}</p></div>
        </div>
        <label className="rejection-reason">
          <span>Motivo del rechazo <b>*</b></span>
          <textarea ref={textareaRef} value={reason} disabled={pending} maxLength={1000} rows={5} placeholder="Ejemplo: La propuesta no responde la consulta principal y debe ser más clara." onChange={(event) => setReason(event.target.value)} aria-invalid={reason.length > 0 && !valid} aria-describedby="rejection-help" />
          <small id="rejection-help" className={reason.length > 0 && !valid ? "invalid" : ""}>{reason.length}/1000 · mínimo 12 caracteres y 3 palabras significativas.</small>
        </label>
        <div className="rejection-explanation">
          <RefreshCcw size={18} />
          <p><strong>Si solicitás una nueva versión</strong> el redactor usa este motivo como instrucción puntual para corregir el borrador. La nueva versión vuelve a pasar los controles y queda pendiente de aprobación; no se envía automáticamente ni modifica la identidad del agente.</p>
        </div>
        {Boolean(error) && <div className="query-state error compact" role="alert"><CircleAlert /><strong>No se pudo registrar el rechazo</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal. Podés reintentar sin duplicar la solicitud."}</p></div>}
        {pending && <div className="regeneration-progress" role="status"><LoaderCircle className="spin" /><div><strong>Registrando una decisión inmutable…</strong><p>El botón queda bloqueado para evitar solicitudes duplicadas.</p></div></div>}
        <footer>
          <button className="secondary" disabled={pending} onClick={onCancel}>Cancelar</button>
          <button className="secondary danger rejection-action" disabled={!valid || pending} onClick={() => onSubmit("MANUAL_REVIEW", reason)}><XCircle />Rechazar y dejar en revisión</button>
          <button className="primary rejection-action" disabled={!valid || pending} onClick={() => onSubmit("REGENERATE", reason)}>{pending ? <LoaderCircle className="spin" /> : <RefreshCcw />}Rechazar y solicitar nueva versión</button>
        </footer>
      </motion.section>
    </motion.div>
  );
}

export function splitApprovalHistory<T extends { status: string; reviewerId?: string | null; sendability?: { sendable: boolean } }>(items: T[]) {
  const resolved = items.filter((item) => item.status !== "PENDING" || item.sendability?.sendable === false);
  return {
    human: resolved.filter((item) => Boolean(item.reviewerId)),
    automatic: resolved.filter((item) => !item.reviewerId),
  };
}

export function approvalContactDetails(contact: Contact & { company: Company }) {
  const valueOrFallback = (value?: string | null, fallback = "No informado") => value?.trim() || fallback;
  return {
    name: valueOrFallback([contact.firstName, contact.lastName].filter(Boolean).join(" ")),
    role: valueOrFallback(contact.role, "Cargo no informado"),
    company: valueOrFallback(contact.company.name, "Empresa no informada"),
    email: valueOrFallback(contact.email),
    phone: valueOrFallback(contact.phone),
    linkedinUrl: contact.linkedinUrl?.trim() || null,
  };
}

export function approvalCompanyDetails(company: Company) {
  const valueOrFallback = (value?: string | null, fallback = "No informado") => value?.trim() || fallback;
  const domain = company.domain?.trim() || null;
  return {
    name: valueOrFallback(company.name, "Empresa no informada"),
    industry: valueOrFallback(company.normalizedIndustry ?? company.industry),
    country: valueOrFallback(company.providerCountry ?? company.country),
    employees: company.employees > 0 ? `${company.employees.toLocaleString("es-AR")} empleados` : "No informado",
    domain: domain ?? "No informado",
    websiteUrl: domain ? (/^https?:\/\//i.test(domain) ? domain : `https://${domain}`) : null,
  };
}

const richEmailFonts = [
  { label: "Sans Serif", value: "Arial" },
  { label: "Serif", value: "Georgia" },
  { label: "Ancho fijo", value: "Courier New" },
  { label: "Wide", value: "Arial Black" },
  { label: "Narrow", value: "Arial Narrow" },
  { label: "Comic Sans MS", value: "Comic Sans MS" },
  { label: "Garamond", value: "Garamond" },
  { label: "Georgia", value: "Georgia" },
  { label: "Tahoma", value: "Tahoma" },
  { label: "Trebuchet MS", value: "Trebuchet MS" },
  { label: "Verdana", value: "Verdana" },
] as const;

const richEmailSizes = [
  { label: "Pequeño", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Grande", value: "4" },
  { label: "Muy grande", value: "5" },
] as const;

const richEmailColors = ["#17283b", "#5b6f84", "#0a8189", "#1463d6", "#6d42c7", "#b33d49", "#c66a14", "#287a4b"];

function ApprovalMessageEditor({
  editing,
  subject,
  body,
  bodyHtml,
  attachments,
  approvalId,
  pending,
  error,
  onSubjectChange,
  onBodyChange,
  onBodyHtmlChange,
  onUploadAttachment,
  onRemoveAttachment,
  onCancel,
  onSave,
  variableTokens = [],
  showAttachments = true,
  footerNote = "Al guardar se recalcula la huella de seguridad. El correo seguirá detenido hasta que una persona pulse “Aprobar y encolar”.",
}: {
  editing: boolean;
  subject: string;
  body: string;
  bodyHtml: string;
  attachments: Array<{ id?: string; source?: "ASSET" | "MESSAGE"; name: string; version?: number; sha256?: string; sizeBytes?: number; mimeType?: string }>;
  approvalId: string;
  pending: boolean;
  error?: unknown;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onBodyHtmlChange: (value: string) => void;
  onUploadAttachment: (file: File) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onCancel: () => void;
  onSave: () => void;
  variableTokens?: Array<{ label: string; value: string }>;
  showAttachments?: boolean;
  footerNote?: string;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const linkAnchorRef = useRef<HTMLAnchorElement | null>(null);
  const [openFormatMenu, setOpenFormatMenu] = useState<"alignment" | "color" | null>(null);
  const [linkEditor, setLinkEditor] = useState({ open: false, url: "", error: "" });
  useEffect(() => {
    if (!editing || !editorRef.current || editorRef.current.innerHTML === bodyHtml) return;
    editorRef.current.innerHTML = bodyHtml;
  }, [bodyHtml, editing]);

  const syncEditor = () => {
    if (!editorRef.current) return;
    onBodyHtmlChange(editorRef.current.innerHTML);
    onBodyChange(editorRef.current.innerText.replace(/\n{3,}/g, "\n\n").trim());
  };
  const captureSelection = () => {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const editor = editorRef.current;
    if (!range || !editor || !editor.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  };
  const restoreSelection = () => {
    const selection = window.getSelection();
    const range = savedRangeRef.current;
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };
  const runCommand = (command: string, value?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, value);
    captureSelection();
    syncEditor();
  };
  const openLinkEditor = (anchor?: HTMLAnchorElement | null) => {
    captureSelection();
    linkAnchorRef.current = anchor ?? null;
    setOpenFormatMenu(null);
    setLinkEditor({ open: true, url: anchor?.getAttribute("href") ?? "", error: "" });
  };
  const applyLink = () => {
    const entered = linkEditor.url.trim();
    if (!entered) {
      setLinkEditor((current) => ({ ...current, error: "Ingresá una dirección." }));
      return;
    }
    const normalized = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entered)
      ? `mailto:${entered}`
      : /^(https?:\/\/|mailto:)/i.test(entered) ? entered : `https://${entered}`;
    if (!/^(https?:\/\/[^\s]+|mailto:[^\s@]+@[^\s@]+)$/i.test(normalized)) {
      setLinkEditor((current) => ({ ...current, error: "Ingresá una URL o un correo válido." }));
      return;
    }
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    restoreSelection();
    if (linkAnchorRef.current && editor.contains(linkAnchorRef.current)) {
      linkAnchorRef.current.setAttribute("href", normalized);
      linkAnchorRef.current.setAttribute("target", "_blank");
      linkAnchorRef.current.setAttribute("rel", "noopener noreferrer");
    } else {
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      if (range?.collapsed) {
        const anchor = document.createElement("a");
        anchor.href = normalized;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.textContent = entered;
        range.insertNode(anchor);
        range.setStartAfter(anchor);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else {
        document.execCommand("createLink", false, normalized);
        editor.querySelectorAll<HTMLAnchorElement>("a").forEach((anchor) => {
          if (anchor.getAttribute("href") === normalized) {
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
          }
        });
      }
    }
    captureSelection();
    syncEditor();
    linkAnchorRef.current = null;
    setLinkEditor({ open: false, url: "", error: "" });
  };
  if (!editing) return bodyHtml
    ? <div className="approval-rich-email" dangerouslySetInnerHTML={{ __html: bodyHtml }} onClick={(event) => { const anchor = (event.target as HTMLElement).closest("a"); if (anchor instanceof HTMLAnchorElement) { event.preventDefault(); window.open(anchor.href, "_blank", "noopener,noreferrer"); } }} />
    : <pre>{body || "No existe un cuerpo de email enviable."}</pre>;
  return <div className="approval-message-editor">
    <label><span>Asunto final</span><input value={subject} maxLength={500} onChange={(event) => onSubjectChange(event.target.value)} /></label>
    <label><span>Correo final</span></label>
    <div className="rich-email-editor">
      <div className="rich-email-toolbar" role="toolbar" aria-label="Formato del correo">
        <select className="rich-toolbar-select font-family" aria-label="Fuente" title="Fuente" defaultValue="" onMouseDown={captureSelection} onChange={(event) => { runCommand("fontName", event.target.value); event.currentTarget.value = ""; }}>
          <option value="" disabled>Sans Serif</option>
          {richEmailFonts.map((font) => <option key={`${font.label}-${font.value}`} value={font.value}>{font.label}</option>)}
        </select>
        <select className="rich-toolbar-select font-size" aria-label="Tamaño de fuente" title="Tamaño de fuente" defaultValue="" onMouseDown={captureSelection} onChange={(event) => { runCommand("fontSize", event.target.value); event.currentTarget.value = ""; }}>
          <option value="" disabled>Tamaño</option>
          {richEmailSizes.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}
        </select>
        <span />
        <button type="button" title="Negrita" aria-label="Negrita" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("bold")}><Bold /></button>
        <button type="button" title="Cursiva" aria-label="Cursiva" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("italic")}><Italic /></button>
        <button type="button" title="Subrayado" aria-label="Subrayado" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("underline")}><Underline /></button>
        <button type="button" title="Color de texto" aria-label="Color de texto" aria-expanded={openFormatMenu === "color"} onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => setOpenFormatMenu((current) => current === "color" ? null : "color")}><Palette /><ChevronDown className="toolbar-chevron" /></button>
        <button type="button" title="Alineación" aria-label="Alineación" aria-expanded={openFormatMenu === "alignment"} onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => setOpenFormatMenu((current) => current === "alignment" ? null : "alignment")}><AlignLeft /><ChevronDown className="toolbar-chevron" /></button>
        <span />
        <button type="button" title="Lista numerada" aria-label="Lista numerada" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("insertOrderedList")}><ListOrdered /></button>
        <button type="button" title="Lista con viñetas" aria-label="Lista con viñetas" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("insertUnorderedList")}><List /></button>
        <button type="button" title="Reducir sangría" aria-label="Reducir sangría" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("outdent")}><IndentDecrease /></button>
        <button type="button" title="Aumentar sangría" aria-label="Aumentar sangría" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("indent")}><IndentIncrease /></button>
        <button type="button" title="Cita" aria-label="Cita" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("formatBlock", "blockquote")}><Quote /></button>
        <button type="button" title="Tachado" aria-label="Tachado" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("strikeThrough")}><Strikethrough /></button>
        <button type="button" title="Agregar o editar enlace" aria-label="Agregar o editar enlace" aria-expanded={linkEditor.open} onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => openLinkEditor()}><Link2 /></button>
        <span />
        <button type="button" title="Deshacer" aria-label="Deshacer" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("undo")}><Undo2 /></button>
        <button type="button" title="Rehacer" aria-label="Rehacer" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("redo")}><Redo2 /></button>
        <button type="button" title="Quitar formato" aria-label="Quitar formato" onMouseDown={(event) => { event.preventDefault(); captureSelection(); }} onClick={() => runCommand("removeFormat")}><RemoveFormatting /></button>
        {variableTokens.length > 0 && <div className="rich-template-token-menu"><select aria-label="Insertar campo dinámico" defaultValue="" onMouseDown={captureSelection} onChange={(event) => { runCommand("insertText", event.target.value); event.currentTarget.value = ""; }}><option value="" disabled>+ Campo dinámico</option>{variableTokens.map((token) => <option key={token.value} value={`{{${token.value}}}`}>{token.label}</option>)}</select></div>}
        {openFormatMenu === "color" && <div className="rich-toolbar-popover color-picker" role="menu" aria-label="Color de texto">{richEmailColors.map((color) => <button key={color} type="button" role="menuitem" aria-label={`Color ${color}`} style={{ backgroundColor: color }} onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("foreColor", color); setOpenFormatMenu(null); }} />)}</div>}
        {openFormatMenu === "alignment" && <div className="rich-toolbar-popover alignment-picker" role="menu" aria-label="Alineación del texto">
          <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyLeft"); setOpenFormatMenu(null); }}><AlignLeft />Izquierda</button>
          <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyCenter"); setOpenFormatMenu(null); }}><AlignCenter />Centro</button>
          <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyRight"); setOpenFormatMenu(null); }}><AlignRight />Derecha</button>
          <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyFull"); setOpenFormatMenu(null); }}><AlignJustify />Justificar</button>
        </div>}
        {linkEditor.open && <div className="rich-link-popover" role="dialog" aria-label="Editar enlace">
          <Link2 />
          <div><input autoFocus value={linkEditor.url} placeholder="Escribí o pegá un vínculo" aria-label="URL del enlace" onChange={(event) => setLinkEditor({ open: true, url: event.target.value, error: "" })} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyLink(); } if (event.key === "Escape") setLinkEditor({ open: false, url: "", error: "" }); }} />{linkEditor.error && <small>{linkEditor.error}</small>}</div>
          <button type="button" className="link-apply" onClick={applyLink}>Aplicar</button>
          <button type="button" className="link-cancel" aria-label="Cancelar enlace" onClick={() => { linkAnchorRef.current = null; setLinkEditor({ open: false, url: "", error: "" }); }}><X /></button>
        </div>}
      </div>
      <div ref={editorRef} className="rich-email-content" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" onKeyUp={captureSelection} onMouseUp={captureSelection} onInput={() => syncEditor()} onClick={(event) => { const anchor = (event.target as HTMLElement).closest("a"); if (anchor instanceof HTMLAnchorElement) { event.preventDefault(); openLinkEditor(anchor); } }} />
    </div>
    {showAttachments && <section className="message-attachments" aria-label="Archivos adjuntos">
      <div className="message-attachments-head"><div><strong>Archivos PDF</strong><small>Hasta 5 archivos · 10 MB por archivo · 20 MB en total</small></div><button type="button" className="secondary compact" disabled={pending || attachments.length >= 5} onClick={() => fileRef.current?.click()}><Paperclip />Adjuntar PDF</button></div>
      <input ref={fileRef} className="visually-hidden" type="file" accept="application/pdf,.pdf" onChange={(event) => { const file = event.target.files?.[0]; if (file) onUploadAttachment(file); event.currentTarget.value = ""; }} />
      {attachments.length > 0 ? <div className="message-attachment-list">{attachments.map((attachment) => <div key={attachment.id ?? `${attachment.name}-${attachment.sha256}`}><FileText /><span><strong>{attachment.name}</strong><small>{attachment.sizeBytes ? `${(attachment.sizeBytes / 1024 / 1024).toFixed(2)} MB` : `Versión ${attachment.version ?? 1}`}</small></span>{attachment.source === "MESSAGE" && attachment.id && <a className="icon-button" href={`/api/approvals/${approvalId}/attachments/${attachment.id}`} target="_blank" rel="noreferrer" aria-label={`Abrir ${attachment.name}`} title="Abrir PDF"><ExternalLink /></a>}{attachment.id && <button type="button" className="icon-button danger" disabled={pending} onClick={() => onRemoveAttachment(attachment.id!)} aria-label={`Quitar ${attachment.name}`} title="Quitar adjunto"><Trash2 /></button>}</div>)}</div> : <p className="message-attachments-empty">Todavía no hay archivos adjuntos.</p>}
    </section>}
    <small>{footerNote}</small>
    {Boolean(error) && <div className="form-error" role="alert">{error instanceof ApiError ? error.message : "No se pudo guardar la edición"}</div>}
    <div className="approval-editor-actions">
      <button className="secondary" disabled={pending} onClick={onCancel}>Cancelar</button>
      <button className="primary" disabled={pending || !subject.trim() || body.trim().length < 20} onClick={onSave}>{pending ? <LoaderCircle className="spin" /> : <Save />}Guardar cambios</button>
    </div>
  </div>;
}

export function BatchApprovalConfirmation({ count, pending, error, onCancel, onConfirm }: {
  count: number;
  pending: boolean;
  error?: unknown;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirmRef.current?.focus();
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onCancel();
    };
    document.addEventListener("keydown", closeWithEscape);
    return () => document.removeEventListener("keydown", closeWithEscape);
  }, [onCancel, pending]);
  return (
    <motion.div className="modal-backdrop" role="presentation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(event) => { if (event.target === event.currentTarget && !pending) onCancel(); }}>
      <motion.section className="modal batch-approval-confirmation" role="dialog" aria-modal="true" aria-labelledby="batch-approval-title" aria-describedby="batch-approval-description" initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}>
        <span className="eyebrow">APROBACIÓN POR LOTE</span>
        <h2 id="batch-approval-title">¿Aprobar {count} {count === 1 ? "mensaje" : "mensajes"}?</h2>
        <div id="batch-approval-description" className="batch-approval-explanation">
          <ShieldCheck />
          <div>
            <strong>Se aprobará el contenido actual de la selección.</strong>
            <p>La aprobación no implica un envío inmediato. Cada mensaje seguirá el modo vigente, SAFE SEND, el scheduler y el límite diario configurado.</p>
          </div>
        </div>
        {Boolean(error) && <div className="query-state error compact" role="alert"><CircleAlert /><strong>No se pudo aprobar el lote</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal. Podés reintentar sin duplicar la aprobación."}</p></div>}
        <div className="modal-actions">
          <button className="secondary" disabled={pending} onClick={onCancel}>Cancelar</button>
          <button ref={confirmRef} className="approve" disabled={pending} onClick={onConfirm}>{pending ? <LoaderCircle className="spin" /> : <CheckCircle2 />}Aprobar {count} {count === 1 ? "elemento" : "elementos"}</button>
        </div>
      </motion.section>
    </motion.div>
  );
}

export function ApprovalsPage() {
  const client = useQueryClient();
  const [selected, setSelected] = useState<Approval | null>(null);
  const [humanHistoryLimit, setHumanHistoryLimit] = useState(12);
  const [automaticHistoryLimit, setAutomaticHistoryLimit] = useState(12);
  const [selectedInitialIds, setSelectedInitialIds] = useState<string[]>([]);
  const [batchApprovalIntent, setBatchApprovalIntent] = useState<string[] | null>(null);
  const batchApprovalSubmitting = useRef(false);
  const [rejecting, setRejecting] = useState<Approval | null>(null);
  const [calendarEditId, setCalendarEditId] = useState<string | null>(null);
  const [calendarTitle, setCalendarTitle] = useState("");
  const [calendarBody, setCalendarBody] = useState("");
  const [calendarHtml, setCalendarHtml] = useState("");
  const [calendarTemplateId, setCalendarTemplateId] = useState("");
  const editCalendar = useMutation({
    mutationFn: ({ item, templateVersionId }: { item: Approval; templateVersionId?: string }) => api(`/meeting-invitations/${item.invitationDraft!.id}/content`, {
      method: "PATCH",
      body: JSON.stringify({ expectedPayloadHash: item.invitationDraft!.payloadHash, ...(templateVersionId ? { templateVersionId } : { title: calendarTitle, description: calendarBody }) }),
    }, { timeoutMs: 600_000 }),
    onSuccess: async () => { setCalendarEditId(null); setCalendarTemplateId(""); await client.invalidateQueries({ queryKey: ["approvals"] }); },
  });
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [editedBodyHtml, setEditedBodyHtml] = useState("");
  const [selectedTemplateVersionId, setSelectedTemplateVersionId] = useState("");
  const [proposalMemberIds, setProposalMemberIds] = useState<string[]>([]);
  const [proposalStarts, setProposalStarts] = useState<string[]>([]);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["approvals"],
    queryFn: () => api<{ items: Approval[]; outreachMode: "DRY_RUN" | "TEST" | "PRODUCTION"; safeSend: { required: boolean; emailRecipient: string | null } }>("/approvals"),
    refetchInterval: 4000,
  });
  const { data: selectedTemplates } = useQuery({
    queryKey: ["campaign-email-templates", selected?.campaign.id],
    queryFn: () => api<{ items: CampaignEmailTemplate[] }>(`/campaigns/${selected!.campaign.id}/email-templates`),
    enabled: Boolean(selected?.campaign.id && (selected?.message || selected?.meeting) && selected.status === "PENDING"),
  });
  useEffect(() => {
    if (!selected?.id || !data?.items) return;
    const fresh = data.items.find((item) => item.id === selected.id);
    if (!fresh) return;
    const wasAwaitingRegeneration = selected.regenerationRequest?.status === "REQUESTED" || selected.regenerationRequest?.status === "GENERATING";
    const replacementApprovalId = wasAwaitingRegeneration && fresh.regenerationRequest?.status === "PENDING_APPROVAL"
      ? fresh.regenerationRequest.newMessage?.approval?.id
      : null;
    const replacement = replacementApprovalId
      ? data.items.find((item) => item.id === replacementApprovalId)
      : null;
    setSelected(replacement ?? fresh);
  }, [data, selected?.id]);
  useEffect(() => setSelectedTemplateVersionId(""), [selected?.id]);
  useEffect(() => {
    const proposal = selected?.meetingProposal;
    if (!proposal) {
      setProposalMemberIds([]);
      setProposalStarts([]);
      return;
    }
    setProposalMemberIds(proposal.meetingTeam.map((member) => member.id));
    setProposalStarts(proposal.options.map((option) => localDateTimeValue(option.startsAt, option.timezone)));
  }, [selected?.id, selected?.payloadHash]);
  const decide = useMutation({
    mutationFn: ({ item, decision }: { item: Approval; decision: string }) =>
      api(item.invitationDraft?.operationKind === "UPDATE" ? `/meeting-invitation-revisions/${item.invitationDraft.id}/decision` : item.meeting ? `/meetings/${item.meeting.id}/decision` : `/approvals/${item.id}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => {
      setSelected(null);
      client.invalidateQueries({ queryKey: ["approvals"] });
      client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const editMessage = useMutation({
    mutationFn: ({ item, subject, body, bodyHtml }: { item: Approval; subject: string; body: string; bodyHtml: string }) =>
      api<{ ok: boolean; changed: boolean; payloadHash: string }>(`/approvals/${item.id}/message`, {
        method: "PATCH",
        body: JSON.stringify({ subject, body, bodyHtml, expectedPayloadHash: item.payloadHash }),
      }),
    onSuccess: async () => {
      setEditingMessageId(null);
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const updateMeetingProposal = useMutation({
    mutationFn: ({ item }: { item: Approval }) => {
      const proposal = item.meetingProposal;
      if (!proposal?.options.length || !item.payloadHash) throw new ApiError("MEETING_PROPOSAL_INVALID", "La propuesta no tiene datos suficientes para guardarse", 400);
      const timezone = proposal.options[0]!.timezone;
      const durationMinutes = Math.max(1, Math.round((Date.parse(proposal.options[0]!.endsAt) - Date.parse(proposal.options[0]!.startsAt)) / 60_000));
      return api(`/approvals/${item.id}/meeting-options`, {
        method: "PATCH",
        body: JSON.stringify({
          memberIds: proposalMemberIds,
          options: proposalStarts.map((value) => {
            const startsAt = zonedDateTimeLocalToUtc(value, timezone);
            return { startsAt, endsAt: new Date(Date.parse(startsAt) + durationMinutes * 60_000).toISOString(), timezone };
          }),
          expectedPayloadHash: item.payloadHash,
        }),
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const updateAttachment = useMutation({
    mutationFn: ({ item, include }: { item: Approval; include: boolean }) =>
      api<{ ok: boolean; changed: boolean; included: boolean; payloadHash: string }>(`/approvals/${item.id}/attachment`, {
        method: "PATCH",
        body: JSON.stringify({ include, expectedPayloadHash: item.payloadHash ?? item.message?.contentHash }),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const uploadMessageAttachment = useMutation({
    mutationFn: async ({ item, file }: { item: Approval; file: File }) => {
      if (!/\.pdf$/i.test(file.name) || (file.type && file.type !== "application/pdf"))
        throw new ApiError("PDF_INVALID", "Seleccioná un archivo PDF", 400);
      if (file.size > 10 * 1024 * 1024)
        throw new ApiError("PDF_SIZE_INVALID", "Cada PDF debe pesar como máximo 10 MB", 413);
      return api(`/approvals/${item.id}/attachments`, { method: "POST", body: JSON.stringify({ filename: file.name, mimeType: "application/pdf", contentBase64: await fileAsBase64(file), expectedPayloadHash: item.payloadHash }) }, { timeoutMs: 60_000 });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const removeMessageAttachment = useMutation({
    mutationFn: ({ item, attachmentId }: { item: Approval; attachmentId: string }) => api(`/approvals/${item.id}/attachments/${attachmentId}`, { method: "DELETE", body: JSON.stringify({ expectedPayloadHash: item.payloadHash }) }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const rejectProposal = useMutation({
    mutationFn: ({ item, mode, reason }: { item: Approval; mode: "MANUAL_REVIEW" | "REGENERATE"; reason: string }) =>
      api<{ ok: boolean; status: string; regeneration?: { id: string; status: string } | null }>(`/approvals/${item.id}/reject`, {
        method: "POST",
        body: JSON.stringify({ mode, reason, expectedPayloadHash: item.payloadHash }),
      }, { timeoutMs: 20_000 }),
    onSuccess: async () => {
      setRejecting(null);
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const regenerateInvitation = useMutation({
    mutationFn: (meetingId: string) => api(`/meetings/${meetingId}/regenerate-invitation`, { method: "POST" }, { timeoutMs: 600_000 }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["approvals"] }),
  });
  const retryRegeneration = useMutation({
    mutationFn: (requestId: string) => api(`/commercial-reply-regenerations/${requestId}/retry`, { method: "POST" }, { timeoutMs: 20_000 }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const approveBatch = useMutation({
    mutationFn: (messageIds: string[]) => api("/approvals/batch", { method: "POST", body: JSON.stringify({ messageIds }) }),
    onSuccess: () => {
      setBatchApprovalIntent(null);
      setSelectedInitialIds([]);
      setSelected(null);
      client.invalidateQueries({ queryKey: ["approvals"] });
    },
    onSettled: () => {
      batchApprovalSubmitting.current = false;
    },
  });
  const applyTemplate = useMutation({
    mutationFn: ({ item, templateVersionId }: { item: Approval; templateVersionId: string }) => api(`/approvals/${item.id}/apply-email-template`, {
      method: "POST",
      body: JSON.stringify({ templateVersionId, expectedPayloadHash: item.payloadHash ?? item.message?.contentHash }),
    }),
    onSuccess: async () => {
      setEditingMessageId(null);
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const restoreAiDraft = useMutation({
    mutationFn: ({ item }: { item: Approval }) => api(`/approvals/${item.id}/restore-ai-draft`, {
      method: "POST",
      body: JSON.stringify({ expectedPayloadHash: item.payloadHash ?? item.message?.contentHash }),
    }),
    onSuccess: async () => {
      setEditingMessageId(null);
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const applyTemplatesBatch = useMutation({
    mutationFn: () => api("/approvals/apply-email-templates", {
      method: "POST",
      body: JSON.stringify({ approvalIds: (data?.items ?? []).filter((item) => item.message && selectedInitialIds.includes(item.message.id)).map((item) => item.id), mode: "RECOMMENDED" }),
    }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["approvals"] });
      await client.invalidateQueries({ queryKey: ["campaign"] });
    },
  });
  const items = data?.items ?? [];
  const pending = items.filter((item) => item.status === "PENDING" && item.sendability?.sendable !== false);
  const { human: humanHistory, automatic: automaticHistory } = splitApprovalHistory(items);
  const selectedClaims = parsedArray<{ claim: string; supporting_fact: string }>(selected?.message?.claimsUsedJson);
  const selectedAudit = parsedClaimAudit(selected?.message?.claimAuditJson);
  const selectedAttachments = parsedArray<{ id?: string; source?: "ASSET" | "MESSAGE"; name: string; version?: number; sha256?: string; sizeBytes?: number; mimeType?: string }>(selected?.message?.attachmentsJson);
  const selectedInbound = selected?.sourceMessage;
  const selectedClassification = parsedObject<InboundClassificationDetail>(selectedInbound?.classificationJson);
  const selectedIntent = selectedClassification?.semantic_intent && selectedClassification.semantic_intent !== "OTHER"
    ? selectedClassification.semantic_intent
    : selectedClassification?.category ?? selectedInbound?.classification ?? "NO DISPONIBLE";
  const selectedGeneration = parsedObject<CommercialReplyMetadata>(selected?.message?.generationMetadataJson);
  const selectedOriginalGeneration = parsedObject<{ subject: string; body: string; claims_used: Array<{ claim: string; supporting_fact: string }>; source_evidence_used: string[]; agent: string; prompt_version: string; hermesTaskId?: string; hermesRunId?: string; receivedAt: string; hash: string }>(selected?.message?.originalGenerationJson);
  const selectedWarnings = uniqueText([
    ...(selectedClassification?.warnings ?? []),
    ...(selectedGeneration?.warnings ?? []),
    ...(selectedGeneration?.qualityWarnings ?? []),
    ...(selectedGeneration?.review?.warnings ?? []),
    selectedGeneration?.humanEdit?.edited && selectedGeneration.activeDraftSource === "HUMAN_EDIT"
      ? "El contenido fue editado por una persona. Revisá la versión final completa antes de aprobarla; la auditoría automática corresponde al borrador original."
      : undefined,
  ]);
  const selectedInvitation = parsedObject<MeetingInvitationContent>(selected?.invitationDraft?.contentJson);
  const selectedMeetingTeam = parsedArray<MeetingTeamMember>(selected?.meeting?.meetingTeamJson);
  const selectedCalendarPayload = parsedObject<CalendarMeetingPayload>(selected?.invitationDraft?.calendarPayloadJson);
  const selectedBaseline = parsedObject<{ summary: string; description: string; organizerEmail: string; startsAt: string; endsAt: string; timezone: string; attendees: string[]; hasGoogleMeet: boolean; reminders: unknown; visibility: string | null; status: string | null }>(selected?.invitationDraft?.baselineSnapshotJson);
  const selectedPatch = parsedObject<{ summary: string; description: string }>(selected?.invitationDraft?.patchPayloadJson);
  const selectedChangedFields = parsedArray<string>(selected?.invitationDraft?.changedFieldsJson);
  const isMeetingUpdate = selected?.invitationDraft?.operationKind === "UPDATE";
  const calendarEditing = Boolean(selected?.invitationDraft && calendarEditId === selected.invitationDraft.id);
  const calendarChoices = (selectedTemplates?.items ?? []).filter(t => t.type === (isMeetingUpdate ? "CALENDAR_UPDATE" : "CALENDAR_INVITATION")).flatMap(t => t.versions.filter(v => v.status === "PUBLISHED").map(v => ({ template: t, version: v })));
  const beginCalendarEdit = () => {
    editCalendar.reset(); setCalendarEditId(selected!.invitationDraft!.id); setCalendarTitle(selectedInvitation?.title ?? selected!.meeting!.title);
    const body = selectedCalendarPayload?.description ?? selected!.invitationDraft!.description;
    setCalendarBody(body); setCalendarHtml(body.split("\n").map(line => `<div>${line.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;") || "<br>"}</div>`).join(""));
  };

  const isInitialOutreach = selected?.message?.actionKind === "INITIAL";
  const recommendedTemplateType = selected?.message ? recommendedTemplateTypeForMessage(selected.message) : null;
  const compatibleTemplates = templatesInFlowOrder(selectedTemplates?.items ?? []).filter((template) => initialTemplateCompatible(selected?.message?.actionKind, template.type));
  const compatibleTemplateChoices = compatibleTemplates.flatMap((template) => {
    const snapshotVersionId = selected?.emailTemplateSnapshot?.find((item) => item.type === template.type)?.versionId;
    const version = snapshotVersionId ? template.versions.find((item) => item.id === snapshotVersionId) : template.currentVersion;
    return version ? [{ template, version }] : [];
  });
  const aiDraftOptionValue = "AI_GENERATED_DRAFT";
  const aiDraftAvailable = selected?.message?.aiProvider !== "TEMPLATE" && selectedGeneration?.author?.provider !== "TEMPLATE" && Boolean(selectedGeneration?.aiDraftBaseline || selectedOriginalGeneration);
  const effectiveTemplateVersionId = selectedTemplateVersionId || compatibleTemplateChoices.find(({ template }) => template.type === recommendedTemplateType)?.version.id || compatibleTemplateChoices[0]?.version.id || (aiDraftAvailable ? aiDraftOptionValue : "");
  const alternativeDraftError = applyTemplate.error ?? restoreAiDraft.error;
  const individualRecipient = selected?.audienceType === "B2C";
  const selectedCompany = selected?.message && !individualRecipient ? approvalCompanyDetails(selected.recipientDetails?.company ? { ...selected.message.contact.company, name: selected.recipientDetails.company.name ?? "", domain: selected.recipientDetails.company.domain ?? "", industry: selected.recipientDetails.company.industry ?? "", country: selected.recipientDetails.company.country ?? "", employees: selected.recipientDetails.company.employees ?? 0 } : selected.message.contact.company) : null;
  const isEditingSelected = Boolean(selected?.message && editingMessageId === selected.message.id);
  const selectedMeetingProposal = selected?.message?.actionKind === "MEETING_OPTIONS" ? selected.meetingProposal : null;
  const proposalTimezone = selectedMeetingProposal?.options[0]?.timezone ?? "America/Argentina/Buenos_Aires";
  const proposalDurationMinutes = selectedMeetingProposal?.options[0]
    ? Math.max(1, Math.round((Date.parse(selectedMeetingProposal.options[0].endsAt) - Date.parse(selectedMeetingProposal.options[0].startsAt)) / 60_000))
    : 30;
  const meetingProposalValid = proposalMemberIds.length > 0 && proposalStarts.length >= 2 && proposalStarts.every(Boolean) && new Set(proposalStarts).size === proposalStarts.length;
  const beginMessageEdit = () => {
    if (!selected?.message) return;
    editMessage.reset();
    setEditedSubject(selected.message.subject ?? "");
    setEditedBody(selected.message.body);
    setEditedBodyHtml(selected.message.bodyHtml ?? emailTextToHtml(selected.message.body));
    setEditingMessageId(selected.message.id);
  };
  const cancelMessageEdit = () => {
    if (editMessage.isPending || uploadMessageAttachment.isPending || removeMessageAttachment.isPending) return;
    editMessage.reset();
    uploadMessageAttachment.reset();
    removeMessageAttachment.reset();
    setEditingMessageId(null);
  };
  const confirmBatchApproval = () => {
    if (!batchApprovalIntent?.length || batchApprovalSubmitting.current) return;
    batchApprovalSubmitting.current = true;
    approveBatch.mutate([...batchApprovalIntent]);
  };
  return (
    <>
      <PageTitle
        eyebrow="APROBACIONES"
        title="Control humano antes de cada salida"
        body="Revisá el contenido exacto. Si cambia después de aprobarse, la autorización se invalida."
      />
      <div className="approval-layout">
        <section className="approval-queue">
          <div className="section-head">
            <h2>Pendientes</h2>
            <span>{pending.length}</span>
          </div>
          <div className={`approval-refresh${isFetching && !isLoading ? " active" : " idle"}`} role="status" aria-live="polite" aria-hidden={!(isFetching && !isLoading)}><RefreshCcw className={isFetching && !isLoading ? "spin" : ""} size={13} />Actualizando sin ocultar la selección…</div>
          {selectedInitialIds.length > 0 && <div className="batch-bar"><div><strong>{selectedInitialIds.length} mensajes iniciales seleccionados</strong><small>Podés aplicar la plantilla recomendada sin aprobar, o aprobar el contenido actual.</small></div><div className="batch-actions"><button className="secondary" disabled={applyTemplatesBatch.isPending} onClick={() => applyTemplatesBatch.mutate()}>{applyTemplatesBatch.isPending ? <LoaderCircle className="spin" /> : <FileText />}Aplicar plantillas</button><button className="approve" disabled={approveBatch.isPending} onClick={() => { approveBatch.reset(); setBatchApprovalIntent([...selectedInitialIds]); }}><CheckCircle2 />Aprobar lote</button></div></div>}
          {approveBatch.error && <div className="form-error">{approveBatch.error instanceof ApiError ? approveBatch.error.message : "No se pudo aprobar el lote"}</div>}
          {applyTemplatesBatch.error && <div className="form-error">{applyTemplatesBatch.error instanceof ApiError ? applyTemplatesBatch.error.message : "No se pudieron aplicar las plantillas"}</div>}
          {isLoading ? <div className="loading" role="status">Cargando aprobaciones…</div> : isError ? <div className="query-state error" role="alert"><CircleAlert /><strong>No se pudieron cargar las aprobaciones</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal."}</p><button className="secondary" onClick={() => void refetch()}><RefreshCcw size={15} />Reintentar</button></div> : pending.length ? (
            pending.map((item) => (
              <button
                key={item.id}
                className={selected?.id === item.id ? "selected" : ""}
                onClick={() => setSelected(item)}
              >
                <span className="batch-selector">{item.message?.actionKind === "INITIAL" && <input aria-label="Seleccionar para aprobación por lote" type="checkbox" checked={selectedInitialIds.includes(item.message.id)} onClick={(event) => event.stopPropagation()} onChange={(event) => setSelectedInitialIds((current) => event.target.checked ? [...current, item.message!.id] : current.filter((id) => id !== item.message!.id))} />}</span>
                <span className="avatar">
                  {item.message?.contact.firstName.slice(0, 1) ?? "R"}
                </span>
                <div>
                  <strong>
                    {item.message ? `${item.message.contact.firstName} ${item.message.contact.lastName}` : item.meeting?.title ?? "Reunión"}
                  </strong>
                  <small>
                    {item.campaign.name}{item.message ? item.audienceType === "B2C" ? "" : ` · ${item.message.contact.company.name}` : " · aprobación de Calendar"}
                  </small>
                  <small>{presentationText(item.message?.actionKind ?? item.kind ?? "SALIDA")} · {formatDate(item.createdAt)}</small>
                </div>
                <Clock3 />
                <ChevronRight />
              </button>
            ))
          ) : (
            <Empty
              title="Bandeja al día"
              body="No hay salidas esperando decisión."
            />
          )}
          {humanHistory.length > 0 && <details className="approval-history"><summary>Decisiones humanas <span>{humanHistory.length}</span></summary>{humanHistory.slice(0, humanHistoryLimit).map((item) => <button key={item.id} className={selected?.id === item.id ? "selected" : ""} onClick={() => setSelected(item)}><Status value={item.status} /><span><strong>{item.message?.subject ?? item.meeting?.title ?? "Aprobación histórica"}</strong><small>{item.campaign.name} · {formatDate(item.createdAt)}</small></span><ChevronRight size={16} /></button>)}<div className="approval-history-pagination"><span>Mostrando {Math.min(humanHistoryLimit, humanHistory.length)} de {humanHistory.length}</span>{humanHistory.length > humanHistoryLimit && <button className="secondary" onClick={() => setHumanHistoryLimit((limit) => limit + 12)}>Ver 12 más</button>}</div></details>}
          {automaticHistory.length > 0 && <details className="approval-history"><summary>Registros automáticos <span>{automaticHistory.length}</span></summary>{automaticHistory.slice(0, automaticHistoryLimit).map((item) => <button key={item.id} className={selected?.id === item.id ? "selected" : ""} onClick={() => setSelected(item)}><Status value={item.status} /><span><strong>{item.message?.subject ?? item.meeting?.title ?? "Registro automático"}</strong><small>{item.campaign.name} · {formatDate(item.createdAt)} · {item.requestedBy === "SYSTEM:CLAIM_CHECK" ? "Validación automática de claims" : "Acción del sistema"}</small></span><ChevronRight size={16} /></button>)}<div className="approval-history-pagination"><span>Mostrando {Math.min(automaticHistoryLimit, automaticHistory.length)} de {automaticHistory.length}</span>{automaticHistory.length > automaticHistoryLimit && <button className="secondary" onClick={() => setAutomaticHistoryLimit((limit) => limit + 12)}>Ver 12 más</button>}</div></details>}
        </section>
        <section className="approval-preview">
          {selected?.message ? (
            <>
              <div className="preview-head">
                <div>
                  <span className="eyebrow">{isInitialOutreach ? "BORRADOR DE OUTREACH" : "RESPUESTA COMERCIAL A REVISAR"}</span>
                  <h2>{selected.message.subject}</h2>
                </div>
                <Status value={selected.status} />
              </div>
              {selected.status !== "PENDING" && <div className="approval-replaced-state" role="status"><CircleAlert /><div><strong>{selected.status === "INVALIDATED" || selected.status === "SUPERSEDED" ? "Contenido reemplazado" : "Aprobación resuelta"}</strong><p>Esta versión se conserva como evidencia histórica y ya no puede aprobarse ni enviarse.</p></div></div>}
              {selected.sendability?.sendable === false && <div className="approval-replaced-state" role="alert"><CircleAlert /><div><strong>Artefacto no enviable</strong><p>Este resultado es un diagnóstico operativo y no un mensaje para el contacto. No puede aprobarse, encolarse ni enviarse.</p><ul>{selected.sendability.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div></div>}
              <div className="approval-primary-meta" aria-label="Metadatos principales de la propuesta">
                <div><small>Intención</small><strong>{presentationText(selectedIntent)}</strong></div>
                <div><small>Confianza</small><strong>{typeof selectedClassification?.confidence === "number" ? `${Math.round(selectedClassification.confidence * 100)}%` : "—"}</strong></div>
                <div><small>Agente y perfil</small><strong>{selected.message.aiAgent ?? "SDR IA"} · {selected.message.aiProfile ?? selectedGeneration?.profile ?? "—"}</strong></div>
                <div><small>Proveedor y modelo</small><strong>{selectedGeneration?.author?.provider ?? selected.message.aiProvider ?? selectedGeneration?.provider ?? "—"} · {selectedGeneration?.author?.model ?? selectedGeneration?.model ?? "—"}</strong></div>
                <div><small>Generación</small><strong>{selected.message.aiProvider === "TEMPLATE" ? "Plantilla" : presentationText(selectedGeneration?.generationMode ?? "NO DISPONIBLE")}</strong></div>
              </div>
              <ApprovalRecipientCard key={selected.message.contact.id} contact={selected.message.contact} details={selected.recipientDetails} individual={individualRecipient} />
              {selectedCompany && <section className="approval-company-card" aria-labelledby="approval-company-title">
                <div className="approval-company-heading">
                  <div className="approval-company-icon" aria-hidden="true"><Building2 size={19} /></div>
                  <div>
                    <span className="eyebrow">EMPRESA</span>
                    <h3 id="approval-company-title">Información de la empresa</h3>
                    <p>Contexto de la organización utilizado para revisar la pertinencia del mensaje.</p>
                  </div>
                </div>
                <dl className="approval-company-grid">
                  <div><dt>Empresa</dt><dd>{selectedCompany.name}</dd></div>
                  <div><dt>Industria</dt><dd>{selectedCompany.industry}</dd></div>
                  <div><dt>País</dt><dd>{selectedCompany.country}</dd></div>
                  <div><dt>Tamaño</dt><dd>{selectedCompany.employees}</dd></div>
                  <div><dt>Dominio</dt><dd>{selectedCompany.websiteUrl ? <a href={selectedCompany.websiteUrl} target="_blank" rel="noreferrer">{selectedCompany.domain}<ExternalLink size={13} aria-hidden="true" /></a> : selectedCompany.domain}</dd></div>
                </dl>
              </section>}
              {data?.outreachMode === "TEST" && (
                <div className="safe-send-recipient">
                  <ShieldCheck />
                  <div><strong>SAFE SEND · modo TEST</strong><p>Destinatario lógico: <b>{selected.message.intendedRecipient}</b>. Destinatario efectivo: <b>{selected.message.actualRecipient ?? data.safeSend.emailRecipient ?? "la casilla interna configurada"}</b>. El contacto externo no será contactado.</p></div>
                </div>
              )}
              {isInitialOutreach && selected.attachmentOption && <section className="approval-attachment-choice" aria-labelledby="approval-attachment-title">
                <PackageOpen aria-hidden="true" />
                <div>
                  <strong id="approval-attachment-title">Adjunto del correo inicial</strong>
                  {selected.attachmentOption.asset ? <>
                    <p>{selected.attachmentOption.asset.name} · v{selected.attachmentOption.asset.version}</p>
                    <small>Este PDF acompaña únicamente a este primer correo. No se repetirá en seguimientos, respuestas ni invitaciones de reunión.</small>
                  </> : <>
                    <p>La campaña no tiene un PDF configurado para enviar.</p>
                    <small>Los documentos aprobados en la oferta sirven como evidencia; no se adjuntan automáticamente.</small>
                  </>}
                  {updateAttachment.error && <p className="inline-error" role="alert">{updateAttachment.error instanceof ApiError ? updateAttachment.error.message : "No se pudo cambiar el adjunto."}</p>}
                </div>
                {selected.attachmentOption.asset && <label className="approval-attachment-toggle">
                  <input
                    type="checkbox"
                    checked={selected.attachmentOption.included}
                    disabled={updateAttachment.isPending || (!selected.attachmentOption.included && (!selected.attachmentOption.asset.approved || !selected.attachmentOption.asset.active))}
                    onChange={(event) => updateAttachment.mutate({ item: selected, include: event.target.checked })}
                  />
                  <span>{updateAttachment.isPending ? "Guardando…" : selected.attachmentOption.included ? "Enviar PDF" : "No enviar PDF"}</span>
                </label>}
              </section>}
              {selected.delivery?.status === "CANCELLED" && <div className="approval-delivery-state error" role="alert"><CircleAlert /><div><strong>El correo no fue enviado</strong><p>{selected.delivery.lastError === "OUTREACH_PERIOD_ENDED" ? "La aprobación se realizó después de la fecha configurada para enviar primeros contactos. Gmail no fue contactado y SAFE SEND no generó ningún correo." : `La acción de envío fue cancelada${selected.delivery.lastError ? `: ${presentationText(selected.delivery.lastError, "cause")}` : "."}`}</p><small>Cancelado {formatDate(selected.delivery.updatedAt)} · 0 envíos al proveedor</small></div></div>}
              {selected.delivery?.status === "FAILED_REQUIRES_REVIEW" && <div className="approval-delivery-state error" role="alert"><CircleAlert /><div><strong>El envío requiere revisión</strong><p>{presentationText(selected.delivery.lastError ?? "No se pudo completar el envío", "cause")}</p><small>Intentos registrados: {selected.delivery.attempts}</small></div></div>}
              {selectedWarnings.length > 0 && <div className="approval-warning" role="status"><CircleAlert /><div><strong>Advertencias para la revisión</strong><ul>{selectedWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div></div>}
              {selected.status === "PENDING" && selectedMeetingProposal && <section className="meeting-proposal-editor" aria-labelledby="meeting-proposal-editor-title">
                <div className="meeting-proposal-editor-head">
                  <div><span className="eyebrow">COORDINACIÓN MANUAL</span><h3 id="meeting-proposal-editor-title">Horarios y equipo de esta propuesta</h3><p>Confirmá acá lo acordado internamente por WhatsApp o teléfono. No se consulta la disponibilidad de Calendar.</p></div>
                  <span>{proposalDurationMinutes} min · {proposalTimezone}</span>
                </div>
                <div className="meeting-proposal-columns">
                  <fieldset>
                    <legend>Participantes de Vicnet</legend>
                    {selectedMeetingProposal.availableMembers.map((member) => <label className="checkbox-row" key={member.id}>
                      <input type="checkbox" checked={proposalMemberIds.includes(member.id)} onChange={(event) => setProposalMemberIds((current) => event.target.checked ? [...current, member.id] : current.filter((id) => id !== member.id))} />
                      <span><strong>{member.firstName} {member.lastName}</strong><small>{member.jobTitle} · {member.email}</small></span>
                    </label>)}
                    {!selectedMeetingProposal.availableMembers.length && <p className="muted">La campaña no tiene integrantes disponibles.</p>}
                  </fieldset>
                  <fieldset>
                    <legend>Opciones que se enviarán</legend>
                    {proposalStarts.map((value, index) => <div className="meeting-proposal-slot" key={index}>
                      <label><span>Opción {index + 1}</span><input type="datetime-local" value={value} onChange={(event) => setProposalStarts((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} /></label>
                      <button type="button" className="icon-button danger" aria-label={`Quitar opción ${index + 1}`} disabled={proposalStarts.length <= 2} onClick={() => setProposalStarts((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 /></button>
                    </div>)}
                    <button type="button" className="secondary compact" disabled={proposalStarts.length >= 8} onClick={() => {
                      const latest = proposalStarts[proposalStarts.length - 1];
                      const next = latest ? new Date(`${latest}:00`) : new Date();
                      next.setDate(next.getDate() + 1);
                      setProposalStarts((current) => [...current, localDateTimeInputValue(next)]);
                    }}><Plus />Agregar horario</button>
                  </fieldset>
                </div>
                {updateMeetingProposal.error && <div className="form-error" role="alert">{updateMeetingProposal.error instanceof ApiError ? updateMeetingProposal.error.message : "No se pudo actualizar la propuesta"}</div>}
                {!meetingProposalValid && <small className="inline-error">Seleccioná al menos un integrante y dos horarios distintos.</small>}
                <div className="meeting-proposal-actions"><button type="button" className="primary" disabled={!meetingProposalValid || updateMeetingProposal.isPending} onClick={() => updateMeetingProposal.mutate({ item: selected })}>{updateMeetingProposal.isPending ? <LoaderCircle className="spin" /> : <Save />}Actualizar propuesta</button></div>
              </section>}
              {selected.status === "PENDING" && recommendedTemplateType && (compatibleTemplateChoices.length > 0 || aiDraftAvailable) && <section className="approval-template-picker" aria-label="Aplicar alternativa de redacción">
                <div><span className="eyebrow">PLANTILLA OPCIONAL</span><strong>Usar una alternativa de la campaña</strong><small>Reemplaza el asunto y cuerpo de este borrador. No lo aprueba ni lo envía.</small></div>
                <select value={effectiveTemplateVersionId} onChange={(event) => setSelectedTemplateVersionId(event.target.value)} aria-label="Plantilla de correo">
                  {aiDraftAvailable && <option value={aiDraftOptionValue}>Borrador generado por IA · original</option>}
                  {compatibleTemplateChoices.map(({ template, version }) => <option key={version.id} value={version.id}>{template.label}{template.type === recommendedTemplateType ? " · recomendada" : ""} · v{version.version}</option>)}
                </select>
                <button className="secondary" disabled={!effectiveTemplateVersionId || applyTemplate.isPending || restoreAiDraft.isPending} onClick={() => {
                  if (!selected) return;
                  if (effectiveTemplateVersionId === aiDraftOptionValue) restoreAiDraft.mutate({ item: selected });
                  else applyTemplate.mutate({ item: selected, templateVersionId: effectiveTemplateVersionId });
                }}>{applyTemplate.isPending || restoreAiDraft.isPending ? <LoaderCircle className="spin" /> : <FileText />}Aplicar</button>
                {alternativeDraftError && <div className="form-error">{alternativeDraftError instanceof ApiError ? alternativeDraftError.message : "No se pudo aplicar la alternativa"}</div>}
              </section>}
              {isInitialOutreach ? <div className="approval-message-comparison approval-outreach-draft">
                <article className="proposed">
                  <header><div><span className="eyebrow">BORRADOR DE OUTREACH</span><h3>{selected.message.subject || "Sin asunto"}</h3></div>{selected.status === "PENDING" && !isEditingSelected ? <button className="secondary compact" onClick={beginMessageEdit}>Editar correo</button> : <Send size={18} />}</header>
                  <div className="message-participants"><small>Para</small><strong>{selected.message.intendedRecipient ?? "Destinatario no definido"}</strong><small>Creado</small><strong>{formatDate(selected.message.createdAt)}</strong></div>
                  <ApprovalMessageEditor editing={isEditingSelected} subject={editedSubject} body={isEditingSelected ? editedBody : selected.message.body} bodyHtml={isEditingSelected ? editedBodyHtml : selected.message.bodyHtml ?? ""} attachments={selectedAttachments} approvalId={selected.id} pending={editMessage.isPending || uploadMessageAttachment.isPending || removeMessageAttachment.isPending} error={editMessage.error ?? uploadMessageAttachment.error ?? removeMessageAttachment.error} onSubjectChange={setEditedSubject} onBodyChange={setEditedBody} onBodyHtmlChange={setEditedBodyHtml} onUploadAttachment={(file) => uploadMessageAttachment.mutate({ item: selected, file })} onRemoveAttachment={(attachmentId) => removeMessageAttachment.mutate({ item: selected, attachmentId })} onCancel={cancelMessageEdit} onSave={() => editMessage.mutate({ item: selected, subject: editedSubject, body: editedBody, bodyHtml: editedBodyHtml })} />
                </article>
                {isInitialOutreach && selectedOriginalGeneration && <details className="technical-disclosure approval-technical"><summary>Borrador original del agente — trazabilidad</summary><div className="approval-context-grid"><div className="full"><small>Asunto original</small><strong>{selectedOriginalGeneration.subject}</strong></div><div className="full"><small>Texto original</small><pre>{selectedOriginalGeneration.body}</pre></div><div className="full"><small>Claims y evidencia originales</small><strong>{selectedOriginalGeneration.claims_used.map((claim) => claim.claim).join(" · ") || "Sin claims"}</strong><p>{selectedOriginalGeneration.source_evidence_used.join(" · ") || "Sin evidencia"}</p></div><div><small>Task / run</small><strong>{selectedOriginalGeneration.hermesTaskId ?? "—"} · {selectedOriginalGeneration.hermesRunId ?? "—"}</strong></div><div><small>Hash</small><strong className="technical-value">{selectedOriginalGeneration.hash}</strong></div></div></details>}
              </div> : <div className={`approval-message-comparison approval-reply-comparison${isEditingSelected ? " editing-message" : ""}`}>
                {isEditingSelected ? <details className="approval-source-message">
                  <summary>
                    <div><span className="eyebrow">MENSAJE RECIBIDO</span><strong>{selectedInbound?.subject ?? "Sin asunto registrado"}</strong><small>{selectedInbound?.intendedRecipient ?? selected.message.contact.email} · {formatDate(selectedInbound?.providerTimestamp ?? selectedInbound?.createdAt)}</small></div>
                    <ChevronDown size={17} aria-hidden="true" />
                  </summary>
                  <div className="message-participants"><small>De</small><strong>{selectedInbound?.intendedRecipient ?? selected.message.contact.email}</strong><small>Recibido</small><strong>{formatDate(selectedInbound?.providerTimestamp ?? selectedInbound?.createdAt)}</strong></div>
                  <pre>{selectedClassification?.latest_inbound_text ?? selectedInbound?.body ?? "El mensaje de origen no está disponible."}</pre>
                </details> : <article>
                  <header><div><span className="eyebrow">MENSAJE RECIBIDO</span><h3>{selectedInbound?.subject ?? "Sin asunto registrado"}</h3></div><Mail size={18} /></header>
                  <div className="message-participants"><small>De</small><strong>{selectedInbound?.intendedRecipient ?? selected.message.contact.email}</strong><small>Recibido</small><strong>{formatDate(selectedInbound?.providerTimestamp ?? selectedInbound?.createdAt)}</strong></div>
                  <pre>{selectedClassification?.latest_inbound_text ?? selectedInbound?.body ?? "El mensaje de origen no está disponible."}</pre>
                </article>}
                <article className="proposed">
                  <header><div><span className="eyebrow">RESPUESTA PROPUESTA</span><h3>{selected.message.subject}</h3></div>{selected.status === "PENDING" && !isEditingSelected ? <button className="secondary compact" onClick={beginMessageEdit}>Editar correo</button> : <Send size={18} />}</header>
                  <div className="message-participants"><small>Para</small><strong>{selected.message.intendedRecipient}</strong><small>Creada</small><strong>{formatDate(selected.message.createdAt)}</strong></div>
                  <ApprovalMessageEditor editing={isEditingSelected} subject={editedSubject} body={isEditingSelected ? editedBody : selected.message.body} bodyHtml={isEditingSelected ? editedBodyHtml : selected.message.bodyHtml ?? ""} attachments={selectedAttachments} approvalId={selected.id} pending={editMessage.isPending || uploadMessageAttachment.isPending || removeMessageAttachment.isPending} error={editMessage.error ?? uploadMessageAttachment.error ?? removeMessageAttachment.error} onSubjectChange={setEditedSubject} onBodyChange={setEditedBody} onBodyHtmlChange={setEditedBodyHtml} onUploadAttachment={(file) => uploadMessageAttachment.mutate({ item: selected, file })} onRemoveAttachment={(attachmentId) => removeMessageAttachment.mutate({ item: selected, attachmentId })} onCancel={cancelMessageEdit} onSave={() => editMessage.mutate({ item: selected, subject: editedSubject, body: editedBody, bodyHtml: editedBodyHtml })} />
                </article>
              </div>}
              {(selectedClassification?.questions?.length ?? 0) > 0 && <section className="question-coverage"><div className="section-head"><h3>Preguntas detectadas y cobertura</h3><span>{selectedClassification?.questions?.length}</span></div>{selectedClassification?.questions?.map((question) => { const covered = selectedGeneration?.questionsCovered?.find((item) => item.question_id === question.id); return <div key={question.id}><CheckCircle2 size={17} /><span><strong>{question.text}</strong><small>{covered?.answer_evidence ?? "Sin evidencia de respuesta declarada"}</small></span></div>; })}</section>}
              {(selectedGeneration?.missingOfferInformation?.length ?? 0) > 0 && <section className="offer-gaps" aria-label="Información faltante en la oferta"><CircleAlert /><div><strong>Información que la oferta todavía no respalda</strong><ul>{selectedGeneration?.missingOfferInformation?.map((item) => <li key={item}>{item}</li>)}</ul><p>La respuesta debe declarar estos límites; no puede completarlos por inferencia.</p></div></section>}
              {selected.regenerationRequest && <section className={`regeneration-state ${selected.regenerationRequest.status.toLowerCase()}`} role="status"><RefreshCcw className={selected.regenerationRequest.status === "GENERATING" ? "spin" : ""} /><div><strong>{selected.regenerationRequest.status === "GENERATING" || selected.regenerationRequest.status === "REQUESTED" ? "Preparando una nueva versión" : selected.regenerationRequest.status === "PENDING_APPROVAL" ? "Nueva versión disponible para aprobación" : selected.regenerationRequest.status === "REVIEW_REJECTED" ? "Director IA rechazó la nueva redacción" : "No se pudo generar la nueva versión"}</strong><p>{selected.regenerationRequest.errorMessage ?? `Motivo del rechazo: ${selected.regenerationRequest.reason}`}</p><small>Solicitud {selected.regenerationRequest.id} · no se realizó ningún envío.</small>{selected.regenerationRequest.status === "FAILED" && <button className="secondary" disabled={retryRegeneration.isPending} onClick={() => retryRegeneration.mutate(selected.regenerationRequest!.id)}>{retryRegeneration.isPending ? <LoaderCircle className="spin" /> : <RefreshCcw />}Reintentar generación</button>}{retryRegeneration.error && <p className="inline-error" role="alert">{retryRegeneration.error instanceof ApiError ? retryRegeneration.error.message : "No se pudo iniciar el reintento."}</p>}</div></section>}
              <div className="approval-context-grid">
                <div className="full"><small>Claims declarados por el redactor</small>{selectedClaims.length ? selectedClaims.map((claim) => <p key={`${claim.claim}:${claim.supporting_fact}`}><strong>{claim.claim}</strong><span>Soporte: {claim.supporting_fact}</span></p>) : <p><span>No declaró claims factuales adicionales.</span></p>}</div>
                <div className="full"><small>Auditoría independiente de claims</small>{selectedAudit.length ? selectedAudit.map((claim) => { const limitation = !claim.supported && isGroundingLimitDisclosure(claim.claim); return <p key={`${claim.claim}:${claim.supporting_fact ?? ""}`}><strong>{claim.supported ? "Respaldado" : limitation ? "Límite declarado" : "Bloqueado"} · {claim.claim}</strong><span>{claim.supporting_fact ? `Soporte: ${claim.supporting_fact}` : limitation ? "Declaración prudente: no afirma una capacidad del producto" : "Sin soporte declarado"}</span></p>; }) : <p><span>El auditor no detectó afirmaciones factuales adicionales.</span></p>}</div>
                <div><small>Controles aplicados</small><strong>Claim Audit + Claim Checker · DNC · aprobación humana · {deliveryControlLabel(data?.outreachMode)}</strong></div>
                <div><small>Vigencia</small><strong>{isInitialOutreach ? "Mientras el contenido y el destinatario sigan siendo válidos" : selected.expiresAt ? `Hasta ${formatDate(selected.expiresAt)}` : "Sin vencimiento configurado"}</strong></div>
                <div className="full"><small>Acción posterior</small><strong>{postApprovalAction(selected.message.actionKind, data?.outreachMode)}</strong></div>
              </div>
              {(selectedGeneration?.messageStrategy || selectedGeneration?.qualityAssessment || selectedGeneration?.finalQualityAssessment) && <details className="technical-disclosure approval-technical"><summary>Calidad editorial y estrategia del mensaje</summary><div className="approval-context-grid"><div><small>Política y modo</small><strong>{selectedGeneration.copyPolicy ?? "—"} · {selectedGeneration.qualityMode ?? "—"}</strong></div><div><small>Evaluación vigente</small><strong>{selectedGeneration.finalQualityAssessment?.score ?? selectedGeneration.qualityAssessment?.score ?? "—"} / 100 · {(selectedGeneration.finalQualityAssessment ?? selectedGeneration.qualityAssessment)?.passed === false ? "Requiere corrección" : "Sin bloqueos"}</strong></div><div><small>Ángulo</small><strong>{presentationText(selectedGeneration.messageStrategy?.value_angle ?? "NO DISPONIBLE")}</strong></div><div><small>CTA</small><strong>{presentationText(selectedGeneration.messageStrategy?.cta_mode ?? "NO DISPONIBLE")}</strong></div><div className="full"><small>Product Fact y señal seleccionados</small><strong>{selectedGeneration.messageStrategy?.focus_product_fact_id ?? "Sin Product Fact"}</strong><p>{selectedGeneration.messageStrategy?.prospect_signal_id ?? "Sin señal pública seleccionada"}</p></div>{((selectedGeneration.finalQualityAssessment ?? selectedGeneration.qualityAssessment)?.blockingIssues?.length ?? 0) > 0 && <div className="full"><small>Problemas bloqueantes</small><ul>{(selectedGeneration.finalQualityAssessment ?? selectedGeneration.qualityAssessment)?.blockingIssues?.map((item) => <li key={item}>{item}</li>)}</ul></div>}{((selectedGeneration.finalQualityAssessment ?? selectedGeneration.qualityAssessment)?.warnings?.length ?? 0) > 0 && <div className="full"><small>Advertencias editoriales</small><ul>{(selectedGeneration.finalQualityAssessment ?? selectedGeneration.qualityAssessment)?.warnings?.map((item) => <li key={item}>{item}</li>)}</ul></div>}</div></details>}
              <details className="technical-disclosure approval-technical"><summary>Ver trazabilidad técnica</summary><div className="approval-context-grid"><div><small>Regla y clasificador</small><strong>{selectedClassification?.rule_id ?? "—"} · {selectedClassification?.classifier_version ?? "—"}</strong></div><div><small>Versiones</small><strong>{selected.message.aiPromptVersion ?? selectedGeneration?.promptVersion ?? "—"} · {selected.message.aiResultVersion ?? "—"}</strong></div><div><small>Task y run de autor</small><strong className="technical-value">{selected.message.hermesTaskId ?? "—"} · {selected.message.hermesRunId ?? "—"}</strong></div><div><small>Task y run de revisión</small><strong className="technical-value">{selectedGeneration?.review?.taskId ?? "—"} · {selectedGeneration?.review?.runId ?? "—"}</strong></div><div className="full"><small>Hash de aprobación</small><strong className="technical-value">{selected.payloadHash ?? "—"}</strong></div></div></details>
              {(selected.versionHistory?.length ?? 0) > 0 && <details className="version-history"><summary>Historial de versiones y rechazos <span>{selected.versionHistory?.length}</span></summary><div>{selected.versionHistory?.map((version, index, versions) => <article key={version.id} className={version.id === selected.message?.id ? "current" : ""}><div><strong>Versión {versions.length - index}</strong><Status value={version.approval?.status ?? version.status} /></div><p>{version.subject}</p><small>{formatDate(version.createdAt)} · {version.previousVersionId ? `derivada de ${version.previousVersionId}` : index === versions.length - 1 ? "versión inicial" : "recuperada del mismo inbound"}</small>{version.approval?.reviewerNote && <blockquote>Motivo: {version.approval.reviewerNote}</blockquote>}</article>)}</div></details>}
              {selectedAttachments.length > 0 && <details className="technical-disclosure approval-technical"><summary>Huella técnica del PDF aprobado</summary><div className="approval-context-grid"><div className="full"><small>Versión exacta incluida en el hash</small><strong>{selectedAttachments.map((asset) => `${asset.name} · v${asset.version ?? 1} · ${asset.sha256?.slice(0, 12)}…`).join(" · ")}</strong></div></div></details>}
              <div className="approval-guard">
                <ShieldCheck />
                <div>
                  <strong>Autorización vinculada al contenido</strong>
                  <p>
                    Se validarán destinatario, exclusiones y hash nuevamente al
                    enviar.
                  </p>
                </div>
              </div>
              {(decide.error || rejectProposal.error) && (
                <div className="form-error">
                  {(decide.error ?? rejectProposal.error) instanceof ApiError
                    ? (decide.error ?? rejectProposal.error as ApiError).message
                    : "No se pudo procesar"}
                </div>
              )}
              {selected.status === "PENDING" && selected.sendability?.sendable !== false && !isEditingSelected && <div className="decision-buttons">
                <button
                  className="reject"
                  disabled={rejectProposal.isPending || !selected.payloadHash}
                  onClick={() => { rejectProposal.reset(); setRejecting(selected); }}
                >
                  <XCircle />
                  Rechazar
                </button>
                <button
                  className="approve"
                  disabled={decide.isPending || selected.payloadValid === false || (!isInitialOutreach && Boolean(selected.expiresAt) && selected.expiresAt! <= new Date().toISOString())}
                  onClick={() => {
                    if (window.confirm(approvalDecisionConfirmation(data?.outreachMode))) decide.mutate({ item: selected, decision: "APPROVE" });
                  }}
                >
                  <CheckCircle2 />
                  Aprobar y encolar
                </button>
              </div>}
            </>
          ) : selected?.meeting ? (
            <>
              <div className="preview-head">
                <div><span className="eyebrow">{isMeetingUpdate ? "ACTUALIZACIÓN DE INVITACIÓN EXISTENTE" : "REUNIÓN A REVISAR"} · VERSIÓN {selected.invitationDraft?.version ?? "HISTÓRICA"}</span><h2>{selectedCalendarPayload?.title ?? selected.meeting.title}</h2></div>
                <Status value={selected.status} />
              </div>
              {selectedInvitation && (
                <div className="meeting-invitation-review">
                  <section><small>Objetivo</small><p>{selectedInvitation.objective}</p></section>
                  <section><small>Agenda</small><ol>{selectedInvitation.agenda.map((item) => <li key={item}>{item}</li>)}</ol></section>
                  <section><small>Resultado esperado</small><p>{selectedInvitation.expectedOutcome}</p></section>
                  <section><small>Preparación opcional</small><p>{selectedInvitation.optionalPreparation ?? "No se requiere preparación previa."}</p></section>
                  <section className="full"><small>Descripción exacta que recibirá Calendar</small>
                    {selected.status === "PENDING" && selected.invitationDraft && !calendarEditing && <button className="secondary" onClick={beginCalendarEdit} disabled={editCalendar.isPending || regenerateInvitation.isPending || decide.isPending}><Pencil />Editar invitación</button>}
                    {calendarEditing ? <ApprovalMessageEditor editing subject={calendarTitle} body={calendarBody} bodyHtml={calendarHtml} attachments={[]} approvalId={selected.id} pending={editCalendar.isPending} error={editCalendar.error} onSubjectChange={setCalendarTitle} onBodyChange={setCalendarBody} onBodyHtmlChange={setCalendarHtml} onUploadAttachment={() => {}} onRemoveAttachment={() => {}} onCancel={() => setCalendarEditId(null)} onSave={() => editCalendar.mutate({ item: selected })} showAttachments={false} footerNote="Calendar guarda texto simple. Se validará el contenido y se guardará una nueva versión pendiente de aprobación. El horario y los participantes se conservan." /> : <pre>{selectedCalendarPayload?.description ?? selected.invitationDraft?.description}</pre>}
                    {selected.status === "PENDING" && !calendarEditing && <div className="template-alternative"><label>Aplicar una plantilla de Calendar<select value={calendarTemplateId} onChange={e => setCalendarTemplateId(e.target.value)} disabled={editCalendar.isPending}><option value="">Elegí una versión publicada…</option>{calendarChoices.map(({ template, version }) => <option key={version.id} value={version.id}>{template.label} · v{version.version}</option>)}</select></label><button className="secondary" disabled={!calendarTemplateId || editCalendar.isPending || decide.isPending || regenerateInvitation.isPending} onClick={() => editCalendar.mutate({ item: selected, templateVersionId: calendarTemplateId })}>{editCalendar.isPending ? <LoaderCircle className="spin" /> : <FileText />}Aplicar plantilla</button><small>Reemplaza título y contenido de esta invitación; requiere una nueva aprobación.</small></div>}
                  </section>
                </div>
              )}
              {isMeetingUpdate && selectedBaseline && selectedPatch && (
                <div className="meeting-invitation-review meeting-update-comparison">
                  <section><small>Contenido actual · título</small><p>{selectedBaseline.summary}</p></section>
                  <section><small>Contenido propuesto · título</small><p>{selectedPatch.summary}</p></section>
                  <section><small>Contenido actual · descripción</small><pre>{selectedBaseline.description}</pre></section>
                  <section><small>Contenido propuesto · descripción</small><pre>{selectedPatch.description}</pre></section>
                  <section className="full"><small>Diferencias exactas</small><p>{selectedChangedFields.map((field) => field === "summary" ? "Título" : "Descripción").join(" · ")}. No se enviarán otros campos en el patch.</p></section>
                </div>
              )}
              <div className="meeting-review-grid">
                <div><small>Fecha y hora</small><strong>{formatDate(selectedCalendarPayload?.startsAt ?? selected.meeting.startsAt)}</strong></div>
                <div><small>Zona horaria</small><strong>{selectedCalendarPayload?.timezone ?? selected.meeting.timezone ?? "—"}</strong></div>
                <div><small>Duración</small><strong>{selectedCalendarPayload?.durationMinutes ?? selected.meeting.durationMinutes ?? 30} minutos</strong></div>
                <div><small>Organizador</small><strong>{selectedCalendarPayload?.organizerEmail ?? selected.meeting.organizerEmail ?? "—"}</strong></div>
                <div className="full"><small>Equipo comercial</small><strong>{selectedMeetingTeam.map((member) => `${member.firstName} ${member.lastName} · ${member.jobTitle}`).join(" | ") || "Sin equipo registrado"}</strong></div>
                <div className="full"><small>Participantes lógicos previstos</small><strong>{selectedCalendarPayload?.logicalAttendees.join(", ") || parsedArray<string>(selected.meeting.attendeesJson).join(", ") || "Ninguno"}</strong></div>
                <div className="full"><small>Asistentes efectivos que recibirá Calendar</small><strong>{selectedCalendarPayload?.effectiveAttendees.join(", ") || (data?.outreachMode === "TEST" ? data.safeSend.emailRecipient : null) || "Ninguno"}</strong></div>
                <div><small>Google Meet</small><strong>{(selectedCalendarPayload?.addGoogleMeet ?? selected.meeting.addGoogleMeet) ? "Habilitado" : "Deshabilitado"}</strong></div>
                <div><small>Notificaciones</small><strong>{selectedCalendarPayload?.sendUpdates === "none" ? "No enviar" : "Enviar a asistentes efectivos"}</strong></div>
                <div><small>Vigencia de aprobación</small><strong>{selected.expiresAt ? `Hasta ${formatDate(selected.expiresAt)}` : "Mientras el payload aprobado no cambie"}</strong></div>
                <div><small>Acción posterior</small><strong>{isMeetingUpdate ? "Verificar ETag y baseline; aplicar un único events.patch" : "Crear Calendar/Meet con el horario confirmado manualmente"}</strong></div>
              </div>
              {isMeetingUpdate && <div className="approval-guard"><ShieldCheck /><div><strong>Campos preservados</strong><p>Event ID, Calendar ID, organizador, inicio, fin, zona horaria, asistentes, Google Meet, conferenceData, recordatorios, visibilidad, estado e idempotencia. La notificación se dirige únicamente a los asistentes efectivos ya verificados.</p></div></div>}
              {selected.invitationDraft && (
                <details className="meeting-generation-detail">
                  <summary>Ver generación, evidencia y advertencias</summary>
                  <div className="meeting-review-grid">
                    <div><small>Modo de generación</small><strong>{selected.invitationDraft.generationMode === "AI" ? "RevOps IA" : selected.invitationDraft.generationMode === "MANUAL" ? "Edición manual" : selected.invitationDraft.generationMode === "TEMPLATE" ? "Plantilla publicada" : "Regla automática"}</strong></div>
                    <div><small>Proveedor y modelo</small><strong>{[selected.invitationDraft.provider, selected.invitationDraft.profile, selected.invitationDraft.model].filter(Boolean).join(" · ") || "Fallback local"}</strong></div>
                    <div><small>Versiones</small><strong>{selected.invitationDraft.promptVersion} · contrato {selected.invitationDraft.contentVersion}</strong></div>
                    <div><small>Hash aprobado</small><strong className="technical-value">{selected.invitationDraft.payloadHash}</strong></div>
                    <div className="full"><small>Evidencia de grounding</small><ul>{selectedInvitation?.groundingEvidence.map((item) => <li key={`${item.field}:${item.sourceId}`}>{item.field}: {item.sourceType} · {item.sourceId}</li>)}</ul></div>
                    {(selectedInvitation?.warnings.length ?? 0) > 0 && <div className="full warning-detail"><small>Advertencias</small><ul>{selectedInvitation?.warnings.map((item) => <li key={item}>{item}</li>)}</ul></div>}
                  </div>
                </details>
              )}
              <div className="approval-guard"><ShieldCheck /><div><strong>{isMeetingUpdate ? "La actualización todavía no fue aplicada" : "Calendar todavía no fue llamado"}</strong><p>{isMeetingUpdate ? "Al aprobar se vuelve a leer el evento y se detiene el proceso si cambió el ETag o cualquier dato del baseline. En TEST, la notificación queda limitada al asistente interno efectivo." : "Al aprobar se crea la reunión con el horario y los participantes confirmados manualmente, usando exactamente el payload cuyo hash aparece arriba. En TEST, ningún invitado externo recibe la invitación."}</p></div></div>
              {decide.error && <div className="form-error">{decide.error instanceof ApiError ? decide.error.message : "No se pudo procesar"}</div>}
              {!calendarEditing && editCalendar.error && <div className="form-error" role="alert">{editCalendar.error instanceof ApiError ? editCalendar.error.message : "No se pudo guardar la invitación"}</div>}
              {regenerateInvitation.error && <div className="form-error" role="alert">{regenerateInvitation.error instanceof ApiError ? regenerateInvitation.error.message : "No se pudo regenerar. La versión anterior se conserva; podés reintentar."}</div>}
              <div className="decision-buttons">
                {!isMeetingUpdate && <button className="secondary" disabled={regenerateInvitation.isPending || decide.isPending || editCalendar.isPending || calendarEditing} onClick={() => regenerateInvitation.mutate(selected.meeting!.id)}><RefreshCcw />{regenerateInvitation.isPending ? "Redactando y validando…" : "Regenerar invitación"}</button>}
                <button className="reject" disabled={regenerateInvitation.isPending || decide.isPending || editCalendar.isPending || calendarEditing} onClick={() => decide.mutate({ item: selected, decision: "REJECT" })}><XCircle />Rechazar</button>
                <button className="approve" disabled={regenerateInvitation.isPending || decide.isPending || editCalendar.isPending || calendarEditing} onClick={() => decide.mutate({ item: selected, decision: "APPROVE" })}><CalendarPlus />{isMeetingUpdate ? "Aprobar actualización" : "Aprobar creación de reunión"}</button>
              </div>
            </>
          ) : (
            <Empty
              title="Elegí una aprobación"
              body="Vas a ver el mensaje, destinatario y controles aplicados antes de decidir."
            />
          )}
        </section>
      </div>
      <AnimatePresence>{rejecting?.message && <CommercialRejectionModal approval={rejecting} pending={rejectProposal.isPending} error={rejectProposal.error} onCancel={() => { if (!rejectProposal.isPending) { rejectProposal.reset(); setRejecting(null); } }} onSubmit={(mode, reason) => rejectProposal.mutate({ item: rejecting, mode, reason })} />}</AnimatePresence>
      <AnimatePresence>{batchApprovalIntent && <BatchApprovalConfirmation count={batchApprovalIntent.length} pending={approveBatch.isPending} error={approveBatch.error} onCancel={() => { if (!approveBatch.isPending) { approveBatch.reset(); setBatchApprovalIntent(null); } }} onConfirm={confirmBatchApproval} />}</AnimatePresence>
    </>
  );
}

function IntegrationEditor({
  item,
  close,
}: {
  item: IntegrationSetting;
  close: () => void;
}) {
  const client = useQueryClient();
  const [mode, setMode] = useState(item.mode);
  const [parameters, setParameters] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      item.definition.parameters.map((field) => [
        field.key,
        String(item.parameters[field.key] ?? ""),
      ]),
    ),
  );
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api(`/integrations/${item.provider}`, {
        method: "PUT",
        body: JSON.stringify({
          mode: item.provider === "PLATFORM" ? "SIMULATED" : mode,
          parameters,
          secrets,
        }),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["integrations"] });
      close();
    },
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ status: string }>(`/integrations/${item.provider}/test`, {
        method: "POST",
      }, {
        timeoutMs: 15 * 60_000,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["integrations"] }),
  });
  const clearCredentials = useMutation({
    mutationFn: () =>
      api(`/integrations/${item.provider}`, {
        method: "PUT",
        body: JSON.stringify({ mode: "SIMULATED", clearSecrets: true }),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["integrations"] });
      close();
    },
  });
  const connectGoogle = useMutation({
    mutationFn: () =>
      api<{ authorizationUrl: string }>(
        "/integrations/google/oauth/start",
        { headers: { Accept: "application/json" } },
      ),
    onSuccess: ({ authorizationUrl }) => {
      window.location.assign(authorizationUrl);
    },
  });
  const error =
    save.error ?? test.error ?? clearCredentials.error ?? connectGoogle.error;
  const renderField = (field: FieldDefinition, secret = false) => {
    const configured = item.configuredKeys.includes(field.key);
    const value = secret
      ? (secrets[field.key] ?? "")
      : (parameters[field.key] ?? "");
    const setValue = (next: string) =>
      secret
        ? setSecrets((current) => ({ ...current, [field.key]: next }))
        : setParameters((current) => ({ ...current, [field.key]: next }));
    const common = {
      value,
      onChange: (
        event: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => setValue(event.target.value),
    };
    return (
      <label
        key={field.key}
        className={field.type === "textarea" ? "full" : ""}
      >
        <span>
          {field.label}
          {field.required && <b> *</b>}
          {secret && configured && (
            <em>
              <LockKeyhole size={11} /> Configurado
            </em>
          )}
        </span>
        {field.key === "decisionMakersOnly" ? (
          <select {...common}>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        ) : field.type === "textarea" ? (
          <textarea {...common} placeholder={field.placeholder} />
        ) : (
          <input
            {...common}
            type={
              secret
                ? "password"
                : field.type === "number"
                  ? "number"
                  : (field.type ?? "text")
            }
            placeholder={
              secret && configured
                ? "Dejar vacío para conservar"
                : field.placeholder
            }
          />
        )}
        {field.help && <small>{field.help}</small>}
      </label>
    );
  };
  return (
    <motion.div
      className="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={close}
    >
      <motion.div
        className="modal integration-modal"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="icon-button close" onClick={close}>
          <X />
        </button>
        <div className="integration-editor-head">
          <BrandIcon name={item.provider} />
          <div>
            <span className="eyebrow">
              {item.definition.category.toUpperCase()}
            </span>
            <h2>{item.label}</h2>
            <p>{item.definition.description}</p>
          </div>
        </div>
        {!item.vaultReady && item.definition.secretFields.length > 0 && (
          <div className="form-error">
            <CircleAlert size={17} />
            Falta definir la clave maestra de cifrado en el servidor.
          </div>
        )}
        {item.provider !== "PLATFORM" && (
          <div className="mode-selector">
            <button
              className={mode === "SIMULATED" ? "active" : ""}
              aria-pressed={mode === "SIMULATED"}
              onClick={() => setMode("SIMULATED")}
            >
              Simulado
            </button>
            <button
              className={mode === "LIVE" ? "active live" : ""}
              aria-pressed={mode === "LIVE"}
              onClick={() => setMode("LIVE")}
            >
              En vivo
            </button>
            <button
              className={mode === "DISABLED" ? "active off" : ""}
              aria-pressed={mode === "DISABLED"}
              onClick={() => setMode("DISABLED")}
            >
              Desactivado
            </button>
          </div>
        )}
        <div className="settings-section">
          <div className="section-label">
            <Settings2 />
            <div>
              <strong>Parámetros</strong>
              <small>Comportamiento y endpoints de esta conexión</small>
            </div>
          </div>
          <div className="form-grid settings-form">
            {item.definition.parameters.map((field) => renderField(field))}
          </div>
        </div>
        {item.definition.secretFields.length > 0 && (
          <div className="settings-section secret-section">
            <div className="section-label">
              <KeyRound />
              <div>
                <strong>Credenciales cifradas</strong>
                <small>Nunca vuelven al navegador después de guardarse</small>
              </div>
            </div>
            <div className="form-grid settings-form">
              {item.definition.secretFields.map((field) =>
                renderField(field, true),
              )}
            </div>
          </div>
        )}
        {error && (
          <div className="form-error">
            <CircleAlert size={17} />
            {error instanceof ApiError
              ? error.message
              : "No se pudo completar la operación"}
          </div>
        )}
        {test.isSuccess && (
          <div className="form-success">
            <CheckCircle2 />
            Conexión verificada correctamente.
          </div>
        )}
        <div className="modal-actions integration-actions">
          {item.provider === "GOOGLE_WORKSPACE" && (
            <button
              className="secondary"
              type="button"
              disabled={
                connectGoogle.isPending ||
                !item.configuredKeys.includes("clientId") ||
                !item.configuredKeys.includes("clientSecret")
              }
              onClick={() => connectGoogle.mutate()}
            >
              <ExternalLink size={16} />
              {connectGoogle.isPending ? "Abriendo Google…" : "Conectar Google"}
            </button>
          )}
          {item.provider !== "PLATFORM" && (
            <button
              className="secondary"
              type="button"
              disabled={test.isPending || item.mode !== "LIVE"}
              onClick={() => test.mutate()}
            >
              <Wifi size={16} />
              {test.isPending ? "Probando…" : "Probar conexión"}
            </button>
          )}
          {item.configuredKeys.length > 0 && (
            <button
              className="reject"
              type="button"
              disabled={clearCredentials.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `¿Borrar todas las credenciales guardadas de ${item.label}?`,
                  )
                )
                  clearCredentials.mutate();
              }}
            >
              <Trash2 size={15} />
              Borrar credenciales
            </button>
          )}
          <span />
          <button className="secondary" onClick={close}>
            Cancelar
          </button>
          <button
            className="primary"
            disabled={
              save.isPending ||
              (!item.vaultReady && item.definition.secretFields.length > 0)
            }
            onClick={() => save.mutate()}
          >
            <Save size={16} />
            {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function IntegrationsPage() {
  const [selected, setSelected] = useState<IntegrationSetting | null>(null);
  const { data: runtime } = useQuery({
    queryKey: ["health"],
    queryFn: () =>
      api<{
        outreachMode: "DRY_RUN" | "TEST" | "PRODUCTION";
        productionArmed: boolean;
        appliance: { enabled: boolean };
        actionScheduler: { enabled: boolean; running: boolean; intervalMs: number; startedAt?: string | null; lastTickAt?: string | null; lastCompletedAt?: string | null; lastError?: string | null };
        gmailPolling: { enabled: boolean; running: boolean; intervalMs: number; startedAt?: string | null; lastTickAt?: string | null; lastCompletedAt?: string | null; lastError?: string | null };
      }>("/health"),
  });
  const { data, isPending } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api<{ items: IntegrationSetting[] }>("/integrations"),
  });
  const { data: readiness } = useQuery({
    queryKey: ["integrations-readiness"],
    queryFn: () => api<{ items: IntegrationReadiness[] }>("/integrations/readiness"),
  });
  const items = data?.items ?? [];
  const live = items.filter((item) => item.status === "LIVE").length;
  const configured = items.filter(
    (item) => item.configuredKeys.length > 0 || item.provider === "PLATFORM",
  ).length;
  const categories = [
    ...new Set(items.map((item) => item.definition.category)),
  ];
  const codexConnectionPending = Boolean(
    runtime?.appliance.enabled &&
    readiness &&
    hermesCodexConnectionRequired(readiness.items),
  );
  return (
    <>
      <PageTitle
        eyebrow="CONFIGURACIÓN"
        title="Centro de conexiones"
        body="Credenciales, parámetros y modos de operación en un único lugar seguro."
      />
      <div className="metrics integration-metrics">
        <div>
          <span>PROVEEDORES</span>
          <strong>{items.length - 1}</strong>
          <small>adaptadores disponibles</small>
        </div>
        <div>
          <span>CONFIGURADOS</span>
          <strong>
            {configured}/{items.length}
          </strong>
          <small>con parámetros guardados</small>
        </div>
        <div>
          <span>EN VIVO</span>
          <strong>{live}</strong>
          <small>conexiones verificadas</small>
        </div>
        <div>
          <span>SEGURIDAD</span>
          <strong>
            <LockKeyhole size={25} />
          </strong>
          <small>AES-256-GCM en servidor</small>
        </div>
      </div>
      {runtime ? <section className="runtime-operations" aria-label="Estado operativo de automatizaciones">
        <header><div><span className="eyebrow">OPERACIÓN AUTOMÁTICA</span><h2>Scheduler, polling y seguridad de salida</h2></div><Status value={runtime.productionArmed ? "PRODUCTION_ARMED" : `${runtime.outreachMode}_SAFE`} /></header>
        <div className="runtime-operation-grid">
          {[["Scheduler", runtime.actionScheduler], ["Gmail polling", runtime.gmailPolling]].map(([label, state]) => {
            const value = state as typeof runtime.actionScheduler;
            const waiting = value.enabled && !value.running && !value.lastError;
            return <article key={label as string} className={value.lastError ? "error" : value.enabled ? "enabled" : "disabled"}>
              <div><strong>{label as string}</strong><Status value={!value.enabled ? "DISABLED" : value.lastError ? "ERROR" : value.running ? "RUNNING" : "WAITING"} /></div>
              <p>{!value.enabled ? "Deshabilitado" : value.running ? "Procesando un ciclo ahora" : waiting ? "Habilitado y en espera entre ciclos" : "Requiere atención"}</p>
              <small>Último ciclo: {formatDate(value.lastCompletedAt ?? value.lastTickAt)}</small>
              <small>Intervalo: {Math.round(value.intervalMs / 1000)} segundos</small>
              {value.lastError ? <small className="runtime-error">{value.lastError}</small> : null}
            </article>;
          })}
          <article className="safety"><div><strong>Salida comercial</strong><Status value={runtime.outreachMode} /></div><p>{runtime.outreachMode === "TEST" ? "TEST activo: Claim Check, SAFE SEND y el control paso a paso conservan la salida controlada." : runtime.productionArmed ? "Producción armada" : "Producción desarmada"}</p><small>productionArmed: {runtime.productionArmed ? "sí" : "no"}</small></article>
        </div>
      </section> : null}
      {codexConnectionPending && (
        <div className="warning-banner hermes-onboarding" role="alert">
          <KeyRound />
          <div>
            <strong>Conectá OpenAI/Codex para activar los agentes</strong>
            <p>
              El Appliance ya asignó los perfiles a OpenAI/Codex. Abrí Hermes
              mediante el túnel SSH, vinculá una cuenta en Claves y después
              ejecutá “Probar conexión” en la integración Hermes. La credencial
              se comparte de forma segura entre los perfiles.
            </p>
          </div>
        </div>
      )}
      {!items.every(
        (item) => item.vaultReady || item.definition.secretFields.length === 0,
      ) && (
        <div className="warning-banner">
          <CircleAlert />
          <div>
            <strong>Configuración inicial pendiente</strong>
            <p>
              Definí CREDENTIAL_ENCRYPTION_KEY en el servidor para habilitar el
              guardado seguro de credenciales.
            </p>
          </div>
        </div>
      )}
      {isPending ? (
        <div className="loading">Cargando configuración…</div>
      ) : (
        categories.map((category) => (
          <section className="integration-category" key={category}>
            <div className="category-title">
              <span>{category}</span>
              <i />
            </div>
            <div className="integration-grid">
              {items
                .filter((item) => item.definition.category === category)
                .map((item) => (
                  <article
                    key={item.provider}
                    className={item.status === "ERROR" ? "has-error" : ""}
                  >
                    <BrandIcon name={item.provider} />
                    <div>
                      <h3>{item.label}</h3>
                      <p>{item.definition.description}</p>
                    </div>
                    <Status value={item.status} />
                    <div className="integration-card-foot">
                      <span className={`mode mode-${(item.provider === "PLATFORM" ? runtime?.outreachMode ?? "DRY_RUN" : item.mode).toLowerCase()}`}>
                        {presentationText(item.provider === "PLATFORM"
                          ? runtime?.outreachMode ?? "DRY_RUN"
                          : item.mode)}
                      </span>
                      <small>
                        {item.provider === "HERMES" && codexConnectionPending
                          ? "OpenAI/Codex pendiente"
                          : item.lastError
                          ? "Requiere atención"
                          : item.lastCheckAt
                            ? `Verificado ${formatDate(item.lastCheckAt)}`
                            : item.configuredKeys.length
                              ? "Credenciales guardadas"
                              : "Pendiente de configurar"}
                      </small>
                      <button
                        className="secondary"
                        onClick={() => setSelected(item)}
                      >
                        Configurar
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </article>
                ))}
            </div>
          </section>
        ))
      )}
      {readiness?.items.length ? (
        <section className="integration-category">
          <div className="category-title"><span>READINESS OPERATIVO</span><i /></div>
          <div className="table-card readiness-table">
            <table>
              <thead><tr><th>Proveedor</th><th>Validación</th><th>Conexión</th><th>Campos pendientes</th><th>Capacidades</th></tr></thead>
              <tbody>{readiness.items.map((item) => (
                <tr key={item.provider}>
                  <td><strong>{items.find((setting) => setting.provider === item.provider)?.label ?? presentationText(item.provider)}</strong><small>{presentationText(item.mode)}</small></td>
                  <td><Status value={item.validationLevel} /></td>
                  <td>{item.connectionTested ? <Status value={item.lastTestResult ?? "TESTED"} /> : "No probada"}</td>
                  <td>{item.requiredFieldsMissing.length ? item.requiredFieldsMissing.join(", ") : "Completo"}</td>
                  <td>
                    <div className="capability-list">
                      {item.capabilities.map((capability) => (
                        <div className="capability-item" key={capability.name}>
                          <span><strong>{capability.name}</strong><small>{capability.operation}</small></span>
                          <Status value={capability.status} />
                          {capability.lastError ? <small className="capability-error" title={capability.lastError}>{capability.lastError}</small> : null}
                          {capability.lastTestAt ? <small>{formatDate(capability.lastTestAt)}</small> : null}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      ) : null}
      <div className="info-banner">
        <ShieldCheck />
        <div>
          <strong>Credenciales protegidas y aisladas</strong>
          <p>
            Las conexiones salen únicamente desde el backend. Los secretos se
            cifran antes de persistirse, nunca se muestran nuevamente y no se
            comparten bases de datos con Hermes ni con otros proveedores.
          </p>
        </div>
      </div>
      <AnimatePresence>
        {selected && (
          <IntegrationEditor item={selected} close={() => setSelected(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
type AgentRosterItem = {
  id: string;
  name: string;
  status: string;
  profile: string;
  provider: string;
  model: string;
  responsibility: string;
  inherited: boolean;
  fallback?: string | null;
  lastVerifiedAt?: string | null;
  diagnostic?: { code: string; message: string } | null;
};

type AgentSoulDetail = {
  profile: string;
  logicalPath: string;
  content: string;
  exists: boolean;
  source: string;
  status: string;
  hash: string;
  modifiedAt?: string | null;
  warnings: string[];
  appliesTo: "NEW_SESSIONS";
  runtime?: { provider?: string | null; model?: string | null; description?: string | null; personality?: string | null; capabilities?: unknown; skills?: unknown; tools?: unknown; operationalRules?: string[] } | null;
  routing?: AgentRosterItem | null;
  history: Array<{ id: string; version: number; content: string; contentHash: string; previousHash?: string | null; source: string; status: string; reason: string; createdAt: string; appliedAt?: string | null; author?: { displayName: string; username: string } | null }>;
};

function AgentSoulDrawer({ agent, isAdmin, close }: { agent: AgentRosterItem; isAdmin: boolean; close: () => void }) {
  const client = useQueryClient();
  const [tab, setTab] = useState<"identity" | "operation" | "history">("identity");
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<{ nextHash: string; warnings: string[]; diff: Array<{ kind: string; line: string }>; normalizedContent: string } | null>(null);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["agent-soul", agent.profile], queryFn: () => api<AgentSoulDetail>(`/agents/${agent.profile}/soul`) });
  useEffect(() => { if (data && !editing) setContent(data.content); }, [data, editing]);
  const previewChange = useMutation({
    mutationFn: () => api<{ nextHash: string; warnings: string[]; diff: Array<{ kind: string; line: string }>; normalizedContent: string }>(`/agents/${agent.profile}/soul/preview`, { method: "POST", body: JSON.stringify({ content, expectedHash: data?.hash, reason }) }),
    onSuccess: setPreview,
  });
  const saveSoul = useMutation({
    mutationFn: () => api<AgentSoulDetail>(`/agents/${agent.profile}/soul`, { method: "PUT", headers: { "X-Vicnet-Intent": "agent-soul-write" }, body: JSON.stringify({ content: preview?.normalizedContent ?? content, expectedHash: data?.hash, reason }) }),
    onSuccess: async (next) => { setContent(next.content); setPreview(null); setEditing(false); setReason(""); await client.invalidateQueries({ queryKey: ["agent-soul", agent.profile] }); },
  });
  const revertSoul = useMutation({
    mutationFn: (versionId: string) => api<AgentSoulDetail>(`/agents/${agent.profile}/soul/revert`, { method: "POST", headers: { "X-Vicnet-Intent": "agent-soul-write" }, body: JSON.stringify({ versionId, expectedHash: data?.hash, reason }) }),
    onSuccess: async () => { setReason(""); await client.invalidateQueries({ queryKey: ["agent-soul", agent.profile] }); },
  });
  const mutationError = previewChange.error ?? saveSoul.error ?? revertSoul.error;
  return <motion.div className="agent-drawer-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={close}>
    <motion.aside className="agent-drawer" role="dialog" aria-modal="true" aria-labelledby="agent-detail-title" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} onMouseDown={(event) => event.stopPropagation()}>
      <header><div><span className="eyebrow">ADMINISTRACIÓN DE AGENTE</span><h2 id="agent-detail-title">{agent.name}</h2><p>{agent.profile}</p></div><button className="icon-button" aria-label="Cerrar detalle del agente" onClick={close}><X /></button></header>
      <nav className="agent-detail-tabs" role="tablist" aria-label="Detalle del agente">
        {[["identity", "Identidad"], ["operation", "Operación"], ["history", "Historial"]].map(([value, label]) => <button key={value} role="tab" aria-selected={tab === value} className={tab === value ? "active" : ""} onClick={() => setTab(value as typeof tab)}>{label}</button>)}
      </nav>
      {isLoading ? <div className="loading" role="status">Leyendo identidad efectiva desde Hermes…</div> : isError ? <div className="query-state error" role="alert"><CircleAlert /><strong>No se pudo verificar SOUL.md</strong><p>{error instanceof ApiError ? error.message : "Hermes no respondió."}</p><button className="secondary" onClick={() => void refetch()}><RefreshCcw size={15} />Reintentar</button></div> : data ? <div className="agent-drawer-body">
        {tab === "identity" && <>
          <div className="soul-summary"><div><small>Fuente efectiva</small><strong>{presentationText(data.source)}</strong></div><div><small>Estado</small><Status value={data.status} /></div><div><small>Modificado</small><strong>{formatDate(data.modifiedAt)}</strong></div><div><small>Hash</small><code>{data.hash.slice(0, 16)}…</code></div></div>
          <div className="new-session-notice"><RefreshCcw size={16} /><span><strong>Se aplica a sesiones nuevas</strong><small>Las sesiones ya iniciadas pueden conservar la identidad anterior.</small></span></div>
          {data.warnings.length > 0 && <div className="approval-warning"><CircleAlert /><ul>{data.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div>}
          <div className="soul-editor-head"><div><strong>SOUL.md efectivo</strong><small>{data.logicalPath}</small></div>{isAdmin && !editing && <button className="secondary" onClick={() => { setEditing(true); setContent(data.content); setPreview(null); }}>{data.source === "EXPLICIT_FILE" ? "Editar identidad" : "Crear SOUL.md explícito"}</button>}</div>
          {editing ? <div className="soul-edit-form"><label><span>Identidad estable</span><textarea value={content} onChange={(event) => { setContent(event.target.value); setPreview(null); }} spellCheck /><small>Incluí identidad, misión, criterio, tono, incertidumbre, evidencia, límites y escalamiento. No incluyas credenciales, rutas ni procedimientos temporales.</small></label><label><span>Motivo del cambio</span><input value={reason} onChange={(event) => { setReason(event.target.value); setPreview(null); }} maxLength={500} placeholder="Explicá por qué se modifica esta identidad" /></label>{preview ? <div className="soul-diff" aria-label="Vista previa de cambios">{preview.diff.map((line, index) => <code className={`diff-${line.kind}`} key={`${index}:${line.kind}`}>{line.kind === "add" ? "+" : line.kind === "remove" ? "−" : " "} {line.line || " "}</code>)}</div> : null}{mutationError && <div className="form-error" role="alert">{mutationError instanceof ApiError ? mutationError.message : "No se pudo completar el cambio"}</div>}<div className="modal-actions"><button className="secondary" onClick={() => { setEditing(false); setPreview(null); setReason(""); setContent(data.content); }}>Cancelar</button>{preview ? <button className="primary" disabled={saveSoul.isPending} onClick={() => { if (window.confirm("¿Guardar esta nueva versión de SOUL.md? Se aplicará a sesiones nuevas.")) saveSoul.mutate(); }}><Save size={15} />{saveSoul.isPending ? "Verificando…" : "Confirmar y guardar"}</button> : <button className="primary" disabled={previewChange.isPending || reason.trim().length < 8} onClick={() => previewChange.mutate()}><Check size={15} />{previewChange.isPending ? "Validando…" : "Ver diff"}</button>}</div></div> : <pre className="soul-content">{data.content || "Hermes no devolvió un archivo explícito. Revisá la fuente efectiva antes de crear uno."}</pre>}
        </>}
        {tab === "operation" && <><div className="operation-readonly"><ShieldCheck /><div><strong>Configuración técnica de sólo lectura</strong><p>La identidad está separada del modelo, herramientas, routing, contratos y validadores determinísticos.</p></div></div><div className="agent-operation-grid"><div><small>Proveedor</small><strong>{data.runtime?.provider ?? agent.provider}</strong></div><div><small>Modelo</small><strong>{data.runtime?.model ?? agent.model}</strong></div><div className="full"><small>Descripción del perfil</small><strong>{data.runtime?.description ?? agent.responsibility}</strong></div><div className="full"><small>Routing desde Vicnet</small><pre>{JSON.stringify(data.routing ?? {}, null, 2)}</pre></div><div><small>Capacidades</small><pre>{JSON.stringify(data.runtime?.capabilities ?? {}, null, 2)}</pre></div><div><small>Skills</small><pre>{JSON.stringify(data.runtime?.skills ?? {}, null, 2)}</pre></div><div><small>Herramientas</small><pre>{JSON.stringify(data.runtime?.tools ?? {}, null, 2)}</pre></div><div><small>Reglas operativas cargadas</small><ul>{data.runtime?.operationalRules?.map((rule) => <li key={rule}>{rule}</li>) ?? <li>No verificables</li>}</ul></div></div></>}
        {tab === "history" && <><div className="history-intro"><strong>Versiones auditables</strong><p>Revertir siempre crea una versión nueva; nunca borra la evidencia anterior.</p></div>{data.history.length ? <div className="soul-history">{data.history.map((version) => <article key={version.id}><div><strong>Versión {version.version} · {presentationText(version.status)}</strong><small>{formatDate(version.appliedAt ?? version.createdAt)} · {version.author?.displayName ?? "Sistema"}</small></div><p>{version.reason}</p><code>{version.contentHash}</code>{isAdmin && version.contentHash !== data.hash && <button className="secondary" disabled={revertSoul.isPending || reason.trim().length < 8} onClick={() => { if (window.confirm(`¿Crear una nueva versión basada en la versión ${version.version}?`)) revertSoul.mutate(version.id); }}>Revertir como nueva versión</button>}</article>)}</div> : <Empty title="Sin historial administrado" body="La primera edición creará un backup y una versión auditada." />}{isAdmin && <label className="history-reason"><span>Motivo para una reversión</span><input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="Obligatorio antes de revertir" /></label>}</>}
      </div> : null}
    </motion.aside>
  </motion.div>;
}

export function AgentsPage() {
  const [selected, setSelected] = useState<AgentRosterItem | null>(null);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<{ user: { role: string } }>("/auth/me") });
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["agents"],
    queryFn: () =>
      api<{
        items: AgentRosterItem[];
      }>("/agents"),
  });
  return (
    <>
      <PageTitle
        eyebrow="AGENTES"
        title="Equipo comercial coordinado"
        body="Roles claros, límites visibles y evidencia común para cada ejecución."
      />
      {isLoading ? <div className="loading">Cargando agentes…</div> : isError ? <div className="query-state error"><CircleAlert /><strong>No se pudieron cargar los agentes</strong><p>{error instanceof ApiError ? error.message : "Ocurrió un problema temporal."}</p><button className="secondary" onClick={() => void refetch()}><RefreshCcw size={15} />Reintentar</button></div> : data?.items.length ? <div className="agent-grid">
        {data?.items.map((agent, index) => (
          <article key={agent.id}>
            <span className={`agent-orb orb-${index}`}>
              <Bot />
            </span>
            <div>
              <span className="eyebrow">
                AGENTE {String(index + 1).padStart(2, "0")}
              </span>
              <h2>{agent.name}</h2>
              <p>{agent.responsibility}</p>
            </div>
            <div className="agent-foot">
              <Status value={agent.status} />
              <div className="agent-model"><strong>{agent.provider} · {agent.model}</strong><small>{agent.inherited ? "Configuración heredada" : "Configuración propia"}{agent.fallback ? ` · fallback ${agent.fallback}` : ""}</small><small title="Perfil técnico">Perfil: {agent.profile}</small></div>
            </div>
            {agent.diagnostic ? <div className="agent-diagnostic" role="status"><CircleAlert size={15} /><span><strong>{presentationText(agent.diagnostic.code)}</strong><small>{agent.diagnostic.message}</small></span></div> : null}
            <button className="agent-manage-button secondary" onClick={() => setSelected(agent)}><Settings2 size={15} />Ver identidad y operación</button>
          </article>
        ))}
      </div> : <Empty title="Sin agentes configurados" body="Todavía no hay agentes disponibles para mostrar." />}
      <AnimatePresence>{selected && <AgentSoulDrawer agent={selected} isAdmin={me?.user.role === "ADMIN"} close={() => setSelected(null)} />}</AnimatePresence>
    </>
  );
}
